import React, { useState, useEffect, useRef, useCallback, useMemo, createContext, useContext } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Helmet, HelmetProvider } from 'react-helmet-async';

// Register Service Worker for PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then((reg) => console.log('SW registered:', reg.scope))
      .catch((err) => console.log('SW registration failed:', err));
  });
}

// Configure axios to prevent caching of API requests
axios.interceptors.request.use((config) => {
  // Add cache-busting headers to prevent browser caching
  config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
  config.headers['Pragma'] = 'no-cache';
  config.headers['Expires'] = '0';
  return config;
});

// Enhanced SEO Component with comprehensive social media support
const SEO = ({ 
  title, 
  description, 
  keywords, 
  image, 
  url, 
  type = 'website',
  structuredData = null,
  twitterHandle,
  author 
}) => {
  const siteTitle = "Send2Maroc";
  const fullTitle = title ? `${title} | ${siteTitle}` : `${siteTitle} - 🚛 Europa → Marokko Transport | Gratis Registreren`;
  const defaultDescription = "🌍 Platform voor baggagetransport Europa-Marokko. Klanten vinden betrouwbare transportbedrijven. Transportbedrijven: 100% gratis registreren, onbeperkte opdrachten, verdien honderden per transport! 🇳🇱🇧🇪🇫🇷🇩🇪 → 🇲🇦";
  const defaultImage = "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=630&q=80";
  const baseUrl = window.location.origin;
  
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description || defaultDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      {author && <meta name="author" content={author} />}
      <link rel="canonical" href={url || baseUrl} />
      
      {/* Enhanced Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description || defaultDescription} />
      <meta property="og:image" content={image || defaultImage} />
      <meta property="og:image:alt" content={`${title || siteTitle} - Professional baggage transport service`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={url || baseUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:locale" content="nl_NL" />
      <meta property="og:locale:alternate" content="fr_FR" />
      <meta property="og:locale:alternate" content="ar_AR" />
      
      {/* Enhanced Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle || "@Send2Maroc"} />
      <meta name="twitter:creator" content={twitterHandle || "@Send2Maroc"} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description || defaultDescription} />
      <meta name="twitter:image" content={image || defaultImage} />
      <meta name="twitter:image:alt" content={`${title || siteTitle} - Baggage transport from Europe to Morocco`} />
      
      {/* LinkedIn specific */}
      <meta property="og:image:secure_url" content={image || defaultImage} />
      
      {/* WhatsApp optimization */}
      <meta property="og:image:type" content="image/jpeg" />
      
      {/* Apple iOS */}
      <meta name="apple-mobile-web-app-title" content={siteTitle} />
      <meta name="application-name" content={siteTitle} />
      
      {/* Theme colors for different platforms */}
      <meta name="theme-color" content="#3B82F6" />
      <meta name="msapplication-TileColor" content="#3B82F6" />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mobile Performance & Click Protection Utilities
const useClickProtection = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  
  const protectedClick = useCallback(async (action, delay = 1000) => {
    if (isProcessing) {
      console.log('🛡️ Click ignored - already processing');
      return;
    }
    
    setIsProcessing(true);
    try {
      await action();
    } catch (error) {
      console.error('Protected click error:', error);
    } finally {
      setTimeout(() => setIsProcessing(false), delay);
    }
  }, [isProcessing]);
  
  return { protectedClick, isProcessing };
};

// Debounce hook for search and input
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

// Logo hook for managing logo state throughout the app
const useLogo = () => {
  const [logo, setLogo] = useState(null);

  useEffect(() => {
    // Load logo from localStorage on mount
    const savedLogo = localStorage.getItem('send2maroc_logo');
    if (savedLogo) {
      setLogo(savedLogo);
    }

    // Listen for logo updates
    const handleLogoUpdate = (event) => {
      setLogo(event.detail);
    };

    window.addEventListener('logoUpdated', handleLogoUpdate);
    return () => window.removeEventListener('logoUpdated', handleLogoUpdate);
  }, []);

  return logo;
};

// Language translations
const translations = {
  nl: {
    title: "Send2Maroc",
    subtitle: "Verbind klanten met transportbedrijven voor persoonlijke bagage",
    loginTitle: "Inloggen",
    registerTitle: "Registreren",
    email: "E-mail",
    password: "Wachtwoord",
    fullName: "Volledige naam",
    companyName: "Bedrijfsnaam",
    countryOrigin: "Land van herkomst",
    userType: "Gebruikerstype",
    customer: "Klant",
    transportCompany: "Transportbedrijf",
    admin: "Beheerder",
    login: "Inloggen",
    register: "Registreren",
    switchToLogin: "Heeft u al een account? Inloggen",
    switchToRegister: "Heeft u nog geen account? Registreren",
    googleSignIn: "Inloggen met Google",
    orDivider: "Of",
    dashboard: "Dashboard",
    logout: "Uitloggen",
    rememberMe: "Ingelogd blijven",
    calcBadge: "Direct een prijsindicatie",
    calcTitle: "Bereken je transportkosten",
    calcSubtitle: "Vul in wat en waarheen je wilt versturen en zie meteen een schatting",
    calcPickup: "Ophaallocatie",
    calcPickupPlaceholder: "Bijv. Amsterdam",
    calcDestination: "Bestemming",
    calcDestinationPlaceholder: "Bijv. Casablanca",
    calcKilos: "Aantal kilo's",
    calcKilosPlaceholder: "Bijv. 25",
    calcEstimateLabel: "Geschatte transportkosten",
    calcPerKg: "per kilo",
    calcCta: "Boek nu - Log in",
    calcEnterDetails: "Vul ophaallocatie, bestemming en aantal kilo's in voor een prijsindicatie",
    calcDisclaimer: "Dit is een richtprijs. De transporteur bevestigt de definitieve prijs.",
    createRequest: "Nieuwe aanvraag",
    myRequests: "Mijn aanvragen",
    availableRequests: "Beschikbare aanvragen",
    myQuotes: "Mijn offertes",
    pickupCity: "Ophaalplaats",
    deliveryCity: "Afleverplaats",
    baggageSize: "Bagagegrootte",
    baggageWeight: "Bagagegewicht",
    preferredDate: "Gewenste datum",
    specialRequirements: "Bijzondere eisen",
    baggageImage: "Foto van bagage",
    uploadImage: "Upload foto",
    contactPhone: "Contacttelefoon",
    contactAddress: "Ophaaladres (optioneel)",
    submit: "Versturen",
    viewQuotes: "Offertes bekijken",
    submitQuote: "Offerte indienen",
    price: "Prijs",
    estimatedDays: "Geschatte dagen",
    message: "Bericht",
    accept: "Accepteren",
    reject: "Afwijzen",
    status: "Status",
    pending: "In afwachting",
    accepted: "Geaccepteerd",
    rejected: "Afgewezen",
    active: "Actief",
    completed: "Voltooid",
    delivered: "Afgeleverd",
    cancelled: "Geannuleerd",
    language: "Taal",
    allRequests: "Alle Verzoeken",
    dutch: "Nederlands",
    french: "Français",
    arabic: "العربية",
    homepage: "Startpagina",
    forCustomers: "Voor klanten",
    forCompanies: "Voor transportbedrijven",
    customerLogin: "Klant Inloggen",
    companyRegistration: "Bedrijf Registreren",
    homeDescription: "Verbind klanten in Europa met betrouwbare transportbedrijven voor het verzenden van persoonlijke bagage naar Marokko. Veilig, betrouwbaar en betaalbaar.",
    getStarted: "Aan de slag",
    howItWorks: "Hoe het werkt",
    step1: "Maak een aanvraag",
    step1Desc: "Beschrijf uw bagage en gewenste verzendlocaties",
    step2: "Ontvang offertes",
    step2Desc: "Transportbedrijven bieden hun services aan",
    step3: "Kies en verzend",
    step3Desc: "Selecteer het beste aanbod en verzend uw bagage",
    // Admin translations
    adminDashboard: "Beheerder Dashboard",
    websiteCustomization: "Website Aanpassing",
    logoManagement: "Logo Beheer",
    designSettings: "Design Instellingen",
    contentSettings: "Content Instellingen",
    colorTheme: "Kleurthema",
    heroImage: "Hero Afbeelding",
    featureImage: "Feature Afbeelding",
    siteTitle: "Site Titel",
    siteDescription: "Site Beschrijving",
    saveChanges: "Wijzigingen Opslaan",
    previewChanges: "Voorbeeld Bekijken",
    resetToDefault: "Reset naar Standaard",
    blueWhiteTheme: "Blauw & Wit",
    redOrangeTheme: "Rood & Oranje",
    greenBlackTheme: "Groen & Zwart",
    customColors: "Aangepaste Kleuren",
    primaryColor: "Primaire Kleur",
    secondaryColor: "Secundaire Kleur",
    uploadImage: "Afbeelding Uploaden",
    currentImage: "Huidige Afbeelding",
    websiteSettings: "Website Instellingen",
    // Company landing page translations
    companyPageTitle: "Vind transportopdrachten tussen Europa en Marokko",
    companyPageSubtitle: "Voor Transportbedrijven",
    companyIntro: "Ben jij een transporteur actief tussen Europa en Marokko? Send2Maroc brengt je rechtstreeks in contact met particuliere en zakelijke klanten die transport nodig hebben.",
    companyDailyOrders: "Ontvang dagelijks nieuwe opdrachten – zonder tussenpersonen.",
    companyBenefit1: "Geen dure advertenties nodig",
    companyBenefit2: "Werk met echte klanten",
    companyBenefit3: "Korte communicatielijnen, directe betaling",
    companyCTA: "Meld je vandaag nog aan, laat je profiel verifiëren en begin met rijden!",
    registerNow: "Nu Registreren",
    backToHome: "← Terug naar home",
    exportRules: "Export Regelgeving",
    exportRulesTitle: "Exportregels naar Marokko",
    exportRulesSubtitle: "Belangrijke informatie voor het exporteren van goederen naar Marokko",
    
    // Invoice translations
    invoice: "Factuur",
    invoiceNumber: "Factuurnummer",
    invoiceDate: "Datum",
    from: "Van",
    to: "Aan",
    description: "Omschrijving",
    amount: "Bedrag",
    total: "Totaal",
    transportService: "Transport Service",
    what: "Wat",
    pickupLocation: "Ophaallocatie",
    deliveryLocation: "Afleverlocatie",
    pickupDate: "Ophaaldatum",
    deliveryDate: "Afleverdatum",
    tracking: "Tracking",
    printDownloadPDF: "Print / Download PDF",
    close: "Sluiten",
    invoiceNotFound: "Factuur Niet Gevonden",
    invoiceNotFoundMessage: "Deze factuur bestaat niet of is niet beschikbaar.",
    thankYouMessage: "Bedankt voor uw vertrouwen in",
    questionsContact: "Bij vragen kunt u contact met ons opnemen.",
    whyChooseUs: "Waarom kiezen voor Send2Maroc?",
    whyChooseSubtitle: "Het platform dat transportbedrijven direct verbindt met klanten",
    whyChooseUsQuestion: "Waarom ons kiezen?",
    whyChooseTitle: "Waarom kiezen voor ons platform?",
    whyChooseDescription: "Ontdek waarom duizenden klanten ons vertrouwen voor hun baggagetransport tussen Europa en Marokko.",
    secureReliable: "Veilig & Betrouwbaar",
    secureReliableDesc: "Geverifieerde transportbedrijven en veilige transacties voor uw gemoedsrust. Elke transport wordt verzekerd en gevolgd.",
    multipleQuotes: "Meerdere Offertes", 
    multipleQuotesDesc: "Vergelijk prijzen van verschillende transportbedrijven en kies de beste deal. Transparante prijzen zonder verborgen kosten.",
    multilingualPlatform: "Meertalig Platform",
    multilingualPlatformDesc: "Ondersteunt Nederlands, Frans en Arabisch voor gemakkelijke communicatie met alle gebruikers en transportbedrijven.",
    footerCopyright: "© 2025 Send2Maroc. Alle rechten voorbehouden. Verbindt Europa met Marokko door betrouwbaar baggagetransport.",
    directPayment: "Directe Betaling",
    directPaymentDesc: "Geen lange wachttijden. Klanten betalen direct aan jou, zonder tussenpersonen die commissie vragen.",
    verifiedClients: "Geverifieerde Klanten",
    verifiedClientsDesc: "Alle klanten worden gescreend. Werk alleen met serieuze opdrachtgevers die echt transport nodig hebben.",
    dailyOrders: "Dagelijkse Opdrachten",
    dailyOrdersDesc: "Nieuwe vervoersverzoeken elke dag. Houd je vrachtwagen vol en je agenda gevuld met betrouwbare opdrachten.",
    forTransportCompanies: "Voor Transportbedrijven",
    directContact: "Direct Contact",
    clientsToYourBusiness: "Klanten → Jouw Bedrijf",
    noIntermediaries: "Geen tussenpersonen",
    alreadyAccount: "Al een account? Inloggen",
    // Pricing section
    pricingTitle: "Transparante Prijzen",
    pricingSubtitle: "Investeer klein, verdien groot",
    monthlyFee: "Gratis",
    exVat: "excl. BTW",
    pricingBenefit1: "Onbeperkte opdrachten ontvangen",
    pricingBenefit2: "Direct contact met klanten",
    pricingBenefit3: "Geen commissie op verdiende geld",
    pricingBenefit4: "Volledige controle over prijzen",
    earningsPotential: "Verdien potentieel honderden euro's per opdracht",
    pricingCTA: "Registreer gratis en ontvang direct toegang tot dagelijkse opdrachten. Geen commissie, geen verborgen kosten. 100% gratis!",
    // Dashboard translations
    customerPanel: "Klant Panel",
    transportPanel: "Transport Panel",
    manageRequests: "Beheer uw aanvragen",
    manageYourBusiness: "Beheer uw business",
    language: "Taal",
    // Registration form translations
    companyInformation: "Bedrijfsgegevens",
    contactInformation: "Contactgegevens", 
    transportInformation: "Transport Informatie",
    finalDetails: "Laatste Details",
    companyName: "Bedrijfsnaam",
    businessRegistration: "KvK Registratie",
    companyDescription: "Bedrijfsomschrijving",
    yearsExperience: "Jaren Ervaring",
    // Company approval translations
    companyApproval: "Bedrijf Goedkeuring",
    pendingCompanies: "Wachtende Bedrijven",
    allCompanies: "Alle Bedrijven",
    approveCompany: "Bedrijf Goedkeuren",
    rejectCompany: "Bedrijf Afwijzen",
    approved: "Goedgekeurd",
    rejected: "Afgewezen",
    pending: "In Afwachting",
    approvalStatus: "Goedkeuringsstatus",
    registrationDate: "Registratiedatum",
    companyDetails: "Bedrijfsdetails",
    approveConfirm: "Weet u zeker dat u dit bedrijf wilt goedkeuren?",
    rejectConfirm: "Weet u zeker dat u dit bedrijf wilt afwijzen?",
    rejectionReason: "Reden voor afwijzing",
    noCompanies: "Geen bedrijven gevonden",
    userManagement: "Gebruikersbeheer",
    platformStatistics: "Platform Statistieken",
    totalUsers: "Totaal Gebruikers",
    customers: "Klanten",
    transportCompanies: "Transportbedrijven",
    activeRequests: "Actieve Aanvragen",
    completedRequests: "Voltooide Aanvragen",
    pendingQuotes: "Wachtende Offertes",
    acceptedQuotes: "Geaccepteerde Offertes",
    recentActivity: "Recente Activiteit",
    editUser: "Gebruiker Bewerken",
    deleteUser: "Gebruiker Verwijderen",
    save: "Opslaan",
    cancel: "Annuleren",
    confirmDelete: "Weet je zeker dat je deze gebruiker wilt verwijderen?",
    // Chat translations
    chat: "Chat",
    startChat: "Chat Starten",
    sendMessage: "Bericht Versturen",
    typeMessage: "Typ je bericht...",
    send: "Versturen",
    chatWith: "Chat met",
    noMessages: "Nog geen berichten",
    conversations: "Gesprekken",
    newMessage: "Nieuw bericht",
    sendImage: "Afbeelding Versturen",
    imageMessage: "Afbeelding",
    lastMessage: "Laatste bericht",
    unreadMessages: "ongelezen berichten",
    deleteConversation: "Gesprek Verwijderen",
    confirmDeleteConversation: "Weet je zeker dat je dit gesprek wilt verwijderen? Alle berichten worden permanent gewist.",
    conversationDeleted: "Gesprek succesvol verwijderd",
    // Terms and Conditions translations
    acceptTerms: "Ik ga akkoord met de",
    termsAndConditions: "Algemene Voorwaarden",
    acceptAndClose: "Accepteren en Sluiten",
    close: "Sluiten",
    loginTermsMessage: "Door in te loggen accepteert u automatisch onze",
    generalConversation: "Algemeen gesprek",
    generalConversation: "Algemeen gesprek",
    // Tracking translations
    tracking: "Tracking",
    myProfile: "Mijn Profiel",
    profileAndStatus: "Profiel & Status",
    availableTransportRequests: "Beschikbare Transportaanvragen",
    cancel: "Annuleren",
    save: "Opslaan",
    priceList: "Prijslijst",
    loading: "Laden...",
    transporterNotFound: "Transporteur Niet Gevonden",
    bookingLinkInvalid: "Deze boekingslink is niet (meer) geldig.",
    loadMore: "Meer laden",
    previous: "Vorige",
    next: "Volgende",
    becomePartner: "Word Partner",
    startTodayReceiveRequests: "Start vandaag nog en ontvang direct transportaanvragen",
    companyName: "Bedrijfsnaam",
    yourCompanyName: "Uw bedrijfsnaam",
    phoneNumber: "Telefoonnummer",
    country: "Land",
    password: "Wachtwoord",
    minCharacters: "Minimaal 8 karakters",
    confirmPassword: "Bevestig Wachtwoord",
    repeatPassword: "Herhaal wachtwoord",
    goToPayment: "Doorgaan naar Betaling",
    monthlySubscription: "Gratis account",
    exclVAT: "excl. BTW",
    inclVAT: "incl. 21% BTW",
    benefitsForPartners: "Voordelen voor partners:",
    directAccessRequests: "Direct toegang tot nieuwe transportaanvragen",
    chatDirectly: "Chat direct met klanten",
    monthlySubscriptionAmount: "Gratis registreren - geen maandelijkse kosten",
    earnExtra: "Verdien tot €500-1000 per maand extra",
    freeRegister: "Gratis Registreren",
    registeringText: "Registreren...",
    freeRegisterBadge: "100% Gratis Registreren",
    freeRegisterDesc: "Geen abonnement, geen commissie, geen verborgen kosten. 100% gratis!",
    registrationSuccessAlert: "✅ Registratie succesvol! Uw account wordt beoordeeld door onze admin. U ontvangt bericht zodra uw account is goedgekeurd.",
    registrationErrorPrefix: "Fout: ",
    registrationFailed: "Registratie mislukt",
    registrationErrorGeneral: "Er ging iets mis bij de registratie: ",
    inviteCodeOptional: "Uitnodigingscode (optioneel)",
    inviteCodeDesc: "Heeft u een uitnodigingscode ontvangen? Vul deze in voor een snelle registratie.",
    emailPlaceholder: "info@uwbedrijf.nl",
    // Services & About page keys
    ourServices: "Onze Diensten",
    reliableTransport: "Betrouwbaar baggagetransport van Europa naar Marokko",
    personalBaggage: "Persoonlijke Bagage",
    personalBaggageDesc: "Verzend uw persoonlijke spullen veilig naar familie en vrienden in Marokko.",
    businessGoods: "Zakelijke Goederen",
    businessGoodsDesc: "Professioneel transport voor zakelijke zendingen en handelsgoederen.",
    documents: "Documenten",
    documentsDesc: "Veilige verzending van belangrijke documenten en papieren.",
    whySend2Maroc: "Waarom Send2Maroc?",
    hundredPercentSafe: "100% Veilig",
    verifiedCompanies: "Alle transportbedrijven zijn geverifieerd en verzekerd",
    bestPrices: "Beste Prijzen",
    compareQuotes: "Vergelijk offertes en kies de beste deal",
    fastReliable: "Snel & Betrouwbaar",
    averageDelivery: "Gemiddeld 5-7 dagen van Europa naar Marokko",
    startNow: "Nu Beginnen",
    // PWA Install
    iosInstallHint: "Tik op 'Deel' en dan 'Zet op beginscherm' om de app te installeren",
    installAppHint: "Installeer de Send2Maroc app voor snelle toegang",
    installText: "Installeren",
    laterText: "Later",
    alreadyAccount: "Al een account?",
    loginHere: "Log hier in",
    firstMonthFree: "100% Gratis!",
    thenMonthly: "Geen commissie, 100% gratis",
    enterCompanyName: "Voer uw bedrijfsnaam in",
    enterValidEmail: "Voer een geldig email adres in",
    enterPhone: "Voer uw telefoonnummer in",
    passwordMin8: "Wachtwoord moet minimaal 8 karakters zijn",
    passwordsNotMatch: "Wachtwoorden komen niet overeen",
    paymentCouldNotStart: "Betaling kon niet worden gestart",
    goingToPayment: "Naar betaling...",
    submitQuote: "Offerte Indienen",
    quote: "Offerte",
    quoteDetails: "Offerte Details",
    clearAllFilters: "Wis Alle Filters",
    showFilters: "Toon Filters",
    hideFilters: "Verberg Filters",
    departureCity: "Vertrek Stad",
    typeCityName: "Typ stad naam...",
    requestQuote: "Offerte Aanvragen",
    price: "Prijs",
    contact: "Contact",
    aboutUs: "Over Ons",
    services: "Diensten",
    coverage: "Werkgebied",
    termsConditions: "Voorwaarden",
    blogNews: "Blog & Nieuws",
    status: "Status",
    pickup: "Ophalen",
    delivery: "Levering",
    remarks: "Opmerking",
    estimatedDelivery: "Geschatte levering",
    days: "dagen",
    trackingUpdates: "Tracking Updates",
    pickedUp: "Opgehaald!",
    updateStatus: "Status Bijwerken",
    shipmentStatus: "Verzending Status",
    trackingHistory: "Tracking Geschiedenis",
    currentStatus: "Huidige Status",
    lastUpdate: "Laatste Update",
    addUpdate: "Update Toevoegen",
    statusPending: "Wachtend",
    statusPickedUp: "Opgehaald",
    statusInTransit: "Onderweg",
    statusAtCustoms: "Bij de Douane",
    statusOutForDelivery: "Uit voor Bezorging",
    statusDelivered: "Bezorgd",
    location: "Locatie",
    notes: "Notities",
    updateTime: "Update Tijd",
    noTrackingYet: "Nog geen tracking updates",
    // New tracking translations
    companyStatus: "Bedrijfsstatus",
    generalUpdates: "Algemene Updates",
    pickupConfirmed: "Ophaling Bevestigd",
    updateCompanyStatus: "Bedrijfsstatus Bijwerken",
    statusMessage: "Status Bericht",
    currentLocation: "Huidige Locatie",
    messageForCustomers: "Bericht voor alle klanten",
    pickupLocation: "Ophaallocatie",
    pickupNotes: "Ophaal notities",
    // My Offers translations
    myOffers: "Mijn Offertes",
    acceptedOffers: "Geaccepteerde Offertes",
    removeRequest: "Verwijder Aanvraag",
    confirmRemoveRequest: "Weet je zeker dat je deze afgeleverde aanvraag wilt verwijderen?",
    requestRemoved: "Aanvraag succesvol verwijderd",
    viewRequestDetails: "Bekijk Aanvraag Details",
    offerDetails: "Offerte Details",
    requestInfo: "Aanvraag Informatie",
    customerInfo: "Klant Informatie",
    viewFullRequest: "Bekijk Volledige Aanvraag",
    // Company directory translations
    availableCompanies: "Beschikbare Bedrijven",
    companyDirectory: "Bedrijven Directory",
    viewProfile: "Bekijk Profiel",
    contactCompany: "Contact Bedrijf",
    companyProfile: "Bedrijf Profiel",
    companyDescription: "Bedrijfsbeschrijving",
    priceList: "Prijslijst",
    phoneNumber: "Telefoonnummer",
    currentLocation: "Huidige Locatie",
    departureDates: "Vertrekdata",
    toMorocco: "Naar Marokko",
    toEurope: "Naar Europa",
    reviews: "Reviews",
    averageRating: "Gemiddelde Beoordeling",
    totalReviews: "Totaal Reviews",
    writeReview: "Schrijf Review",
    rateCompany: "Beoordeel Bedrijf",
    reviewText: "Review Tekst",
    rating: "Beoordeling",
    stars: "sterren",
    submitReview: "Verstuur Review",
    noCompanies: "Geen bedrijven beschikbaar",
    noReviews: "Nog geen reviews",
    profilePicture: "Profielfoto",
    updateProfile: "Profiel Bijwerken",
    updateStatus: "Status Bijwerken",
    noPriceList: "Geen prijslijst beschikbaar",
    noDescription: "Geen beschrijving beschikbaar",
    contactInfo: "Contactinformatie",
    optional: "optioneel",
    close: "Sluiten",
    onlineStatus: "Online Status",
    goOnline: "Online Gaan",
    goOffline: "Offline Gaan",
    currentlyOnline: "Momenteel Online",
    currentlyOffline: "Momenteel Offline",
    languages: "Talen",
    communicationLanguages: "Communicatietalen",
    selectLanguages: "Selecteer Talen",
    dutch: "Nederlands",
    french: "Frans",
    arabic: "Arabisch",
    english: "Engels",
    german: "Duits",
    spanish: "Spaans",
    clearTracking: "Tracking Wissen",
    clearTrackingConfirm: "Weet je zeker dat je alle tracking updates wilt wissen?",
    trackingCleared: "Tracking succesvol gewist",
    // Search and filter translations
    searchCompanies: "Zoek Bedrijven",
    searchPlaceholder: "Zoek op bedrijfsnaam of locatie...",
    filterBy: "Filter Op",
    showFilters: "Toon Filters",
    hideFilters: "Verberg Filters",
    allLanguages: "Alle Talen",
    allCountries: "Alle Landen",
    countryFilter: "Land van Herkomst",
    departureCountryFilter: "Vertrek Land",
    departureDateFilter: "Vertrekdatum naar Marokko",
    selectDate: "Selecteer Datum",
    selectCountry: "Selecteer Land",
    clearFilters: "Filters Wissen",
    companiesFound: "bedrijven gevonden",
    // Country translations
    netherlands: "Nederland",
    belgium: "België", 
    germany: "Duitsland",
    france: "Frankrijk",
    spain: "Spanje",
    italy: "Italië",
    portugal: "Portugal",
    austria: "Oostenrijk",
    switzerland: "Zwitserland",
    luxembourg: "Luxemburg",
    denmark: "Denemarken",
    sweden: "Zweden",
    norway: "Noorwegen",
    finland: "Finland",
    poland: "Polen",
    czech_republic: "Tsjechië",
    hungary: "Hongarije",
    morocco: "Marokko",
    algeria: "Algerije",
    tunisia: "Tunesië",
    egypt: "Egypte",
    united_kingdom: "Verenigd Koninkrijk",
    ireland: "Ierland",
    not_specified: "Niet opgegeven",
    other: "Anders",
    // Welcome Screen translations
    welcomeBack: "Welkom terug",
    welcomeAdmin: "Welkom Admin! Beheer het platform en bekijk statistieken.",
    welcomeCustomer: "Welkom! Verstuur uw bagage eenvoudig naar Marokko.",
    welcomeTransport: "Welkom transportbedrijf! Vind nieuwe klanten en bouw uw business uit.",
    brandName: "BaggageTransport",
    brandSubtitle: "Europa → Marokko",
    europeToMorocco: "Europa → Marokko",
    reliableConnection: "Betrouwbare verbinding",
    europeToMoroccoDesc: "Veilig en snel transport tussen Europa en Marokko via ons netwerk van vertrouwde transporteurs.",
    realTimeTracking: "Real-time Tracking",
    alwaysUpToDate: "Altijd up-to-date",
    realTimeTrackingDesc: "Volg uw bagage in real-time met onze geavanceerde tracking systeem en blijf altijd op de hoogte.",
    securePlatform: "Veilig Platform",
    hundredPercentSecure: "100% beveiligd",
    securePlatformDesc: "Uw gegevens en transacties zijn volledig beveiligd met de nieuwste beveiligingstechnologieën.",
    whereToGo: "Waar wilt u naartoe?",
    adminDashboardTitle: "Admin Dashboard",
    adminDashboardDesc: "Gebruikers beheren en statistieken bekijken",
    myDashboardTitle: "Mijn Dashboard",
    myDashboardDesc: "Beheer uw vervoerverzoeken en track uw bagage",
    transportDashboardTitle: "Transportbedrijf Dashboard",
    transportDashboardDesc: "Bekijk beschikbare verzoeken en beheer uw offertes",
    goToDashboard: "Ga naar dashboard",
    trustedPartner: "Uw betrouwbare partner voor internationale bagagevervoer",
    // Login page
    loginSubtitle: "Log in om uw transportaanvragen te beheren",
    registerSubtitle: "Registreer om transportaanvragen te plaatsen",
    forgotPasswordQuestion: "Wachtwoord vergeten?",
    registerAsCustomerLink: "Registreer als klant",
    backToLoginLink: "Terug naar inloggen",
    // Sidebar menu subtitles
    newTransportOpportunities: "Nieuwe transportkansen",
    acceptedJobs: "Geaccepteerde opdrachten",
    bookingsFromMyCustomers: "Boekingen van mijn klanten",
    chatWithCustomers: "Chat met klanten",
    statusUpdates: "Status updates",
    conversations: "Gesprekken",
    // Dashboard header subtitles
    viewNewOpportunities: "Bekijk nieuwe transportkansen en dien offertes in",
    manageAcceptedJobs: "Beheer uw geaccepteerde opdrachten en leveringen",
    bookAndManageCustomers: "Boek en beheer zendingen van uw vaste klanten",
    communicateDirectly: "Communiceer direct met uw klanten",
    updateShipmentStatus: "Update de status van uw zendingen",
    manageBusinessProfile: "Beheer uw bedrijfsprofiel en status",
    transportDashboard: "Transport Dashboard",
    transportCompanyLabel: "Transportbedrijf",
    // Search and pagination
    searchCityCustomer: "Zoek op stad, klant naam...",
    ofRequests: "van",
    requests: "aanvragen",
    results: "resultaten",
    showing: "Toont",
    of: "van",
    // Alert messages
    successStatusChanged: "Status succesvol gewijzigd!",
    successCompanyStatusUpdated: "Bedrijfsstatus succesvol bijgewerkt!",
    errorUpdatingCompanyStatus: "Fout bij het bijwerken van bedrijfsstatus",
    updatesCleared: "updates gewist",
    successProfileUpdated: "Profiel succesvol bijgewerkt!",
    errorUpdatingProfile: "Fout bij het bijwerken van profiel",
    successStatusUpdated: "Status succesvol bijgewerkt!",
    errorUpdatingStatus: "Fout bij het bijwerken van status",
    successStatusUpdatedTo: "Status succesvol bijgewerkt naar",
    errorUpdatingOnlineStatus: "Fout bij het bijwerken van online status",
    errorClearingTracking: "Fout bij het wissen van tracking",
    // Form labels
    noRequestsAvailable: "Geen beschikbare aanvragen",
    noRequestsFound: "Geen aanvragen gevonden",
    noBookingsYet: "Nog geen boekingen",
    createFirstBooking: "Maak uw eerste boeking aan",
    noConversations: "Geen gesprekken",
    selectConversation: "Selecteer een gesprek om te beginnen",
    // More alerts
    bookingSuccessfullyCreated: "Boeking succesvol aangemaakt!",
    errorCreatingBooking: "Fout bij aanmaken boeking. Probeer opnieuw.",
    errorUpdatingBookingStatus: "Fout bij updaten status. Probeer opnieuw.",
    enterValidPrice: "Voer een geldige prijs in",
    priceSuccessfullyAdded: "Prijs succesvol toegevoegd!",
    errorUpdatingPrice: "Fout bij updaten prijs. Probeer opnieuw.",
    confirmDeleteBooking: "Weet u zeker dat u deze boeking wilt verwijderen?",
    bookingDeleted: "Boeking succesvol verwijderd!",
    errorDeletingBooking: "Fout bij verwijderen boeking",
    // More alerts for transport company
    errorConfirmingPickup: "Fout bij het bevestigen van ophaling",
    confirmDeliveryQuestion: "Bevestig je dat je de bagage hebt afgeleverd?",
    deliveryConfirmedByCompany: "Levering door bedrijf bevestigd! Wacht op bevestiging van de klant.",
    errorConfirmingDelivery: "Fout bij het bevestigen van levering",
    // Missing keys
    awaitingConfirmation: "In afwachting van bevestiging",
    bookMyShipment: "Boek mijn zending",
    bookingInProgress: "Boeking in behandeling",
    bookingSuccessful: "Boeking succesvol",
    bookingsFromOwnCustomers: "Boekingen van mijn klanten",
    clickToUpload: "Klik om te uploaden",
    companyStatus: "Bedrijfsstatus",
    confirm: "Bevestigen",
    confirmed: "Bevestigd",
    delete: "Verwijderen",
    discoverSend2Maroc: "Ontdek Send2Maroc",
    dragPhotosHere: "Sleep foto's hierheen",
    europe: "Europa",
    forCustomersDesc: "Verstuur eenvoudig uw bagage naar Marokko met betrouwbare vervoerders",
    forCustomersTitle: "Voor Klanten",
    forTransportCompaniesDesc: "Vind nieuwe klanten en laat uw transportbedrijf groeien",
    forTransportCompaniesTitle: "Voor Transportbedrijven",
    howItWorksDesc: "Ontdek hoe Send2Maroc werkt",
    howItWorksTitle: "Hoe werkt het",
    howItWorksButton: "Zo werkt het",
    trackAndTrace: "Track & Trace",
    trackYourShipment: "Volg je zending",
    enterTrackingCode: "Voer je tracking code in",
    search: "Zoeken",
    back: "Terug",
    inTransit: "Onderweg",
    menu: "Menu",
    newBooking: "Nieuwe boeking",
    ourServicesDesc: "Betrouwbare transportdiensten tussen Europa en Marokko",
    ourServicesTitle: "Onze Diensten",
    ownCustomers: "Mijn klanten",
    photosRemaining: "foto's over",
    pickupConfirmed: "Ophaling bevestigd",
    receiveQuotes: "Ontvang offertes",
    requestTransport: "Vraag transport aan",
    saveTrackingCode: "Bewaar trackingcode",
    trackMyShipment: "Volg mijn zending",
    updateCompanyStatus: "Status bijwerken",
    // Form fields
    baggage_description: "Beschrijving bagage",
    delivery_address: "Afleveradres",
    delivery_city: "Afleverstad",
    pickup_address: "Ophaaladres",
    pickup_city: "Ophaalstad",
    pickup_date: "Ophaaldatum",
    special_instructions: "Speciale instructies",
    weight: "Gewicht",
    ownCustomersDescription: "Uw klanten blijven uw klanten. Boek en beheer hun zendingen hier."
  },
  en: {
    title: "Send2Maroc",
    subtitle: "Connect customers with transport companies for personal baggage",
    loginTitle: "Login",
    registerTitle: "Register",
    email: "Email",
    password: "Password",
    fullName: "Full name",
    companyName: "Company name",
    countryOrigin: "Country of origin",
    userType: "User type",
    customer: "Customer",
    transportCompany: "Transport company",
    admin: "Administrator",
    login: "Login",
    register: "Register",
    switchToLogin: "Already have an account? Login",
    switchToRegister: "Don't have an account? Register",
    googleSignIn: "Sign in with Google",
    orDivider: "Or",
    dashboard: "Dashboard",
    logout: "Logout",
    rememberMe: "Keep me logged in",
    calcBadge: "Instant price estimate",
    calcTitle: "Calculate your transport cost",
    calcSubtitle: "Enter what and where you want to ship and see an estimate instantly",
    calcPickup: "Pickup location",
    calcPickupPlaceholder: "E.g. Amsterdam",
    calcDestination: "Destination",
    calcDestinationPlaceholder: "E.g. Casablanca",
    calcKilos: "Number of kilos",
    calcKilosPlaceholder: "E.g. 25",
    calcEstimateLabel: "Estimated transport cost",
    calcPerKg: "per kilo",
    calcCta: "Book now - Log in",
    calcEnterDetails: "Enter pickup location, destination and number of kilos for a price estimate",
    calcDisclaimer: "This is an indicative price. The transporter confirms the final price.",
    createRequest: "New request",
    myRequests: "My requests",
    availableRequests: "Available requests",
    myQuotes: "My quotes",
    pickupCity: "Pickup city",
    deliveryCity: "Delivery city",
    baggageSize: "Baggage size",
    baggageWeight: "Baggage weight",
    preferredDate: "Preferred date",
    specialRequirements: "Special requirements",
    baggageImage: "Baggage photo",
    uploadImage: "Upload photo",
    contactPhone: "Contact phone",
    contactAddress: "Pickup address (optional)",
    submit: "Submit",
    viewQuotes: "View quotes",
    submitQuote: "Submit quote",
    price: "Price",
    estimatedDays: "Estimated days",
    message: "Message",
    accept: "Accept",
    reject: "Reject",
    status: "Status",
    pending: "Pending",
    accepted: "Accepted",
    rejected: "Rejected",
    active: "Active",
    completed: "Completed",
    delivered: "Delivered",
    cancelled: "Cancelled",
    language: "Language",
    allRequests: "All Requests",
    dutch: "Dutch",
    french: "French",
    arabic: "Arabic",
    english: "English",
    homepage: "Homepage",
    forCustomers: "For customers",
    forCompanies: "For transport companies",
    customerLogin: "Customer Login",
    companyRegistration: "Company Registration",
    homeDescription: "Connect customers in Europe with reliable transport companies for shipping personal baggage to Morocco. Safe, reliable and affordable.",
    getStarted: "Get Started",
    howItWorks: "How it works",
    step1: "Create a request",
    step1Desc: "Describe your baggage and desired shipping locations",
    step2: "Receive quotes",
    step2Desc: "Transport companies offer their services",
    step3: "Choose and ship",
    step3Desc: "Select the best offer and ship your baggage",
    adminDashboard: "Administrator Dashboard",
    websiteCustomization: "Website Customization",
    logoManagement: "Logo Management",
    designSettings: "Design Settings",
    contentSettings: "Content Settings",
    colorTheme: "Color Theme",
    heroImage: "Hero Image",
    featureImage: "Feature Image",
    siteTitle: "Site Title",
    siteDescription: "Site Description",
    saveChanges: "Save Changes",
    previewChanges: "Preview Changes",
    resetToDefault: "Reset to Default",
    blueWhiteTheme: "Blue & White",
    redOrangeTheme: "Red & Orange",
    greenBlackTheme: "Green & Black",
    customColors: "Custom Colors",
    primaryColor: "Primary Color",
    secondaryColor: "Secondary Color",
    currentImage: "Current Image",
    websiteSettings: "Website Settings",
    companyPageTitle: "Find transport jobs between Europe and Morocco",
    companyPageSubtitle: "Connect with customers, receive requests, and grow your transport business",
    monthlySubscription: "Free account",
    perMonth: "per month",
    unlimitedJobs: "Unlimited jobs",
    directCustomerContact: "Direct customer contact",
    noCommission: "No commission",
    simplePricing: "Simple pricing",
    registerNow: "Register Now",
    whyChooseUs: "Why choose us?",
    benefit1Title: "No commission",
    benefit1Desc: "You keep 100% of your earnings",
    benefit2Title: "Direct contact",
    benefit2Desc: "Communicate directly with customers",
    benefit3Title: "Verified customers",
    benefit3Desc: "Serious requests from verified customers",
    benefit4Title: "Simple system",
    benefit4Desc: "Easy to use, anytime, anywhere",
    benefitGrowTitle: "Grow your business",
    benefitGrowDesc: "Reach new customers throughout Europe",
    benefitFlexibleTitle: "Flexible",
    benefitFlexibleDesc: "Choose which jobs you accept",
    frequentRoutes: "Frequent routes",
    readyToGrow: "Ready to grow your transport business?",
    joinNow: "Join now and start receiving jobs from day one.",
    netherlands: "Netherlands",
    belgium: "Belgium",
    france: "France",
    germany: "Germany",
    spain: "Spain",
    morocco: "Morocco",
    forgotPassword: "Forgot password?",
    resetPassword: "Reset password",
    sendResetLink: "Send reset link",
    backToLogin: "Back to login",
    resetEmailSent: "Password reset link sent to your email",
    invalidEmail: "Invalid email address",
    passwordResetSuccess: "Password successfully reset",
    confirmPassword: "Confirm password",
    repeatPassword: "Repeat password",
    passwordsNotMatch: "Passwords do not match",
    passwordMin8: "Password must be at least 8 characters",
    enterValidEmail: "Enter a valid email address",
    enterCompanyName: "Enter company name",
    enterPhone: "Enter phone number",
    goToPayment: "Continue to Payment",
    goingToPayment: "Going to payment...",
    paymentCouldNotStart: "Payment could not be started",
    termsOfService: "Terms of Service",
    privacyPolicy: "Privacy Policy",
    acceptTerms: "I accept the terms and conditions",
    cookieNotice: "Cookie Notice",
    acceptCookies: "Accept Cookies",
    declineCookies: "Decline",
    cookieText: "We use cookies to improve your experience",
    aboutUs: "About Us",
    contactUs: "Contact Us",
    ourServices: "Our Services",
    coverageAreas: "Coverage Areas",
    blogNews: "Blog & News",
    faq: "FAQ",
    support: "Support",
    searchPlaceholder: "Search...",
    noResultsFound: "No results found",
    loading: "Loading...",
    error: "Error",
    success: "Success",
    warning: "Warning",
    info: "Information",
    close: "Close",
    cancel: "Cancel",
    save: "Save",
    edit: "Edit",
    delete: "Delete",
    confirm: "Confirm",
    yes: "Yes",
    no: "No",
    back: "Back",
    next: "Next",
    previous: "Previous",
    finish: "Finish",
    refresh: "Refresh",
    download: "Download",
    upload: "Upload",
    share: "Share",
    copy: "Copy",
    print: "Print",
    exportRules: "Export Rules",
    sweden: "Sweden",
    statistics: "Statistics",
    users: "Users",
    companies: "Companies",
    requests: "Requests",
    quotes: "Quotes",
    revenue: "Revenue",
    growth: "Growth",
    today: "Today",
    thisWeek: "This week",
    thisMonth: "This month",
    thisYear: "This year",
    allTime: "All time",
    filter: "Filter",
    sort: "Sort",
    ascending: "Ascending",
    descending: "Descending",
    date: "Date",
    name: "Name",
    amount: "Amount",
    from: "From",
    to: "To",
    route: "Route",
    tracking: "Tracking",
    invoice: "Invoice",
    payment: "Payment",
    paid: "Paid",
    unpaid: "Unpaid",
    overdue: "Overdue",
    total: "Total",
    subtotal: "Subtotal",
    tax: "Tax",
    discount: "Discount",
    chat: "Chat",
    sendMessage: "Send message",
    typeMessage: "Type a message...",
    online: "Online",
    offline: "Offline",
    lastSeen: "Last seen",
    notifications: "Notifications",
    markAsRead: "Mark as read",
    markAllAsRead: "Mark all as read",
    clearAll: "Clear all",
    settings: "Settings",
    profile: "Profile",
    account: "Account",
    security: "Security",
    preferences: "Preferences",
    help: "Help",
    documentation: "Documentation",
    feedback: "Feedback",
    reportBug: "Report a bug",
    requestFeature: "Request a feature",
    version: "Version",
    copyright: "Copyright",
    allRightsReserved: "All rights reserved",
    myDashboardTitle: "My Dashboard",
    myDashboardDesc: "Manage your transport requests and track your baggage",
    transportDashboardTitle: "Transport Company Dashboard",
    transportDashboardDesc: "View available requests and manage your quotes",
    goToDashboard: "Go to dashboard",
    trustedPartner: "Your trusted partner for international baggage transport",
    europeToMorocco: "Europe → Morocco",
    europeToMoroccoDesc: "Safe and fast transport between Europe and Morocco through our network of trusted transporters.",
    brandName: "Send2Maroc",
    brandSubtitle: "Baggage Transport Europe-Morocco",
    welcomeBack: "Welcome back",
    welcomeAdmin: "Administrator Dashboard - Manage users, requests and website settings",
    welcomeCustomer: "View your requests and track your shipments",
    welcomeTransport: "View available requests and manage your quotes",
    realTimeTracking: "Real-time Tracking",
    alwaysUpToDate: "Always up to date",
    realTimeTrackingDesc: "Track your shipment in real-time from pickup to delivery",
    securePlatform: "Secure Platform",
    hundredPercentSecure: "100% Secure",
    securePlatformDesc: "Your data and payments are fully secured",
    reliableConnection: "Reliable connection",
    whereToGo: "Where would you like to go?",
    adminDashboardTitle: "Admin Dashboard",
    adminDashboardDesc: "Manage users, companies and website settings",
    customerDashboardTitle: "Customer Dashboard",
    customerDashboardDesc: "Create requests and track your shipments",
    backToHome: "Back to home",
    backToHomepage: "Back to homepage",
    allLanguages: "All languages",
    german: "German",
    spanish: "Spanish",
    ownCustomers: "Own Customers",
    newBooking: "New Booking",
    shareBookingLink: "Share Booking Link",
    confirmBooking: "Confirm Booking",
    pickupLocation: "Pickup Location",
    deliveryLocation: "Delivery Location",
    pickupDate: "Pickup Date",
    deliveryDate: "Delivery Date",
    agreedPrice: "Agreed Price",
    addPrice: "Add Price",
    editPrice: "Edit Price",
    trackingCode: "Tracking Code",
    statusHistory: "Status History",
    baggagePhotos: "Baggage Photos",
    shareTracking: "Share Tracking",
    shareInvoice: "Share Invoice",
    viewInvoice: "View Invoice",
    chatViaWhatsApp: "Chat via WhatsApp",
    deleteBooking: "Delete Booking",
    bookingDetails: "Booking Details",
    customerName: "Customer Name",
    phoneNumber: "Phone Number",
    description: "Description",
    awaitingConfirmation: "Awaiting Confirmation",
    confirmed: "Confirmed",
    pickedUp: "Picked Up",
    inTransit: "In Transit",
    arrivedMorocco: "Arrived in Morocco",
    inviteCode: "Invite Code",
    inviteCodeOptional: "Invite Code (optional)",
    inviteCodeDesc: "Have you received an invite code? Enter it to register directly without payment.",
    freeRegistration: "Free Registration",
    accountPendingApproval: "Your account is awaiting approval by the administrator. You will receive an email once your account is approved.",
    accountRejected: "Your account has unfortunately not been approved. Please contact support for more information.",
    trackYourShipment: "Track Your Shipment",
    enterTrackingCode: "Enter your tracking code",
    search: "Search",
    notFound: "Not Found",
    trackingCodeNotFound: "Tracking code not found. Please check the code and try again.",
    shipmentDetails: "Shipment Details",
    transporter: "Transporter",
    expectedDelivery: "Expected Delivery",
    registerAsCustomer: "Register as customer",
    registerAsCompany: "Register as company",
    quickActions: "Quick Actions",
    recentActivity: "Recent Activity",
    viewAll: "View all",
    noRequests: "No requests yet",
    createFirstRequest: "Create your first request",
    availableJobs: "Available Jobs",
    activeShipments: "Active Shipments",
    completedShipments: "Completed Shipments",
    pendingQuotes: "Pending Quotes",
    acceptedQuotes: "Accepted Quotes",
    rejectedQuotes: "Rejected Quotes",
    totalEarnings: "Total Earnings",
    thisMonthEarnings: "This Month",
    pendingPayments: "Pending Payments",
    newRequest: "New Request",
    sendRequest: "Send Request",
    requestDetails: "Request Details",
    quoteDetails: "Quote Details",
    submitYourQuote: "Submit Your Quote",
    yourPrice: "Your Price",
    deliveryTime: "Delivery Time",
    days: "days",
    additionalNotes: "Additional Notes",
    optional: "optional",
    trackMyShipment: "Track My Shipment",
    bookMyShipment: "Book My Shipment",
    bookingInProgress: "Booking in progress...",
    bookingsFromOwnCustomers: "Bookings from Own Customers",
    requestTransport: "Request Transport",
    receiveQuotes: "Receive Quotes",
    selectAndShip: "Select and Ship",
    viewProfile: "View Profile",
    editProfile: "Edit Profile",
    myProfile: "My Profile",
    profileSettings: "Profile Settings",
    accountSettings: "Account Settings",
    changePassword: "Change Password",
    currentPassword: "Current Password",
    newPassword: "New Password",
    confirmNewPassword: "Confirm New Password",
    updatePassword: "Update Password",
    profileUpdated: "Profile updated successfully",
    passwordChanged: "Password changed successfully",
    activeRequests: "Active Requests",
    completedRequests: "Completed Requests",
    multipleQuotes: "Multiple Quotes",
    availableTransportRequests: "Available Transport Requests",
    requestQuote: "Request Quote",
    myOffers: "My Offers",
    acceptedOffers: "Accepted Offers",
    availableCompanies: "Available Companies",
    noQuotesYet: "No quotes yet",
    waitingForQuotes: "Waiting for quotes from transport companies",
    quotesReceived: "quotes received",
    selectBestOffer: "Select the best offer",
    viewAndAccept: "View and accept",
    transportCompanies: "Transport Companies",
    verifiedCompanies: "Verified Companies",
    findTransporter: "Find Transporter",
    ourServices: "Our Services",
    howItWorksTitle: "How It Works",
    step1Title: "Create Request",
    step1Description: "Describe your baggage and select pickup and delivery locations",
    step2Title: "Receive Quotes",
    step2Description: "Transport companies will send you competitive quotes",
    step3Title: "Select and Ship",
    step3Description: "Choose the best offer and ship your baggage safely",
    whyChooseSend2Maroc: "Why Choose Send2Maroc?",
    trustedTransporters: "Trusted Transporters",
    trustedTransportersDesc: "All our transport partners are verified and reliable",
    competitivePrices: "Competitive Prices",
    competitivePricesDesc: "Compare quotes and choose the best price",
    easyTracking: "Easy Tracking",
    easyTrackingDesc: "Track your shipment from pickup to delivery",
    customerSupport: "Customer Support",
    customerSupportDesc: "We are here to help you every step of the way",
    getStartedToday: "Get Started Today",
    joinThousands: "Join thousands of satisfied customers",
    startShipping: "Start Shipping",
    learnMore: "Learn More",
    contactInformation: "Contact Information",
    businessHours: "Business Hours",
    mondayFriday: "Monday - Friday",
    saturday: "Saturday",
    sunday: "Sunday",
    closed: "Closed",
    sendUsMessage: "Send Us a Message",
    yourName: "Your Name",
    yourEmail: "Your Email",
    subject: "Subject",
    yourMessage: "Your Message",
    sendMessage: "Send Message",
    messageSent: "Message sent successfully",
    thankYouContact: "Thank you for contacting us. We will get back to you soon.",
    frequentlyAskedQuestions: "Frequently Asked Questions",
    faqQuestion1: "How long does shipping take?",
    faqAnswer1: "Shipping typically takes 3-7 business days depending on the route and transport company.",
    faqQuestion2: "How do I track my shipment?",
    faqAnswer2: "You can track your shipment using the tracking code provided by the transport company.",
    faqQuestion3: "What items can I ship?",
    faqAnswer3: "Personal baggage, household items, and goods that comply with customs regulations.",
    faqQuestion4: "How do I pay?",
    faqAnswer4: "Payment is arranged directly with the transport company after accepting their quote.",
    newTransportOpportunities: "New transport opportunities",
    acceptedJobs: "Accepted jobs",
    bookingsFromMyCustomers: "Bookings from my customers",
    chatWithCustomers: "Chat with customers",
    profileAndStatus: "Profile & Status",
    conversations: "Conversations",
    statusUpdates: "Status updates",
    loginSubtitle: "Log in to manage your transport requests",
    registerSubtitle: "Register to place transport requests",
    loginTermsMessage: "By logging in you automatically accept our",
    termsAndConditions: "Terms and Conditions",
    forgotPasswordQuestion: "Forgot password?",
    registerAsCustomerLink: "Register as customer",
    backToLoginLink: "Back to login",
    discoverSend2Maroc: "Discover Send2Maroc",
    forCustomersTitle: "For Customers",
    forCustomersDesc: "Ship your baggage easily to Morocco",
    forTransportCompaniesTitle: "For Transport Companies",
    forTransportCompaniesDesc: "Join our network of trusted partners",
    howItWorksTitle: "How It Works",
    howItWorksDesc: "Discover the simple process of baggage shipping",
    ourServicesTitle: "Our Services",
    ourServicesDesc: "Safe, reliable and affordable transport",
    loading: "Loading...",
    transporterNotFound: "Transporter Not Found",
    bookingLinkInvalid: "This booking link is no longer valid.",
    bookingSuccessful: "Booking Successful!",
    saveTrackingCode: "Save this code to track your shipment!",
    uploadPhotosDesc: "Upload up to 3 photos of your baggage/boxes (max 5MB per photo)",
    clickToUpload: "Click to upload photos",
    dragPhotosHere: "Or drag photos here",
    photosRemaining: "photos remaining",
    invoiceLoading: "Loading invoice...",
    trackAndTrace: "Track & Trace",
    trackingDataLoading: "Loading tracking data...",
    notFoundTitle: "Not Found",
    enterTrackingCodeTitle: "Enter your tracking code",
    enterTrackingCodeDesc: "You can find the tracking code in the message you received from the transporter.",
    statusLabel: "Status",
    fromLabel: "From",
    toLabel: "To",
    customerLabel: "Customer",
    transporterLabel: "Transporter",
    descriptionLabel: "Description",
    pickupDateLabel: "Pickup date",
    expectedDeliveryLabel: "Expected delivery",
    companyProfile: "Company Profile",
    companyProfileLoading: "Loading company profile...",
    companyNameLabel: "Company name",
    contactPersonLabel: "Contact person",
    countryOfOriginLabel: "Country of origin",
    activeSinceLabel: "Active since",
    approvalStatusLabel: "Approval status",
    phoneNumberLabel: "Phone number",
    currentLocationLabel: "Current location",
    averageRatingLabel: "Average rating",
    onlineStatusLabel: "Online status",
    companyDescriptionLabel: "Company description",
    priceListLabel: "Price list",
    departureToMorocco: "Departure to Morocco",
    europe: "Europe",
    morocco: "Morocco",
    menu: "Menu",
    backToHomepage: "Back to homepage",
    goBackHome: "Go back home",
    // Dashboard header subtitles
    viewNewOpportunities: "View new transport opportunities and submit quotes",
    manageAcceptedJobs: "Manage your accepted jobs and deliveries",
    bookAndManageCustomers: "Book and manage shipments from your regular customers",
    communicateDirectly: "Communicate directly with your customers",
    updateShipmentStatus: "Update the status of your shipments",
    manageBusinessProfile: "Manage your company profile and status",
    transportDashboard: "Transport Dashboard",
    transportCompanyLabel: "Transport Company",
    // Search and pagination
    searchCityCustomer: "Search by city, customer name...",
    ofRequests: "of",
    requests: "requests",
    results: "results",
    showing: "Showing",
    of: "of",
    // Alert messages
    successStatusChanged: "Status successfully changed!",
    successCompanyStatusUpdated: "Company status successfully updated!",
    errorUpdatingCompanyStatus: "Error updating company status",
    updatesCleared: "updates cleared",
    successProfileUpdated: "Profile successfully updated!",
    errorUpdatingProfile: "Error updating profile",
    successStatusUpdated: "Status successfully updated!",
    errorUpdatingStatus: "Error updating status",
    successStatusUpdatedTo: "Status successfully updated to",
    errorUpdatingOnlineStatus: "Error updating online status",
    errorClearingTracking: "Error clearing tracking",
    // Form labels
    noRequestsAvailable: "No requests available",
    noRequestsFound: "No requests found",
    noBookingsYet: "No bookings yet",
    createFirstBooking: "Create your first booking",
    noConversations: "No conversations",
    selectConversation: "Select a conversation to get started",
    // More alerts
    bookingSuccessfullyCreated: "Booking successfully created!",
    errorCreatingBooking: "Error creating booking. Please try again.",
    errorUpdatingBookingStatus: "Error updating status. Please try again.",
    enterValidPrice: "Please enter a valid price",
    priceSuccessfullyAdded: "Price successfully added!",
    errorUpdatingPrice: "Error updating price. Please try again.",
    confirmDeleteBooking: "Are you sure you want to delete this booking?",
    bookingDeleted: "Booking successfully deleted!",
    errorDeletingBooking: "Error deleting booking",
    // More alerts for transport company
    errorConfirmingPickup: "Error confirming pickup",
    confirmDeliveryQuestion: "Confirm that you have delivered the baggage?",
    deliveryConfirmedByCompany: "Delivery confirmed by company! Waiting for customer confirmation.",
    errorConfirmingDelivery: "Error confirming delivery",
    // Missing keys
    awaitingConfirmation: "Awaiting confirmation",
    bookMyShipment: "Book my shipment",
    bookingInProgress: "Booking in progress",
    bookingSuccessful: "Booking successful",
    bookingsFromOwnCustomers: "Bookings from my customers",
    clickToUpload: "Click to upload",
    companyStatus: "Company status",
    confirm: "Confirm",
    confirmed: "Confirmed",
    delete: "Delete",
    discoverSend2Maroc: "Discover Send2Maroc",
    dragPhotosHere: "Drag photos here",
    europe: "Europe",
    forCustomersDesc: "Send your luggage easily to Morocco with trusted transporters",
    forCustomersTitle: "For Customers",
    forTransportCompaniesDesc: "Find new customers and grow your transport business",
    forTransportCompaniesTitle: "For Transport Companies",
    howItWorksDesc: "Discover how Send2Maroc works",
    howItWorksTitle: "How it works",
    howItWorksButton: "How it works",
    trackAndTrace: "Track & Trace",
    trackYourShipment: "Track your shipment",
    enterTrackingCode: "Enter your tracking code",
    search: "Search",
    back: "Back",
    inTransit: "In transit",
    menu: "Menu",
    newBooking: "New booking",
    ourServicesDesc: "Reliable transport services between Europe and Morocco",
    ourServicesTitle: "Our Services",
    ownCustomers: "My customers",
    photosRemaining: "photos remaining",
    pickupConfirmed: "Pickup confirmed",
    receiveQuotes: "Receive quotes",
    requestTransport: "Request transport",
    saveTrackingCode: "Save tracking code",
    trackMyShipment: "Track my shipment",
    updateCompanyStatus: "Update company status",
    // Form fields
    baggage_description: "Baggage description",
    delivery_address: "Delivery address",
    delivery_city: "Delivery city",
    pickup_address: "Pickup address",
    pickup_city: "Pickup city",
    pickup_date: "Pickup date",
    special_instructions: "Special instructions",
    weight: "Weight",
    ownCustomersDescription: "Your customers stay your customers. Book and manage their shipments here.",
    // Registration form keys
    becomePartner: "Become Partner",
    startTodayReceiveRequests: "Start today and receive transport requests directly",
    yourCompanyName: "Your company name",
    country: "Country",
    minCharacters: "Minimum 8 characters",
    alreadyAccount: "Already have an account?",
    loginHere: "Login here",
    benefitsForPartners: "Benefits for partners:",
    directAccessRequests: "Direct access to new transport requests",
    chatDirectly: "Chat directly with customers",
    firstMonthFree: "100% Free!",
    thenMonthly: "No commission, completely free",
    earnExtra: "Earn up to €500-1000 per month extra",
    freeRegister: "Register for Free",
    registeringText: "Registering...",
    freeRegisterBadge: "100% Free Registration",
    freeRegisterDesc: "No subscription, no commission, no hidden costs. 100% free!",
    registrationSuccessAlert: "✅ Registration successful! Your account is being reviewed by our admin. You will receive a message once your account is approved.",
    registrationErrorPrefix: "Error: ",
    registrationFailed: "Registration failed",
    registrationErrorGeneral: "Something went wrong during registration: ",
    inviteCodeOptional: "Invite code (optional)",
    inviteCodeDesc: "Do you have an invite code? Enter it for quick registration.",
    emailPlaceholder: "info@yourcompany.com",
    monthlySubscriptionAmount: "Free registration - no monthly costs",
    monthlyFee: "Free",
    haveInviteCode: "I have an invite code",
    inviteCodePlaceholder: "Enter your invite code",
    // Services & About page keys
    ourServices: "Our Services",
    reliableTransport: "Reliable baggage transport from Europe to Morocco",
    personalBaggage: "Personal Baggage",
    personalBaggageDesc: "Safely send your personal belongings to family and friends in Morocco.",
    businessGoods: "Business Goods",
    businessGoodsDesc: "Professional transport for business shipments and trade goods.",
    documents: "Documents",
    documentsDesc: "Safe shipping of important documents and papers.",
    whySend2Maroc: "Why Send2Maroc?",
    hundredPercentSafe: "100% Safe",
    verifiedCompanies: "All transport companies are verified and insured",
    bestPrices: "Best Prices",
    compareQuotes: "Compare quotes and choose the best deal",
    fastReliable: "Fast & Reliable",
    averageDelivery: "Average 5-7 days from Europe to Morocco",
    startNow: "Start Now",
    iosInstallHint: "Tap 'Share' then 'Add to Home Screen' to install the app",
    installAppHint: "Install the Send2Maroc app for quick access",
    installText: "Install",
    laterText: "Later"
  },
  fr: {
    title: "Send2Maroc",
    subtitle: "Connecter les clients avec les entreprises de transport pour les bagages personnels",
    loginTitle: "Se connecter",
    registerTitle: "S'inscrire",
    email: "E-mail",
    password: "Mot de passe",
    fullName: "Nom complet",
    companyName: "Nom de l'entreprise",
    countryOrigin: "Pays d'origine",
    userType: "Type d'utilisateur",
    customer: "Client",
    transportCompany: "Entreprise de transport",
    admin: "Administrateur",
    login: "Se connecter",
    register: "S'inscrire",
    switchToLogin: "Vous avez déjà un compte ? Se connecter",
    switchToRegister: "Vous n'avez pas de compte ? S'inscrire",
    googleSignIn: "Se connecter avec Google",
    orDivider: "Ou",
    dashboard: "Tableau de bord",
    logout: "Se déconnecter",
    rememberMe: "Rester connecté",
    calcBadge: "Estimation de prix instantanée",
    calcTitle: "Calculez vos frais de transport",
    calcSubtitle: "Indiquez quoi et où vous voulez expédier et voyez une estimation immédiatement",
    calcPickup: "Lieu de ramassage",
    calcPickupPlaceholder: "Ex. Amsterdam",
    calcDestination: "Destination",
    calcDestinationPlaceholder: "Ex. Casablanca",
    calcKilos: "Nombre de kilos",
    calcKilosPlaceholder: "Ex. 25",
    calcEstimateLabel: "Coût de transport estimé",
    calcPerKg: "par kilo",
    calcCta: "Réserver - Se connecter",
    calcEnterDetails: "Saisissez le lieu de ramassage, la destination et le nombre de kilos pour une estimation",
    calcDisclaimer: "Ceci est un prix indicatif. Le transporteur confirme le prix final.",
    createRequest: "Nouvelle demande",
    myRequests: "Mes demandes",
    availableRequests: "Demandes disponibles",
    myQuotes: "Mes devis",
    pickupCity: "Ville de ramassage",
    deliveryCity: "Ville de livraison",
    baggageSize: "Taille des bagages",
    baggageWeight: "Poids des bagages",
    preferredDate: "Date préférée",
    specialRequirements: "Exigences spéciales",
    baggageImage: "Photo du bagage",
    uploadImage: "Télécharger photo",
    contactPhone: "Téléphone de contact",
    contactAddress: "Adresse de ramassage (optionnel)",
    submit: "Soumettre",
    viewQuotes: "Voir les devis",
    submitQuote: "Soumettre un devis",
    price: "Prix",
    estimatedDays: "Jours estimés",
    message: "Message",
    accept: "Accepter",
    reject: "Rejeter",
    status: "Statut",
    pending: "En attente",
    accepted: "Accepté",
    rejected: "Rejeté",
    active: "Actif",
    completed: "Terminé",
    delivered: "Livré",
    cancelled: "Annulé",
    language: "Langue",
    allRequests: "Toutes Demandes",
    dutch: "Nederlands",
    french: "Français",
    arabic: "العربية",
    homepage: "Accueil",
    forCustomers: "Pour les clients",
    forCompanies: "Pour les entreprises de transport",
    customerLogin: "Connexion Client",
    companyRegistration: "Inscription Entreprise",
    homeDescription: "Connectez les clients en Europe avec des entreprises de transport fiables pour l'expédition de bagages personnels vers le Maroc. Sûr, fiable et abordable.",
    getStarted: "Commencer",
    howItWorks: "Comment ça marche",
    step1: "Créer une demande",
    step1Desc: "Décrivez vos bagages et les emplacements d'expédition souhaités",
    step2: "Recevoir des devis",
    step2Desc: "Les entreprises de transport proposent leurs services",
    step3: "Choisir et expédier",
    step3Desc: "Sélectionnez la meilleure offre et expédiez vos bagages",
    // Admin translations
    adminDashboard: "Tableau de Bord Admin",
    websiteCustomization: "Personnalisation du Site",
    logoManagement: "Gestion du Logo",
    designSettings: "Paramètres de Design",
    contentSettings: "Paramètres de Contenu",
    colorTheme: "Thème de Couleur",
    heroImage: "Image Héro",
    featureImage: "Image de Fonctionnalité",
    siteTitle: "Titre du Site",
    siteDescription: "Description du Site",
    saveChanges: "Sauvegarder les Modifications",
    previewChanges: "Aperçu des Modifications",
    resetToDefault: "Réinitialiser par Défaut",
    blueWhiteTheme: "Bleu & Blanc",
    redOrangeTheme: "Rouge & Orange",
    greenBlackTheme: "Vert & Noir",
    customColors: "Couleurs Personnalisées",
    primaryColor: "Couleur Primaire",
    secondaryColor: "Couleur Secondaire",
    uploadImage: "Télécharger une Image",
    currentImage: "Image Actuelle",
    websiteSettings: "Paramètres du Site",
    // Company landing page translations
    companyPageTitle: "Trouvez des missions de transport entre l'Europe et le Maroc",
    companyPageSubtitle: "Pour les Entreprises de Transport",
    companyIntro: "Êtes-vous un transporteur actif entre l'Europe et le Maroc ? Send2Maroc vous met directement en contact avec des clients particuliers et professionnels qui ont besoin de transport.",
    companyDailyOrders: "Recevez de nouvelles missions chaque jour – sans intermédiaires.",
    companyBenefit1: "Pas besoin de publicités coûteuses",
    companyBenefit2: "Travaillez avec de vrais clients",
    companyBenefit3: "Communication directe, paiement immédiat",
    companyCTA: "Inscrivez-vous dès aujourd'hui, faites vérifier votre profil et commencez à conduire !",
    registerNow: "S'inscrire Maintenant",
    backToHome: "← Retour à l'accueil",
    exportRules: "Réglementation d'Exportation",
    exportRulesTitle: "Règles d'exportation vers le Maroc",
    exportRulesSubtitle: "Informations importantes pour l'exportation de marchandises vers le Maroc",
    
    // Invoice translations
    invoice: "Facture",
    invoiceNumber: "Numéro de facture",
    invoiceDate: "Date",
    from: "De",
    to: "À",
    description: "Description",
    amount: "Montant",
    total: "Total",
    transportService: "Service de transport",
    what: "Quoi",
    pickupLocation: "Lieu de collecte",
    deliveryLocation: "Lieu de livraison",
    pickupDate: "Date de collecte",
    deliveryDate: "Date de livraison",
    tracking: "Suivi",
    printDownloadPDF: "Imprimer / Télécharger PDF",
    close: "Fermer",
    invoiceNotFound: "Facture non trouvée",
    invoiceNotFoundMessage: "Cette facture n'existe pas ou n'est pas disponible.",
    thankYouMessage: "Merci pour votre confiance en",
    questionsContact: "Pour toute question, vous pouvez nous contacter.",
    whyChooseUs: "Pourquoi choisir Send2Maroc ?",
    whyChooseSubtitle: "La plateforme qui connecte directement les transporteurs aux clients",
    whyChooseUsQuestion: "Pourquoi nous choisir ?",
    whyChooseTitle: "Pourquoi choisir notre plateforme ?",
    whyChooseDescription: "Découvrez pourquoi des milliers de clients nous font confiance pour leur transport de bagages entre l'Europe et le Maroc.",
    secureReliable: "Sécurisé & Fiable",
    secureReliableDesc: "Entreprises de transport vérifiées et transactions sécurisées pour votre tranquillité d'esprit. Chaque transport est assuré et suivi.",
    multipleQuotes: "Devis Multiples",
    multipleQuotesDesc: "Comparez les prix de différentes entreprises de transport et choisissez la meilleure offre. Prix transparents sans frais cachés.",
    multilingualPlatform: "Plateforme Multilingue",
    multilingualPlatformDesc: "Supporte le néerlandais, le français et l'arabe pour une communication facile avec tous les utilisateurs et transporteurs.",
    footerCopyright: "© 2025 Send2Maroc. Tous droits réservés. Connecte l'Europe au Maroc grâce à un transport de bagages fiable.",
    directPayment: "Paiement Direct",
    directPaymentDesc: "Pas d'attente. Les clients vous paient directement, sans intermédiaires qui prennent des commissions.",
    verifiedClients: "Clients Vérifiés",
    verifiedClientsDesc: "Tous les clients sont vérifiés. Travaillez uniquement avec des donneurs d'ordre sérieux qui ont vraiment besoin de transport.",
    dailyOrders: "Missions Quotidiennes",
    dailyOrdersDesc: "Nouvelles demandes de transport chaque jour. Gardez votre camion plein et votre agenda rempli de missions fiables.",
    forTransportCompanies: "Pour les Entreprises de Transport",
    directContact: "Contact Direct",
    clientsToYourBusiness: "Clients → Votre Entreprise",
    noIntermediaries: "Pas d'intermédiaires",
    alreadyAccount: "Vous avez déjà un compte? Se connecter",
    // Pricing section
    pricingTitle: "Prix Transparents",
    pricingSubtitle: "Investissez peu, gagnez beaucoup",
    monthlyFee: "Gratuit",
    exVat: "hors TVA",
    pricingBenefit1: "Missions illimitées à recevoir",
    pricingBenefit2: "Contact direct avec les clients",
    pricingBenefit3: "Aucune commission sur les gains",
    pricingBenefit4: "Contrôle total sur vos prix",
    earningsPotential: "Gagnez potentiellement des centaines d'euros par mission",
    pricingCTA: "Inscrivez-vous gratuitement et recevez un accès direct aux missions quotidiennes. Pas de commission, pas de frais cachés. 100% gratuit !",
    // Dashboard translations
    customerPanel: "Panneau Client",
    transportPanel: "Panneau Transport",
    manageRequests: "Gérer vos demandes",
    manageYourBusiness: "Gérer votre entreprise",
    language: "Langue",
    allRequests: "Toutes Demandes",
    // Registration form translations
    companyInformation: "Informations sur l'Entreprise",
    contactInformation: "Informations de Contact", 
    transportInformation: "Informations de Transport",
    finalDetails: "Derniers Détails",
    companyName: "Nom de l'Entreprise",
    businessRegistration: "Immatriculation",
    companyDescription: "Description de l'Entreprise",
    yearsExperience: "Années d'Expérience",
    // Company approval translations
    companyApproval: "Approbation d'Entreprise",
    pendingCompanies: "Entreprises en Attente",
    allCompanies: "Toutes les Entreprises",
    approveCompany: "Approuver l'Entreprise",
    rejectCompany: "Rejeter l'Entreprise",
    approved: "Approuvée",
    rejected: "Rejetée",
    pending: "En Attente",
    approvalStatus: "Statut d'Approbation",
    registrationDate: "Date d'Inscription",
    companyDetails: "Détails de l'Entreprise",
    approveConfirm: "Êtes-vous sûr de vouloir approuver cette entreprise?",
    rejectConfirm: "Êtes-vous sûr de vouloir rejeter cette entreprise?",
    rejectionReason: "Raison du Rejet",
    noCompanies: "Aucune entreprise trouvée",
    userManagement: "Gestion des Utilisateurs",
    platformStatistics: "Statistiques de la Plateforme",
    totalUsers: "Total Utilisateurs",
    customers: "Clients",
    transportCompanies: "Entreprises de Transport",
    activeRequests: "Demandes Actives",
    completedRequests: "Demandes Terminées",
    pendingQuotes: "Devis en Attente",
    acceptedQuotes: "Devis Acceptés",
    recentActivity: "Activité Récente",
    editUser: "Modifier l'Utilisateur",
    deleteUser: "Supprimer l'Utilisateur",
    save: "Sauvegarder",
    cancel: "Annuler",
    confirmDelete: "Êtes-vous sûr de vouloir supprimer cet utilisateur?",
    // Chat translations
    chat: "Chat",
    startChat: "Commencer le Chat",
    sendMessage: "Envoyer Message",
    typeMessage: "Tapez votre message...",
    send: "Envoyer",
    chatWith: "Chat avec",
    noMessages: "Aucun message",
    conversations: "Conversations",
    newMessage: "Nouveau message",
    sendImage: "Envoyer Image",
    imageMessage: "Image",
    lastMessage: "Dernier message",
    unreadMessages: "messages non lus",
    deleteConversation: "Supprimer Conversation",
    confirmDeleteConversation: "Êtes-vous sûr de vouloir supprimer cette conversation? Tous les messages seront définitivement supprimés.",
    conversationDeleted: "Conversation supprimée avec succès",
    // Terms and Conditions translations
    acceptTerms: "J'accepte les",
    termsAndConditions: "Conditions Générales",
    acceptAndClose: "Accepter et Fermer",
    close: "Fermer",
    loginTermsMessage: "En vous connectant, vous acceptez automatiquement nos",
    generalConversation: "Conversation générale",
    generalConversation: "Conversation générale",
    // Tracking translations  
    tracking: "Suivi",
    myProfile: "Mon Profil",
    profileAndStatus: "Profil & Statut",
    availableTransportRequests: "Demandes de Transport Disponibles",
    cancel: "Annuler",
    save: "Enregistrer",
    priceList: "Liste de Prix",
    loading: "Chargement...",
    transporterNotFound: "Transporteur Introuvable",
    bookingLinkInvalid: "Ce lien de réservation n'est plus valide.",
    loadMore: "Charger plus",
    previous: "Précédent",
    next: "Suivant",
    becomePartner: "Devenir Partenaire",
    startTodayReceiveRequests: "Commencez aujourd'hui et recevez directement des demandes de transport",
    companyName: "Nom de l'entreprise",
    yourCompanyName: "Votre nom d'entreprise",
    phoneNumber: "Numéro de téléphone",
    country: "Pays",
    password: "Mot de passe",
    minCharacters: "Minimum 8 caractères",
    confirmPassword: "Confirmer le mot de passe",
    repeatPassword: "Répétez le mot de passe",
    goToPayment: "Continuer vers le paiement",
    monthlySubscription: "Compte gratuit",
    exclVAT: "hors TVA",
    inclVAT: "TVA 21% incluse",
    benefitsForPartners: "Avantages pour les partenaires:",
    directAccessRequests: "Accès direct aux nouvelles demandes de transport",
    chatDirectly: "Chattez directement avec les clients",
    monthlySubscriptionAmount: "Inscription gratuite - pas de frais mensuels",
    earnExtra: "Gagnez jusqu'à €500-1000 par mois en plus",
    freeRegister: "Inscription Gratuite",
    registeringText: "Inscription en cours...",
    freeRegisterBadge: "Inscription 100% Gratuite",
    freeRegisterDesc: "Pas d'abonnement, pas de commission, pas de frais cachés. 100% gratuit !",
    registrationSuccessAlert: "✅ Inscription réussie ! Votre compte est en cours de vérification par notre admin. Vous recevrez un message dès que votre compte sera approuvé.",
    registrationErrorPrefix: "Erreur : ",
    registrationFailed: "Inscription échouée",
    registrationErrorGeneral: "Une erreur s'est produite lors de l'inscription : ",
    inviteCodeOptional: "Code d'invitation (optionnel)",
    inviteCodeDesc: "Avez-vous reçu un code d'invitation ? Saisissez-le pour une inscription rapide.",
    emailPlaceholder: "info@votreentreprise.com",
    alreadyAccount: "Déjà un compte?",
    loginHere: "Connectez-vous ici",
    firstMonthFree: "100% Gratuit !",
    thenMonthly: "Pas de commission, 100% gratuit",
    enterCompanyName: "Entrez le nom de votre entreprise",
    enterValidEmail: "Entrez une adresse email valide",
    enterPhone: "Entrez votre numéro de téléphone",
    passwordMin8: "Le mot de passe doit contenir au moins 8 caractères",
    passwordsNotMatch: "Les mots de passe ne correspondent pas",
    paymentCouldNotStart: "Le paiement n'a pas pu être démarré",
    goingToPayment: "Vers le paiement...",
    submitQuote: "Soumettre une offre",
    quote: "Offre",
    quoteDetails: "Détails de l'offre",
    clearAllFilters: "Effacer tous les filtres",
    showFilters: "Afficher les filtres",
    hideFilters: "Masquer les filtres",
    departureCity: "Ville de départ",
    typeCityName: "Tapez le nom de la ville...",
    requestQuote: "Demander un devis",
    price: "Prix",
    contact: "Contact",
    aboutUs: "À propos de nous",
    services: "Services",
    coverage: "Zone de couverture",
    termsConditions: "Conditions générales",
    blogNews: "Blog et actualités",
    status: "Statut",
    pickup: "Ramassage",
    delivery: "Livraison",
    remarks: "Remarques",
    estimatedDelivery: "Livraison estimée",
    days: "jours",
    trackingUpdates: "Mises à jour du Suivi",
    pickedUp: "Récupéré!",
    updateStatus: "Mettre à jour le Statut",
    shipmentStatus: "Statut de l'Expédition",
    trackingHistory: "Historique du Suivi",
    currentStatus: "Statut Actuel",
    lastUpdate: "Dernière Mise à jour",
    addUpdate: "Ajouter une Mise à jour",
    statusPending: "En Attente",
    statusPickedUp: "Récupéré",
    statusInTransit: "En Transit",
    statusAtCustoms: "En Douane",
    statusOutForDelivery: "En Livraison",
    statusDelivered: "Livré",
    location: "Emplacement",
    notes: "Notes",
    updateTime: "Heure de Mise à jour",
    noTrackingYet: "Aucune mise à jour de suivi",
    messageForCustomers: "Message pour tous les clients",
    pickupLocation: "Lieu de ramassage",
    pickupNotes: "Notes de ramassage",
    // My Offers translations
    myOffers: "Mes Offres",
    acceptedOffers: "Offres Acceptées",
    removeRequest: "Supprimer la Demande",
    confirmRemoveRequest: "Êtes-vous sûr de vouloir supprimer cette demande livrée?",
    requestRemoved: "Demande supprimée avec succès",
    viewRequestDetails: "Voir Détails de la Demande",
    offerDetails: "Détails de l'Offre",
    requestInfo: "Informations de la Demande",
    customerInfo: "Informations Client",
    viewFullRequest: "Voir Demande Complète",
    // Company directory translations
    availableCompanies: "Entreprises Disponibles",
    companyDirectory: "Répertoire d'Entreprises",
    viewProfile: "Voir Profil",
    contactCompany: "Contacter Entreprise",
    companyProfile: "Profil Entreprise",
    companyDescription: "Description Entreprise",
    priceList: "Liste de Prix",
    phoneNumber: "Numéro Téléphone",
    currentLocation: "Localisation Actuelle",
    departureDates: "Dates de Départ",
    toMorocco: "Vers Maroc",
    toEurope: "Vers Europe",
    reviews: "Avis",
    averageRating: "Note Moyenne",
    totalReviews: "Total Avis",
    writeReview: "Écrire Avis",
    rateCompany: "Noter Entreprise",
    reviewText: "Texte Avis",
    rating: "Note",
    stars: "étoiles",
    submitReview: "Soumettre Avis",
    noCompanies: "Aucune entreprise disponible",
    noReviews: "Aucun avis",
    profilePicture: "Photo Profil",
    updateProfile: "Mettre à Jour Profil",
    updateStatus: "Mettre à Jour Statut",
    noPriceList: "Aucune liste de prix disponible",
    noDescription: "Aucune description disponible",
    contactInfo: "Informations de Contact",
    optional: "optionnel",
    close: "Fermer",
    onlineStatus: "Statut En Ligne",
    goOnline: "Aller En Ligne",
    goOffline: "Aller Hors Ligne",
    currentlyOnline: "Actuellement En Ligne",
    currentlyOffline: "Actuellement Hors Ligne",
    languages: "Langues",
    communicationLanguages: "Langues de Communication",
    selectLanguages: "Sélectionner Langues",
    dutch: "Néerlandais",
    french: "Français",
    arabic: "Arabe",
    english: "Anglais",
    german: "Allemand",
    spanish: "Espagnol",
    clearTracking: "Effacer Tracking",
    clearTrackingConfirm: "Êtes-vous sûr de vouloir effacer toutes les mises à jour de tracking?",
    trackingCleared: "Tracking effacé avec succès",
    // Search and filter translations
    searchCompanies: "Rechercher Entreprises",
    searchPlaceholder: "Rechercher par nom ou localisation...",
    filterBy: "Filtrer Par",
    showFilters: "Afficher Filtres",
    hideFilters: "Masquer Filtres",
    allLanguages: "Toutes Langues",
    allCountries: "Tous Pays",
    countryFilter: "Pays d'Origine",
    departureCountryFilter: "Pays de Départ",
    departureDateFilter: "Date Départ vers Maroc",
    selectDate: "Sélectionner Date",
    selectCountry: "Sélectionner Pays",
    clearFilters: "Effacer Filtres",
    companiesFound: "entreprises trouvées",
    // Country translations
    netherlands: "Pays-Bas",
    belgium: "Belgique",
    germany: "Allemagne", 
    france: "France",
    spain: "Espagne",
    italy: "Italie",
    portugal: "Portugal",
    austria: "Autriche",
    switzerland: "Suisse",
    luxembourg: "Luxembourg",
    denmark: "Danemark",
    sweden: "Suède",
    norway: "Norvège",
    finland: "Finlande",
    poland: "Pologne",
    czech_republic: "République tchèque",
    hungary: "Hongrie",
    morocco: "Maroc",
    algeria: "Algérie",
    tunisia: "Tunisie",
    egypt: "Égypte",
    united_kingdom: "Royaume-Uni",
    ireland: "Irlande",
    not_specified: "Non spécifié",
    other: "Autre",
    // Welcome Screen translations
    welcomeBack: "Bon retour",
    welcomeAdmin: "Bienvenue Admin ! Gérez la plateforme et consultez les statistiques.",
    welcomeCustomer: "Bienvenue ! Expédiez facilement vos bagages au Maroc.",
    welcomeTransport: "Bienvenue entreprise de transport ! Trouvez de nouveaux clients et développez votre activité.",
    brandName: "BaggageTransport",
    brandSubtitle: "Europe → Maroc",
    europeToMorocco: "Europe → Maroc",
    reliableConnection: "Connexion fiable",
    europeToMoroccoDesc: "Transport sûr et rapide entre l'Europe et le Maroc via notre réseau de transporteurs de confiance.",
    realTimeTracking: "Suivi en Temps Réel",
    alwaysUpToDate: "Toujours à jour",
    realTimeTrackingDesc: "Suivez vos bagages en temps réel avec notre système de suivi avancé et restez toujours informé.",
    securePlatform: "Plateforme Sécurisée",
    hundredPercentSecure: "100% sécurisé",
    securePlatformDesc: "Vos données et transactions sont entièrement sécurisées avec les dernières technologies de sécurité.",
    whereToGo: "Où souhaitez-vous aller ?",
    adminDashboardTitle: "Tableau de Bord Admin",
    adminDashboardDesc: "Gérer les utilisateurs et consulter les statistiques",
    myDashboardTitle: "Mon Tableau de Bord",
    myDashboardDesc: "Gérez vos demandes de transport et suivez vos bagages",
    transportDashboardTitle: "Tableau de Bord Entreprise",
    transportDashboardDesc: "Consultez les demandes disponibles et gérez vos devis",
    goToDashboard: "Aller au tableau de bord",
    trustedPartner: "Votre partenaire de confiance pour le transport international de bagages",
    // Login page
    loginSubtitle: "Connectez-vous pour gérer vos demandes de transport",
    registerSubtitle: "Inscrivez-vous pour placer des demandes de transport",
    forgotPasswordQuestion: "Mot de passe oublié ?",
    registerAsCustomerLink: "S'inscrire en tant que client",
    backToLoginLink: "Retour à la connexion",
    // Sidebar menu subtitles
    newTransportOpportunities: "Nouvelles opportunités de transport",
    acceptedJobs: "Missions acceptées",
    bookingsFromMyCustomers: "Réservations de mes clients",
    chatWithCustomers: "Discussion avec les clients",
    statusUpdates: "Mises à jour de statut",
    conversations: "Conversations",
    // Dashboard header subtitles
    viewNewOpportunities: "Consultez les nouvelles opportunités de transport et soumettez des devis",
    manageAcceptedJobs: "Gérez vos missions acceptées et vos livraisons",
    bookAndManageCustomers: "Réservez et gérez les envois de vos clients réguliers",
    communicateDirectly: "Communiquez directement avec vos clients",
    updateShipmentStatus: "Mettez à jour le statut de vos envois",
    manageBusinessProfile: "Gérez votre profil d'entreprise et votre statut",
    transportDashboard: "Tableau de Bord Transport",
    transportCompanyLabel: "Entreprise de Transport",
    // Search and pagination
    searchCityCustomer: "Rechercher par ville, nom du client...",
    ofRequests: "de",
    requests: "demandes",
    results: "résultats",
    showing: "Affichage",
    of: "de",
    // Alert messages
    successStatusChanged: "Statut modifié avec succès !",
    successCompanyStatusUpdated: "Statut de l'entreprise mis à jour avec succès !",
    errorUpdatingCompanyStatus: "Erreur lors de la mise à jour du statut de l'entreprise",
    updatesCleared: "mises à jour effacées",
    successProfileUpdated: "Profil mis à jour avec succès !",
    errorUpdatingProfile: "Erreur lors de la mise à jour du profil",
    successStatusUpdated: "Statut mis à jour avec succès !",
    errorUpdatingStatus: "Erreur lors de la mise à jour du statut",
    successStatusUpdatedTo: "Statut mis à jour avec succès à",
    errorUpdatingOnlineStatus: "Erreur lors de la mise à jour du statut en ligne",
    errorClearingTracking: "Erreur lors de l'effacement du suivi",
    // Form labels
    noRequestsAvailable: "Aucune demande disponible",
    noRequestsFound: "Aucune demande trouvée",
    noBookingsYet: "Pas encore de réservations",
    createFirstBooking: "Créez votre première réservation",
    noConversations: "Aucune conversation",
    selectConversation: "Sélectionnez une conversation pour commencer",
    // More alerts
    bookingSuccessfullyCreated: "Réservation créée avec succès !",
    errorCreatingBooking: "Erreur lors de la création de la réservation. Veuillez réessayer.",
    errorUpdatingBookingStatus: "Erreur lors de la mise à jour du statut. Veuillez réessayer.",
    enterValidPrice: "Veuillez entrer un prix valide",
    priceSuccessfullyAdded: "Prix ajouté avec succès !",
    errorUpdatingPrice: "Erreur lors de la mise à jour du prix. Veuillez réessayer.",
    confirmDeleteBooking: "Êtes-vous sûr de vouloir supprimer cette réservation ?",
    bookingDeleted: "Réservation supprimée avec succès !",
    errorDeletingBooking: "Erreur lors de la suppression de la réservation",
    // More alerts for transport company
    errorConfirmingPickup: "Erreur lors de la confirmation du ramassage",
    confirmDeliveryQuestion: "Confirmez-vous que vous avez livré les bagages ?",
    deliveryConfirmedByCompany: "Livraison confirmée par l'entreprise ! En attente de confirmation du client.",
    errorConfirmingDelivery: "Erreur lors de la confirmation de la livraison",
    // Missing keys
    awaitingConfirmation: "En attente de confirmation",
    bookMyShipment: "Réserver mon envoi",
    bookingInProgress: "Réservation en cours",
    bookingSuccessful: "Réservation réussie",
    bookingsFromOwnCustomers: "Réservations de mes clients",
    clickToUpload: "Cliquez pour télécharger",
    companyStatus: "Statut de l'entreprise",
    confirm: "Confirmer",
    confirmed: "Confirmé",
    delete: "Supprimer",
    discoverSend2Maroc: "Découvrez Send2Maroc",
    dragPhotosHere: "Glissez les photos ici",
    europe: "Europe",
    forCustomersDesc: "Envoyez facilement vos bagages au Maroc avec des transporteurs de confiance",
    forCustomersTitle: "Pour les clients",
    forTransportCompaniesDesc: "Trouvez de nouveaux clients et développez votre entreprise de transport",
    forTransportCompaniesTitle: "Pour les entreprises de transport",
    howItWorksDesc: "Découvrez comment fonctionne Send2Maroc",
    howItWorksTitle: "Comment ça marche",
    howItWorksButton: "Comment ça marche",
    trackAndTrace: "Suivi des colis",
    trackYourShipment: "Suivez votre envoi",
    enterTrackingCode: "Entrez votre code de suivi",
    search: "Rechercher",
    back: "Retour",
    inTransit: "En transit",
    menu: "Menu",
    newBooking: "Nouvelle réservation",
    ourServicesDesc: "Services de transport fiables entre l'Europe et le Maroc",
    ourServicesTitle: "Nos services",
    ownCustomers: "Mes clients",
    photosRemaining: "photos restantes",
    pickupConfirmed: "Ramassage confirmé",
    receiveQuotes: "Recevoir des devis",
    requestTransport: "Demander un transport",
    saveTrackingCode: "Sauvegarder le code de suivi",
    trackMyShipment: "Suivre mon envoi",
    updateCompanyStatus: "Mettre à jour le statut",
    // Form fields
    baggage_description: "Description des bagages",
    delivery_address: "Adresse de livraison",
    delivery_city: "Ville de livraison",
    pickup_address: "Adresse de ramassage",
    pickup_city: "Ville de ramassage",
    pickup_date: "Date de ramassage",
    special_instructions: "Instructions spéciales",
    weight: "Poids",
    ownCustomersDescription: "Vos clients restent vos clients. Réservez et gérez leurs envois ici.",
    ourServices: "Nos Services",
    reliableTransport: "Transport fiable de bagages de l'Europe vers le Maroc",
    personalBaggage: "Bagages Personnels",
    personalBaggageDesc: "Envoyez vos effets personnels en toute sécurité à votre famille et amis au Maroc.",
    businessGoods: "Marchandises Professionnelles",
    businessGoodsDesc: "Transport professionnel pour les envois commerciaux et marchandises.",
    documents: "Documents",
    documentsDesc: "Envoi sécurisé de documents importants et papiers.",
    whySend2Maroc: "Pourquoi Send2Maroc ?",
    hundredPercentSafe: "100% Sécurisé",
    verifiedCompanies: "Toutes les entreprises de transport sont vérifiées et assurées",
    bestPrices: "Meilleurs Prix",
    compareQuotes: "Comparez les offres et choisissez la meilleure",
    fastReliable: "Rapide & Fiable",
    averageDelivery: "En moyenne 5-7 jours de l'Europe au Maroc",
    startNow: "Commencer",
    iosInstallHint: "Appuyez sur 'Partager' puis 'Sur l'écran d'accueil' pour installer l'app",
    installAppHint: "Installez l'app Send2Maroc pour un accès rapide",
    installText: "Installer",
    laterText: "Plus tard"
  },
  ar: {
    title: "Send2Maroc",
    subtitle: "ربط العملاء بشركات النقل للأمتعة الشخصية",
    loginTitle: "تسجيل الدخول",
    registerTitle: "إنشاء حساب",
    email: "البريد الإلكتروني",
    password: "كلمة المرور",
    fullName: "الاسم الكامل",
    companyName: "اسم الشركة",
    countryOrigin: "بلد المنشأ",
    userType: "نوع المستخدم",
    customer: "عميل",
    transportCompany: "شركة النقل",
    admin: "مدير",
    login: "تسجيل الدخول",
    register: "إنشاء حساب",
    switchToLogin: "لديك حساب بالفعل؟ تسجيل الدخول",
    switchToRegister: "لا تملك حساب؟ إنشاء حساب",
    googleSignIn: "تسجيل الدخول بواسطة Google",
    orDivider: "أو",
    dashboard: "لوحة التحكم",
    logout: "تسجيل الخروج",
    rememberMe: "ابقني مسجلاً للدخول",
    calcBadge: "تقدير فوري للسعر",
    calcTitle: "احسب تكلفة النقل",
    calcSubtitle: "أدخل ما تريد شحنه وإلى أين لترى التقدير فوراً",
    calcPickup: "موقع الاستلام",
    calcPickupPlaceholder: "مثال: أمستردام",
    calcDestination: "الوجهة",
    calcDestinationPlaceholder: "مثال: الدار البيضاء",
    calcKilos: "عدد الكيلوغرامات",
    calcKilosPlaceholder: "مثال: 25",
    calcEstimateLabel: "تكلفة النقل التقديرية",
    calcPerKg: "لكل كيلوغرام",
    calcCta: "احجز الآن - تسجيل الدخول",
    calcEnterDetails: "أدخل موقع الاستلام والوجهة وعدد الكيلوغرامات للحصول على تقدير السعر",
    calcDisclaimer: "هذا سعر استرشادي. الناقل يؤكد السعر النهائي.",
    createRequest: "طلب جديد",
    myRequests: "طلباتي",
    availableRequests: "الطلبات المتاحة",
    myQuotes: "عروض الأسعار",
    pickupCity: "مدينة الاستلام",
    deliveryCity: "مدينة التسليم",
    baggageSize: "حجم الأمتعة",
    baggageWeight: "وزن الأمتعة",
    preferredDate: "التاريخ المفضل",
    specialRequirements: "متطلبات خاصة",
    baggageImage: "صورة الأمتعة",
    uploadImage: "رفع صورة",
    contactPhone: "هاتف الاتصال",
    contactAddress: "عنوان الاستلام (اختياري)",
    submit: "إرسال",
    viewQuotes: "عرض الأسعار",
    submitQuote: "تقديم عرض سعر",
    price: "السعر",
    estimatedDays: "الأيام المقدرة",
    message: "رسالة",
    accept: "قبول",
    reject: "رفض",
    status: "الحالة",
    pending: "في الانتظار",
    accepted: "مقبول",
    rejected: "مرفوض",
    active: "نشط",
    completed: "مكتمل",
    delivered: "تم التسليم",
    cancelled: "ملغي",
    language: "اللغة",
    allRequests: "جميع الطلبات",
    dutch: "Nederlands",
    french: "Français",
    arabic: "العربية",
    homepage: "الصفحة الرئيسية",
    forCustomers: "للعملاء",
    forCompanies: "لشركات النقل",
    customerLogin: "تسجيل دخول العميل",
    companyRegistration: "تسجيل الشركة",
    homeDescription: "ربط العملاء في أوروبا بشركات النقل الموثوقة لشحن الأمتعة الشخصية إلى المغرب. آمن وموثوق وبأسعار معقولة.",
    getStarted: "ابدأ الآن",
    howItWorks: "كيف يعمل",
    step1: "إنشاء طلب",
    step1Desc: "وصف أمتعتك ومواقع الشحن المرغوبة",
    step2: "استلام عروض الأسعار",
    step2Desc: "شركات النقل تقدم خدماتها",
    step3: "اختيار والشحن",
    step3Desc: "حدد أفضل عرض واشحن أمتعتك",
    // Admin translations  
    adminDashboard: "لوحة تحكم المدير",
    websiteCustomization: "تخصيص الموقع",
    logoManagement: "إدارة الشعار",
    designSettings: "إعدادات التصميم",
    contentSettings: "إعدادات المحتوى",
    colorTheme: "نظام الألوان",
    heroImage: "الصورة الرئيسية",
    featureImage: "صورة الميزة",
    siteTitle: "عنوان الموقع",
    siteDescription: "وصف الموقع",
    saveChanges: "حفظ التغييرات",
    previewChanges: "معاينة التغييرات",
    resetToDefault: "إعادة تعيين افتراضي",
    blueWhiteTheme: "أزرق وأبيض",
    redOrangeTheme: "أحمر وبرتقالي",
    greenBlackTheme: "أخضر وأسود",
    customColors: "ألوان مخصصة",
    primaryColor: "اللون الأساسي",
    secondaryColor: "اللون الثانوي",
    uploadImage: "رفع صورة",
    currentImage: "الصورة الحالية",
    websiteSettings: "إعدادات الموقع",
    // Company landing page translations
    companyPageTitle: "ابحث عن مهام النقل بين أوروبا والمغرب",
    companyPageSubtitle: "لشركات النقل",
    companyIntro: "هل أنت ناقل نشط بين أوروبا والمغرب؟ Send2Maroc يربطك مباشرة بالعملاء الأفراد والشركات الذين يحتاجون إلى النقل.",
    companyDailyOrders: "استقبل مهام جديدة يومياً – بدون وسطاء.",
    companyBenefit1: "لا حاجة لإعلانات مكلفة",
    companyBenefit2: "اعمل مع عملاء حقيقيين",
    companyBenefit3: "تواصل مباشر، دفع فوري",
    companyCTA: "سجل اليوم، تحقق من ملفك الشخصي وابدأ القيادة!",
    registerNow: "سجل الآن",
    backToHome: "← العودة للرئيسية",
    exportRules: "قواعد التصدير",
    exportRulesTitle: "قواعد التصدير إلى المغرب",
    exportRulesSubtitle: "معلومات مهمة لتصدير البضائع إلى المغرب",
    
    // Invoice translations
    invoice: "فاتورة",
    invoiceNumber: "رقم الفاتورة",
    invoiceDate: "التاريخ",
    from: "من",
    to: "إلى",
    description: "الوصف",
    amount: "المبلغ",
    total: "المجموع",
    transportService: "خدمة النقل",
    what: "ماذا",
    pickupLocation: "موقع الاستلام",
    deliveryLocation: "موقع التسليم",
    pickupDate: "تاريخ الاستلام",
    deliveryDate: "تاريخ التسليم",
    tracking: "التتبع",
    printDownloadPDF: "طباعة / تحميل PDF",
    close: "إغلاق",
    invoiceNotFound: "الفاتورة غير موجودة",
    invoiceNotFoundMessage: "هذه الفاتورة غير موجودة أو غير متاحة.",
    thankYouMessage: "شكراً لثقتكم في",
    questionsContact: "لأي استفسارات، يمكنكم التواصل معنا.",
    whyChooseUs: "لماذا تختار Send2Maroc؟",
    whyChooseSubtitle: "المنصة التي تربط شركات النقل مباشرة بالعملاء",
    whyChooseUsQuestion: "لماذا تختارنا؟",
    whyChooseTitle: "لماذا تختار منصتنا؟",
    whyChooseDescription: "اكتشف لماذا يثق الآلاف من العملاء بنا لنقل أمتعتهم بين أوروبا والمغرب.",
    secureReliable: "آمن وموثوق",
    secureReliableDesc: "شركات نقل معتمدة ومعاملات آمنة لراحة بالك. كل عملية نقل مؤمنة ومتتبعة.",
    multipleQuotes: "عروض أسعار متعددة",
    multipleQuotesDesc: "قارن أسعار شركات نقل مختلفة واختر أفضل صفقة. أسعار شفافة بدون تكاليف مخفية.",
    multilingualPlatform: "منصة متعددة اللغات",
    multilingualPlatformDesc: "يدعم الهولندية والفرنسية والعربية للتواصل السهل مع جميع المستخدمين وشركات النقل.",
    footerCopyright: "© 2025 Send2Maroc. جميع الحقوق محفوظة. يربط أوروبا بالمغرب من خلال نقل الأمتعة الموثوق.",
    directPayment: "دفع مباشر",
    directPaymentDesc: "لا انتظار. العملاء يدفعون لك مباشرة، بدون وسطاء يأخذون عمولات.",
    verifiedClients: "عملاء موثقون",
    verifiedClientsDesc: "جميع العملاء تم فحصهم. اعمل فقط مع أصحاب مهام جديين يحتاجون النقل فعلاً.",
    dailyOrders: "مهام يومية",
    dailyOrdersDesc: "طلبات نقل جديدة كل يوم. اجعل شاحنتك ممتلئة وجدولك مليء بالمهام الموثوقة.",
    forTransportCompanies: "لشركات النقل",
    directContact: "تواصل مباشر",
    clientsToYourBusiness: "العملاء → شركتك",
    noIntermediaries: "بدون وسطاء",
    alreadyAccount: "هل لديك حساب بالفعل؟ تسجيل الدخول",
    // Pricing section
    pricingTitle: "أسعار شفافة",
    pricingSubtitle: "استثمار قليل، ربح كبير",
    monthlyFee: "مجاني",
    exVat: "بدون ضريبة القيمة المضافة",
    pricingBenefit1: "مهام غير محدودة للاستلام",
    pricingBenefit2: "تواصل مباشر مع العملاء",
    pricingBenefit3: "لا عمولة على الأرباح",
    pricingBenefit4: "تحكم كامل في الأسعار",
    earningsPotential: "اربح مئات اليوروهات المحتملة لكل مهمة",
    pricingCTA: "سجل مجاناً واحصل على وصول فوري للمهام اليومية. بدون عمولة، بدون رسوم خفية. مجاني 100%!",
    // Dashboard translations
    customerPanel: "لوحة العميل",
    transportPanel: "لوحة النقل",
    manageRequests: "إدارة طلباتك",
    manageYourBusiness: "إدارة عملك",
    language: "اللغة",
    // Registration form translations
    companyInformation: "معلومات الشركة",
    contactInformation: "معلومات الاتصال", 
    transportInformation: "معلومات النقل",
    finalDetails: "التفاصيل النهائية",
    companyName: "اسم الشركة",
    businessRegistration: "تسجيل الأعمال",
    companyDescription: "وصف الشركة",
    yearsExperience: "سنوات الخبرة",
    // Company approval translations
    companyApproval: "موافقة الشركة",
    pendingCompanies: "الشركات المعلقة",
    allCompanies: "جميع الشركات",
    approveCompany: "الموافقة على الشركة",
    rejectCompany: "رفض الشركة",
    approved: "موافق عليها",
    rejected: "مرفوضة",
    pending: "معلقة",
    approvalStatus: "حالة الموافقة",
    registrationDate: "تاريخ التسجيل",
    companyDetails: "تفاصيل الشركة",
    approveConfirm: "هل أنت متأكد من الموافقة على هذه الشركة؟",
    rejectConfirm: "هل أنت متأكد من رفض هذه الشركة؟",
    rejectionReason: "سبب الرفض",
    noCompanies: "لم يتم العثور على شركات",
    userManagement: "إدارة المستخدمين",
    platformStatistics: "إحصائيات المنصة",
    totalUsers: "إجمالي المستخدمين",
    customers: "العملاء",
    transportCompanies: "شركات النقل",
    activeRequests: "الطلبات النشطة",
    completedRequests: "الطلبات المكتملة", 
    pendingQuotes: "عروض الأسعار المعلقة",
    acceptedQuotes: "عروض الأسعار المقبولة",
    recentActivity: "النشاط الأخير",
    editUser: "تعديل المستخدم",
    deleteUser: "حذف المستخدم",
    save: "حفظ",
    cancel: "إلغاء",
    confirmDelete: "هل أنت متأكد من حذف هذا المستخدم؟",
    // Chat translations
    chat: "دردشة",
    startChat: "بدء الدردشة",
    sendMessage: "إرسال رسالة",
    typeMessage: "اكتب رسالتك...",
    send: "إرسال",
    chatWith: "دردشة مع",
    noMessages: "لا توجد رسائل",
    conversations: "المحادثات",
    newMessage: "رسالة جديدة",
    sendImage: "إرسال صورة",
    imageMessage: "صورة",
    lastMessage: "آخر رسالة",
    unreadMessages: "رسائل غير مقروءة",
    deleteConversation: "حذف المحادثة",
    confirmDeleteConversation: "هل أنت متأكد من أنك تريد حذف هذه المحادثة؟ سيتم حذف جميع الرسائل نهائياً.",
    conversationDeleted: "تم حذف المحادثة بنجاح",
    // Terms and Conditions translations
    acceptTerms: "أوافق على",
    termsAndConditions: "الشروط والأحكام",
    acceptAndClose: "قبول وإغلاق",
    close: "إغلاق",
    loginTermsMessage: "بتسجيل الدخول، فإنك توافق تلقائياً على",
    generalConversation: "محادثة عامة",
    generalConversation: "محادثة عامة",
    // Tracking translations
    tracking: "التتبع",
    myProfile: "ملفي الشخصي",
    profileAndStatus: "الملف الشخصي والحالة",
    availableTransportRequests: "طلبات النقل المتاحة",
    cancel: "إلغاء",
    save: "حفظ",
    priceList: "قائمة الأسعار",
    loading: "جاري التحميل...",
    transporterNotFound: "لم يتم العثور على الناقل",
    bookingLinkInvalid: "رابط الحجز هذا لم يعد صالحاً.",
    loadMore: "تحميل المزيد",
    previous: "السابق",
    next: "التالي",
    becomePartner: "كن شريكًا",
    startTodayReceiveRequests: "ابدأ اليوم واستلم طلبات النقل مباشرة",
    companyName: "اسم الشركة",
    yourCompanyName: "اسم شركتك",
    phoneNumber: "رقم الهاتف",
    country: "البلد",
    password: "كلمة المرور",
    minCharacters: "8 أحرف على الأقل",
    confirmPassword: "تأكيد كلمة المرور",
    repeatPassword: "كرر كلمة المرور",
    goToPayment: "المتابعة إلى الدفع",
    monthlySubscription: "حساب مجاني",
    exclVAT: "باستثناء ضريبة القيمة المضافة",
    inclVAT: "شامل ضريبة 21%",
    benefitsForPartners: "مزايا للشركاء:",
    directAccessRequests: "وصول مباشر لطلبات النقل الجديدة",
    chatDirectly: "دردش مباشرة مع العملاء",
    monthlySubscriptionAmount: "تسجيل مجاني - بدون رسوم شهرية",
    earnExtra: "اكسب حتى €500-1000 إضافية شهريًا",
    freeRegister: "تسجيل مجاني",
    registeringText: "...جاري التسجيل",
    freeRegisterBadge: "تسجيل مجاني 100%",
    freeRegisterDesc: "!بدون اشتراك، بدون عمولة، بدون رسوم خفية. مجاني 100%",
    registrationSuccessAlert: "✅ !تم التسجيل بنجاح! يتم مراجعة حسابك من قبل المسؤول. ستتلقى رسالة بمجرد الموافقة على حسابك",
    registrationErrorPrefix: "خطأ: ",
    registrationFailed: "فشل التسجيل",
    registrationErrorGeneral: "حدث خطأ أثناء التسجيل: ",
    inviteCodeOptional: "رمز الدعوة (اختياري)",
    inviteCodeDesc: "هل لديك رمز دعوة؟ أدخله للتسجيل السريع.",
    emailPlaceholder: "info@yourcompany.com",
    alreadyAccount: "لديك حساب بالفعل؟",
    loginHere: "تسجيل الدخول هنا",
    firstMonthFree: "!مجاني 100%",
    thenMonthly: "بدون عمولة، مجاني 100%",
    enterCompanyName: "أدخل اسم شركتك",
    enterValidEmail: "أدخل بريد إلكتروني صحيح",
    enterPhone: "أدخل رقم هاتفك",
    passwordMin8: "يجب أن تكون كلمة المرور 8 أحرف على الأقل",
    passwordsNotMatch: "كلمات المرور غير متطابقة",
    paymentCouldNotStart: "تعذر بدء الدفع",
    goingToPayment: "الانتقال إلى الدفع...",
    submitQuote: "تقديم عرض",
    quote: "عرض",
    quoteDetails: "تفاصيل العرض",
    clearAllFilters: "مسح كل الفلاتر",
    showFilters: "إظهار الفلاتر",
    hideFilters: "إخفاء الفلاتر",
    departureCity: "مدينة المغادرة",
    typeCityName: "اكتب اسم المدينة...",
    requestQuote: "طلب عرض أسعار",
    price: "السعر",
    contact: "الاتصال",
    aboutUs: "من نحن",
    services: "الخدمات",
    coverage: "منطقة التغطية",
    termsConditions: "الشروط والأحكام",
    blogNews: "المدونة والأخبار",
    status: "الحالة",
    pickup: "الاستلام",
    delivery: "التسليم",
    remarks: "ملاحظات",
    estimatedDelivery: "التسليم المقدر",
    days: "أيام",
    trackingUpdates: "تحديثات التتبع",
    pickedUp: "تم الاستلام!",
    updateStatus: "تحديث الحالة",
    shipmentStatus: "حالة الشحنة",
    trackingHistory: "تاريخ التتبع",
    currentStatus: "الحالة الحالية",
    lastUpdate: "آخر تحديث",
    addUpdate: "إضافة تحديث",
    statusPending: "قيد الانتظار",
    statusPickedUp: "تم الاستلام",
    statusInTransit: "في الطريق",
    statusAtCustoms: "في الجمارك",
    statusOutForDelivery: "خرج للتسليم",
    statusDelivered: "تم التسليم",
    location: "الموقع",
    notes: "ملاحظات",
    updateTime: "وقت التحديث",
    noTrackingYet: "لا توجد تحديثات تتبع",
    messageForCustomers: "رسالة لجميع العملاء",
    pickupLocation: "موقع الاستلام",
    pickupNotes: "ملاحظات الاستلام",
    // My Offers translations
    myOffers: "عروضي",
    acceptedOffers: "العروض المقبولة",
    removeRequest: "حذف الطلب",
    confirmRemoveRequest: "هل أنت متأكد من أنك تريد حذف هذا الطلب المسلم؟",
    requestRemoved: "تم حذف الطلب بنجاح",
    viewRequestDetails: "عرض تفاصيل الطلب",
    offerDetails: "تفاصيل العرض",
    requestInfo: "معلومات الطلب",
    customerInfo: "معلومات العميل",
    viewFullRequest: "عرض الطلب الكامل",
    // Company directory translations
    availableCompanies: "الشركات المتاحة",
    companyDirectory: "دليل الشركات",
    viewProfile: "عرض الملف الشخصي",
    contactCompany: "اتصال بالشركة",
    companyProfile: "ملف الشركة",
    companyDescription: "وصف الشركة",
    priceList: "قائمة الأسعار",
    phoneNumber: "رقم الهاتف",
    currentLocation: "الموقع الحالي",
    departureDates: "تواريخ المغادرة",
    toMorocco: "إلى المغرب",
    toEurope: "إلى أوروبا",
    reviews: "التقييمات",
    averageRating: "التقييم المتوسط",
    totalReviews: "إجمالي التقييمات",
    writeReview: "كتابة تقييم",
    rateCompany: "تقييم الشركة",
    reviewText: "نص التقييم",
    rating: "التقييم",
    stars: "نجوم",
    submitReview: "إرسال التقييم",
    noCompanies: "لا توجد شركات متاحة",
    noReviews: "لا توجد تقييمات",
    profilePicture: "صورة الملف الشخصي",
    updateProfile: "تحديث الملف الشخصي",
    updateStatus: "تحديث الحالة",
    noPriceList: "لا توجد قائمة أسعار متاحة",
    noDescription: "لا يوجد وصف متاح",
    contactInfo: "معلومات الاتصال",
    optional: "اختياري",
    close: "إغلاق",
    onlineStatus: "الحالة على الإنترنت",
    goOnline: "الاتصال بالإنترنت",
    goOffline: "قطع الاتصال",
    currentlyOnline: "متصل حاليًا",
    currentlyOffline: "غير متصل حاليًا",
    languages: "اللغات",
    communicationLanguages: "لغات التواصل",
    selectLanguages: "اختر اللغات",
    dutch: "الهولندية",
    french: "الفرنسية",
    arabic: "العربية",
    english: "الإنجليزية",
    german: "الألمانية",
    spanish: "الإسبانية",
    clearTracking: "مسح التتبع",
    clearTrackingConfirm: "هل أنت متأكد من أنك تريد مسح جميع تحديثات التتبع؟",
    trackingCleared: "تم مسح التتبع بنجاح",
    // Search and filter translations
    searchCompanies: "البحث عن الشركات",
    searchPlaceholder: "البحث بالاسم أو الموقع...",
    filterBy: "تصفية حسب",
    showFilters: "إظهار المرشحات",
    hideFilters: "إخفاء المرشحات",
    allLanguages: "جميع اللغات",
    allCountries: "جميع البلدان",
    countryFilter: "بلد المنشأ",
    departureCountryFilter: "بلد المغادرة",
    departureDateFilter: "تاريخ المغادرة إلى المغرب",
    selectDate: "اختر التاريخ",
    selectCountry: "اختر البلد",
    clearFilters: "مسح المرشحات",
    companiesFound: "شركة موجودة",
    // Country translations
    netherlands: "هولندا",
    belgium: "بلجيكا",
    germany: "ألمانيا",
    france: "فرنسا",
    spain: "إسبانيا", 
    italy: "إيطاليا",
    portugal: "البرتغال",
    austria: "النمسا",
    switzerland: "سويسرا",
    luxembourg: "لوكسمبورغ",
    denmark: "الدنمارك",
    sweden: "السويد",
    norway: "النرويج",
    finland: "فنلندا",
    poland: "بولندا",
    czech_republic: "جمهورية التشيك",
    hungary: "المجر",
    morocco: "المغرب",
    algeria: "الجزائر",
    tunisia: "تونس",
    egypt: "مصر",
    united_kingdom: "المملكة المتحدة",
    ireland: "إيرلندا",
    not_specified: "غير محدد",
    other: "أخرى",
    // Welcome Screen translations
    welcomeBack: "أهلاً بعودتك",
    welcomeAdmin: "مرحباً أيها المدير! قم بإدارة المنصة وعرض الإحصائيات.",
    welcomeCustomer: "مرحباً! اشحن أمتعتك بسهولة إلى المغرب.",
    welcomeTransport: "مرحباً بشركة النقل! اعثر على عملاء جدد وطور عملك.",
    brandName: "BaggageTransport",
    brandSubtitle: "أوروبا ← المغرب",
    europeToMorocco: "أوروبا ← المغرب",
    reliableConnection: "اتصال موثوق",
    europeToMoroccoDesc: "نقل آمن وسريع بين أوروبا والمغرب عبر شبكة الناقلين الموثوقين لدينا.",
    realTimeTracking: "التتبع في الوقت الفعلي",
    alwaysUpToDate: "محدث دائماً",
    realTimeTrackingDesc: "تتبع أمتعتك في الوقت الفعلي مع نظام التتبع المتقدم لدينا وابق مطلعاً دائماً.",
    securePlatform: "منصة آمنة",
    hundredPercentSecure: "آمنة 100%",
    securePlatformDesc: "بياناتك ومعاملاتك محمية بالكامل بأحدث تقنيات الأمان.",
    whereToGo: "إلى أين تريد الذهاب؟",
    adminDashboardTitle: "لوحة تحكم المدير",
    adminDashboardDesc: "إدارة المستخدمين وعرض الإحصائيات",
    myDashboardTitle: "لوحة التحكم الخاصة بي",
    myDashboardDesc: "إدارة طلبات النقل وتتبع أمتعتك",
    transportDashboardTitle: "لوحة تحكم شركة النقل",
    transportDashboardDesc: "عرض الطلبات المتاحة وإدارة عروض الأسعار",
    goToDashboard: "اذهب إلى لوحة التحكم",
    trustedPartner: "شريكك الموثوق لنقل الأمتعة الدولي",
    // Login page
    loginSubtitle: "تسجيل الدخول لإدارة طلبات النقل",
    registerSubtitle: "سجل لتقديم طلبات النقل",
    forgotPasswordQuestion: "نسيت كلمة المرور؟",
    registerAsCustomerLink: "التسجيل كعميل",
    backToLoginLink: "العودة لتسجيل الدخول",
    // Sidebar menu subtitles
    newTransportOpportunities: "فرص نقل جديدة",
    acceptedJobs: "المهام المقبولة",
    bookingsFromMyCustomers: "حجوزات من عملائي",
    chatWithCustomers: "الدردشة مع العملاء",
    statusUpdates: "تحديثات الحالة",
    conversations: "المحادثات",
    // Dashboard header subtitles
    viewNewOpportunities: "عرض فرص النقل الجديدة وتقديم عروض الأسعار",
    manageAcceptedJobs: "إدارة المهام المقبولة والتسليمات",
    bookAndManageCustomers: "حجز وإدارة شحنات عملائك الدائمين",
    communicateDirectly: "تواصل مباشرة مع عملائك",
    updateShipmentStatus: "تحديث حالة شحناتك",
    manageBusinessProfile: "إدارة ملف شركتك وحالتها",
    transportDashboard: "لوحة تحكم النقل",
    transportCompanyLabel: "شركة النقل",
    // Search and pagination
    searchCityCustomer: "البحث حسب المدينة أو اسم العميل...",
    ofRequests: "من",
    requests: "طلبات",
    results: "نتائج",
    showing: "عرض",
    of: "من",
    // Alert messages
    successStatusChanged: "تم تغيير الحالة بنجاح!",
    successCompanyStatusUpdated: "تم تحديث حالة الشركة بنجاح!",
    errorUpdatingCompanyStatus: "خطأ في تحديث حالة الشركة",
    updatesCleared: "تم مسح التحديثات",
    successProfileUpdated: "تم تحديث الملف الشخصي بنجاح!",
    errorUpdatingProfile: "خطأ في تحديث الملف الشخصي",
    successStatusUpdated: "تم تحديث الحالة بنجاح!",
    errorUpdatingStatus: "خطأ في تحديث الحالة",
    successStatusUpdatedTo: "تم تحديث الحالة بنجاح إلى",
    errorUpdatingOnlineStatus: "خطأ في تحديث حالة الاتصال",
    errorClearingTracking: "خطأ في مسح التتبع",
    // Form labels
    noRequestsAvailable: "لا توجد طلبات متاحة",
    noRequestsFound: "لم يتم العثور على طلبات",
    noBookingsYet: "لا توجد حجوزات بعد",
    createFirstBooking: "أنشئ حجزك الأول",
    noConversations: "لا توجد محادثات",
    selectConversation: "اختر محادثة للبدء",
    // More alerts
    bookingSuccessfullyCreated: "تم إنشاء الحجز بنجاح!",
    errorCreatingBooking: "خطأ في إنشاء الحجز. يرجى المحاولة مرة أخرى.",
    errorUpdatingBookingStatus: "خطأ في تحديث الحالة. يرجى المحاولة مرة أخرى.",
    enterValidPrice: "يرجى إدخال سعر صالح",
    priceSuccessfullyAdded: "تمت إضافة السعر بنجاح!",
    errorUpdatingPrice: "خطأ في تحديث السعر. يرجى المحاولة مرة أخرى.",
    confirmDeleteBooking: "هل أنت متأكد أنك تريد حذف هذا الحجز؟",
    bookingDeleted: "تم حذف الحجز بنجاح!",
    errorDeletingBooking: "خطأ في حذف الحجز",
    // More alerts for transport company
    errorConfirmingPickup: "خطأ في تأكيد الاستلام",
    confirmDeliveryQuestion: "هل تؤكد أنك قمت بتسليم الأمتعة؟",
    deliveryConfirmedByCompany: "تم تأكيد التسليم من قبل الشركة! في انتظار تأكيد العميل.",
    errorConfirmingDelivery: "خطأ في تأكيد التسليم",
    // Missing keys
    awaitingConfirmation: "في انتظار التأكيد",
    bookMyShipment: "احجز شحنتي",
    bookingInProgress: "الحجز قيد التنفيذ",
    bookingSuccessful: "تم الحجز بنجاح",
    bookingsFromOwnCustomers: "حجوزات من عملائي",
    clickToUpload: "انقر للتحميل",
    companyStatus: "حالة الشركة",
    confirm: "تأكيد",
    confirmed: "مؤكد",
    delete: "حذف",
    discoverSend2Maroc: "اكتشف Send2Maroc",
    dragPhotosHere: "اسحب الصور هنا",
    europe: "أوروبا",
    forCustomersDesc: "أرسل أمتعتك بسهولة إلى المغرب مع ناقلين موثوقين",
    forCustomersTitle: "للعملاء",
    forTransportCompaniesDesc: "ابحث عن عملاء جدد وطور عملك في مجال النقل",
    forTransportCompaniesTitle: "لشركات النقل",
    howItWorksDesc: "اكتشف كيف يعمل Send2Maroc",
    howItWorksTitle: "كيف يعمل",
    howItWorksButton: "كيف يعمل",
    trackAndTrace: "تتبع الشحنة",
    trackYourShipment: "تتبع شحنتك",
    enterTrackingCode: "أدخل رمز التتبع",
    search: "بحث",
    back: "رجوع",
    inTransit: "في الطريق",
    menu: "القائمة",
    newBooking: "حجز جديد",
    ourServicesDesc: "خدمات نقل موثوقة بين أوروبا والمغرب",
    ourServicesTitle: "خدماتنا",
    ownCustomers: "عملائي",
    photosRemaining: "صور متبقية",
    pickupConfirmed: "تم تأكيد الاستلام",
    receiveQuotes: "استلم عروض الأسعار",
    requestTransport: "اطلب النقل",
    saveTrackingCode: "احفظ رمز التتبع",
    trackMyShipment: "تتبع شحنتي",
    updateCompanyStatus: "تحديث حالة الشركة",
    // Form fields
    baggage_description: "وصف الأمتعة",
    delivery_address: "عنوان التسليم",
    delivery_city: "مدينة التسليم",
    pickup_address: "عنوان الاستلام",
    pickup_city: "مدينة الاستلام",
    pickup_date: "تاريخ الاستلام",
    special_instructions: "تعليمات خاصة",
    weight: "الوزن",
    ownCustomersDescription: "عملاؤك يبقون عملاءك. احجز وأدر شحناتهم هنا.",
    ourServices: "خدماتنا",
    reliableTransport: "نقل أمتعة موثوق من أوروبا إلى المغرب",
    personalBaggage: "أمتعة شخصية",
    personalBaggageDesc: "أرسل متعلقاتك الشخصية بأمان إلى العائلة والأصدقاء في المغرب.",
    businessGoods: "بضائع تجارية",
    businessGoodsDesc: "نقل احترافي للشحنات التجارية والبضائع.",
    documents: "وثائق",
    documentsDesc: "إرسال آمن للوثائق والأوراق المهمة.",
    whySend2Maroc: "لماذا Send2Maroc؟",
    hundredPercentSafe: "آمن 100%",
    verifiedCompanies: "جميع شركات النقل معتمدة ومؤمنة",
    bestPrices: "أفضل الأسعار",
    compareQuotes: "قارن العروض واختر الأفضل",
    fastReliable: "سريع وموثوق",
    averageDelivery: "متوسط 5-7 أيام من أوروبا إلى المغرب",
    startNow: "ابدأ الآن",
    iosInstallHint: "اضغط على 'مشاركة' ثم 'أضف إلى الشاشة الرئيسية' لتثبيت التطبيق",
    installAppHint: "ثبّت تطبيق Send2Maroc للوصول السريع",
    installText: "تثبيت",
    laterText: "لاحقاً"
  }
};

// AuthContext for managing authentication state
const AuthContext = React.createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on app start
    // localStorage = "remember me" (persists across browser restarts)
    // sessionStorage = default (cleared when browser/tab is closed)
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      // Verify token and get user info
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUserInfo();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserInfo = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      // Show message if account not approved
      if (error.response?.status === 403) {
        alert(error.response?.data?.detail || 'Uw account is niet goedgekeurd.');
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { 
        email, 
        password, 
        terms_accepted: true // Automatically accept terms on login
      });
      const { access_token, user_type, user_id } = response.data;
      
      // Store token based on "remember me" choice
      // Clear both first to avoid stale tokens lingering
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      if (rememberMe) {
        localStorage.setItem('token', access_token);
      } else {
        sessionStorage.setItem('token', access_token);
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      await fetchUserInfo();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      const { access_token } = response.data;
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      await fetchUserInfo();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const googleSignIn = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/google-signin`, { 
        session_id: sessionId 
      });
      const { access_token, user } = response.data;
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setUser(user);
      return { success: true, user };
    } catch (error) {
      console.error('Google Sign-in error:', error);
      return { success: false, error: error.response?.data?.detail || 'Google sign-in failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    // Force a full page reload to clear all cached state
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{ user, login, register, googleSignIn, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Language Context
const LanguageContext = React.createContext();

const LanguageProvider = ({ children }) => {
  // Load language from localStorage or default to 'nl'
  const [language, setLanguageState] = useState(() => {
    try {
      const saved = localStorage.getItem('selectedLanguage');
      return saved || 'nl';
    } catch {
      return 'nl';
    }
  });

  // Sync language state with localStorage on mount and storage changes
  useEffect(() => {
    const syncLanguage = () => {
      try {
        const saved = localStorage.getItem('selectedLanguage');
        if (saved && saved !== language) {
          setLanguageState(saved);
        }
      } catch {
        // Ignore localStorage errors
      }
    };
    
    // Listen for storage changes (e.g., from other tabs)
    window.addEventListener('storage', syncLanguage);
    
    // Also sync on mount
    syncLanguage();
    
    return () => window.removeEventListener('storage', syncLanguage);
  }, []);

  // Wrapper to save language to localStorage
  const setLanguage = (lang) => {
    try {
      localStorage.setItem('selectedLanguage', lang);
    } catch {
      // Ignore localStorage errors
    }
    setLanguageState(lang);
  };

  const t = (key) => {
    return translations[language]?.[key] || translations['nl']?.[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const useLanguage = () => {
  const context = React.useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Country translation helper function
const translateCountry = (country, t) => {
  if (!country || country === '' || country === null) {
    return t('not_specified');
  }
  
  // Convert country name to translation key (lowercase, replace spaces with underscores)
  const countryKey = country.toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
  
  // Try to get translation, fallback to original country name if not found
  const translatedCountry = t(countryKey);
  
  // If translation returns the same key (meaning no translation found), return original
  return translatedCountry === countryKey ? country : translatedCountry;
};

// Sidebar Menu Component
const SidebarMenu = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const menuItems = [
    { path: '/about', label: t('aboutUs'), icon: '🏢' },
    { path: '/how-it-works', label: t('howItWorksTitle'), icon: '🔄' },
    { path: '/services', label: t('services'), icon: '📦' },
    { path: '/coverage', label: t('coverage'), icon: '🌍' },
    { path: '/track', label: t('trackAndTrace'), icon: '📍' },
    { path: '/export-rules', label: t('exportRules'), icon: '📋' },
    { path: '/contact', label: t('contact'), icon: '📞' },
    { path: '/terms', label: t('termsConditions'), icon: '📄' },
    { path: '/blog', label: t('blogNews'), icon: '📰' }
  ];

  const handleNavigate = (path) => {
    navigate(path);
    onClose();
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 h-full w-80 bg-white shadow-2xl z-50 
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">{t('menu')}</h2>
            <button 
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl"
            >
              ×
            </button>
          </div>
        </div>
        
        {/* Menu Items */}
        <div className="p-4">
          <nav>
            {menuItems.map((item, index) => (
              <button
                key={index}
                onClick={() => handleNavigate(item.path)}
                className="
                  w-full flex items-center p-4 mb-2 rounded-lg
                  hover:bg-gray-100 transition-colors duration-200
                  text-left group
                "
              >
                <span className="text-2xl mr-4 group-hover:scale-110 transition-transform duration-200">
                  {item.icon}
                </span>
                <span className="text-gray-700 font-medium group-hover:text-blue-600">
                  {item.label}
                </span>
              </button>
            ))}
          </nav>
          
          {/* Action Buttons */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <button
              onClick={() => handleNavigate('/login')}
              className="
                w-full bg-blue-600 text-white py-3 px-4 rounded-lg mb-3
                hover:bg-blue-700 transition-colors duration-200 font-medium
              "
            >
              {t('login')}
            </button>
            <button
              onClick={() => handleNavigate('/companies')}
              className="
                w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg
                hover:bg-gray-200 transition-colors duration-200 font-medium
              "
            >
              {t('forCompanies')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// Homepage Component
const Homepage = () => {
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const logo = useLogo();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Price estimate calculator (hero) state
  const [calcPickup, setCalcPickup] = useState('');
  const [calcDestination, setCalcDestination] = useState('');
  const [calcKilos, setCalcKilos] = useState('');
  const calcKilosNum = parseFloat(calcKilos);
  const calcValid = calcPickup.trim() && calcDestination.trim() && calcKilosNum > 0;
  const calcLow = calcValid ? Math.round(calcKilosNum * 2.0) : 0;
  const calcHigh = calcValid ? Math.round(calcKilosNum * 2.5) : 0;

  // Terms and Conditions content in multiple languages
  const getTermsContent = (lang) => {
    const terms = {
      en: {
        title: "Terms and Conditions",
        content: `
          <h2 class="text-xl font-bold mb-4">Terms and Conditions for Send2Maroc</h2>
          
          <h3 class="text-lg font-semibold mb-2">1. Platform Role</h3>
          <p class="mb-4">Send2Maroc operates solely as a <strong>mediator platform</strong> connecting customers in Europe with transport companies offering baggage transport services to Morocco. We facilitate connections between parties but do not provide transport services ourselves.</p>
          
          <h3 class="text-lg font-semibold mb-2">2. Limitation of Liability</h3>
          <p class="mb-4"><strong>IMPORTANT:</strong> Send2Maroc.com is <strong>NOT RESPONSIBLE</strong> for:</p>
          <ul class="list-disc pl-6 mb-4">
            <li>Lost, stolen, or missing goods during transport</li>
            <li>Damage to goods during handling, loading, or transport</li>
            <li>Delays in pickup or delivery</li>
            <li>Actions or omissions of transport companies</li>
            <li>Quality of transport services provided by third parties</li>
          </ul>
          
          <h3 class="text-lg font-semibold mb-2">3. Direct Agreement</h3>
          <p class="mb-4">All transport agreements are made directly between customers and transport companies. Send2Maroc is not a party to these agreements and assumes no obligations or responsibilities arising from them.</p>
          
          <h3 class="text-lg font-semibold mb-2">4. User Responsibilities</h3>
          <p class="mb-4">Users agree to:</p>
          <ul class="list-disc pl-6 mb-4">
            <li>Provide accurate information</li>
            <li>Package goods appropriately for transport</li>
            <li>Comply with customs and import/export regulations</li>
            <li>Resolve disputes directly with transport companies</li>
          </ul>
          
          <h3 class="text-lg font-semibold mb-2">5. Platform Usage</h3>
          <p class="mb-4">By using Send2Maroc, you acknowledge that you understand our role as a mediator platform and accept full responsibility for your transactions with transport companies.</p>
          
          <p class="text-sm text-gray-600 mt-6">Last updated: ${new Date().toLocaleDateString()}</p>
        `
      },
      nl: {
        title: "Algemene Voorwaarden",
        content: `
          <h2 class="text-xl font-bold mb-4">Algemene Voorwaarden voor Send2Maroc</h2>
          
          <h3 class="text-lg font-semibold mb-2">1. Platformrol</h3>
          <p class="mb-4">Send2Maroc functioneert uitsluitend als een <strong>bemiddelingsplatform</strong> dat klanten in Europa verbindt met transportbedrijven die bagagetransportdiensten naar Marokko aanbieden. Wij faciliteren verbindingen tussen partijen maar verlenen zelf geen transportdiensten.</p>
          
          <h3 class="text-lg font-semibold mb-2">2. Beperking van Aansprakelijkheid</h3>
          <p class="mb-4"><strong>BELANGRIJK:</strong> Send2Maroc.com is <strong>NIET VERANTWOORDELIJK</strong> voor:</p>
          <ul class="list-disc pl-6 mb-4">
            <li>Verloren, gestolen of vermiste goederen tijdens transport</li>
            <li>Schade aan goederen tijdens behandeling, laden of transport</li>
            <li>Vertragingen bij ophalen of bezorgen</li>
            <li>Handelingen of nalatigheden van transportbedrijven</li>
            <li>Kwaliteit van transportdiensten geleverd door derden</li>
          </ul>
          
          <h3 class="text-lg font-semibold mb-2">3. Directe Overeenkomst</h3>
          <p class="mb-4">Alle transportovereenkomsten worden direct gesloten tussen klanten en transportbedrijven. Send2Maroc is geen partij bij deze overeenkomsten en neemt geen verplichtingen of verantwoordelijkheden daaruit voortvloeiend op zich.</p>
          
          <h3 class="text-lg font-semibold mb-2">4. Gebruikersverantwoordelijkheden</h3>
          <p class="mb-4">Gebruikers stemmen ermee in om:</p>
          <ul class="list-disc pl-6 mb-4">
            <li>Juiste informatie te verstrekken</li>
            <li>Goederen op de juiste manier in te pakken voor transport</li>
            <li>Douane- en import/exportregelingen na te leven</li>
            <li>Geschillen direct op te lossen met transportbedrijven</li>
          </ul>
          
          <h3 class="text-lg font-semibold mb-2">5. Platformgebruik</h3>
          <p class="mb-4">Door Send2Maroc te gebruiken, erkent u dat u onze rol als bemiddelingsplatform begrijpt en volledige verantwoordelijkheid accepteert voor uw transacties met transportbedrijven.</p>
          
          <p class="text-sm text-gray-600 mt-6">Laatst bijgewerkt: ${new Date().toLocaleDateString()}</p>
        `
      },
      fr: {
        title: "Conditions Générales",
        content: `
          <h2 class="text-xl font-bold mb-4">Conditions Générales pour Send2Maroc</h2>
          
          <h3 class="text-lg font-semibold mb-2">1. Rôle de la Plateforme</h3>
          <p class="mb-4">Send2Maroc fonctionne uniquement comme une <strong>plateforme de médiation</strong> reliant les clients en Europe aux entreprises de transport offrant des services de transport de bagages vers le Maroc. Nous facilitons les connexions entre les parties mais ne fournissons pas de services de transport nous-mêmes.</p>
          
          <h3 class="text-lg font-semibold mb-2">2. Limitation de Responsabilité</h3>
          <p class="mb-4"><strong>IMPORTANT :</strong> Send2Maroc.com n'est <strong>PAS RESPONSABLE</strong> de :</p>
          <ul class="list-disc pl-6 mb-4">
            <li>Marchandises perdues, volées ou manquantes pendant le transport</li>
            <li>Dommages aux marchandises pendant la manutention, le chargement ou le transport</li>
            <li>Retards dans la collecte ou la livraison</li>
            <li>Actions ou omissions des entreprises de transport</li>
            <li>Qualité des services de transport fournis par des tiers</li>
          </ul>
          
          <h3 class="text-lg font-semibold mb-2">3. Accord Direct</h3>
          <p class="mb-4">Tous les accords de transport sont conclus directement entre les clients et les entreprises de transport. Send2Maroc n'est pas partie à ces accords et n'assume aucune obligation ou responsabilité en découlant.</p>
          
          <h3 class="text-lg font-semibold mb-2">4. Responsabilités des Utilisateurs</h3>
          <p class="mb-4">Les utilisateurs acceptent de :</p>
          <ul class="list-disc pl-6 mb-4">
            <li>Fournir des informations exactes</li>
            <li>Emballer les marchandises de manière appropriée pour le transport</li>
            <li>Se conformer aux réglementations douanières et d'import/export</li>
            <li>Résoudre les litiges directement avec les entreprises de transport</li>
          </ul>
          
          <h3 class="text-lg font-semibold mb-2">5. Utilisation de la Plateforme</h3>
          <p class="mb-4">En utilisant Send2Maroc, vous reconnaissez comprendre notre rôle de plateforme de médiation et acceptez la pleine responsabilité de vos transactions avec les entreprises de transport.</p>
          
          <p class="text-sm text-gray-600 mt-6">Dernière mise à jour : ${new Date().toLocaleDateString()}</p>
        `
      },
      es: {
        title: "Términos y Condiciones",
        content: `
          <h2 class="text-xl font-bold mb-4">Términos y Condiciones para Send2Maroc</h2>
          
          <h3 class="text-lg font-semibold mb-2">1. Rol de la Plataforma</h3>
          <p class="mb-4">Send2Maroc opera únicamente como una <strong>plataforma mediadora</strong> que conecta clientes en Europa con empresas de transporte que ofrecen servicios de transporte de equipajes a Marruecos. Facilitamos conexiones entre partes pero no proporcionamos servicios de transporte nosotros mismos.</p>
          
          <h3 class="text-lg font-semibold mb-2">2. Limitación de Responsabilidad</h3>
          <p class="mb-4"><strong>IMPORTANTE:</strong> Send2Maroc.com <strong>NO ES RESPONSABLE</strong> de:</p>
          <ul class="list-disc pl-6 mb-4">
            <li>Mercancías perdidas, robadas o faltantes durante el transporte</li>
            <li>Daños a mercancías durante manipulación, carga o transporte</li>
            <li>Retrasos en recogida o entrega</li>
            <li>Acciones u omisiones de empresas de transporte</li>
            <li>Calidad de servicios de transporte proporcionados por terceros</li>
          </ul>
          
          <h3 class="text-lg font-semibold mb-2">3. Acuerdo Directo</h3>
          <p class="mb-4">Todos los acuerdos de transporte se realizan directamente entre clientes y empresas de transporte. Send2Maroc no es parte de estos acuerdos y no asume obligaciones o responsabilidades derivadas de ellos.</p>
          
          <h3 class="text-lg font-semibold mb-2">4. Responsabilidades del Usuario</h3>
          <p class="mb-4">Los usuarios acuerdan:</p>
          <ul class="list-disc pl-6 mb-4">
            <li>Proporcionar información precisa</li>
            <li>Empacar mercancías apropiadamente para transporte</li>
            <li>Cumplir con regulaciones aduaneras y de importación/exportación</li>
            <li>Resolver disputas directamente con empresas de transporte</li>
          </ul>
          
          <h3 class="text-lg font-semibold mb-2">5. Uso de la Plataforma</h3>
          <p class="mb-4">Al usar Send2Maroc, usted reconoce que entiende nuestro rol como plataforma mediadora y acepta plena responsabilidad por sus transacciones con empresas de transporte.</p>
          
          <p class="text-sm text-gray-600 mt-6">Última actualización: ${new Date().toLocaleDateString()}</p>
        `
      },
      ar: {
        title: "الشروط والأحكام",
        content: `
          <h2 class="text-xl font-bold mb-4">الشروط والأحكام لموقع Send2Maroc</h2>
          
          <h3 class="text-lg font-semibold mb-2">1. دور المنصة</h3>
          <p class="mb-4">تعمل Send2Maroc فقط كـ<strong>منصة وساطة</strong> تربط العملاء في أوروبا بشركات النقل التي تقدم خدمات نقل الأمتعة إلى المغرب. نحن نسهل الاتصالات بين الأطراف ولكننا لا نقدم خدمات النقل بأنفسنا.</p>
          
          <h3 class="text-lg font-semibold mb-2">2. تحديد المسؤولية</h3>
          <p class="mb-4"><strong>مهم:</strong> Send2Maroc.com <strong>غير مسؤول</strong> عن:</p>
          <ul class="list-disc pl-6 mb-4">
            <li>البضائع المفقودة أو المسروقة أو المفقودة أثناء النقل</li>
            <li>أضرار البضائع أثناء المناولة أو التحميل أو النقل</li>
            <li>التأخير في الاستلام أو التسليم</li>
            <li>أفعال أو إهمال شركات النقل</li>
            <li>جودة خدمات النقل المقدمة من أطراف ثالثة</li>
          </ul>
          
          <h3 class="text-lg font-semibold mb-2">3. الاتفاق المباشر</h3>
          <p class="mb-4">جميع اتفاقيات النقل تتم مباشرة بين العملاء وشركات النقل. Send2Maroc ليست طرفاً في هذه الاتفاقيات ولا تتحمل أي التزامات أو مسؤوليات تنشأ عنها.</p>
          
          <h3 class="text-lg font-semibold mb-2">4. مسؤوليات المستخدم</h3>
          <p class="mb-4">يوافق المستخدمون على:</p>
          <ul class="list-disc pl-6 mb-4">
            <li>تقديم معلومات دقيقة</li>
            <li>تعبئة البضائع بشكل مناسب للنقل</li>
            <li>الامتثال للوائح الجمركية والاستيراد/التصدير</li>
            <li>حل النزاعات مباشرة مع شركات النقل</li>
          </ul>
          
          <h3 class="text-lg font-semibold mb-2">5. استخدام المنصة</h3>
          <p class="mb-4">باستخدام Send2Maroc، فإنك تقر بأنك تفهم دورنا كمنصة وساطة وتقبل المسؤولية الكاملة عن معاملاتك مع شركات النقل.</p>
          
          <p class="text-sm text-gray-600 mt-6">آخر تحديث: ${new Date().toLocaleDateString()}</p>
        `
      }
    };
    return terms[lang] || terms['en'];
  };

  const homeStructuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Send2Maroc",
      "description": t('homeDescription'),
      "url": `${window.location.origin}/`,
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${window.location.origin}/companies?search={search_term_string}`,
        "query-input": "required name=search_term_string"
      },
      "mainEntity": {
        "@type": "Service",
        "name": "Baggagetransport Europa naar Marokko",
        "description": t('homeDescription'),
        "provider": {
          "@type": "Organization",
          "name": "Send2Maroc"
        },
        "areaServed": ["Netherlands", "France", "Belgium", "Morocco"]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Send2Maroc",
      "description": "Platform connecting customers in Europe with reliable transport companies for baggage delivery to Morocco",
      "url": `${window.location.origin}/`,
      "logo": `${window.location.origin}/logo.png`,
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "availableLanguage": ["Dutch", "French", "Arabic"],
          "url": `${window.location.origin}/login`
        }
      ],
      "areaServed": [
        {"@type": "Country", "name": "Netherlands"},
        {"@type": "Country", "name": "France"},
        {"@type": "Country", "name": "Belgium"},
        {"@type": "Country", "name": "Morocco"}
      ],
      "serviceType": "Baggage Transport Platform"
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": `${window.location.origin}/`
        }
      ]
    },
    {
      "@context": "https://schema.org", 
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Hoe werkt Send2Maroc?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Send2Maroc verbindt klanten in Europa met betrouwbare transportbedrijven voor baggagetransport naar Marokko. U plaatst een verzoek, ontvangt offertes en kiest het beste aanbod."
          }
        },
        {
          "@type": "Question", 
          "name": "Is Send2Maroc veilig?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Ja, we werken alleen met geverifieerde en betrouwbare transportbedrijven. Alle bedrijven worden gecontroleerd voordat ze op ons platform worden toegelaten."
          }
        },
        {
          "@type": "Question",
          "name": "Naar welke landen transport Send2Maroc?", 
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Send2Maroc specialiseert zich in baggagetransport van Europa (Nederland, Frankrijk, België) naar Marokko."
          }
        }
      ]
    }
  ];

  const handleCustomerLogin = () => {
    console.log('Customer button clicked, navigating to:', '/login');
    navigate('/login');
  };

  const handleCompanyLogin = () => {
    console.log('Company button clicked, navigating to:', '/companies');
    navigate('/companies');
  };

  // Update document language attribute based on current language
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  return (
    <>
      <SEO 
        title="Betrouwbaar Baggagetransport"
        description={t('homeDescription')}
        keywords="bagage transport, Marokko, Europa, Nederland, Frankrijk, transportbedrijf, bagage verzenden, luggage transport, Morocco, Europe, shipping"
        structuredData={homeStructuredData}
      />
      
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-lg border-b-2 border-blue-100">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
              {/* Hamburger Menu Button */}
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors duration-200 shadow-md flex-shrink-0"
                title="Menu openen"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              
              {logo ? (
                <img 
                  src={logo} 
                  alt="Send2Maroc Logo" 
                  className="h-8 sm:h-12 w-auto object-contain flex-shrink-0"
                />
              ) : (
                <>
                  <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-600 to-blue-800 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
                    <svg className="w-4 h-4 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="text-lg sm:text-2xl font-bold text-blue-900 truncate block">{t('title')}</span>
                    <p className="text-xs sm:text-sm text-blue-600 font-medium truncate">{t('europeToMorocco')}</p>
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center">
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="px-2 sm:px-4 py-2 border-2 border-blue-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white flex-shrink-0"
              >
                <option value="nl">{t('dutch')}</option>
                <option value="en">{t('english')}</option>
                <option value="fr">{t('french')}</option>
                <option value="ar">{t('arabic')}</option>
              </select>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with authentic Moroccan-inspired design */}
      <div className="relative min-h-[60vh] sm:min-h-[70vh] lg:min-h-[80vh] flex items-center overflow-hidden">
        {/* Background with Moroccan zellige tile pattern */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(135deg, rgba(30, 64, 175, 0.9) 0%, rgba(37, 99, 235, 0.85) 50%, rgba(59, 130, 246, 0.8) 100%), 
                             url('https://customer-assets.emergentagent.com/job_euro-morocco-cargo/artifacts/b0dia8w6_milad-alizadeh-JibMa0FbyHw-unsplash.jpg')`
          }}
        ></div>
        
        {/* Moroccan decorative arches - hidden on mobile for cleaner look */}
        <div className="absolute top-0 left-0 w-full h-20 overflow-hidden hidden sm:block">
          <svg viewBox="0 0 1200 100" className="w-full h-full text-white/10">
            <path d="M0,100 Q150,0 300,100 Q450,0 600,100 Q750,0 900,100 Q1050,0 1200,100 L1200,0 L0,0 Z" fill="currentColor"/>
          </svg>
        </div>
        
        {/* Traditional Moroccan geometric patterns - reduced opacity on mobile */}
        <div className="absolute inset-0 opacity-5 sm:opacity-15">
          <div className="w-full h-full" style={{
            backgroundImage: `url('data:image/svg+xml,<svg width="120" height="120" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg"><defs><pattern id="moroccan-star" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse"><polygon points="30,5 35,20 50,20 38,30 43,45 30,35 17,45 22,30 10,20 25,20" fill="%23ffffff" fill-opacity="0.1"/><circle cx="30" cy="30" r="3" fill="%23ffffff" fill-opacity="0.15"/></pattern></defs><rect width="120" height="120" fill="url(%23moroccan-star)"/></svg>')`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-8 sm:py-12">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left side - Content */}
            <div className="text-center lg:text-left">
              <div className="mb-3 sm:mb-4">
                {/* Moroccan-inspired badge */}
                <div className="inline-flex items-center px-3 sm:px-6 py-2 sm:py-3 bg-white/20 backdrop-blur-md rounded-full text-white text-xs sm:text-sm font-medium mb-3 sm:mb-4 border border-white/30">
                  <svg className="w-3 h-3 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  ✨ {t('trustedPartner')}
                </div>
              </div>
              
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-3 sm:mb-4 leading-tight px-2 sm:px-0">
                <span className="block">{t('title')}</span>
                <span className="block text-base sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-medium text-blue-100 mt-2">{t('europeToMorocco')}</span>
              </h1>
              
              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-blue-100 mb-5 sm:mb-6 leading-relaxed max-w-2xl mx-auto lg:mx-0 px-2 sm:px-0">
                {t('homeDescription')}
              </p>
              
              {/* CTA Buttons with Moroccan arch styling */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start max-w-sm sm:max-w-md mx-auto lg:max-w-none lg:mx-0 px-2 sm:px-0">
                <button 
                  onClick={handleCustomerLogin}
                  className="group relative inline-flex items-center justify-center px-5 sm:px-8 py-3 sm:py-4 text-sm sm:text-base md:text-lg font-semibold text-white bg-white/20 backdrop-blur-md border-2 border-white/30 rounded-xl sm:rounded-2xl hover:bg-white hover:text-blue-900 transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl min-h-[44px] sm:min-h-[48px] w-full sm:w-auto"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/5 rounded-xl sm:rounded-2xl"></div>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 group-hover:text-blue-900 relative z-10 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="relative z-10">{t('customerLogin')}</span>
                </button>
                
                <button 
                  onClick={() => navigate('/track')}
                  className="group relative inline-flex items-center justify-center px-5 sm:px-8 py-3 sm:py-4 text-sm sm:text-base md:text-lg font-semibold text-white bg-green-500/90 backdrop-blur-md border-2 border-green-400/50 rounded-xl sm:rounded-2xl hover:bg-green-400 hover:text-green-900 transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl min-h-[44px] sm:min-h-[48px] w-full sm:w-auto"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {t('trackAndTrace')}
                </button>
                
                <button 
                  onClick={handleCompanyLogin}
                  className="group relative inline-flex items-center justify-center px-5 sm:px-8 py-3 sm:py-4 text-sm sm:text-base md:text-lg font-semibold text-blue-900 bg-white rounded-xl sm:rounded-2xl hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl border-2 border-white/20 min-h-[44px] sm:min-h-[48px] w-full sm:w-auto"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  {t('companyRegistration')}
                </button>
              </div>
            </div>
            
            {/* Right side - Transport/Morocco imagery */}
            <div className="relative lg:block hidden">
              <div className="relative w-full h-96">
                {/* Main transport image with Moroccan frame */}
                <div className="absolute inset-0 rounded-3xl overflow-hidden shadow-2xl border-4 border-white/20">
                  <img 
                    src="https://images.unsplash.com/photo-1702219044834-5951d7d58e62?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxtb3JvY2NvJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHxibHVlfDE3NjI5OTM0ODB8MA&ixlib=rb-4.1.0&q=85"
                    alt="Hassan II Mosque - Professional Morocco architecture and transport destination"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/40 via-transparent to-transparent"></div>
                  
                  {/* Overlay with route information */}
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-bold">🇪🇺</span>
                          </div>
                          <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                          <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-bold">🇲🇦</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-blue-900 font-bold text-lg">{t('europeToMorocco')}</p>
                          <p className="text-blue-600 text-sm">{t('reliableConnection')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Floating Moroccan-inspired decorative elements */}
                <div className="absolute -top-6 -right-6 w-20 h-20 bg-white/90 rounded-full flex items-center justify-center backdrop-blur-sm shadow-xl border-4 border-blue-200">
                  <span className="text-2xl">📦</span>
                </div>
                <div className="absolute -bottom-6 -left-6 w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center shadow-xl border-4 border-white">
                  <span className="text-white text-xl">🚚</span>
                </div>
                
                {/* Traditional Moroccan corner decoration */}
                <div className="absolute top-4 left-4 w-12 h-12 border-l-4 border-t-4 border-white/40 rounded-tl-lg"></div>
                <div className="absolute top-4 right-4 w-12 h-12 border-r-4 border-t-4 border-white/40 rounded-tr-lg"></div>
                <div className="absolute bottom-4 left-4 w-12 h-12 border-l-4 border-b-4 border-white/40 rounded-bl-lg"></div>
                <div className="absolute bottom-4 right-4 w-12 h-12 border-r-4 border-b-4 border-white/40 rounded-br-lg"></div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom Moroccan arch decoration */}
        <div className="absolute bottom-0 left-0 w-full h-20 overflow-hidden rotate-180">
          <svg viewBox="0 0 1200 100" className="w-full h-full text-white/10">
            <path d="M0,100 Q150,0 300,100 Q450,0 600,100 Q750,0 900,100 Q1050,0 1200,100 L1200,0 L0,0 Z" fill="currentColor"/>
          </svg>
        </div>
      </div>

      {/* Price Estimate Calculator Section */}
      <section className="relative z-20 px-3 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-14 sm:pb-16 bg-gradient-to-b from-blue-600 via-blue-500 to-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl border border-blue-100 p-5 sm:p-8 overflow-hidden" data-testid="price-calculator">
            <div className="text-center mb-6">
              <div className="inline-flex items-center px-3 py-1.5 bg-blue-100 rounded-full text-blue-800 text-xs sm:text-sm font-semibold mb-3">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                {t('calcBadge')}
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">{t('calcTitle')}</h2>
              <p className="text-sm sm:text-base text-gray-600 mt-1.5 max-w-md mx-auto">{t('calcSubtitle')}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="flex items-center text-sm font-semibold text-gray-700 mb-1.5">
                  <svg className="w-4 h-4 mr-1.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {t('calcPickup')}
                </label>
                <input
                  type="text"
                  value={calcPickup}
                  onChange={(e) => setCalcPickup(e.target.value)}
                  placeholder={t('calcPickupPlaceholder')}
                  data-testid="calc-pickup-input"
                  className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-colors"
                />
              </div>
              <div>
                <label className="flex items-center text-sm font-semibold text-gray-700 mb-1.5">
                  <svg className="w-4 h-4 mr-1.5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                  {t('calcDestination')}
                </label>
                <input
                  type="text"
                  value={calcDestination}
                  onChange={(e) => setCalcDestination(e.target.value)}
                  placeholder={t('calcDestinationPlaceholder')}
                  data-testid="calc-destination-input"
                  className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-colors"
                />
              </div>
              <div>
                <label className="flex items-center text-sm font-semibold text-gray-700 mb-1.5">
                  <svg className="w-4 h-4 mr-1.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                  </svg>
                  {t('calcKilos')}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    inputMode="numeric"
                    value={calcKilos}
                    onChange={(e) => setCalcKilos(e.target.value)}
                    placeholder={t('calcKilosPlaceholder')}
                    data-testid="calc-kilos-input"
                    className="w-full px-4 py-3.5 pr-12 bg-gray-50 border-2 border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-colors"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium pointer-events-none">kg</span>
                </div>
              </div>
            </div>

            {/* Result */}
            <div className="mt-6">
              {calcValid ? (
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-5 sm:p-6 text-center shadow-lg" data-testid="calc-result">
                  <p className="text-xs sm:text-sm text-blue-100 mb-1 uppercase tracking-wide">{t('calcEstimateLabel')}</p>
                  <p className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight" data-testid="calc-result-amount">
                    € {calcLow} – € {calcHigh}
                  </p>
                  <p className="text-xs text-blue-200 mt-2">{calcKilosNum} kg × € 2,00 – € 2,50 {t('calcPerKg')}</p>
                  <button
                    onClick={() => navigate('/login')}
                    data-testid="calc-cta-button"
                    className="mt-5 inline-flex items-center justify-center px-6 py-3.5 bg-white hover:bg-blue-50 text-blue-700 font-bold rounded-xl shadow-md transition-colors w-full min-h-[52px] active:scale-[0.98]"
                  >
                    {t('calcCta')}
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="bg-blue-50 border-2 border-dashed border-blue-200 rounded-2xl p-5 text-center" data-testid="calc-placeholder">
                  <svg className="w-7 h-7 mx-auto mb-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm text-blue-700 font-medium">{t('calcEnterDetails')}</p>
                </div>
              )}
            </div>

            <p className="text-[11px] sm:text-xs text-gray-400 text-center mt-4 px-2">{t('calcDisclaimer')}</p>
          </div>
        </div>
      </section>

      {/* How It Works Section with Moroccan-inspired design */}
      <section id="how-it-works" className="py-12 sm:py-16 lg:py-20 bg-white relative overflow-hidden">
        {/* Moroccan pattern background */}
        <div className="absolute inset-0 opacity-5">
          <div className="w-full h-full" style={{
            backgroundImage: `url('data:image/svg+xml,<svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><defs><pattern id="moroccan" x="0" y="0" width="50" height="50" patternUnits="userSpaceOnUse"><polygon points="25,0 50,25 25,50 0,25" fill="%23dbeafe"/></pattern></defs><rect width="100" height="100" fill="url(%23moroccan)"/></svg>')`,
            backgroundSize: '100px 100px'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <div className="inline-flex items-center px-3 sm:px-4 py-2 bg-blue-100 rounded-full text-blue-800 text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
              {t('howItWorksButton')}
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-blue-900 mb-4 sm:mb-6 px-4">
              {t('howItWorks')}
            </h2>
            <div className="w-16 sm:w-24 h-1 bg-gradient-to-r from-blue-500 to-blue-700 mx-auto rounded-full"></div>
          </div>
          
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 lg:gap-12">
            {/* Step 1 */}
            <div className="text-center group px-4">
              <div className="relative mb-6 sm:mb-8">
                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <span className="text-white font-bold text-xl sm:text-2xl">1</span>
                </div>
                <div className="absolute -inset-4 bg-blue-100 rounded-3xl -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-blue-900 mb-3 sm:mb-4">{t('step1')}</h3>
              <p className="text-sm sm:text-base text-blue-700 leading-relaxed">{t('step1Desc')}</p>
            </div>
            
            {/* Step 2 */}
            <div className="text-center group px-4">
              <div className="relative mb-6 sm:mb-8">
                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <span className="text-white font-bold text-xl sm:text-2xl">2</span>
                </div>
                <div className="absolute -inset-4 bg-blue-100 rounded-3xl -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-blue-900 mb-3 sm:mb-4">{t('step2')}</h3>
              <p className="text-sm sm:text-base text-blue-700 leading-relaxed">{t('step2Desc')}</p>
            </div>
            
            {/* Step 3 */}
            <div className="text-center group px-4 sm:col-span-2 md:col-span-1">
              <div className="relative mb-6 sm:mb-8">
                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <span className="text-white font-bold text-xl sm:text-2xl">3</span>
                </div>
                <div className="absolute -inset-4 bg-blue-100 rounded-3xl -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-blue-900 mb-3 sm:mb-4">{t('step3')}</h3>
              <p className="text-sm sm:text-base text-blue-700 leading-relaxed">{t('step3Desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section with authentic Moroccan imagery */}
      <section id="features" className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-blue-50 via-white to-blue-50 relative overflow-hidden">
        {/* Traditional Moroccan tadelakt-style background */}
        <div className="absolute inset-0 opacity-5">
          <div className="w-full h-full" style={{
            backgroundImage: `url('data:image/svg+xml,<svg width="200" height="200" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><defs><pattern id="moroccan-tiles" x="0" y="0" width="50" height="50" patternUnits="userSpaceOnUse"><rect width="50" height="50" fill="%23dbeafe"/><path d="M25,0 L50,25 L25,50 L0,25 Z" fill="%23bfdbfe" stroke="%2393c5fd" stroke-width="0.5"/><circle cx="25" cy="25" r="8" fill="%2360a5fa" fill-opacity="0.3"/></pattern></defs><rect width="200" height="200" fill="url(%23moroccan-tiles)"/></svg>')`,
            backgroundSize: '200px 200px'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            {/* Left side - Moroccan cultural image */}
            <div className="relative order-2 lg:order-1">
              <div className="aspect-w-4 aspect-h-3 rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl sm:shadow-2xl relative">
                {/* Moroccan cultural image */}
                <img 
                  src="https://images.unsplash.com/photo-1615482475488-8f1ff9addba5?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Beautiful Mosque in Marrakech representing authentic Moroccan culture"
                  className="w-full h-64 sm:h-80 md:h-96 object-cover"
                />
                
                {/* Moroccan-style overlay with geometric pattern */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-transparent to-blue-600/30"></div>
                
                {/* Traditional Moroccan geometric overlay */}
                <div className="absolute inset-0 opacity-20" style={{
                  backgroundImage: `url('data:image/svg+xml,<svg width="80" height="80" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="%23ffffff" fill-opacity="0.4"><polygon points="40,10 50,30 70,30 55,45 60,65 40,50 20,65 25,45 10,30 30,30"/><circle cx="40" cy="40" r="5" fill="%23ffffff" fill-opacity="0.6"/></g></svg>')`,
                  backgroundSize: '80px 80px'
                }}></div>
                
                {/* Cultural connection badge */}
                <div className="absolute bottom-4 sm:bottom-6 left-4 right-4 sm:left-6 sm:right-6">
                  <div className="bg-white/95 backdrop-blur-md rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-blue-200/20 shadow-xl">
                    <div className="flex items-center justify-center space-x-2 sm:space-x-4">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-500 rounded-full flex items-center justify-center shadow-md">
                          <span className="text-white text-xs font-bold">🇪🇺</span>
                        </div>
                        <span className="text-blue-900 font-medium text-xs sm:text-base">{t('europe')}</span>
                      </div>
                      
                      <div className="flex items-center space-x-1">
                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-400 rounded-full animate-pulse"></div>
                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-600 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                      </div>
                      
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <span className="text-red-600 font-medium text-xs sm:text-base">{t('morocco')}</span>
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-red-500 rounded-full flex items-center justify-center shadow-md">
                          <span className="text-white text-xs font-bold">🇲🇦</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating Moroccan-inspired elements - hidden on mobile */}
              <div className="absolute -top-8 -right-8 w-20 h-20 bg-white rounded-3xl shadow-xl flex items-center justify-center border-4 border-blue-100 hidden sm:flex">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              
              <div className="absolute -bottom-8 -left-8 w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-xl flex items-center justify-center border-4 border-white hidden sm:flex">
                <span className="text-white text-2xl">⭐</span>
              </div>
              
              {/* Traditional Moroccan corner decorations - hidden on mobile */}
              <div className="absolute top-6 left-6 hidden sm:block">
                <div className="w-8 h-8 border-l-4 border-t-4 border-white/60 rounded-tl-xl"></div>
              </div>
              <div className="absolute top-6 right-6 hidden sm:block">
                <div className="w-8 h-8 border-r-4 border-t-4 border-white/60 rounded-tr-xl"></div>
              </div>
            </div>
            
            {/* Right side - Enhanced features with Moroccan styling */}
            <div className="order-1 lg:order-2">
              <div className="mb-6 sm:mb-8">
                <div className="inline-flex items-center px-3 sm:px-6 py-2 sm:py-3 bg-blue-100 rounded-full text-blue-800 text-xs sm:text-sm font-medium mb-4 sm:mb-6 border border-blue-200">
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />  
                  </svg>
                  ⭐ {t('whyChooseUsQuestion')}
                </div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-blue-900 mb-4 sm:mb-6 px-4 sm:px-0">
                  {t('whyChooseTitle')}
                </h2>
                <p className="text-blue-700 text-sm sm:text-base md:text-lg leading-relaxed px-4 sm:px-0">
                  {t('whyChooseDescription')}
                </p>
              </div>
              
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-start space-x-3 sm:space-x-4 group p-3 sm:p-4 rounded-2xl hover:bg-blue-50/50 transition-all duration-300">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-base sm:text-lg md:text-xl font-bold text-blue-900 mb-1 sm:mb-2">🛡️ {t('secureReliable')}</h4>
                    <p className="text-sm sm:text-base text-blue-700 leading-relaxed">{t('secureReliableDesc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 sm:space-x-4 group p-3 sm:p-4 rounded-2xl hover:bg-blue-50/50 transition-all duration-300">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-base sm:text-lg md:text-xl font-bold text-blue-900 mb-1 sm:mb-2">💎 {t('multipleQuotes')}</h4>
                    <p className="text-sm sm:text-base text-blue-700 leading-relaxed">{t('multipleQuotesDesc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 sm:space-x-4 group p-3 sm:p-4 rounded-2xl hover:bg-blue-50/50 transition-all duration-300">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-base sm:text-lg md:text-xl font-bold text-blue-900 mb-1 sm:mb-2">🌍 {t('multilingualPlatform')}</h4>
                    <p className="text-sm sm:text-base text-blue-700 leading-relaxed">{t('multilingualPlatformDesc')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Links Section */}
      <section className="py-10 sm:py-12 lg:py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10 lg:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 mb-3 sm:mb-4">{t('discoverSend2Maroc')}</h2>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              Leer meer over onze diensten en hoe wij u kunnen helpen bij uw baggage transport naar Marokko.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-blue-100 rounded-xl group-hover:bg-blue-600 transition-all duration-300 mb-3 sm:mb-4">
                <svg className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600 group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-blue-900 mb-2">{t('forCustomersTitle')}</h3>
              <p className="text-gray-600 mb-3 sm:mb-4 text-sm px-2">{t('forCustomersDesc')}</p>
              <button
                onClick={handleCustomerLogin}
                className="inline-flex items-center px-3 sm:px-4 py-2 text-sm sm:text-base text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200"
              >
                Aanmelden als klant
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-blue-100 rounded-xl group-hover:bg-blue-600 transition-all duration-300 mb-3 sm:mb-4">
                <svg className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600 group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-blue-900 mb-2">{t('forTransportCompaniesTitle')}</h3>
              <p className="text-gray-600 mb-3 sm:mb-4 text-sm px-2">{t('forTransportCompaniesDesc')}</p>
              <button
                onClick={handleCompanyLogin}
                className="inline-flex items-center px-3 sm:px-4 py-2 text-sm sm:text-base text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200"
              >
                Registreer uw bedrijf
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-blue-100 rounded-xl group-hover:bg-blue-600 transition-all duration-300 mb-3 sm:mb-4">
                <svg className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600 group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-blue-900 mb-2">{t('howItWorksTitle')}</h3>
              <p className="text-gray-600 mb-3 sm:mb-4 text-sm px-2">{t('howItWorksDesc')}</p>
              <a
                href="#how-it-works"
                className="inline-flex items-center px-3 sm:px-4 py-2 text-sm sm:text-base text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200"
              >
                Meer informatie
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>

            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-blue-100 rounded-xl group-hover:bg-blue-600 transition-all duration-300 mb-3 sm:mb-4">
                <svg className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600 group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-blue-900 mb-2">{t('ourServicesTitle')}</h3>
              <p className="text-gray-600 mb-3 sm:mb-4 text-sm px-2">{t('ourServicesDesc')}</p>
              <a
                href="#features"
                className="inline-flex items-center px-3 sm:px-4 py-2 text-sm sm:text-base text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200"
              >
                Bekijk voordelen
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer with Moroccan-inspired design */}
      <footer className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-8 sm:py-10 lg:py-12 relative overflow-hidden">
        {/* Moroccan pattern overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="w-full h-full" style={{
            backgroundImage: `url('data:image/svg+xml,<svg width="80" height="80" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="%23ffffff" fill-opacity="0.1"><path d="M40 40h40v-40h-40v40zm0-40V0h-40v40h40z"/></g></svg>')`,
            backgroundSize: '80px 80px'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="flex items-center justify-center space-x-3 sm:space-x-4 mb-4 sm:mb-6">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10" />
              </svg>
            </div>
            <div className="text-left">
              <h3 className="text-xl sm:text-2xl font-bold">{t('title')}</h3>
              <p className="text-blue-200 text-xs sm:text-sm">{t('trustedPartner')}</p>
            </div>
          </div>
          <div className="w-20 sm:w-24 h-1 bg-gradient-to-r from-blue-400 to-blue-300 mx-auto rounded-full mb-4 sm:mb-6"></div>
          <p className="text-blue-200 max-w-2xl mx-auto leading-relaxed text-sm sm:text-base px-4">
            {t('footerCopyright')}
          </p>
        </div>
      </footer>
    </div>
    
    {/* Sidebar Menu */}
    <SidebarMenu isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
    </>
  );
};

// Reusable Page Header Component with Navigation
const PageHeader = () => {
  const { language, setLanguage, t } = useLanguage();
  const logo = useLogo();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <>
      <header className="bg-white shadow-lg border-b-2 border-blue-100">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
              {/* Hamburger Menu Button */}
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors duration-200 shadow-md flex-shrink-0"
                title={t('menu') || 'Menu'}
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              
              {logo ? (
                <img 
                  src={logo} 
                  alt="Send2Maroc Logo" 
                  className="h-8 sm:h-12 w-auto object-contain flex-shrink-0 cursor-pointer"
                  onClick={() => window.location.href = '/'}
                />
              ) : (
                <>
                  <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-600 to-blue-800 rounded-full flex items-center justify-center shadow-lg flex-shrink-0 cursor-pointer" onClick={() => window.location.href = '/'}>
                    <svg className="w-4 h-4 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1 cursor-pointer" onClick={() => window.location.href = '/'}>
                    <span className="text-lg sm:text-2xl font-bold text-blue-900 truncate block">{t('title')}</span>
                    <p className="text-xs sm:text-sm text-blue-600 font-medium truncate">{t('europeToMorocco')}</p>
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center">
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="px-2 sm:px-4 py-2 border-2 border-blue-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white flex-shrink-0"
              >
                <option value="nl">{t('dutch')}</option>
                <option value="en">{t('english')}</option>
                <option value="fr">{t('french')}</option>
                <option value="ar">{t('arabic')}</option>
              </select>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar Menu */}
      <SidebarMenu isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
    </>
  );
};

// Export Rules Component
const ExportRules = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      <SEO 
        title={t('exportRules')}
        description={t('exportRulesSubtitle')}
        keywords="export, Morocco, regulations, douane, customs"
      />
      
      <PageHeader />
      
      <div className="container mx-auto px-4 py-8">

        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">{t('exportRulesTitle')}</h1>
          <p className="text-gray-600 text-lg">{t('exportRulesSubtitle')}</p>
          <p className="text-sm text-gray-500 mt-4">
            Bron: RVO.nl (Rijksdienst voor Ondernemend Nederland) - Laatste update: Februari 2024
          </p>
        </div>

        {/* Content Sections */}
        <div className="space-y-6">
          {/* Product Requirements */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              📦 {t('language') === 'nl' ? 'Product- en Verpakkingseisen' : t('language') === 'fr' ? 'Exigences produits et emballages' : t('language') === 'ar' ? 'متطلبات المنتج والتعبئة' : 'Product & Packaging Requirements'}
            </h2>
            <p className="text-gray-700 mb-4">
              {t('language') === 'nl' 
                ? 'Producten moeten voldoen aan Marokkaanse producteisen en verpakkingseisen voordat ze op de markt gebracht mogen worden.'
                : t('language') === 'fr'
                ? 'Les produits doivent répondre aux exigences marocaines en matière de produits et d\'emballage avant d\'être mis sur le marché.'
                : t('language') === 'ar'
                ? 'يجب أن تستوفي المنتجات متطلبات المنتج والتعبئة المغربية قبل طرحها في السوق.'
                : 'Products must meet Moroccan product and packaging requirements before being placed on the market.'}
            </p>
          </div>

          {/* Export Declaration */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              📋 {t('language') === 'nl' ? 'Uitvoeraangifte' : t('language') === 'fr' ? 'Déclaration d\'exportation' : t('language') === 'ar' ? 'إقرار التصدير' : 'Export Declaration'}
            </h2>
            <p className="text-gray-700 mb-4">
              {t('language') === 'nl'
                ? 'Bij export buiten de EU moet u aangifte doen bij de Nederlandse douane (uitvoeraangifte of EX-A).'
                : t('language') === 'fr'
                ? 'Pour l\'exportation hors UE, vous devez faire une déclaration auprès des douanes néerlandaises (déclaration d\'exportation ou EX-A).'
                : t('language') === 'ar'
                ? 'للتصدير خارج الاتحاد الأوروبي، يجب عليك تقديم إقرار للجمارك الهولندية (إقرار التصدير أو EX-A).'
                : 'For export outside the EU, you must declare to Dutch customs (export declaration or EX-A).'}
            </p>
            <a 
              href="https://www.belastingdienst.nl/wps/wcm/connect/bldcontentnl/belastingdienst/douane_voor_bedrijven/uitvoer/uitvoer/uitvoer-van-goederen/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              {t('language') === 'nl' ? 'Meer informatie →' : t('language') === 'fr' ? 'Plus d\'informations →' : t('language') === 'ar' ? 'مزيد من المعلومات ←' : 'More information →'}
            </a>
          </div>

          {/* Important Documents */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              📄 {t('language') === 'nl' ? 'Belangrijke Documenten' : t('language') === 'fr' ? 'Documents importants' : t('language') === 'ar' ? 'الوثائق المهمة' : 'Important Documents'}
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>{t('language') === 'nl' ? 'Handelsfactuur' : t('language') === 'fr' ? 'Facture commerciale' : t('language') === 'ar' ? 'الفاتورة التجارية' : 'Commercial invoice'}</li>
              <li>{t('language') === 'nl' ? 'Paklijst' : t('language') === 'fr' ? 'Liste de colisage' : t('language') === 'ar' ? 'قائمة التعبئة' : 'Packing list'}</li>
              <li>{t('language') === 'nl' ? 'Certificaat van oorsprong' : t('language') === 'fr' ? 'Certificat d\'origine' : t('language') === 'ar' ? 'شهادة المنشأ' : 'Certificate of origin'}</li>
              <li>{t('language') === 'nl' ? 'Bill of Lading / Air Waybill' : t('language') === 'fr' ? 'Connaissement / Lettre de transport aérien' : t('language') === 'ar' ? 'بوليصة الشحن / بوليصة الشحن الجوي' : 'Bill of Lading / Air Waybill'}</li>
            </ul>
          </div>

          {/* Trade Agreement */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              🤝 {t('language') === 'nl' ? 'Handelsakkoord EU-Marokko' : t('language') === 'fr' ? 'Accord commercial UE-Maroc' : t('language') === 'ar' ? 'اتفاقية التجارة الاتحاد الأوروبي-المغرب' : 'EU-Morocco Trade Agreement'}
            </h2>
            <p className="text-gray-700">
              {t('language') === 'nl'
                ? 'Sinds 2000 bestaat er een handelsakkoord tussen de EU en Marokko met afschaffing van invoerrechten voor industriële producten en verlaging voor sommige landbouw- en visserijproducten.'
                : t('language') === 'fr'
                ? 'Depuis 2000, il existe un accord commercial entre l\'UE et le Maroc avec suppression des droits d\'importation pour les produits industriels et réduction pour certains produits agricoles et de la pêche.'
                : t('language') === 'ar'
                ? 'منذ عام 2000، يوجد اتفاق تجاري بين الاتحاد الأوروبي والمغرب مع إلغاء رسوم الاستيراد على المنتجات الصناعية وتخفيضها على بعض المنتجات الزراعية ومنتجات الصيد.'
                : 'Since 2000, there has been a trade agreement between the EU and Morocco with abolition of import duties for industrial products and reduction for some agricultural and fishery products.'}
            </p>
          </div>

          {/* Access2Markets */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-blue-800 mb-4 flex items-center gap-2">
              🌍 Access2Markets
            </h2>
            <p className="text-gray-700 mb-4">
              {t('language') === 'nl'
                ? 'De Access2Markets portal van de Europese Commissie is dé bron voor uitgebreide informatie over exportregels, producteisen, invoerrechten en benodigde documenten.'
                : t('language') === 'fr'
                ? 'Le portail Access2Markets de la Commission européenne est LA source d\'informations détaillées sur les règles d\'exportation, les exigences produits, les droits d\'importation et les documents nécessaires.'
                : t('language') === 'ar'
                ? 'بوابة Access2Markets التابعة للمفوضية الأوروبية هي المصدر الشامل للمعلومات حول قواعد التصدير ومتطلبات المنتج ورسوم الاستيراد والوثائق المطلوبة.'
                : 'The Access2Markets portal of the European Commission is THE source for comprehensive information on export rules, product requirements, import duties and required documents.'}
            </p>
            <a 
              href="https://trade.ec.europa.eu/access-to-markets/nl/content/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('language') === 'nl' ? 'Bezoek Access2Markets →' : t('language') === 'fr' ? 'Visiter Access2Markets →' : t('language') === 'ar' ? 'زيارة Access2Markets ←' : 'Visit Access2Markets →'}
            </a>
          </div>

          {/* Prohibited Items */}
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-red-800 mb-4 flex items-center gap-2">
              ⚠️ {t('language') === 'nl' ? 'Invoerverboden' : t('language') === 'fr' ? 'Interdictions d\'importation' : t('language') === 'ar' ? 'حظر الاستيراد' : 'Import Prohibitions'}
            </h2>
            <p className="text-gray-700">
              {t('language') === 'nl'
                ? 'Marokko verbiedt de invoer van producten die niet voldoen aan gezondheidsnormen (fytosanitaire en veterinaire normen), producten zonder correcte etikettering, en namaakproducten.'
                : t('language') === 'fr'
                ? 'Le Maroc interdit l\'importation de produits ne respectant pas les normes sanitaires (normes phytosanitaires et vétérinaires), de produits sans étiquetage correct et de produits contrefaits.'
                : t('language') === 'ar'
                ? 'يحظر المغرب استيراد المنتجات التي لا تلبي المعايير الصحية (المعايير النباتية والبيطرية)، والمنتجات بدون وسم صحيح، والمنتجات المقلدة.'
                : 'Morocco prohibits the import of products that do not meet health standards (phytosanitary and veterinary standards), products without correct labeling, and counterfeit products.'}
            </p>
          </div>

          {/* Useful Links */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              🔗 {t('language') === 'nl' ? 'Nuttige Links' : t('language') === 'fr' ? 'Liens utiles' : t('language') === 'ar' ? 'روابط مفيدة' : 'Useful Links'}
            </h2>
            <div className="space-y-3">
              <a href="https://www.rvo.nl/onderwerpen/landen-en-gebieden/marokko/exportregels-producteisen" target="_blank" rel="noopener noreferrer" className="block text-blue-600 hover:text-blue-800 underline">
                RVO.nl - {t('language') === 'nl' ? 'Exportregels Marokko (Nederlands)' : t('language') === 'fr' ? 'Règles d\'exportation Maroc (Néerlandais)' : t('language') === 'ar' ? 'قواعد التصدير المغرب (هولندي)' : 'Export Rules Morocco (Dutch)'}
              </a>
              <a href="https://www.douane.gov.ma" target="_blank" rel="noopener noreferrer" className="block text-blue-600 hover:text-blue-800 underline">
                {t('language') === 'nl' ? 'Marokkaanse Douane' : t('language') === 'fr' ? 'Douane marocaine' : t('language') === 'ar' ? 'الجمارك المغربية' : 'Moroccan Customs'}
              </a>
              <a href="https://www.kvk.nl/internationaal/certificaat-van-oorsprong-bewijs-waar-product-is-gemaakt/" target="_blank" rel="noopener noreferrer" className="block text-blue-600 hover:text-blue-800 underline">
                KVK - {t('language') === 'nl' ? 'Certificaat van Oorsprong' : t('language') === 'fr' ? 'Certificat d\'Origine' : t('language') === 'ar' ? 'شهادة المنشأ' : 'Certificate of Origin'}
              </a>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-gray-50 rounded-lg p-6 text-sm text-gray-600">
            <p className="font-semibold mb-2">
              {t('language') === 'nl' ? 'Disclaimer:' : t('language') === 'fr' ? 'Avertissement:' : t('language') === 'ar' ? 'تنويه:' : 'Disclaimer:'}
            </p>
            <p>
              {t('language') === 'nl'
                ? 'Deze informatie is een samenvatting van de officiële regelgeving. Voor complete en actuele informatie verwijzen wij u naar de officiële bronnen (RVO.nl, Access2Markets, Marokkaanse Douane). Send2Maroc is niet verantwoordelijk voor eventuele onjuistheden. Raadpleeg altijd een professional voor specifiek advies.'
                : t('language') === 'fr'
                ? 'Cette information est un résumé de la réglementation officielle. Pour des informations complètes et actualisées, nous vous renvoyons aux sources officielles (RVO.nl, Access2Markets, Douane marocaine). Send2Maroc n\'est pas responsable des éventuelles inexactitudes. Consultez toujours un professionnel pour des conseils spécifiques.'
                : t('language') === 'ar'
                ? 'هذه المعلومات ملخص للوائح الرسمية. للحصول على معلومات كاملة ومحدثة، نحيلك إلى المصادر الرسمية (RVO.nl، Access2Markets، الجمارك المغربية). Send2Maroc غير مسؤول عن أي معلومات غير دقيقة. استشر دائمًا محترفًا للحصول على نصيحة محددة.'
                : 'This information is a summary of official regulations. For complete and current information, we refer you to official sources (RVO.nl, Access2Markets, Moroccan Customs). Send2Maroc is not responsible for any inaccuracies. Always consult a professional for specific advice.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Public Booking Page - Klant boekt via persoonlijke transporteur link
const PublicBookingPage = () => {
  const { transporteurId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [transporteur, setTransporteur] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [trackingCode, setTrackingCode] = useState('');
  const [photos, setPhotos] = useState([]);
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_country_code: '+31',
    customer_email: '',
    description: '',
    pickup_location: '',
    delivery_location: '',
    pickup_date: '',
    delivery_date: ''
  });

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    
    // Limit to 3 photos
    if (photos.length + files.length > 3) {
      alert('Je kunt maximaal 3 foto\'s uploaden');
      return;
    }
    
    files.forEach(file => {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert(`Foto ${file.name} is te groot. Maximaal 5MB per foto.`);
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        alert(`${file.name} is geen afbeelding`);
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotos(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    const fetchTransporteurInfo = async () => {
      try {
        const response = await axios.get(`${API}/transporteur/public/${transporteurId}`);
        setTransporteur(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching transporteur:', error);
        setLoading(false);
      }
    };
    
    fetchTransporteurInfo();
  }, [transporteurId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/direct-bookings/public/create/${transporteurId}`, {
        ...formData,
        photos: photos  // Include photos in submission
      });
      setTrackingCode(response.data.tracking_code);
      setSuccess(true);
    } catch (error) {
      console.error('Error creating booking:', error);
      alert('Er is iets misgegaan. Probeer het opnieuw of neem contact op met de transporteur.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (!transporteur) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg max-w-md">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('transporterNotFound')}</h1>
          <p className="text-gray-600 mb-6">{t('bookingLinkInvalid')}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Ga naar Homepage
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-xl shadow-xl max-w-2xl w-full text-center">
          <div className="text-6xl mb-4">✅</div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{t('bookingSuccessful')}</h1>
          <p className="text-lg text-gray-700 mb-6">
            Uw boeking is ontvangen door <strong>{transporteur.name}</strong>
          </p>
          
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 mb-6">
            <p className="text-sm font-medium text-gray-700 mb-2">📦 Uw Tracking Code:</p>
            <div className="text-3xl font-bold text-green-600 mb-4 font-mono">{trackingCode}</div>
            <p className="text-sm text-gray-600 mb-4">{t('saveTrackingCode')}</p>
            <button
              onClick={() => navigate(`/track/${trackingCode}`)}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
            >
              📍 {t('trackMyShipment')}
            </button>
          </div>

          <div className="text-sm text-gray-600 space-y-2">
            <p>✅ De transporteur neemt spoedig contact met u op</p>
            <p>✅ U ontvangt updates over de status van uw zending</p>
            <p>✅ Bij vragen kunt u contact opnemen: {transporteur.phone_number}</p>
          </div>

          <button
            onClick={() => navigate('/')}
            className="mt-6 px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Terug naar Homepage
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      <PageHeader />
      
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <div className="text-center mb-6">
            <div className="text-5xl mb-4">🚚</div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Boek je zending bij {transporteur.name}
            </h1>
            <p className="text-lg text-gray-600">
              Deze boeking gaat rechtstreeks naar jouw vaste transporteur
            </p>
          </div>

          {/* Transporteur Info */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 border border-green-200">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center text-white text-2xl">
                  {transporteur.name.charAt(0)}
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-lg">{transporteur.name}</h3>
                {transporteur.description && (
                  <p className="text-sm text-gray-600 mt-1">{transporteur.description}</p>
                )}
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                  {transporteur.phone_number && (
                    <span>📞 {transporteur.phone_number}</span>
                  )}
                  {transporteur.average_rating > 0 && (
                    <span>⭐ {transporteur.average_rating.toFixed(1)} ({transporteur.total_reviews} reviews)</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Booking Form */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">📋 Boekingsgegevens</h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Uw Naam *
                </label>
                <input
                  type="text"
                  required
                  value={formData.customer_name}
                  onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Voor- en achternaam"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('phoneNumber')} *
                </label>
                <div className="flex gap-2">
                  <select
                    value={formData.customer_country_code}
                    onChange={(e) => setFormData({...formData, customer_country_code: e.target.value})}
                    className="w-24 px-2 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="+31">🇳🇱 +31</option>
                    <option value="+32">🇧🇪 +32</option>
                    <option value="+33">🇫🇷 +33</option>
                    <option value="+49">🇩🇪 +49</option>
                    <option value="+212">🇲🇦 +212</option>
                    <option value="+34">🇪🇸 +34</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+39">🇮🇹 +39</option>
                  </select>
                  <input
                    type="tel"
                    required
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="6 12345678"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('email')} *
              </label>
              <input
                type="email"
                required
                value={formData.customer_email}
                onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="uw.email@voorbeeld.nl"
              />
            </div>

            {/* Shipment Details */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">📦 Zendingdetails</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Wat verstuur je? *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="bijv. 3 koffers, 2 dozen met kleding"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      📍 {t('pickupLocation')} *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.pickup_location}
                      onChange={(e) => setFormData({...formData, pickup_location: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="bijv. Amsterdam, Nederland"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      📍 {t('deliveryLocation')} *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.delivery_location}
                      onChange={(e) => setFormData({...formData, delivery_location: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="bijv. Tanger, Marokko"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      📅 Gewenste ophaaldatum (optioneel)
                    </label>
                    <input
                      type="date"
                      value={formData.pickup_date}
                      onChange={(e) => setFormData({...formData, pickup_date: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      📅 Gewenste afleverdatum (optioneel)
                    </label>
                    <input
                      type="date"
                      value={formData.delivery_date}
                      onChange={(e) => setFormData({...formData, delivery_date: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Photo Upload */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">📷 Foto's van je bagage (optioneel)</h3>
              <p className="text-sm text-gray-600 mb-4">Upload tot 3 foto's van je bagage/dozen (max 5MB per foto)</p>
              
              {/* Photo Preview */}
              {photos.length > 0 && (
                <div className="grid grid-cols-3 gap-4 mb-4">
                  {photos.map((photo, index) => (
                    <div key={index} className="relative group">
                      <img 
                        src={photo} 
                        alt={`Foto ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border-2 border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="absolute top-2 right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Upload Button */}
              {photos.length < 3 && (
                <label className="cursor-pointer">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-green-500 hover:bg-green-50 transition-colors">
                    <div className="text-4xl mb-2">📷</div>
                    <p className="text-sm font-medium text-gray-700">{t('clickToUpload')}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('dragPhotosHere')} ({3 - photos.length} {t('photosRemaining')})</p>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            {/* Submit */}
            <div className="border-t pt-6">
              <button
                type="submit"
                disabled={submitting}
                className="w-full px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 font-bold text-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? `⏳ ${t('bookingInProgress')}` : `✅ ${t('bookMyShipment')}`}
              </button>
              <p className="text-center text-sm text-gray-500 mt-4">
                Door te boeken ga je akkoord dat {transporteur.name} contact met je opneemt
              </p>
            </div>
          </form>
        </div>

        {/* Trust Indicators */}
        <div className="mt-6 bg-blue-50 rounded-lg p-6 border border-blue-200">
          <h3 className="font-bold text-gray-900 mb-3">✨ Waarom boeken via {transporteur.name}?</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>✅ Directe communicatie met jouw vaste transporteur</li>
            <li>✅ Transparante tracking van je zending</li>
            <li>✅ Betrouwbare service naar Marokko</li>
            <li>✅ Persoonlijke begeleiding tijdens het hele proces</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

// Invoice Page - Publieke factuur weergave
const InvoicePage = () => {
  const { bookingId } = useParams();
  const { t } = useLanguage();
  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const logo = useLogo();

  useEffect(() => {
    const fetchInvoiceData = async () => {
      try {
        console.log('Fetching invoice for booking ID:', bookingId);
        const response = await axios.get(`${API}/direct-bookings/${bookingId}/invoice-data`);
        console.log('Invoice data received:', response.data);
        setInvoiceData(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching invoice:', error);
        console.error('Error response:', error.response?.data);
        setError(error.response?.data?.detail || 'Er is een fout opgetreden');
        setLoading(false);
      }
    };
    
    fetchInvoiceData();
  }, [bookingId]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Factuur laden...</p>
        </div>
      </div>
    );
  }

  if (!invoiceData) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center bg-white p-6 sm:p-8 rounded-xl shadow-lg max-w-md w-full">
          <div className="text-5xl sm:text-6xl mb-4">❌</div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{t('invoiceNotFound')}</h1>
          <p className="text-sm sm:text-base text-gray-600 mb-4">{error || t('invoiceNotFoundMessage')}</p>
          <button
            onClick={() => window.close()}
            className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {t('close')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Print Button - Hidden when printing */}
      <div className="print:hidden bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 sm:py-4 flex flex-col sm:flex-row justify-between items-center gap-3">
          <h1 className="text-base sm:text-lg font-bold text-gray-900">{t('invoice')} #{invoiceData.invoice_number}</h1>
          <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
            <button
              onClick={handlePrint}
              className="flex-1 sm:flex-none px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center gap-2 text-sm"
            >
              🖨️ <span className="hidden sm:inline">{t('printDownloadPDF')}</span><span className="sm:hidden">Print</span>
            </button>
            <button
              onClick={() => window.close()}
              className="px-4 sm:px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium text-sm"
            >
              {t('close')}
            </button>
          </div>
        </div>
      </div>

      {/* Invoice Content */}
      <div className="max-w-4xl mx-auto p-8">
        <div className="bg-white shadow-lg rounded-lg p-8 print:shadow-none print:rounded-none">
          {/* Header */}
          <div className="border-b-2 border-gray-300 pb-4 sm:pb-6 mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
              <div className="flex items-start gap-3 sm:gap-4 w-full sm:w-auto">
                {/* Logo */}
                {logo ? (
                  <img 
                    src={logo} 
                    alt="Send2Maroc Logo" 
                    className="h-12 sm:h-16 w-auto object-contain flex-shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-8 h-8 sm:w-10 sm:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10" />
                    </svg>
                  </div>
                )}
                
                <div className="flex-1">
                  <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-1">{t('invoice').toUpperCase()}</h1>
                  <p className="text-base sm:text-lg text-blue-600 font-medium">Send2Maroc</p>
                  <p className="text-xs sm:text-sm text-gray-600">{invoiceData.transporteur.name}</p>
                </div>
              </div>
              <div className="text-left sm:text-right w-full sm:w-auto">
                <div className="text-xs sm:text-sm text-gray-600 mb-1">{t('invoiceNumber')}</div>
                <div className="text-base sm:text-xl font-bold text-gray-900 break-all">{invoiceData.invoice_number}</div>
                <div className="text-xs sm:text-sm text-gray-600 mt-2">{t('invoiceDate')}</div>
                <div className="font-medium text-sm sm:text-base">{new Date(invoiceData.invoice_date).toLocaleDateString('nl-NL')}</div>
              </div>
            </div>
          </div>

          {/* Company & Customer Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
            {/* Van (Transporteur) */}
            <div>
              <h3 className="font-bold text-gray-900 mb-3 uppercase text-xs sm:text-sm">{t('from')}:</h3>
              <div className="space-y-1 text-xs sm:text-sm">
                <p className="font-bold">{invoiceData.transporteur.name}</p>
                {invoiceData.transporteur.address && <p>{invoiceData.transporteur.address}</p>}
                {invoiceData.transporteur.phone && <p>Tel: {invoiceData.transporteur.phone}</p>}
                {invoiceData.transporteur.email && <p>Email: {invoiceData.transporteur.email}</p>}
                {invoiceData.transporteur.kvk && <p>KvK: {invoiceData.transporteur.kvk}</p>}
                {invoiceData.transporteur.btw && <p>BTW: {invoiceData.transporteur.btw}</p>}
              </div>
            </div>

            {/* Aan (Klant) */}
            <div>
              <h3 className="font-bold text-gray-900 mb-3 uppercase text-xs sm:text-sm">{t('to')}:</h3>
              <div className="space-y-1 text-xs sm:text-sm">
                <p className="font-bold">{invoiceData.customer.name}</p>
                <p>Tel: {invoiceData.customer.phone}</p>
                {invoiceData.customer.email && <p>Email: {invoiceData.customer.email}</p>}
              </div>
            </div>
          </div>

          {/* Service Details */}
          <div className="mb-6 sm:mb-8 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="text-left py-3 font-bold text-gray-900 text-sm sm:text-base">{t('description')}</th>
                  <th className="text-right py-3 font-bold text-gray-900 text-sm sm:text-base">{t('amount')}</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="py-3 sm:py-4">
                    <div className="font-medium text-gray-900 mb-1 text-sm sm:text-base">{t('transportService')}</div>
                    <div className="text-xs sm:text-sm text-gray-600 space-y-1">
                      <p><strong>{t('what')}:</strong> {invoiceData.description}</p>
                      <p><strong>{t('from')}:</strong> {invoiceData.pickup_location}</p>
                      <p><strong>{t('to')}:</strong> {invoiceData.delivery_location}</p>
                      {invoiceData.pickup_date && <p><strong>{t('pickupDate')}:</strong> {invoiceData.pickup_date}</p>}
                      {invoiceData.delivery_date && <p><strong>{t('deliveryDate')}:</strong> {invoiceData.delivery_date}</p>}
                      <p><strong>{t('tracking')}:</strong> #{invoiceData.tracking_code}</p>
                    </div>
                  </td>
                  <td className="py-3 sm:py-4 text-right align-top">
                    <span className="font-medium text-sm sm:text-base">€{(invoiceData.agreed_price || 0).toFixed(2)}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="border-t-2 border-gray-300 pt-4 sm:pt-6">
            <div className="flex justify-end">
              <div className="w-full sm:w-64">
                <div className="flex justify-between text-xl sm:text-2xl font-bold">
                  <span>{t('total')}:</span>
                  <span>€{(invoiceData.total || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Notes */}
          <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
            <p className="text-xs sm:text-sm text-gray-600 italic">
              {t('thankYouMessage')} {invoiceData.transporteur.name}. {t('questionsContact')}
            </p>
          </div>

          {/* Status Badge */}
          <div className="mt-6 text-center">
            <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${
              invoiceData.status === 'afgeleverd' ? 'bg-green-100 text-green-800' :
              invoiceData.status === 'bevestigd' ? 'bg-blue-100 text-blue-800' :
              'bg-yellow-100 text-yellow-800'
            }`}>
              Status: {invoiceData.status === 'in_afwachting' ? 'In afwachting' :
                       invoiceData.status === 'bevestigd' ? 'Bevestigd' :
                       invoiceData.status === 'opgehaald' ? 'Opgehaald' :
                       invoiceData.status === 'onderweg' ? 'Onderweg' :
                       invoiceData.status === 'aangekomen_marokko' ? 'Aangekomen in Marokko' :
                       invoiceData.status === 'afgeleverd' ? 'Afgeleverd' :
                       invoiceData.status}
            </span>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            background: white !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          .print\\:rounded-none {
            border-radius: 0 !important;
          }
        }
      `}</style>
    </div>
  );
};

// Public Tracking Page - No authentication required
const PublicTrackingPage = () => {
  const { trackingCode } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchCode, setSearchCode] = useState(trackingCode || '');

  useEffect(() => {
    if (trackingCode) {
      fetchTrackingData(trackingCode);
    } else {
      setLoading(false);
    }
  }, [trackingCode]);

  const fetchTrackingData = async (code) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/direct-bookings/track/${code}`);
      setTrackingData(response.data);
    } catch (err) {
      setError('Tracking code niet gevonden. Controleer de code en probeer opnieuw.');
      setTrackingData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchCode.trim()) {
      navigate(`/track/${searchCode.trim().toUpperCase()}`);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'in_afwachting': return 'bg-yellow-500';
      case 'bevestigd': return 'bg-blue-500';
      case 'opgehaald': return 'bg-purple-500';
      case 'onderweg': return 'bg-orange-500';
      case 'afgeleverd': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'in_afwachting': return t('awaitingConfirmation');
      case 'bevestigd': return t('confirmed');
      case 'opgehaald': return t('pickedUp');
      case 'onderweg': return t('inTransit');
      case 'afgeleverd': return t('delivered');
      default: return status;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-green-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xl">📍</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Send2Maroc</h1>
                <p className="text-sm text-gray-600">{t('trackAndTrace')}</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/')}
              className="px-3 py-2 text-blue-600 hover:text-blue-800 font-medium text-sm"
            >
              ← {t('back') || 'Terug'}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Search Box */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-8">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 text-center">
            📦 {t('trackYourShipment') || 'Volg je zending'}
          </h2>
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={searchCode}
              onChange={(e) => setSearchCode(e.target.value.toUpperCase())}
              placeholder={t('enterTrackingCode') || 'Voer je tracking code in'}
              className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none text-base sm:text-lg font-mono uppercase"
            />
            <button
              type="submit"
              className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-semibold w-full sm:w-auto"
            >
              {t('search') || 'Zoeken'}
            </button>
          </form>
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Tracking gegevens laden...</p>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8 text-center">
            <div className="text-5xl mb-4">❌</div>
            <h3 className="text-xl font-bold text-red-800 mb-2">Niet gevonden</h3>
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* No tracking code entered */}
        {!trackingCode && !loading && !error && (
          <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-8 text-center">
            <div className="text-5xl mb-4">🔍</div>
            <h3 className="text-xl font-bold text-blue-800 mb-2">Voer je tracking code in</h3>
            <p className="text-blue-600">Je vindt de tracking code in het bericht dat je van de transporteur hebt ontvangen.</p>
          </div>
        )}

        {/* Tracking Results */}
        {trackingData && !loading && (
          <div className="space-y-6">
            {/* Status Card */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className={`${getStatusColor(trackingData.status)} text-white p-6`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-90">Status</p>
                    <h3 className="text-2xl font-bold">{getStatusLabel(trackingData.status)}</h3>
                  </div>
                  <div className="text-5xl">
                    {trackingData.status === 'afgeleverd' ? '✅' : 
                     trackingData.status === 'onderweg' ? '🚚' :
                     trackingData.status === 'opgehaald' ? '📦' :
                     trackingData.status === 'bevestigd' ? '✓' : '⏳'}
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <span>Tracking code</span>
                  <span className="font-mono font-bold text-lg text-gray-900">{trackingData.tracking_code}</span>
                </div>
                
                <div className="border-t pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Van</p>
                      <p className="font-semibold text-gray-900">📍 {trackingData.pickup_location}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Naar</p>
                      <p className="font-semibold text-gray-900">📍 {trackingData.delivery_location}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Details Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h4 className="font-bold text-gray-900 mb-4">📋 Zending Details</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Klant</span>
                  <span className="font-medium">{trackingData.customer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Transporteur</span>
                  <span className="font-medium">{trackingData.transporteur_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Omschrijving</span>
                  <span className="font-medium">{trackingData.description}</span>
                </div>
                {trackingData.pickup_date && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ophaaldatum</span>
                    <span className="font-medium">{new Date(trackingData.pickup_date).toLocaleDateString('nl-NL')}</span>
                  </div>
                )}
                {trackingData.delivery_date && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Verwachte levering</span>
                    <span className="font-medium">{new Date(trackingData.delivery_date).toLocaleDateString('nl-NL')}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Status History */}
            {trackingData.status_updates && trackingData.status_updates.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h4 className="font-bold text-gray-900 mb-4">📜 Status Geschiedenis</h4>
                <div className="space-y-4">
                  {trackingData.status_updates.slice().reverse().map((update, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className={`w-3 h-3 rounded-full mt-1.5 ${index === 0 ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{getStatusLabel(update.status)}</p>
                        {update.note && <p className="text-sm text-gray-600">{update.note}</p>}
                        <p className="text-xs text-gray-400">
                          {new Date(update.timestamp).toLocaleString('nl-NL')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-100 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-gray-600">
          <p>© 2025 Send2Maroc - {t('trackAndTrace')}</p>
        </div>
      </footer>
    </div>
  );
};

// Auth Component
const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    user_type: 'customer',
    company_name: '',
    country_origin: '',
    invite_code: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const { login, register, googleSignIn } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const logo = useLogo();

  // Terms and Conditions content in multiple languages
  const getTermsContent = (lang) => {
    const terms = {
      en: {
        title: "Terms and Conditions",
        content: `
          <h2 class="text-xl font-bold mb-4">Terms and Conditions for Send2Maroc</h2>
          
          <h3 class="text-lg font-semibold mb-2">1. Platform Role</h3>
          <p class="mb-4">Send2Maroc operates solely as a <strong>mediator platform</strong> connecting customers in Europe with transport companies offering baggage transport services to Morocco. We facilitate connections between parties but do not provide transport services ourselves.</p>
          
          <h3 class="text-lg font-semibold mb-2">2. Limitation of Liability</h3>
          <p class="mb-4"><strong>IMPORTANT:</strong> Send2Maroc.com is <strong>NOT RESPONSIBLE</strong> for:</p>
          <ul class="list-disc pl-6 mb-4">
            <li>Lost, stolen, or missing goods during transport</li>
            <li>Damage to goods during handling, loading, or transport</li>
            <li>Delays in pickup or delivery</li>
            <li>Actions or omissions of transport companies</li>
            <li>Quality of transport services provided by third parties</li>
          </ul>
          
          <h3 class="text-lg font-semibold mb-2">3. Direct Agreement</h3>
          <p class="mb-4">All transport agreements are made directly between customers and transport companies. Send2Maroc is not a party to these agreements and assumes no obligations or responsibilities arising from them.</p>
          
          <h3 class="text-lg font-semibold mb-2">4. User Responsibilities</h3>
          <p class="mb-4">Users agree to:</p>
          <ul class="list-disc pl-6 mb-4">
            <li>Provide accurate information</li>
            <li>Package goods appropriately for transport</li>
            <li>Comply with customs and import/export regulations</li>
            <li>Resolve disputes directly with transport companies</li>
          </ul>
          
          <h3 class="text-lg font-semibold mb-2">5. Platform Usage</h3>
          <p class="mb-4">By using Send2Maroc, you acknowledge that you understand our role as a mediator platform and accept full responsibility for your transactions with transport companies.</p>
          
          <p class="text-sm text-gray-600 mt-6">Last updated: ${new Date().toLocaleDateString()}</p>
        `
      },
      nl: {
        title: "Algemene Voorwaarden",
        content: `
          <h2 class="text-xl font-bold mb-4">Algemene Voorwaarden voor Send2Maroc</h2>
          
          <h3 class="text-lg font-semibold mb-2">1. Platformrol</h3>
          <p class="mb-4">Send2Maroc functioneert uitsluitend als een <strong>bemiddelingsplatform</strong> dat klanten in Europa verbindt met transportbedrijven die bagagetransportdiensten naar Marokko aanbieden. Wij faciliteren verbindingen tussen partijen maar verlenen zelf geen transportdiensten.</p>
          
          <h3 class="text-lg font-semibold mb-2">2. Beperking van Aansprakelijkheid</h3>
          <p class="mb-4"><strong>BELANGRIJK:</strong> Send2Maroc.com is <strong>NIET VERANTWOORDELIJK</strong> voor:</p>
          <ul class="list-disc pl-6 mb-4">
            <li>Verloren, gestolen of vermiste goederen tijdens transport</li>
            <li>Schade aan goederen tijdens behandeling, laden of transport</li>
            <li>Vertragingen bij ophalen of bezorgen</li>
            <li>Handelingen of nalatigheden van transportbedrijven</li>
            <li>Kwaliteit van transportdiensten geleverd door derden</li>
          </ul>
          
          <h3 class="text-lg font-semibold mb-2">3. Directe Overeenkomst</h3>
          <p class="mb-4">Alle transportovereenkomsten worden direct gesloten tussen klanten en transportbedrijven. Send2Maroc is geen partij bij deze overeenkomsten en neemt geen verplichtingen of verantwoordelijkheden daaruit voortvloeiend op zich.</p>
          
          <h3 class="text-lg font-semibold mb-2">4. Gebruikersverantwoordelijkheden</h3>
          <p class="mb-4">Gebruikers stemmen ermee in om:</p>
          <ul class="list-disc pl-6 mb-4">
            <li>Juiste informatie te verstrekken</li>
            <li>Goederen op de juiste manier in te pakken voor transport</li>
            <li>Douane- en import/exportregelingen na te leven</li>
            <li>Geschillen direct op te lossen met transportbedrijven</li>
          </ul>
          
          <h3 class="text-lg font-semibold mb-2">5. Platformgebruik</h3>
          <p class="mb-4">Door Send2Maroc te gebruiken, erkent u dat u onze rol als bemiddelingsplatform begrijpt en volledige verantwoordelijkheid accepteert voor uw transacties met transportbedrijven.</p>
          
          <p class="text-sm text-gray-600 mt-6">Laatst bijgewerkt: ${new Date().toLocaleDateString()}</p>
        `
      },
      fr: {
        title: "Conditions Générales",
        content: `
          <h2 class="text-xl font-bold mb-4">Conditions Générales pour Send2Maroc</h2>
          
          <h3 class="text-lg font-semibold mb-2">1. Rôle de la Plateforme</h3>
          <p class="mb-4">Send2Maroc fonctionne uniquement comme une <strong>plateforme de médiation</strong> reliant les clients en Europe aux entreprises de transport offrant des services de transport de bagages vers le Maroc. Nous facilitons les connexions entre les parties mais ne fournissons pas de services de transport nous-mêmes.</p>
          
          <h3 class="text-lg font-semibold mb-2">2. Limitation de Responsabilité</h3>
          <p class="mb-4"><strong>IMPORTANT :</strong> Send2Maroc.com n'est <strong>PAS RESPONSABLE</strong> de :</p>
          <ul class="list-disc pl-6 mb-4">
            <li>Marchandises perdues, volées ou manquantes pendant le transport</li>
            <li>Dommages aux marchandises pendant la manutention, le chargement ou le transport</li>
            <li>Retards dans la collecte ou la livraison</li>
            <li>Actions ou omissions des entreprises de transport</li>
            <li>Qualité des services de transport fournis par des tiers</li>
          </ul>
          
          <h3 class="text-lg font-semibold mb-2">3. Accord Direct</h3>
          <p class="mb-4">Tous les accords de transport sont conclus directement entre les clients et les entreprises de transport. Send2Maroc n'est pas partie à ces accords et n'assume aucune obligation ou responsabilité en découlant.</p>
          
          <h3 class="text-lg font-semibold mb-2">4. Responsabilités des Utilisateurs</h3>
          <p class="mb-4">Les utilisateurs acceptent de :</p>
          <ul class="list-disc pl-6 mb-4">
            <li>Fournir des informations exactes</li>
            <li>Emballer les marchandises de manière appropriée pour le transport</li>
            <li>Se conformer aux réglementations douanières et d'import/export</li>
            <li>Résoudre les litiges directement avec les entreprises de transport</li>
          </ul>
          
          <h3 class="text-lg font-semibold mb-2">5. Utilisation de la Plateforme</h3>
          <p class="mb-4">En utilisant Send2Maroc, vous reconnaissez comprendre notre rôle de plateforme de médiation et acceptez la pleine responsabilité de vos transactions avec les entreprises de transport.</p>
          
          <p class="text-sm text-gray-600 mt-6">Dernière mise à jour : ${new Date().toLocaleDateString()}</p>
        `
      },
      es: {
        title: "Términos y Condiciones",
        content: `
          <h2 class="text-xl font-bold mb-4">Términos y Condiciones para Send2Maroc</h2>
          
          <h3 class="text-lg font-semibold mb-2">1. Rol de la Plataforma</h3>
          <p class="mb-4">Send2Maroc opera únicamente como una <strong>plataforma mediadora</strong> que conecta clientes en Europa con empresas de transporte que ofrecen servicios de transporte de equipajes a Marruecos. Facilitamos conexiones entre partes pero no proporcionamos servicios de transporte nosotros mismos.</p>
          
          <h3 class="text-lg font-semibold mb-2">2. Limitación de Responsabilidad</h3>
          <p class="mb-4"><strong>IMPORTANTE:</strong> Send2Maroc.com <strong>NO ES RESPONSABLE</strong> de:</p>
          <ul class="list-disc pl-6 mb-4">
            <li>Mercancías perdidas, robadas o faltantes durante el transporte</li>
            <li>Daños a mercancías durante manipulación, carga o transporte</li>
            <li>Retrasos en recogida o entrega</li>
            <li>Acciones u omisiones de empresas de transporte</li>
            <li>Calidad de servicios de transporte proporcionados por terceros</li>
          </ul>
          
          <h3 class="text-lg font-semibold mb-2">3. Acuerdo Directo</h3>
          <p class="mb-4">Todos los acuerdos de transporte se realizan directamente entre clientes y empresas de transporte. Send2Maroc no es parte de estos acuerdos y no asume obligaciones o responsabilidades derivadas de ellos.</p>
          
          <h3 class="text-lg font-semibold mb-2">4. Responsabilidades del Usuario</h3>
          <p class="mb-4">Los usuarios acuerdan:</p>
          <ul class="list-disc pl-6 mb-4">
            <li>Proporcionar información precisa</li>
            <li>Empacar mercancías apropiadamente para transporte</li>
            <li>Cumplir con regulaciones aduaneras y de importación/exportación</li>
            <li>Resolver disputas directamente con empresas de transporte</li>
          </ul>
          
          <h3 class="text-lg font-semibold mb-2">5. Uso de la Plataforma</h3>
          <p class="mb-4">Al usar Send2Maroc, usted reconoce que entiende nuestro rol como plataforma mediadora y acepta plena responsabilidad por sus transacciones con empresas de transporte.</p>
          
          <p class="text-sm text-gray-600 mt-6">Última actualización: ${new Date().toLocaleDateString()}</p>
        `
      },
      ar: {
        title: "الشروط والأحكام",
        content: `
          <h2 class="text-xl font-bold mb-4">الشروط والأحكام لموقع Send2Maroc</h2>
          
          <h3 class="text-lg font-semibold mb-2">1. دور المنصة</h3>
          <p class="mb-4">تعمل Send2Maroc فقط كـ<strong>منصة وساطة</strong> تربط العملاء في أوروبا بشركات النقل التي تقدم خدمات نقل الأمتعة إلى المغرب. نحن نسهل الاتصالات بين الأطراف ولكننا لا نقدم خدمات النقل بأنفسنا.</p>
          
          <h3 class="text-lg font-semibold mb-2">2. تحديد المسؤولية</h3>
          <p class="mb-4"><strong>مهم:</strong> Send2Maroc.com <strong>غير مسؤول</strong> عن:</p>
          <ul class="list-disc pl-6 mb-4">
            <li>البضائع المفقودة أو المسروقة أو المفقودة أثناء النقل</li>
            <li>أضرار البضائع أثناء المناولة أو التحميل أو النقل</li>
            <li>التأخير في الاستلام أو التسليم</li>
            <li>أفعال أو إهمال شركات النقل</li>
            <li>جودة خدمات النقل المقدمة من أطراف ثالثة</li>
          </ul>
          
          <h3 class="text-lg font-semibold mb-2">3. الاتفاق المباشر</h3>
          <p class="mb-4">جميع اتفاقيات النقل تتم مباشرة بين العملاء وشركات النقل. Send2Maroc ليست طرفاً في هذه الاتفاقيات ولا تتحمل أي التزامات أو مسؤوليات تنشأ عنها.</p>
          
          <h3 class="text-lg font-semibold mb-2">4. مسؤوليات المستخدم</h3>
          <p class="mb-4">يوافق المستخدمون على:</p>
          <ul class="list-disc pl-6 mb-4">
            <li>تقديم معلومات دقيقة</li>
            <li>تعبئة البضائع بشكل مناسب للنقل</li>
            <li>الامتثال للوائح الجمركية والاستيراد/التصدير</li>
            <li>حل النزاعات مباشرة مع شركات النقل</li>
          </ul>
          
          <h3 class="text-lg font-semibold mb-2">5. استخدام المنصة</h3>
          <p class="mb-4">باستخدام Send2Maroc، فإنك تقر بأنك تفهم دورنا كمنصة وساطة وتقبل المسؤولية الكاملة عن معاملاتك مع شركات النقل.</p>
          
          <p class="text-sm text-gray-600 mt-6">آخر تحديث: ${new Date().toLocaleDateString()}</p>
        `
      }
    };
    return terms[lang] || terms['en'];
  };

  // Google OAuth session handling
  useEffect(() => {
    const handleGoogleCallback = async () => {
      const urlHash = window.location.hash;
      if (urlHash.includes('session_id=')) {
        const sessionId = urlHash.split('session_id=')[1].split('&')[0];
        
        if (sessionId) {
          setLoading(true);
          try {
            const result = await googleSignIn(sessionId);
            if (result.success) {
              // Clear the hash from URL
              window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
              // Navigation will be handled by the auth context
            } else {
              setError(result.error);
            }
          } catch (err) {
            setError('Google sign-in failed. Please try again.');
          } finally {
            setLoading(false);
          }
        }
      }
    };

    handleGoogleCallback();
  }, [googleSignIn]);

  // Get user type from URL parameters
  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const type = searchParams.get('type');
    if (type === 'company') {
      setFormData(prev => ({ ...prev, user_type: 'transport_company' }));
    } else {
      // Default to customer for all other cases (including no type parameter)
      setFormData(prev => ({ ...prev, user_type: 'customer' }));
    }
  }, [location.search]);

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);
    setForgotPasswordMessage('');
    
    try {
      const response = await axios.post(`${API}/auth/password-reset`, {
        email: forgotPasswordEmail
      });
      
      setForgotPasswordMessage('Een wachtwoord reset link is verzonden naar uw e-mailadres.');
      setTimeout(() => {
        setShowForgotPassword(false);
        setForgotPasswordEmail('');
        setForgotPasswordMessage('');
      }, 3000);
    } catch (error) {
      setForgotPasswordMessage(error.response?.data?.detail || 'Er is een fout opgetreden. Probeer het opnieuw.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let result;
      if (isLogin) {
        result = await login(formData.email, formData.password, rememberMe);
      } else {
        result = await register(formData);
      }

      if (!result.success) {
        setError(result.error);
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleGoogleSignIn = () => {
    const redirectUrl = `${window.location.origin}/login`;
    const googleAuthUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    window.location.href = googleAuthUrl;
  };

  return (
    <>
      <SEO 
        title={isLogin ? "Inloggen" : "Registreren"}
        description={isLogin ? "Log in bij Send2Maroc om uw baggagetransport te beheren." : "Registreer u bij Send2Maroc voor betrouwbaar baggagetransport van Europa naar Marokko."}
        keywords="inloggen, registreren, account, bagage transport, Send2Maroc"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-6 px-3 sm:px-6 lg:px-8">
      <div className="max-w-sm sm:max-w-md lg:max-w-lg w-full space-y-6 sm:space-y-8">
        <div>
          <div className="flex justify-between items-center mb-6">
            <button 
              onClick={handleBackToHome}
              className="flex items-center text-indigo-600 hover:text-indigo-500 transition-colors"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {t('homepage')}
            </button>
            {logo ? (
              <img 
                src={logo} 
                alt="Send2Maroc Logo" 
                className="h-12 w-auto object-contain"
              />
            ) : (
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10" />
                </svg>
              </div>
            )}
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {isLogin ? t('loginTitle') : 'Klant Registratie'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {isLogin ? t('loginSubtitle') : t('registerSubtitle')}
          </p>
        </div>
        
        <div className="flex justify-center space-x-4 mb-6">
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="nl">{t('dutch')}</option>
            <option value="en">{t('english')}</option>
            <option value="fr">{t('french')}</option>
            <option value="ar">{t('arabic')}</option>
          </select>
        </div>

        <form className="mt-6 sm:mt-8 space-y-4 sm:space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm space-y-3 sm:-space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                {t('email')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="appearance-none rounded-md sm:rounded-none sm:rounded-t-md relative block w-full px-3 py-3 sm:py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 text-sm sm:text-sm"
                placeholder={t('email')}
                value={formData.email}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                {t('password')}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="appearance-none rounded-md sm:rounded-none relative block w-full px-3 py-3 sm:py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 text-sm sm:text-sm"
                placeholder={t('password')}
                value={formData.password}
                onChange={handleChange}
              />
            </div>
            {!isLogin && (
              <>
                <div>
                  <label htmlFor="full_name" className="sr-only">
                    {t('fullName')}
                  </label>
                  <input
                    id="full_name"
                    name="full_name"
                    type="text"
                    required
                    className="appearance-none rounded-md sm:rounded-none relative block w-full px-3 py-3 sm:py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 text-sm sm:text-sm"
                    placeholder={t('fullName')}
                    value={formData.full_name}
                    onChange={handleChange}
                  />
                </div>
                {formData.user_type === 'transport_company' && (
                  <>
                    <div>
                      <label htmlFor="company_name" className="sr-only">
                        {t('companyName')}
                      </label>
                      <input
                        id="company_name"
                        name="company_name"
                        type="text"
                        required
                        className="appearance-none rounded-md sm:rounded-none relative block w-full px-3 py-3 sm:py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 text-sm sm:text-sm"
                        placeholder={t('companyName')}
                        value={formData.company_name}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <label htmlFor="country_origin" className="sr-only">
                        {t('countryOrigin')}
                      </label>
                      <select
                        id="country_origin"
                        name="country_origin"
                        required
                        className="appearance-none rounded-md sm:rounded-none relative block w-full px-3 py-3 sm:py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 text-sm sm:text-sm"
                        value={formData.country_origin}
                        onChange={handleChange}
                      >
                        <option value="">{t('countryOrigin')}</option>
                        <option value="Netherlands">{t('netherlands')}</option>
                        <option value="Belgium">{t('belgium')}</option>
                        <option value="France">{t('france')}</option>
                        <option value="Germany">{t('germany')}</option>
                        <option value="Spain">{t('spain')}</option>
                        <option value="Morocco">{t('morocco')}</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="invite_code" className="sr-only">
                        Uitnodigingscode (optioneel)
                      </label>
                      <input
                        id="invite_code"
                        name="invite_code"
                        type="text"
                        className="appearance-none rounded-md sm:rounded-none relative block w-full px-3 py-3 sm:py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 text-sm sm:text-sm uppercase"
                        placeholder="Uitnodigingscode (optioneel)"
                        value={formData.invite_code}
                        onChange={(e) => setFormData({...formData, invite_code: e.target.value.toUpperCase()})}
                      />
                    </div>
                  </>
                )}
                
                {/* User type selection - only show for company registration */}
                {!isLogin && formData.user_type === 'transport_company' && (
                  <div>
                    <label htmlFor="user_type" className="sr-only">
                      {t('userType')}
                    </label>
                    <input
                      type="hidden"
                      name="user_type"
                      value={formData.user_type}
                    />
                    <div className="px-3 py-2 border border-gray-300 rounded-b-md bg-gray-50 text-sm text-gray-700">
                      {t('transportCompany')} {t('register')}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Keep me logged in - only for login */}
          {isLogin && (
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                data-testid="remember-me-checkbox"
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 cursor-pointer select-none">
                {t('rememberMe')}
              </label>
            </div>
          )}

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          {/* Removed terms message from here - now placed under login button */}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 sm:py-2 px-4 border border-transparent text-base sm:text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 min-h-[44px]"
            >
              {loading ? (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : null}
              {isLogin ? t('login') : t('register')}
            </button>
          </div>

          {/* Casual Terms Message - Under Login Button */}
          {isLogin && (
            <div className="text-center text-sm text-gray-600 mt-3">
              {t('loginTermsMessage')} {' '}
              <button
                type="button"
                onClick={() => setShowTermsModal(true)}
                className="text-blue-600 hover:text-blue-500 underline"
              >
                {t('termsAndConditions')}
              </button>
            </div>
          )}

          {/* Google Sign-In (only for login and only for customers) */}
          {isLogin && formData.user_type === 'customer' && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-gray-50 text-gray-500">{t('orDivider')}</span>
                </div>
              </div>

              <div>
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="group relative w-full flex justify-center py-3 sm:py-2 px-4 border border-gray-300 text-base sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 min-h-[44px]"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {t('googleSignIn')}
                </button>
              </div>
            </>
          )}

          {/* Forgot Password and Registration Links */}
          <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
            {isLogin ? (
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-indigo-600 hover:text-indigo-500 text-sm py-2 px-4 rounded-md min-h-[44px] flex items-center justify-center"
              >
                {t('forgotPasswordQuestion')}
              </button>
            ) : <div className="hidden sm:block"></div>}

            {/* Customer Registration Toggle */}
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
                // Reset form for customer registration
                if (isLogin) {
                  setFormData({
                    email: '',
                    password: '',
                    full_name: '',
                    user_type: 'customer',
                    company_name: '',
                    country_origin: ''
                  });
                }
              }}
              className="text-indigo-600 hover:text-indigo-500 text-sm py-2 px-4 rounded-md min-h-[44px] flex items-center justify-center w-full sm:w-auto"
            >
              {isLogin ? t('registerAsCustomerLink') : t('backToLoginLink')}
            </button>
          </div>

          {/* Registration link removed - companies should register through the dedicated company registration flow */}
        </form>
      </div>
    </div>

    {/* Forgot Password Modal */}
    {showForgotPassword && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-xs sm:max-w-md w-full mx-3 sm:mx-0">
          <div className="flex justify-between items-center p-4 sm:p-6 border-b">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">
              Wachtwoord Vergeten
            </h2>
            <button
              onClick={() => {
                setShowForgotPassword(false);
                setForgotPasswordEmail('');
                setForgotPasswordMessage('');
              }}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold p-1 min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              ×
            </button>
          </div>
          <div className="p-4 sm:p-6">
            <p className="text-gray-600 mb-4">
              Voer uw e-mailadres in en we sturen u een link om uw wachtwoord te resetten.
            </p>
            <form onSubmit={handleForgotPassword}>
              <div className="mb-4">
                <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-700 mb-2">
                  E-mailadres
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  required
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
                  placeholder="uw@email.com"
                />
              </div>
              
              {forgotPasswordMessage && (
                <div className={`mb-4 p-3 rounded-md ${
                  forgotPasswordMessage.includes('verzonden') 
                    ? 'bg-green-50 text-green-700 border border-green-200' 
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {forgotPasswordMessage}
                </div>
              )}
              
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordEmail('');
                    setForgotPasswordMessage('');
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Annuleren
                </button>
                <button
                  type="submit"
                  disabled={forgotPasswordLoading}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center"
                >
                  {forgotPasswordLoading && (
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  Reset Link Versturen
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    )}

    {/* Terms and Conditions Modal */}
    {showTermsModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
          <div className="flex justify-between items-center p-6 border-b">
            <h2 className="text-2xl font-bold text-gray-900">
              {getTermsContent(language).title}
            </h2>
            <button
              onClick={() => setShowTermsModal(false)}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            <div 
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: getTermsContent(language).content }}
            />
          </div>
          <div className="flex justify-end space-x-4 p-6 border-t">
            <button
              onClick={() => setShowTermsModal(false)}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {t('close')}
            </button>
            <button
              onClick={() => {
                setShowTermsModal(false);
              }}
              className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              {t('acceptAndClose')}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

// Company Tracking Timeline Component  
const CompanyTrackingTimeline = ({ companyId }) => {
  const { t } = useLanguage();
  const [trackingUpdates, setTrackingUpdates] = useState([]);

  useEffect(() => {
    if (companyId) {
      fetchCompanyTrackingUpdates();
      // Refresh tracking every 60 seconds
      const interval = setInterval(fetchCompanyTrackingUpdates, 60000);
      return () => clearInterval(interval);
    }
  }, [companyId]);

  const fetchCompanyTrackingUpdates = async () => {
    try {
      const response = await axios.get(`${API}/tracking/company/${companyId}`);
      setTrackingUpdates(response.data);
    } catch (error) {
      console.error('Error fetching company tracking updates:', error);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'in_transit':
        return '🚚';
      case 'at_customs':
        return '🛃';
      case 'at_border':
        return '🚧';
      case 'arrived_morocco':
        return '🇲🇦';
      case 'ready_delivery':
        return '📦';
      default:
        return '📍';
    }
  };

  const formatDateTime = (timestamp) => {
    return new Date(timestamp).toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (trackingUpdates.length === 0) {
    return (
      <div className="text-center text-gray-500 py-6">
        <div className="text-3xl mb-2">📍</div>
        <p>Nog geen updates van dit transportbedrijf</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h4 className="font-semibold text-gray-900 mb-4">
        🚚 {t('companyStatus')}
      </h4>
      
      <div className="space-y-3">
        {trackingUpdates.map((update, index) => (
          <div key={update.id} className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r">
            <div className="flex items-start space-x-3">
              <div className="text-2xl">
                {getStatusIcon(update.status)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium text-blue-900">
                    {update.status.replace('_', ' ').toUpperCase()}
                  </p>
                  <p className="text-xs text-blue-600">
                    {formatDateTime(update.timestamp)}
                  </p>
                </div>
                {update.location && (
                  <p className="text-sm text-blue-800 mb-1">
                    📍 {update.location}
                  </p>
                )}
                <p className="text-sm text-blue-900">
                  {update.message}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Tracking Timeline Component
const TrackingTimeline = ({ quoteId, isCompany = false }) => {
  const { t } = useLanguage();
  const [trackingUpdates, setTrackingUpdates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTrackingUpdates();
    // Refresh tracking every 30 seconds
    const interval = setInterval(fetchTrackingUpdates, 30000);
    return () => clearInterval(interval);
  }, [quoteId]);

  const fetchTrackingUpdates = async () => {
    try {
      const response = await axios.get(`${API}/tracking/${quoteId}`);
      setTrackingUpdates(response.data);
    } catch (error) {
      console.error('Error fetching tracking updates:', error);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return '📦';
      case 'picked_up':
        return '🚚';
      case 'in_transit':
        return '🛣️';
      case 'at_customs':
        return '🛃';
      case 'out_for_delivery':
        return '🚐';
      case 'delivered':
        return '✅';
      default:
        return '📍';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      case 'picked_up':
        return 'bg-blue-100 text-blue-800';
      case 'in_transit':
        return 'bg-yellow-100 text-yellow-800';
      case 'at_customs':
        return 'bg-orange-100 text-orange-800';
      case 'out_for_delivery':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDateTime = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  if (trackingUpdates.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        <div className="text-4xl mb-2">📦</div>
        <p>{t('noTrackingYet')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        {t('trackingHistory')}
      </h3>
      
      <div className="flow-root">
        <ul className="-mb-8">
          {trackingUpdates.map((update, index) => (
            <li key={update.id}>
              <div className="relative pb-8">
                {index !== trackingUpdates.length - 1 ? (
                  <span
                    className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                    aria-hidden="true"
                  />
                ) : null}
                <div className="relative flex space-x-3">
                  <div>
                    <span className="h-8 w-8 rounded-full bg-white flex items-center justify-center ring-8 ring-white text-xl">
                      {getStatusIcon(update.status)}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <span className={`px-2 py-1 text-xs rounded-full font-medium ${getStatusColor(update.status)}`}>
                            {t(`status${update.status.charAt(0).toUpperCase() + update.status.slice(1).replace('_', '')}`)}
                          </span>
                          {update.location && (
                            <span className="text-sm text-gray-500">
                              📍 {update.location}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-900 font-medium">
                          {t('updateTime')}: {formatDateTime(update.timestamp)}
                        </p>
                        <p className="text-xs text-gray-500">
                          door {update.updated_by_name}
                        </p>
                        {update.notes && (
                          <p className="mt-2 text-sm text-gray-700 bg-gray-50 p-2 rounded">
                            {update.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// Company Profile Modal Component
const CompanyProfileModal = ({ isOpen, onClose, companyName, companyId }) => {
  const { t } = useLanguage();
  const [companyProfile, setCompanyProfile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && companyId) {
      fetchCompanyProfile();
    }
  }, [isOpen, companyId]);

  const fetchCompanyProfile = async () => {
    try {
      setLoading(true);
      console.log('Fetching company profile for companyId:', companyId);
      
      if (!companyId) {
        console.error('No companyId provided to fetchCompanyProfile');
        return;
      }
      
      // Get company profile information
      const response = await axios.get(`${API}/users/${companyId}`);
      console.log('Company profile response:', response.data);
      setCompanyProfile(response.data);
    } catch (error) {
      console.error('Error fetching company profile:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-green-500 to-green-600 text-white">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <span className="text-2xl">🏢</span>
            </div>
            <div>
              <h2 className="text-xl font-bold">Bedrijfsprofiel</h2>
              <p className="text-green-100">{companyName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-green-200 text-2xl font-bold p-2 rounded-full hover:bg-white hover:bg-opacity-20"
          >
            ×
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
              <span className="ml-3 text-gray-600">Bedrijfsprofiel laden...</span>
            </div>
          ) : companyProfile ? (
            <div className="space-y-6">
              {/* Company Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-2m-2 0H9m3 0V9a2 2 0 00-2-2H6a2 2 0 00-2 2v8a2 2 0 002 2h4m0 0v2m0-2h4m0 0v2" />
                  </svg>
                  Bedrijfsinformatie
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Bedrijfsnaam</label>
                    <p className="text-gray-900 font-medium">{companyProfile.company_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Contactpersoon</label>
                    <p className="text-gray-900">{companyProfile.full_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">E-mailadres</label>
                    <p className="text-gray-900">{companyProfile.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Land van herkomst</label>
                    <p className="text-gray-900">{companyProfile.country_origin || 'Niet opgegeven'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Actief sinds</label>
                    <p className="text-gray-900">
                      {companyProfile.created_at ? new Date(companyProfile.created_at).toLocaleDateString('nl-NL') : 'Onbekend'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Goedkeuringsstatus</label>
                    <p className={`font-medium ${
                      companyProfile.approval_status === 'approved' ? 'text-green-600' : 
                      companyProfile.approval_status === 'pending' ? 'text-orange-600' : 'text-red-600'
                    }`}>
                      {companyProfile.approval_status === 'approved' ? 'Goedgekeurd' : 
                       companyProfile.approval_status === 'pending' ? 'In behandeling' : 'Afgewezen'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              {(companyProfile.phone_number || companyProfile.current_location) && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    Contactgegevens
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {companyProfile.phone_number && (
                      <div>
                        <label className="text-sm font-medium text-gray-600">Telefoonnummer</label>
                        <p className="text-gray-900">{companyProfile.phone_number}</p>
                      </div>
                    )}
                    {companyProfile.current_location && (
                      <div>
                        <label className="text-sm font-medium text-gray-600">Huidige locatie</label>
                        <p className="text-gray-900">{companyProfile.current_location}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Service Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0h6m-6 0l-1 12a2 2 0 002 2h10a2 2 0 002-2L17 7M9 7V3a2 2 0 012-2h4a2 2 0 012 2v4" />
                  </svg>
                  Service Informatie
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Gemiddelde beoordeling</label>
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl text-yellow-500">⭐</span>
                      <span className="text-lg font-medium">{companyProfile.average_rating?.toFixed(1) || '0.0'}</span>
                      <span className="text-sm text-gray-500">({companyProfile.total_reviews || 0} beoordelingen)</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Online status</label>
                    <p className={`font-medium ${companyProfile.is_online ? 'text-green-600' : 'text-gray-600'}`}>
                      {companyProfile.is_online ? '🟢 Online' : '⚫ Offline'}
                    </p>
                  </div>
                </div>
                
                {companyProfile.company_description && (
                  <div className="mt-4">
                    <label className="text-sm font-medium text-gray-600">Bedrijfsbeschrijving</label>
                    <p className="text-gray-900 mt-1">{companyProfile.company_description}</p>
                  </div>
                )}
                
                {companyProfile.price_list && (
                  <div className="mt-4">
                    <label className="text-sm font-medium text-gray-600">Prijslijst</label>
                    <p className="text-gray-900 mt-1 whitespace-pre-line">{companyProfile.price_list}</p>
                  </div>
                )}
              </div>

              {/* Travel Information */}
              {(companyProfile.departure_dates_morocco?.length > 0 || companyProfile.departure_dates_europe?.length > 0) && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0h6m-6 0l-1 12a2 2 0 002 2h10a2 2 0 002-2L17 7M9 7V3a2 2 0 012-2h4a2 2 0 012 2v4" />
                    </svg>
                    Reisschema
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {companyProfile.departure_dates_morocco?.length > 0 && (
                      <div>
                        <label className="text-sm font-medium text-gray-600">Vertrek naar Marokko</label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {companyProfile.departure_dates_morocco.slice(0, 3).map((date, index) => (
                            <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                              {new Date(date).toLocaleDateString('nl-NL')}
                            </span>
                          ))}
                          {companyProfile.departure_dates_morocco.length > 3 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                              +{companyProfile.departure_dates_morocco.length - 3} meer
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {companyProfile.departure_dates_europe?.length > 0 && (
                      <div>
                        <label className="text-sm font-medium text-gray-600">Vertrek naar Europa</label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {companyProfile.departure_dates_europe.slice(0, 3).map((date, index) => (
                            <span key={index} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                              {new Date(date).toLocaleDateString('nl-NL')}
                            </span>
                          ))}
                          {companyProfile.departure_dates_europe.length > 3 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                              +{companyProfile.departure_dates_europe.length - 3} meer
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Languages */}
              {companyProfile.languages?.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                    </svg>
                    Gesproken talen
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {companyProfile.languages.map((language, index) => (
                      <span key={index} className="px-3 py-1 bg-pink-100 text-pink-800 text-sm rounded-full">
                        {language}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-500">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-2m-2 0H9m3 0V9a2 2 0 00-2-2H6a2 2 0 00-2 2v8a2 2 0 002 2h4m0 0v2m0-2h4m0 0v2" />
                </svg>
                <p className="text-lg font-medium">Bedrijfsprofiel niet gevonden</p>
                <p className="text-sm">Er is geen profielinformatie beschikbaar voor dit bedrijf.</p>
              </div>
            </div>
          )}
        </div>
        
        <div className="flex justify-end space-x-4 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
};

// Customer Profile Modal Component
const CustomerProfileModal = ({ isOpen, onClose, customerName, customerEmail, customerId }) => {
  const { t } = useLanguage();
  const [customerProfile, setCustomerProfile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && customerId) {
      fetchCustomerProfile();
    }
  }, [isOpen, customerId]);

  const fetchCustomerProfile = async () => {
    try {
      setLoading(true);
      // Get customer profile information
      const response = await axios.get(`${API}/users/${customerId}`);
      setCustomerProfile(response.data);
    } catch (error) {
      console.error('Error fetching customer profile:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <span className="text-2xl">👤</span>
            </div>
            <div>
              <h2 className="text-xl font-bold">Klantprofiel</h2>
              <p className="text-blue-100">{customerName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-blue-200 text-2xl font-bold p-2 rounded-full hover:bg-white hover:bg-opacity-20"
          >
            ×
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-gray-600">Profiel laden...</span>
            </div>
          ) : customerProfile ? (
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Basisinformatie
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Volledige naam</label>
                    <p className="text-gray-900">{customerProfile.full_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">E-mailadres</label>
                    <p className="text-gray-900">{customerProfile.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Gebruikerstype</label>
                    <p className="text-gray-900 capitalize">{customerProfile.user_type}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Lid sinds</label>
                    <p className="text-gray-900">
                      {customerProfile.created_at ? new Date(customerProfile.created_at).toLocaleDateString('nl-NL') : 'Onbekend'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Account Status */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Accountstatus
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Account actief</label>
                    <p className={`font-medium ${customerProfile.is_active ? 'text-green-600' : 'text-red-600'}`}>
                      {customerProfile.is_active ? 'Ja' : 'Nee'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">E-mail geverifieerd</label>
                    <p className={`font-medium ${customerProfile.email_verified ? 'text-green-600' : 'text-orange-600'}`}>
                      {customerProfile.email_verified ? 'Ja' : 'Nee'}
                    </p>
                  </div>
                  {customerProfile.terms_accepted && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">Voorwaarden geaccepteerd</label>
                      <p className="text-green-600 font-medium">
                        {customerProfile.terms_accepted_at ? 
                          new Date(customerProfile.terms_accepted_at).toLocaleDateString('nl-NL') : 'Ja'}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Information */}
              {(customerProfile.phone_number || customerProfile.current_location) && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    Contactinformatie
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {customerProfile.phone_number && (
                      <div>
                        <label className="text-sm font-medium text-gray-600">Telefoonnummer</label>
                        <p className="text-gray-900">{customerProfile.phone_number}</p>
                      </div>
                    )}
                    {customerProfile.current_location && (
                      <div>
                        <label className="text-sm font-medium text-gray-600">Locatie</label>
                        <p className="text-gray-900">{customerProfile.current_location}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Recent Activity */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Activiteit
                </h3>
                <div className="text-sm text-gray-600">
                  <p>• Geregistreerd op {customerProfile.created_at ? new Date(customerProfile.created_at).toLocaleDateString('nl-NL') : 'Onbekend'}</p>
                  <p>• Account type: Klant</p>
                  {customerProfile.google_id && <p>• Ingelogd via Google OAuth</p>}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-500">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <p className="text-lg font-medium">Profiel niet gevonden</p>
                <p className="text-sm">Er is geen profielinformatie beschikbaar voor deze klant.</p>
              </div>
            </div>
          )}
        </div>
        
        <div className="flex justify-end space-x-4 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
};

// Improved Chat Component with Image Support
const Chat = ({ quoteId, companyId, otherPartyName, otherPartyType, onClose, companyName, customerName, customerId, customerEmail }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showCompanyProfile, setShowCompanyProfile] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (quoteId || companyId) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 3000); // Poll for new messages every 3 seconds
      return () => clearInterval(interval);
    }
  }, [quoteId, companyId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async () => {
    if (!quoteId && !companyId) return;
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      let response;
      if (quoteId) {
        // Quote-based chat
        response = await axios.get(`${API}/chat/messages/${quoteId}`, { headers });
      } else if (companyId && customerId) {
        // General chat with both customer and company IDs
        response = await axios.get(`${API}/chat/general/${customerId}/${companyId}`, { headers });
      } else if (companyId) {
        // General company chat (customer perspective only)
        response = await axios.get(`${API}/chat/company/${companyId}`, { headers });
      }
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
      
      // Check error type and show appropriate message
      if (error.response?.status === 404 && quoteId) {
        // Quote has been deleted
        setMessages([{
          id: 'error-message',
          message: 'Deze chat is niet meer beschikbaar omdat de bijbehorende offerte is verwijderd.',
          sender_name: 'Systeem',
          sender_type: 'system',
          message_type: 'text',
          created_at: new Date().toISOString()
        }]);
      } else if (error.response?.status === 403) {
        // Access denied - user doesn't have permission
        setMessages([{
          id: 'error-message',
          message: 'U heeft geen toegang tot deze chat. Mogelijk is de offerte nog niet geaccepteerd of behoort deze chat niet tot uw account.',
          sender_name: 'Systeem',
          sender_type: 'system',
          message_type: 'text',
          created_at: new Date().toISOString()
        }]);
      } else if (error.response?.status === 401) {
        // Not authenticated
        setMessages([{
          id: 'error-message',
          message: 'Sessie verlopen. Log opnieuw in om de chat te gebruiken.',
          sender_name: 'Systeem',
          sender_type: 'system',
          message_type: 'text',
          created_at: new Date().toISOString()
        }]);
      } else {
        // Other errors
        setMessages([{
          id: 'error-message',
          message: 'Er was een probleem bij het laden van berichten. Probeer het opnieuw.',
          sender_name: 'Systeem',
          sender_type: 'system',
          message_type: 'text',
          created_at: new Date().toISOString()
        }]);
      }
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('Afbeelding is te groot. Maximum 5MB toegestaan.');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && !selectedImage) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const messageData = {
        message: selectedImage ? t('imageMessage') : newMessage.trim(),
        message_type: selectedImage ? 'image' : 'text'
      };

      // Determine if it's a quote-based or general chat
      if (quoteId) {
        messageData.quote_id = quoteId;
      } else if (companyId && customerId) {
        // General chat - send appropriate ID based on user type
        if (user?.user_type === 'customer') {
          messageData.company_id = companyId;
        } else if (user?.user_type === 'transport_company') {
          messageData.customer_id = customerId;
        }
      } else if (companyId) {
        messageData.company_id = companyId;
      } else {
        alert('Chat configuratie ontbreekt.');
        return;
      }

      if (selectedImage) {
        messageData.image_data = selectedImage;
      }

      await axios.post(`${API}/chat/messages`, messageData, { headers });
      setNewMessage('');
      setSelectedImage(null);
      fetchMessages(); // Refresh messages
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Check if it's a 404 error (quote not found)
      if (error.response?.status === 404 && quoteId) {
        alert('Deze chat is niet meer beschikbaar omdat de bijbehorende offerte is verwijderd.');
      } else {
        alert('Fout bij versturen bericht: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDisplayNames = () => {
    if (companyName && customerName) {
      return {
        company: companyName,
        customer: customerName
      };
    }
    return {
      company: otherPartyName || 'Transportbedrijf',
      customer: otherPartyName || 'Klant'
    };
  };

  const displayNames = getDisplayNames();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 h-5/6 flex flex-col">
        {/* Improved Chat Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold">
                  {t('chatWith')} {otherPartyName}
                </h3>
                <div className="text-sm opacity-90 flex items-center space-x-4">
                  <button 
                    onClick={() => {
                      console.log('Company profile button clicked');
                      console.log('companyId:', companyId);
                      console.log('companyName:', companyName);
                      console.log('otherPartyName:', otherPartyName);
                      console.log('displayNames:', displayNames);
                      setShowCompanyProfile(true);
                    }}
                    className="flex items-center space-x-1 hover:bg-white hover:bg-opacity-20 px-2 py-1 rounded transition-colors"
                    title="Bekijk bedrijfsprofiel"
                  >
                    <span>🏢</span>
                    <span className="underline cursor-pointer hover:text-blue-200">
                      {displayNames.company}
                    </span>
                  </button>
                  <span>👤 {displayNames.customer}</span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors p-2 rounded-full hover:bg-white hover:bg-opacity-20"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          {messages.length === 0 ? (
            <div className="text-center text-gray-500 mt-8">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-lg font-medium">{t('noMessages')}</p>
              <p className="text-sm mt-2">Start het gesprek door een bericht te typen</p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.sender_type === 'customer' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-md flex ${message.sender_type === 'customer' ? 'flex-row-reverse' : 'flex-row'} items-end space-x-2`}>
                    {/* Avatar */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                      message.sender_type === 'customer' ? 'bg-blue-500 ml-2' : 'bg-green-500 mr-2'
                    }`}>
                      {message.sender_type === 'customer' ? '👤' : '🏢'}
                    </div>
                    
                    {/* Message Bubble */}
                    <div
                      className={`px-4 py-3 rounded-2xl max-w-xs shadow-md ${
                        message.sender_type === 'customer'
                          ? 'bg-blue-600 text-white rounded-br-sm'
                          : 'bg-white text-gray-900 border rounded-bl-sm'
                      }`}
                    >
                      <div className="text-xs font-medium mb-1 opacity-75">
                        {message.sender_name}
                      </div>
                      
                      {message.message_type === 'image' ? (
                        <div>
                          <img 
                            src={message.image_data} 
                            alt="Shared image" 
                            className="rounded-lg max-w-full h-auto cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => window.open(message.image_data, '_blank')}
                          />
                          <div className="text-sm mt-1 opacity-75">{t('imageMessage')}</div>
                        </div>
                      ) : (
                        <div className="break-words">{message.message}</div>
                      )}
                      
                      <div className="text-xs opacity-60 mt-2">
                        {formatTime(message.timestamp)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Image Preview */}
        {selectedImage && (
          <div className="border-t bg-gray-50 p-4">
            <div className="flex items-center space-x-3">
              <img src={selectedImage} alt="Preview" className="w-16 h-16 object-cover rounded-lg" />
              <div className="flex-1">
                <p className="text-sm font-medium">Afbeelding geselecteerd</p>
                <p className="text-xs text-gray-500">Klik 'Verstuur' om te verzenden</p>
              </div>
              <button
                onClick={() => setSelectedImage(null)}
                className="text-red-500 hover:text-red-700 p-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Improved Message Input */}
        <div className="border-t bg-white p-6 rounded-b-xl">
          <form onSubmit={sendMessage} className="flex items-end space-x-3">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              className="hidden"
            />
            
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
              className="flex-shrink-0 p-3 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors disabled:opacity-50"
              title={t('sendImage')}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
            
            <div className="flex-1 relative">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={selectedImage ? "Optioneel bericht bij afbeelding..." : t('typeMessage')}
                className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                disabled={loading}
              />
            </div>
            
            <button
              type="submit"
              disabled={loading || (!newMessage.trim() && !selectedImage)}
              className="flex-shrink-0 bg-blue-600 text-white p-3 rounded-2xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {loading ? (
                <svg className="animate-spin w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
              <span className="hidden sm:inline">{t('send')}</span>
            </button>
          </form>
        </div>
      </div>

      {/* Company Profile Modal */}
      <CompanyProfileModal
        isOpen={showCompanyProfile}
        onClose={() => setShowCompanyProfile(false)}
        companyName={companyName}
        companyId={companyId}
      />
    </div>
  );
};

// Admin Dashboard
// Commissions Tab Component for Admin
const CommissionsTab = () => {
  const [commissionData, setCommissionData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCommissions = async () => {
      try {
        const response = await axios.get(`${API}/admin/commissions`);
        setCommissionData(response.data);
      } catch (error) {
        console.error('Error fetching commissions:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCommissions();
  }, []);

  if (loading) return <div className="text-center py-10 text-gray-500">Laden...</div>;
  if (!commissionData) return <div className="text-center py-10 text-gray-500">Kon commissiegegevens niet laden</div>;

  return (
    <div data-testid="commissions-tab" className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Commissie Overzicht</h2>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-lg shadow border-l-4 border-green-500">
          <p className="text-sm text-gray-500">Totale Omzet</p>
          <p className="text-2xl font-bold text-gray-900">€{commissionData.total_revenue.toFixed(2)}</p>
        </div>
        <div className="bg-white p-5 rounded-lg shadow border-l-4 border-indigo-500">
          <p className="text-sm text-gray-500">Jouw Commissie (15%)</p>
          <p className="text-2xl font-bold text-indigo-600">€{commissionData.total_commission.toFixed(2)}</p>
        </div>
        <div className="bg-white p-5 rounded-lg shadow border-l-4 border-blue-500">
          <p className="text-sm text-gray-500">Uitbetaald aan Transporteurs</p>
          <p className="text-2xl font-bold text-blue-600">€{commissionData.total_transporteur_payouts.toFixed(2)}</p>
        </div>
        <div className="bg-white p-5 rounded-lg shadow border-l-4 border-amber-500">
          <p className="text-sm text-gray-500">Aantal Betalingen</p>
          <p className="text-2xl font-bold text-amber-600">{commissionData.payment_count}</p>
        </div>
      </div>

      {/* Payments Table */}
      {commissionData.payments.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-lg shadow">
          <p className="text-gray-500 text-lg">Nog geen betalingen ontvangen</p>
          <p className="text-gray-400 text-sm mt-1">Zodra klanten boekingen betalen via het platform, verschijnen ze hier</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Datum</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transporteur</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Totaal</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Commissie</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Transporteur</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {commissionData.payments.map((p) => (
                <tr key={p.id}>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {p.created_at ? new Date(p.created_at).toLocaleDateString('nl-NL') : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{p.company_name}</td>
                  <td className="px-6 py-4 text-sm text-right text-gray-900">€{(p.total_amount || 0).toFixed(2)}</td>
                  <td className="px-6 py-4 text-sm text-right font-semibold text-indigo-600">€{(p.commission_amount || 0).toFixed(2)}</td>
                  <td className="px-6 py-4 text-sm text-right text-gray-600">€{(p.transporteur_amount || 0).toFixed(2)}</td>
                  <td className="px-6 py-4 text-center">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      p.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {p.payment_status === 'paid' ? 'Betaald' : 'In afwachting'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const [activeTab, setActiveTab] = useState('statistics');
  const [users, setUsers] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Website customization state
  const [websiteSettings, setWebsiteSettings] = useState({
    colorTheme: 'blue-white',
    primaryColor: '#3B82F6',
    secondaryColor: '#FFFFFF',
    siteTitle: 'Send2Maroc',
    siteDescription: 'Verbind klanten in Europa met betrouwbare transportbedrijven voor het verzenden van persoonlijke bagage naar Marokko.',
    heroImage: 'https://customer-assets.emergentagent.com/job_euro-morocco-cargo/artifacts/b0dia8w6_milad-alizadeh-JibMa0FbyHw-unsplash.jpg',
    featureImage: 'https://images.unsplash.com/photo-1702057830533-2afff81a57f2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
  });
  
  // Company approval state
  const [pendingCompanies, setPendingCompanies] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [companyView, setCompanyView] = useState('pending'); // 'pending' or 'all'
  
  // All requests state
  const [allRequests, setAllRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [editingRequest, setEditingRequest] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  
  // Filter states
  const [userFilter, setUserFilter] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [requestFilter, setRequestFilter] = useState('');
  const [requestStatusFilter, setRequestStatusFilter] = useState('');
  
  // Bulk selection states
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [selectedRequests, setSelectedRequests] = useState(new Set());
  const [selectAllUsers, setSelectAllUsers] = useState(false);
  const [selectAllRequests, setSelectAllRequests] = useState(false);
  
  // Create user state
  const [showCreateUserForm, setShowCreateUserForm] = useState(false);
  const [newUser, setNewUser] = useState({
    full_name: '',
    email: '',
    password: '',
    user_type: 'transport_company',
    company_name: '',
    phone_number: '',
    country_origin: 'Netherlands'
  });
  
  // Invite codes state
  const [inviteCodes, setInviteCodes] = useState([]);
  const [showCreateInviteCodeForm, setShowCreateInviteCodeForm] = useState(false);
  const [newInviteCode, setNewInviteCode] = useState({
    code: '',
    description: '',
    valid_until: '',
    max_uses: ''
  });

  useEffect(() => {
    fetchStatistics();
    fetchUsers();
  }, []);

  const fetchStatistics = async () => {
    try {
      const response = await axios.get(`${API}/admin/statistics`);
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };
  
  const createUser = async () => {
    // Validation
    if (!newUser.full_name.trim()) {
      alert('Please enter full name');
      return;
    }
    if (!newUser.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newUser.email)) {
      alert('Please enter a valid email');
      return;
    }
    if (!newUser.password || newUser.password.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }
    if (newUser.user_type === 'transport_company' && !newUser.company_name.trim()) {
      alert('Please enter company name for transport company');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/admin/create-user`, newUser);
      alert('✅ User created successfully!');
      setShowCreateUserForm(false);
      setNewUser({
        full_name: '',
        email: '',
        password: '',
        user_type: 'transport_company',
        company_name: '',
        phone_number: '',
        country_origin: 'Netherlands'
      });
      fetchUsers(); // Refresh user list
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Failed to create user: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Invite Code Functions
  const fetchInviteCodes = async () => {
    try {
      const response = await axios.get(`${API}/admin/invite-codes`);
      setInviteCodes(response.data);
    } catch (error) {
      console.error('Error fetching invite codes:', error);
    }
  };

  const createInviteCode = async () => {
    if (!newInviteCode.code.trim()) {
      alert('Voer een code in');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/admin/invite-codes`, {
        code: newInviteCode.code.toUpperCase(),
        description: newInviteCode.description || null,
        valid_until: newInviteCode.valid_until || null,
        max_uses: newInviteCode.max_uses ? parseInt(newInviteCode.max_uses) : null
      });
      alert('✅ Uitnodigingscode aangemaakt!');
      setShowCreateInviteCodeForm(false);
      setNewInviteCode({ code: '', description: '', valid_until: '', max_uses: '' });
      fetchInviteCodes();
    } catch (error) {
      console.error('Error creating invite code:', error);
      alert('Fout: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const toggleInviteCode = async (codeId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/invite-codes/${codeId}?is_active=${!currentStatus}`);
      fetchInviteCodes();
    } catch (error) {
      console.error('Error toggling invite code:', error);
      alert('Fout bij bijwerken code');
    }
  };

  const deleteInviteCode = async (codeId) => {
    if (!window.confirm('Weet je zeker dat je deze code wilt verwijderen?')) return;
    
    try {
      await axios.delete(`${API}/admin/invite-codes/${codeId}`);
      fetchInviteCodes();
    } catch (error) {
      console.error('Error deleting invite code:', error);
      alert('Fout bij verwijderen code');
    }
  };

  const fetchAllRequests = async () => {
    try {
      const response = await axios.get(`${API}/admin/transport-requests`);
      setAllRequests(response.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  };

  // Request management functions
  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setShowRequestModal(true);
  };

  const handleEditRequest = (request) => {
    setEditingRequest(request);
    setShowRequestModal(true);
  };

  const handleDeleteRequest = async (requestId) => {
    if (!confirm('⚠️ Are you sure you want to delete this transport request? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/admin/transport-requests/${requestId}`);
      if (response.status === 200) {
        await fetchAllRequests();
        alert('✅ Transport request deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting request:', error);
      alert('❌ Error deleting request');
    }
  };

  const handleUpdateRequest = async (updatedData) => {
    try {
      const response = await axios.put(`${API}/admin/transport-requests/${editingRequest.id}`, updatedData);
      if (response.status === 200) {
        await fetchAllRequests();
        setEditingRequest(null);
        setShowRequestModal(false);
        alert('✅ Request updated successfully');
      }
    } catch (error) {
      console.error('Error updating request:', error);
      alert('❌ Error updating request');
    }
  };

  // Filter functions
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.full_name.toLowerCase().includes(userFilter.toLowerCase()) ||
                         user.email.toLowerCase().includes(userFilter.toLowerCase());
    const matchesType = userTypeFilter === '' || user.user_type === userTypeFilter;
    return matchesSearch && matchesType;
  });

  const filteredCompanies = allCompanies.filter(company => {
    const matchesSearch = company.company_name?.toLowerCase().includes(companyFilter.toLowerCase()) ||
                         company.full_name?.toLowerCase().includes(companyFilter.toLowerCase());
    return matchesSearch;
  });

  const filteredRequests = allRequests.filter(request => {
    const matchesSearch = request.pickup_city?.toLowerCase().includes(requestFilter.toLowerCase()) ||
                         request.delivery_city?.toLowerCase().includes(requestFilter.toLowerCase()) ||
                         request.baggage_description?.toLowerCase().includes(requestFilter.toLowerCase());
    const matchesStatus = requestStatusFilter === '' || request.status === requestStatusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleEditUser = (userData) => {
    setEditingUser(userData);
  };

  const handleUpdateUser = async (userId, updateData) => {
    try {
      setLoading(true);
      await axios.put(`${API}/admin/users/${userId}`, updateData);
      setEditingUser(null);
      fetchUsers();
      fetchStatistics();
    } catch (error) {
      console.error('Error updating user:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm(t('confirmDelete'))) {
      try {
        setLoading(true);
        await axios.delete(`${API}/admin/users/${userId}`);
        fetchUsers();
        fetchStatistics();
      } catch (error) {
        console.error('Error deleting user:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  // Website customization functions
  const handleWebsiteSettingsChange = (key, value) => {
    setWebsiteSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const saveWebsiteSettings = async () => {
    try {
      setLoading(true);
      
      // Check if localStorage has enough space
      const settingsString = JSON.stringify(websiteSettings);
      const settingsSize = new Blob([settingsString]).size;
      
      if (settingsSize > 5 * 1024 * 1024) { // 5MB limit
        alert('❌ Instellingen zijn te groot om op te slaan. Probeer kleinere afbeeldingen.');
        return;
      }
      
      // Try to save to localStorage
      localStorage.setItem('websiteSettings', settingsString);
      
      // Verify it was saved
      const saved = localStorage.getItem('websiteSettings');
      if (saved) {
        alert('✅ ' + t('saveChanges') + ' succesvol opgeslagen!');
      } else {
        throw new Error('Kon instellingen niet opslaan');
      }
      
    } catch (error) {
      console.error('Error saving website settings:', error);
      
      if (error.name === 'QuotaExceededError') {
        alert('❌ Niet genoeg opslagruimte. Probeer kleinere afbeeldingen of reset naar standaard.');
      } else {
        alert('❌ Fout bij het opslaan van instellingen: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetWebsiteSettings = () => {
    if (window.confirm(t('resetToDefault') + '?')) {
      setWebsiteSettings({
        colorTheme: 'blue-white',
        primaryColor: '#3B82F6',
        secondaryColor: '#FFFFFF',
        siteTitle: 'Send2Maroc',
        siteDescription: 'Verbind klanten in Europa met betrouwbare transportbedrijven voor het verzenden van persoonlijke bagage naar Marokko.',
        heroImage: 'https://customer-assets.emergentagent.com/job_euro-morocco-cargo/artifacts/b0dia8w6_milad-alizadeh-JibMa0FbyHw-unsplash.jpg',
        featureImage: 'https://images.unsplash.com/photo-1702057830533-2afff81a57f2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
      });
      localStorage.removeItem('websiteSettings');
    }
  };

  const handleImageUpload = (event, imageType) => {
    const file = event.target.files[0];
    if (file) {
      // Check file size (now allow up to 50MB)
      if (file.size > 50 * 1024 * 1024) {
        alert('❌ Afbeelding is te groot. Kies een afbeelding kleiner dan 50MB.');
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        alert('❌ Selecteer alleen afbeelding bestanden.');
        return;
      }
      
      // Show loading state for large files
      if (file.size > 5 * 1024 * 1024) {
        alert('📸 Grote afbeelding wordt verwerkt, even geduld...');
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          // Create a canvas to resize the image
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            // Set maximum dimensions based on image type
            let maxWidth, maxHeight, quality;
            
            if (imageType === 'heroImage') {
              // Hero images can be larger for better quality
              maxWidth = 1920;
              maxHeight = 1080;
              quality = 0.85;
            } else {
              // Feature images smaller
              maxWidth = 1200;
              maxHeight = 800;
              quality = 0.8;
            }
            
            let { width, height } = img;
            
            // Calculate new dimensions while maintaining aspect ratio
            if (width > height) {
              if (width > maxWidth) {
                height = (height * maxWidth) / width;
                width = maxWidth;
              }
            } else {
              if (height > maxHeight) {
                width = (width * maxHeight) / height;
                height = maxHeight;
              }
            }
            
            canvas.width = width;
            canvas.height = height;
            
            // Draw and compress the image
            ctx.drawImage(img, 0, 0, width, height);
            
            // Use different compression based on original file size
            if (file.size > 20 * 1024 * 1024) {
              // Very large files get more compression
              quality = 0.7;
            }
            
            const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
            
            // Check final size
            const finalSize = new Blob([compressedDataUrl]).size;
            console.log(`Original: ${(file.size / 1024 / 1024).toFixed(2)}MB -> Compressed: ${(finalSize / 1024 / 1024).toFixed(2)}MB`);
            
            handleWebsiteSettingsChange(imageType, compressedDataUrl);
            
            if (file.size > 5 * 1024 * 1024) {
              alert('✅ Afbeelding succesvol geoptimaliseerd en geladen!');
            }
          };
          img.src = e.target.result;
        } catch (error) {
          console.error('Error processing image:', error);
          alert('❌ Fout bij het verwerken van de afbeelding. Probeer een andere afbeelding.');
        }
      };
      reader.onerror = () => {
        alert('❌ Fout bij het lezen van het bestand.');
      };
      reader.readAsDataURL(file);
    }
  };

  const generateSitemap = () => {
    const baseUrl = window.location.origin;
    const pages = [
      { url: '/', changefreq: 'daily', priority: '1.0' },
      { url: '/login?type=customer', changefreq: 'monthly', priority: '0.8' },
      { url: '/login?type=company', changefreq: 'monthly', priority: '0.8' },
    ];
    
    const sitemap = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${pages.map(page => `  <url>
    <loc>${baseUrl}${page.url}</loc>
    <lastmod>${new Date().toISOString().split('T')[0]}</lastmod>
    <changefreq>${page.changefreq}</changefreq>
    <priority>${page.priority}</priority>
  </url>`).join('\n')}
</urlset>`;
    
    const blob = new Blob([sitemap], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sitemap.xml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    alert('✅ Sitemap.xml gegenereerd en gedownload!');
  };

  // Company approval functions
  const fetchPendingCompanies = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-companies`);
      setPendingCompanies(response.data);
    } catch (error) {
      console.error('Error fetching pending companies:', error);
    }
  };

  const fetchAllCompanies = async () => {
    try {
      const response = await axios.get(`${API}/admin/all-companies`);
      setAllCompanies(response.data);
    } catch (error) {
      console.error('Error fetching all companies:', error);
    }
  };

  const approveCompany = async (companyId) => {
    if (window.confirm(t('approveConfirm'))) {
      try {
        setLoading(true);
        await axios.post(`${API}/admin/approve-company/${companyId}`);
        alert('✅ Bedrijf goedgekeurd!');
        fetchPendingCompanies();
        fetchAllCompanies();
      } catch (error) {
        console.error('Error approving company:', error);
        alert('❌ Fout bij goedkeuren bedrijf');
      } finally {
        setLoading(false);
      }
    }
  };

  const rejectCompany = async (companyId) => {
    const reason = window.prompt(t('rejectionReason') + ':');
    if (reason && window.confirm(t('rejectConfirm'))) {
      try {
        setLoading(true);
        await axios.post(`${API}/admin/reject-company/${companyId}?rejection_reason=${encodeURIComponent(reason)}`);
        alert('✅ Bedrijf afgewezen!');
        fetchPendingCompanies();
        fetchAllCompanies();
      } catch (error) {
        console.error('Error rejecting company:', error);
        alert('❌ Fout bij afwijzen bedrijf');
      } finally {
        setLoading(false);
      }
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800', 
      rejected: 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  // Load saved settings on component mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('websiteSettings');
    if (savedSettings) {
      setWebsiteSettings(JSON.parse(savedSettings));
    }
  }, []);

  // Load companies when companies tab is active
  useEffect(() => {
    if (activeTab === 'companies') {
      fetchPendingCompanies();
      fetchAllCompanies();
    }
  }, [activeTab]);

  // Load requests when requests tab is active
  useEffect(() => {
    if (activeTab === 'requests') {
      fetchAllRequests();
    }
  }, [activeTab]);

  // Bulk operation functions
  const handleSelectAllUsers = (checked) => {
    setSelectAllUsers(checked);
    if (checked) {
      const filteredUserIds = users
        .filter(user => 
          user.full_name.toLowerCase().includes(userFilter.toLowerCase()) &&
          (userTypeFilter === '' || user.user_type === userTypeFilter)
        )
        .map(user => user.id);
      setSelectedUsers(new Set(filteredUserIds));
    } else {
      setSelectedUsers(new Set());
    }
  };

  const handleUserSelection = (userId, checked) => {
    const newSelected = new Set(selectedUsers);
    if (checked) {
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    setSelectedUsers(newSelected);
    setSelectAllUsers(false);
  };

  const handleSelectAllRequests = (checked) => {
    setSelectAllRequests(checked);
    if (checked) {
      const filteredRequestIds = allRequests
        .filter(request => 
          request.pickup_city?.toLowerCase().includes(requestFilter.toLowerCase()) ||
          request.delivery_city?.toLowerCase().includes(requestFilter.toLowerCase()) ||
          request.customer_name?.toLowerCase().includes(requestFilter.toLowerCase())
        )
        .map(request => request.id);
      setSelectedRequests(new Set(filteredRequestIds));
    } else {
      setSelectedRequests(new Set());
    }
  };

  const handleRequestSelection = (requestId, checked) => {
    const newSelected = new Set(selectedRequests);
    if (checked) {
      newSelected.add(requestId);
    } else {
      newSelected.delete(requestId);
    }
    setSelectedRequests(newSelected);
    setSelectAllRequests(false);
  };

  const bulkDeleteUsers = async () => {
    if (selectedUsers.size === 0) return;
    
    if (window.confirm(`Are you sure you want to delete ${selectedUsers.size} selected users?`)) {
      try {
        setLoading(true);
        const userIds = Array.from(selectedUsers);
        await Promise.all(userIds.map(id => axios.delete(`${API}/admin/users/${id}`)));
        alert('✅ Selected users deleted successfully!');
        setSelectedUsers(new Set());
        setSelectAllUsers(false);
        fetchUsers();
      } catch (error) {
        console.error('Error bulk deleting users:', error);
        alert('❌ Error deleting users');
      } finally {
        setLoading(false);
      }
    }
  };

  const bulkDeleteRequests = async () => {
    if (selectedRequests.size === 0) return;
    
    if (window.confirm(`Are you sure you want to delete ${selectedRequests.size} selected requests?`)) {
      try {
        setLoading(true);
        const requestIds = Array.from(selectedRequests);
        await Promise.all(requestIds.map(id => axios.delete(`${API}/admin/transport-requests/${id}`)));
        alert('✅ Selected requests deleted successfully!');
        setSelectedRequests(new Set());
        setSelectAllRequests(false);
        fetchAllRequests();
      } catch (error) {
        console.error('Error bulk deleting requests:', error);
        alert('❌ Error deleting requests');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-gradient-to-r from-indigo-600 to-purple-700 shadow-lg">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Mobile Menu Button */}
            <div className="flex items-center lg:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-md text-white hover:bg-white/20 focus:outline-none focus:bg-white/20"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
            
            {/* Logo/Title */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-white rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <h1 className="text-lg sm:text-xl font-bold text-white truncate">{t('adminDashboard') || 'Admin Dashboard'}</h1>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-4">
              {/* Language Selector */}
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="px-3 py-1 border border-white/20 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-white/50 bg-white/10 text-white"
              >
                <option value="nl" className="text-gray-900">{t('dutch')}</option>
                <option value="en" className="text-gray-900">{t('english')}</option>
                <option value="fr" className="text-gray-900">{t('french')}</option>
                <option value="ar" className="text-gray-900">{t('arabic')}</option>
              </select>
              
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-white">{user.full_name}</span>
              </div>
              
              <button
                onClick={logout}
                data-testid="logout-button-desktop"
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-md shadow-sm transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>{t('logout')}</span>
              </button>
            </div>
            
            {/* Mobile User Info */}
            <div className="flex items-center lg:hidden">
              <button
                onClick={logout}
                data-testid="logout-button-mobile"
                className="flex items-center space-x-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-md shadow-sm focus:outline-none transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span className="text-sm">{t('logout')}</span>
              </button>
            </div>
          </div>
          
          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden bg-white/10 backdrop-blur-sm border-t border-white/20">
              <div className="px-4 py-3 space-y-3">
                <div className="flex items-center space-x-3 pb-3 border-b border-white/20">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-white">{user.full_name}</span>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-white/80 mb-1">{t('language') || 'Taal'}:</label>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="w-full px-3 py-2 border border-white/20 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-white/50 bg-white/10 text-white"
                  >
                    <option value="nl" className="text-gray-900">{t('dutch')}</option>
                    <option value="en" className="text-gray-900">{t('english')}</option>
                    <option value="fr" className="text-gray-900">{t('french')}</option>
                    <option value="ar" className="text-gray-900">{t('arabic')}</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Enhanced Tab Navigation */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
            {/* Mobile Tab Navigation - Scrollable */}
            <nav className="lg:hidden overflow-x-auto scrollbar-hide">
              <div className="flex min-w-max">
                {[
                  { id: 'statistics', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', label: t('platformStatistics') || 'Stats' },
                  { id: 'users', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z', label: t('userManagement') || 'Users' },
                  { id: 'companies', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4', label: t('companyApproval') || 'Companies' },
                  { id: 'requests', icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10', label: t('allRequests') || 'Requests' },
                  { id: 'website', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065zM15 12a3 3 0 11-6 0 3 3 0 016 0z', label: 'Website' },
                  { id: 'logo', icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z', label: 'Logo' }
                ].map((tab, index, array) => (
                  <button
                    key={tab.id}
                    onClick={() => {setActiveTab(tab.id); setIsMobileMenuOpen(false);}}
                    className={`flex flex-col items-center justify-center px-4 py-3 font-medium text-xs transition-colors min-w-[80px] ${index < array.length - 1 ? 'border-r border-gray-200' : ''} ${
                      activeTab === tab.id 
                        ? 'bg-indigo-50 text-indigo-700 border-b-2 border-indigo-500' 
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    <svg className="w-5 h-5 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                    </svg>
                    <span className="truncate">{tab.label}</span>
                  </button>
                ))}
              </div>
            </nav>

            {/* Desktop Tab Navigation */}
            <nav className="hidden lg:flex flex-wrap">
              <button
                onClick={() => setActiveTab('statistics')}
                className={`flex items-center space-x-2 px-6 py-4 font-medium text-sm transition-colors border-r border-gray-200 ${
                  activeTab === 'statistics' 
                    ? 'bg-indigo-50 text-indigo-700 border-b-2 border-indigo-500' 
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span>{t('platformStatistics') || 'Statistieken'}</span>
              </button>
              
              <button
                onClick={() => setActiveTab('users')}
                className={`flex items-center space-x-2 px-6 py-4 font-medium text-sm transition-colors border-r border-gray-200 ${
                  activeTab === 'users' 
                    ? 'bg-indigo-50 text-indigo-700 border-b-2 border-indigo-500' 
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
                <span>{t('userManagement') || 'Gebruikers'}</span>
              </button>
              
              <button
                onClick={() => setActiveTab('companies')}
                className={`flex items-center space-x-2 px-6 py-4 font-medium text-sm transition-colors border-r border-gray-200 ${
                  activeTab === 'companies' 
                    ? 'bg-indigo-50 text-indigo-700 border-b-2 border-indigo-500' 
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span>{t('companyApproval') || 'Bedrijven'}</span>
              </button>
              
              <button
                onClick={() => setActiveTab('requests')}
                className={`flex items-center space-x-2 px-6 py-4 font-medium text-sm transition-colors border-r border-gray-200 ${
                  activeTab === 'requests' 
                    ? 'bg-indigo-50 text-indigo-700 border-b-2 border-indigo-500' 
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10" />
                </svg>
                <span>{t('allRequests') || 'Alle Verzoeken'}</span>
              </button>
              
              <button
                onClick={() => setActiveTab('website')}
                className={`flex items-center space-x-2 px-6 py-4 font-medium text-sm transition-colors border-r border-gray-200 ${
                  activeTab === 'website' 
                    ? 'bg-indigo-50 text-indigo-700 border-b-2 border-indigo-500' 
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{t('websiteCustomization') || 'Website'}</span>
              </button>
              
              <button
                onClick={() => setActiveTab('logo')}
                className={`flex items-center space-x-2 px-6 py-4 font-medium text-sm transition-colors ${
                  activeTab === 'logo' 
                    ? 'bg-indigo-50 text-indigo-700 border-b-2 border-indigo-500' 
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>{t('logoManagement') || 'Logo'}</span>
              </button>
              
              <button
                onClick={() => { setActiveTab('invite-codes'); fetchInviteCodes(); }}
                className={`flex items-center space-x-2 px-6 py-4 font-medium text-sm transition-colors ${
                  activeTab === 'invite-codes' 
                    ? 'bg-indigo-50 text-indigo-700 border-b-2 border-indigo-500' 
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                </svg>
                <span>Uitnodigingscodes</span>
              </button>
              
              {/* Commissies tab - hidden until commission model is activated
              <button
                onClick={() => { setActiveTab('commissions'); }}
                className={`flex items-center space-x-2 px-6 py-4 font-medium text-sm transition-colors ${
                  activeTab === 'commissions' 
                    ? 'bg-indigo-50 text-indigo-700 border-b-2 border-indigo-500' 
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Commissies</span>
              </button>
              */}
            </nav>
          </div>

          <div className="mt-6">
            {activeTab === 'statistics' && statistics && (
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-6">{t('platformStatistics')}</h2>
                
                {/* Statistics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                  <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('totalUsers')}</h3>
                    <p className="text-3xl font-bold text-blue-600">{statistics.user_stats.total_users}</p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('customers')}</h3>
                    <p className="text-3xl font-bold text-green-600">{statistics.user_stats.customers}</p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('transportCompanies')}</h3>
                    <p className="text-3xl font-bold text-orange-600">{statistics.user_stats.transport_companies}</p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('activeRequests')}</h3>
                    <p className="text-3xl font-bold text-purple-600">{statistics.request_stats.active_requests}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Request Statistics */}
                  <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Transport Requests</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span>Total Requests:</span>
                        <span className="font-semibold">{statistics.request_stats.total_requests}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t('activeRequests')}:</span>
                        <span className="font-semibold text-green-600">{statistics.request_stats.active_requests}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t('completedRequests')}:</span>
                        <span className="font-semibold text-blue-600">{statistics.request_stats.completed_requests}</span>
                      </div>
                    </div>
                  </div>

                  {/* Quote Statistics */}
                  <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Quotes</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span>Total Quotes:</span>
                        <span className="font-semibold">{statistics.quote_stats.total_quotes}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t('pendingQuotes')}:</span>
                        <span className="font-semibold text-yellow-600">{statistics.quote_stats.pending_quotes}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t('acceptedQuotes')}:</span>
                        <span className="font-semibold text-green-600">{statistics.quote_stats.accepted_quotes}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('recentActivity')}</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="bg-white p-4 rounded-lg shadow">
                      <h4 className="font-medium text-gray-900 mb-3">Recent Users</h4>
                      <div className="space-y-2">
                        {statistics.recent_activity.recent_users.slice(0, 5).map(user => (
                          <div key={user.id} className="text-sm">
                            <span className="font-medium">{user.full_name}</span>
                            <span className="text-gray-500 ml-2">({user.user_type})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow">
                      <h4 className="font-medium text-gray-900 mb-3">Recent Requests</h4>
                      <div className="space-y-2">
                        {statistics.recent_activity.recent_requests.slice(0, 5).map(request => (
                          <div key={request.id} className="text-sm">
                            <span className="font-medium">{request.pickup_city} → {request.delivery_city}</span>
                            <span className="text-gray-500 block">{request.status}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow">
                      <h4 className="font-medium text-gray-900 mb-3">Recent Quotes</h4>
                      <div className="space-y-2">
                        {statistics.recent_activity.recent_quotes.slice(0, 5).map(quote => (
                          <div key={quote.id} className="text-sm">
                            <span className="font-medium">€{quote.price}</span>
                            <span className="text-gray-500 ml-2">by {quote.company_name}</span>
                            <span className="text-gray-500 block">{quote.status}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'users' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-medium text-gray-900">{t('userManagement')}</h2>
                  <button
                    onClick={() => setShowCreateUserForm(!showCreateUserForm)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium text-sm flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span>Create User</span>
                  </button>
                </div>
                
                {/* Create User Form */}
                {showCreateUserForm && (
                  <div className="bg-white rounded-lg shadow-lg p-6 mb-6 border border-indigo-200">
                    <h3 className="text-lg font-semibold mb-4">👤 Create New User</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          User Type *
                        </label>
                        <select
                          value={newUser.user_type}
                          onChange={(e) => setNewUser({...newUser, user_type: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="transport_company">Transport Company</option>
                          <option value="customer">Customer</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          value={newUser.full_name}
                          onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="John Doe"
                        />
                      </div>
                      
                      {newUser.user_type === 'transport_company' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Company Name *
                          </label>
                          <input
                            type="text"
                            value={newUser.company_name}
                            onChange={(e) => setNewUser({...newUser, company_name: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="Transport Company BV"
                          />
                        </div>
                      )}
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email *
                        </label>
                        <input
                          type="email"
                          value={newUser.email}
                          onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="user@example.com"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Password *
                        </label>
                        <input
                          type="password"
                          value={newUser.password}
                          onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="Min. 8 characters"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={newUser.phone_number}
                          onChange={(e) => setNewUser({...newUser, phone_number: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="+31 6 12345678"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Country
                        </label>
                        <select
                          value={newUser.country_origin}
                          onChange={(e) => setNewUser({...newUser, country_origin: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="Netherlands">Netherlands</option>
                          <option value="Belgium">Belgium</option>
                          <option value="Germany">Germany</option>
                          <option value="France">France</option>
                          <option value="Spain">Spain</option>
                          <option value="Morocco">Morocco</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="flex justify-end space-x-3 mt-6">
                      <button
                        onClick={() => setShowCreateUserForm(false)}
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={createUser}
                        disabled={loading}
                        className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                      >
                        {loading ? (
                          <>
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Creating...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>Create User</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
                
                {/* User Filters */}
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <h3 className="text-lg font-semibold mb-4">🔍 User Filters</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Search (Name, Email)
                      </label>
                      <input
                        type="text"
                        value={userFilter}
                        onChange={(e) => setUserFilter(e.target.value)}
                        placeholder="Search users..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        User Type
                      </label>
                      <select
                        value={userTypeFilter}
                        onChange={(e) => setUserTypeFilter(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">All Types</option>
                        <option value="customer">Customers</option>
                        <option value="transport_company">Transport Companies</option>
                        <option value="admin">Admins</option>
                      </select>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
                      <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectAllUsers}
                            onChange={(e) => handleSelectAllUsers(e.target.checked)}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <label className="ml-2 text-sm font-medium text-gray-700">
                            Select All ({filteredUsers.length})
                          </label>
                        </div>
                        <h3 className="text-base sm:text-lg font-medium text-gray-900">
                          Found {filteredUsers.length} users
                        </h3>
                      </div>
                      
                      {selectedUsers.size > 0 && (
                        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                          <span className="text-sm text-gray-600">
                            {selectedUsers.size} selected
                          </span>
                          <button
                            onClick={bulkDeleteUsers}
                            disabled={loading}
                            className="px-4 py-3 sm:py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center justify-center space-x-2 min-h-[44px] w-full sm:w-auto"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            <span>Delete Selected</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <ul className="divide-y divide-gray-200">
                    {filteredUsers.map(userData => (
                      <li key={userData.id} className="px-4 sm:px-6 py-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-3 sm:space-y-0">
                          <div className="flex items-start sm:items-center space-x-3 sm:space-x-4 flex-1">
                            <input
                              type="checkbox"
                              checked={selectedUsers.has(userData.id)}
                              onChange={(e) => handleUserSelection(userData.id, e.target.checked)}
                              className="h-4 w-4 mt-1 sm:mt-0 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-medium text-gray-900">{userData.full_name}</h3>
                            <p className="text-sm text-gray-600">{userData.email}</p>
                            <div className="flex items-center space-x-4 mt-1">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                userData.user_type === 'customer' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                              }`}>
                                {t(userData.user_type)}
                              </span>
                              {userData.company_name && (
                                <span className="text-sm text-gray-500">{userData.company_name}</span>
                              )}
                              {userData.country_origin && (
                                <span className="text-sm text-gray-500">{translateCountry(userData.country_origin, t)}</span>
                              )}
                            </div>
                          </div>
                          </div>
                          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
                            <button
                              onClick={() => handleEditUser(userData)}
                              className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 min-h-[44px] w-full sm:w-auto flex items-center justify-center"
                            >
                              {t('editUser')}
                            </button>
                            <button
                              onClick={() => handleDeleteUser(userData.id)}
                              className="px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 min-h-[44px] w-full sm:w-auto flex items-center justify-center"
                              disabled={loading}
                            >
                              {t('deleteUser')}
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {activeTab === 'website' && (
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-6">{t('websiteCustomization')}</h2>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Column - Settings */}
                  <div className="space-y-6">
                    {/* Design Settings */}
                    <div className="bg-white p-6 rounded-lg shadow">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('designSettings')}</h3>
                      
                      {/* Color Theme Selection */}
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-3">{t('colorTheme')}</label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <button
                            onClick={() => handleWebsiteSettingsChange('colorTheme', 'blue-white')}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              websiteSettings.colorTheme === 'blue-white' 
                                ? 'border-blue-500 bg-blue-50' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center space-x-2">
                              <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                              <div className="w-4 h-4 bg-white border border-gray-300 rounded-full"></div>
                              <span className="text-sm">{t('blueWhiteTheme')}</span>
                            </div>
                          </button>
                          
                          <button
                            onClick={() => handleWebsiteSettingsChange('colorTheme', 'red-orange')}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              websiteSettings.colorTheme === 'red-orange' 
                                ? 'border-red-500 bg-red-50' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center space-x-2">
                              <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                              <div className="w-4 h-4 bg-orange-500 rounded-full"></div>
                              <span className="text-sm">{t('redOrangeTheme')}</span>
                            </div>
                          </button>
                          
                          <button
                            onClick={() => handleWebsiteSettingsChange('colorTheme', 'green-black')}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              websiteSettings.colorTheme === 'green-black' 
                                ? 'border-green-500 bg-green-50' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center space-x-2">
                              <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                              <div className="w-4 h-4 bg-gray-800 rounded-full"></div>
                              <span className="text-sm">{t('greenBlackTheme')}</span>
                            </div>
                          </button>
                        </div>
                      </div>

                      {/* Custom Colors */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{t('primaryColor')}</label>
                          <div className="flex items-center space-x-3">
                            <input
                              type="color"
                              value={websiteSettings.primaryColor}
                              onChange={(e) => handleWebsiteSettingsChange('primaryColor', e.target.value)}
                              className="w-12 h-10 rounded-lg border border-gray-300"
                            />
                            <input
                              type="text"
                              value={websiteSettings.primaryColor}
                              onChange={(e) => handleWebsiteSettingsChange('primaryColor', e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{t('secondaryColor')}</label>
                          <div className="flex items-center space-x-3">
                            <input
                              type="color"
                              value={websiteSettings.secondaryColor}
                              onChange={(e) => handleWebsiteSettingsChange('secondaryColor', e.target.value)}
                              className="w-12 h-10 rounded-lg border border-gray-300"
                            />
                            <input
                              type="text"
                              value={websiteSettings.secondaryColor}
                              onChange={(e) => handleWebsiteSettingsChange('secondaryColor', e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Content Settings */}
                    <div className="bg-white p-6 rounded-lg shadow">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('contentSettings')}</h3>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{t('siteTitle')}</label>
                          <input
                            type="text"
                            value={websiteSettings.siteTitle}
                            onChange={(e) => handleWebsiteSettingsChange('siteTitle', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{t('siteDescription')}</label>
                          <textarea
                            value={websiteSettings.siteDescription}
                            onChange={(e) => handleWebsiteSettingsChange('siteDescription', e.target.value)}
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Image Settings */}
                    <div className="bg-white p-6 rounded-lg shadow">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Afbeelding Instellingen</h3>
                      
                      <div className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{t('heroImage')}</label>
                          <div className="space-y-3">
                            <img 
                              src={websiteSettings.heroImage} 
                              alt="Hero" 
                              className="w-full h-32 object-cover rounded-lg border border-gray-300"
                            />
                            <div className="space-y-2">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageUpload(e, 'heroImage')}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                              />
                              <p className="text-xs text-gray-500">
                                💡 Tot 50MB toegestaan. Grote afbeeldingen worden automatisch geoptimaliseerd voor betere prestaties.
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{t('featureImage')}</label>
                          <div className="space-y-3">
                            <img 
                              src={websiteSettings.featureImage} 
                              alt="Feature" 
                              className="w-full h-32 object-cover rounded-lg border border-gray-300"
                            />
                            <div className="space-y-2">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageUpload(e, 'featureImage')}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                              />
                              <p className="text-xs text-gray-500">
                                💡 Tot 50MB toegestaan. Grote afbeeldingen worden automatisch geoptimaliseerd voor betere prestaties.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">SEO Instellingen</h3>
                      
                      <div className="space-y-4">
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <h4 className="font-medium text-gray-900 mb-2">📈 SEO Status</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                            <div className="flex items-center space-x-2">
                              <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                              <span>Meta Tags ✓</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                              <span>Open Graph ✓</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                              <span>Schema Markup ✓</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                              <span>Twitter Cards ✓</span>
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <button
                            onClick={generateSitemap}
                            className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium"
                          >
                            📄 Generate Sitemap.xml
                          </button>
                          <p className="text-xs text-gray-500 mt-2">
                            Download een XML sitemap voor betere zoekmachinevindling
                          </p>
                        </div>
                        
                        <div className="p-4 bg-blue-50 rounded-lg">
                          <h4 className="font-medium text-blue-900 mb-2">💡 SEO Tips</h4>
                          <ul className="text-sm text-blue-800 space-y-1">
                            <li>• Houd beschrijvingen tussen 120-160 karakters</li>
                            <li>• Gebruik relevante zoekwoorden in titels</li>
                            <li>• Upload sitemap.xml naar Google Search Console</li>
                            <li>• Optimaliseer afbeeldingen voor snelheid</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="bg-white p-6 rounded-lg shadow">
                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={saveWebsiteSettings}
                          disabled={loading}
                          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium"
                        >
                          {loading ? 'Opslaan...' : t('saveChanges')}
                        </button>
                        
                        <button
                          onClick={() => window.open('/', '_blank')}
                          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium"
                        >
                          {t('previewChanges')}
                        </button>
                        
                        <button
                          onClick={resetWebsiteSettings}
                          className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 font-medium"
                        >
                          {t('resetToDefault')}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Preview */}
                  <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Live Voorbeeld</h3>
                    
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Mini Homepage Preview */}
                      <div 
                        className="relative h-64 flex items-center justify-center text-white"
                        style={{
                          background: `linear-gradient(135deg, ${websiteSettings.primaryColor}ee 0%, ${websiteSettings.primaryColor}dd 50%, ${websiteSettings.primaryColor}cc 100%), url('${websiteSettings.heroImage}')`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      >
                        <div className="text-center p-4">
                          <h1 className="text-2xl font-bold mb-2">{websiteSettings.siteTitle}</h1>
                          <p className="text-sm opacity-90 mb-4">{websiteSettings.siteDescription.substring(0, 100)}...</p>
                          <div className="flex gap-2 justify-center">
                            <div 
                              className="px-3 py-1 rounded text-xs"
                              style={{ backgroundColor: websiteSettings.secondaryColor, color: websiteSettings.primaryColor }}
                            >
                              Klant Login
                            </div>
                            <div 
                              className="px-3 py-1 rounded text-xs"
                              style={{ backgroundColor: websiteSettings.primaryColor, color: websiteSettings.secondaryColor }}
                            >
                              Bedrijf Registreren
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-4 bg-gray-50">
                        <div className="flex items-center space-x-3 mb-3">
                          <div 
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: websiteSettings.primaryColor }}
                          >
                            <span className="text-white text-xs">✓</span>
                          </div>
                          <div>
                            <h4 className="font-medium text-sm">Veilig & Betrouwbaar</h4>
                            <p className="text-xs text-gray-600">Geverifieerde transportbedrijven</p>
                          </div>
                        </div>
                        
                        <img 
                          src={websiteSettings.featureImage} 
                          alt="Feature preview" 
                          className="w-full h-20 object-cover rounded border"
                        />
                      </div>
                    </div>
                    
                    <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                      <p className="text-sm text-gray-600">
                        💡 <strong>Tip:</strong> Wijzigingen worden live getoond in dit voorbeeld. 
                        Klik op "Voorbeeld Bekijken" om de volledige site te zien.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'companies' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-medium text-gray-900">{t('companyApproval')}</h2>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCompanyView('pending')}
                      className={`px-4 py-2 rounded-lg font-medium ${
                        companyView === 'pending' 
                          ? 'bg-yellow-600 text-white' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {t('pendingCompanies')} ({pendingCompanies.length})
                    </button>
                    <button
                      onClick={() => setCompanyView('all')}
                      className={`px-4 py-2 rounded-lg font-medium ${
                        companyView === 'all' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {t('allCompanies')} ({allCompanies.length})
                    </button>
                  </div>
                </div>

                {/* Company Filters */}
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <h3 className="text-lg font-semibold mb-4">🔍 Company Filters</h3>
                  <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Search (Company Name, Contact Name)
                      </label>
                      <input
                        type="text"
                        value={companyFilter}
                        onChange={(e) => setCompanyFilter(e.target.value)}
                        placeholder="Search companies..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Pending Companies */}
                {companyView === 'pending' && (
                  <div className="bg-white rounded-lg shadow">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">{t('pendingCompanies')}</h3>
                      <p className="text-sm text-gray-600">Bedrijven die wachten op goedkeuring</p>
                    </div>
                    
                    {filteredCompanies.filter(c => c.approval_status === 'pending').length === 0 ? (
                      <div className="p-8 text-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <p className="text-gray-500">{t('noCompanies')}</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {filteredCompanies.filter(c => c.approval_status === 'pending').map((company) => (
                          <div key={company.id} className="p-6">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3 mb-2">
                                  <h4 className="text-lg font-semibold text-gray-900">
                                    {company.company_name || company.full_name}
                                  </h4>
                                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(company.approval_status)}`}>
                                    {t(company.approval_status)}
                                  </span>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                                  <div>
                                    <p><strong>Email:</strong> {company.email}</p>
                                    <p><strong>Telefoon:</strong> {company.phone_number || 'Niet opgegeven'}</p>
                                    <p><strong>Land:</strong> {company.country_origin || 'Niet opgegeven'}</p>
                                  </div>
                                  <div>
                                    <p><strong>{t('registrationDate')}:</strong> {new Date(company.created_at).toLocaleDateString('nl-NL')}</p>
                                    <p><strong>Beschrijving:</strong> {company.company_description || 'Geen beschrijving'}</p>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex space-x-2 ml-4">
                                <button
                                  onClick={() => approveCompany(company.id)}
                                  disabled={loading}
                                  className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
                                >
                                  ✅ {t('approveCompany')}
                                </button>
                                <button
                                  onClick={() => rejectCompany(company.id)}
                                  disabled={loading}
                                  className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50"
                                >
                                  ❌ {t('rejectCompany')}
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* All Companies */}
                {companyView === 'all' && (
                  <div className="bg-white rounded-lg shadow">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">{t('allCompanies')}</h3>
                      <p className="text-sm text-gray-600">Overzicht van alle geregistreerde bedrijven</p>
                    </div>
                    
                    {filteredCompanies.length === 0 ? (
                      <div className="p-8 text-center">
                        <p className="text-gray-500">{t('noCompanies')}</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {filteredCompanies.map((company) => (
                          <div key={company.id} className="p-6">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3 mb-2">
                                  <h4 className="text-lg font-semibold text-gray-900">
                                    {company.company_name || company.full_name}
                                  </h4>
                                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(company.approval_status)}`}>
                                    {t(company.approval_status)}
                                  </span>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                                  <div>
                                    <p><strong>Email:</strong> {company.email}</p>
                                    <p><strong>Telefoon:</strong> {company.phone_number || 'Niet opgegeven'}</p>
                                  </div>
                                  <div>
                                    <p><strong>Land:</strong> {company.country_origin || 'Niet opgegeven'}</p>
                                    <p><strong>{t('registrationDate')}:</strong> {new Date(company.created_at).toLocaleDateString('nl-NL')}</p>
                                  </div>
                                  <div>
                                    {company.approved_at && (
                                      <p><strong>Goedgekeurd op:</strong> {new Date(company.approved_at).toLocaleDateString('nl-NL')}</p>
                                    )}
                                    {company.rejection_reason && (
                                      <p><strong>Afwijzingsreden:</strong> {company.rejection_reason}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {company.approval_status === 'pending' && (
                                <div className="flex space-x-2 ml-4">
                                  <button
                                    onClick={() => approveCompany(company.id)}
                                    disabled={loading}
                                    className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
                                  >
                                    ✅ Goedkeuren
                                  </button>
                                  <button
                                    onClick={() => rejectCompany(company.id)}
                                    disabled={loading}
                                    className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50"
                                  >
                                    ❌ Afwijzen
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          
          {activeTab === 'logo' && (
            <div>
              <LogoGenerator />
            </div>
          )}

          {/* Invite Codes Tab */}
          {activeTab === 'invite-codes' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">🎟️ Uitnodigingscodes</h2>
                <button
                  onClick={() => setShowCreateInviteCodeForm(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                >
                  ➕ Nieuwe Code
                </button>
              </div>

              {/* Create Invite Code Form */}
              {showCreateInviteCodeForm && (
                <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                  <h3 className="text-lg font-semibold mb-4">Nieuwe Uitnodigingscode Aanmaken</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
                      <input
                        type="text"
                        value={newInviteCode.code}
                        onChange={(e) => setNewInviteCode({...newInviteCode, code: e.target.value.toUpperCase()})}
                        placeholder="bijv. GRATIS2024"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md uppercase"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Beschrijving</label>
                      <input
                        type="text"
                        value={newInviteCode.description}
                        onChange={(e) => setNewInviteCode({...newInviteCode, description: e.target.value})}
                        placeholder="bijv. Voor nieuwe partners"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Geldig tot (optioneel)</label>
                      <input
                        type="date"
                        value={newInviteCode.valid_until}
                        onChange={(e) => setNewInviteCode({...newInviteCode, valid_until: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max. aantal keer te gebruiken</label>
                      <input
                        type="number"
                        value={newInviteCode.max_uses}
                        onChange={(e) => setNewInviteCode({...newInviteCode, max_uses: e.target.value})}
                        placeholder="Leeg = onbeperkt"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={createInviteCode}
                      disabled={loading}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50"
                    >
                      {loading ? 'Aanmaken...' : 'Aanmaken'}
                    </button>
                    <button
                      onClick={() => setShowCreateInviteCodeForm(false)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                    >
                      Annuleren
                    </button>
                  </div>
                </div>
              )}

              {/* Invite Codes List */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beschrijving</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gebruikt</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Geldig tot</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acties</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {inviteCodes.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                          Geen uitnodigingscodes gevonden. Maak een nieuwe code aan.
                        </td>
                      </tr>
                    ) : (
                      inviteCodes.map(code => (
                        <tr key={code.id} className={!code.is_active ? 'bg-gray-100' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="font-mono font-bold text-lg">{code.code}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {code.description || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm">
                              {code.times_used}{code.max_uses ? ` / ${code.max_uses}` : ' keer'}
                            </span>
                            {code.users_registered && code.users_registered.length > 0 && (
                              <div className="mt-1">
                                {code.users_registered.map(u => (
                                  <span key={u.id} className={`inline-block text-xs px-2 py-1 rounded mr-1 mb-1 ${
                                    u.approval_status === 'approved' ? 'bg-green-100 text-green-800' :
                                    u.approval_status === 'rejected' ? 'bg-red-100 text-red-800' :
                                    'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {u.company_name || u.full_name}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {code.valid_until ? new Date(code.valid_until).toLocaleDateString('nl-NL') : 'Onbeperkt'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              code.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {code.is_active ? 'Actief' : 'Inactief'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex gap-2">
                              <button
                                onClick={() => toggleInviteCode(code.id, code.is_active)}
                                className={`px-3 py-1 text-sm rounded ${
                                  code.is_active 
                                    ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' 
                                    : 'bg-green-100 text-green-800 hover:bg-green-200'
                                }`}
                              >
                                {code.is_active ? 'Deactiveren' : 'Activeren'}
                              </button>
                              <button
                                onClick={() => deleteInviteCode(code.id)}
                                className="px-3 py-1 text-sm bg-red-100 text-red-800 rounded hover:bg-red-200"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* All Requests Tab */}
          {activeTab === 'commissions' && (
            <CommissionsTab />
          )}

          {activeTab === 'requests' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">📦 All Transport Requests</h2>
                <button
                  onClick={fetchAllRequests}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  🔄 Refresh
                </button>
              </div>

              {/* Filters */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">🔍 Filters</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Search (City, Description)
                    </label>
                    <input
                      type="text"
                      value={requestFilter}
                      onChange={(e) => setRequestFilter(e.target.value)}
                      placeholder="Search requests..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={requestStatusFilter}
                      onChange={(e) => setRequestStatusFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Statuses</option>
                      <option value="active">Active</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Requests List */}
              <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectAllRequests}
                          onChange={(e) => handleSelectAllRequests(e.target.checked)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 text-sm font-medium text-gray-700">
                          Select All ({filteredRequests.length})
                        </label>
                      </div>
                      <h3 className="text-base sm:text-lg font-medium text-gray-900">
                        Found {filteredRequests.length} requests
                      </h3>
                    </div>
                    
                    {selectedRequests.size > 0 && (
                      <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                        <span className="text-sm text-gray-600">
                          {selectedRequests.size} selected
                        </span>
                        <button
                          onClick={bulkDeleteRequests}
                          disabled={loading}
                          className="px-4 py-3 sm:py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center justify-center space-x-2 min-h-[44px] w-full sm:w-auto"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          <span>Delete Selected</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <ul className="divide-y divide-gray-200">
                  {filteredRequests.map((request) => (
                    <li key={request.id} className="px-4 sm:px-6 py-4 hover:bg-gray-50">
                      <div className="flex flex-col sm:flex-row sm:items-start space-y-3 sm:space-y-0">
                        <div className="flex items-start space-x-3 sm:space-x-4 flex-1">
                          <input
                            type="checkbox"
                            checked={selectedRequests.has(request.id)}
                            onChange={(e) => handleRequestSelection(request.id, e.target.checked)}
                            className="h-4 w-4 mt-1 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="space-y-3">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                                  <p className="text-base sm:text-lg font-semibold text-gray-900">
                                    🛣️ {request.pickup_city} → {request.delivery_city}
                                  </p>
                                <span className={`px-3 py-1 text-sm rounded-full font-medium ${
                                  request.status === 'active' ? 'bg-green-100 text-green-800' :
                                  request.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {request.status.toUpperCase()}
                                </span>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
                                <div>
                                  <p className="text-sm text-gray-600">📦 <strong>Description:</strong></p>
                                  <p className="text-sm text-gray-900">{request.baggage_description || 'Not specified'}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-600">⚖️ <strong>Weight:</strong> {request.weight || 'Not specified'}</p>
                                  <p className="text-sm text-gray-600">👤 <strong>Customer:</strong> {request.customer_name || `ID: ${request.customer_id}`}</p>
                                  {request.customer_email && (
                                    <p className="text-sm text-gray-600">📧 <strong>Email:</strong> {request.customer_email}</p>
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm text-gray-600">📅 <strong>Created:</strong> {new Date(request.created_at).toLocaleDateString('nl-NL')}</p>
                                  <p className="text-sm text-gray-600">🚚 <strong>Pickup:</strong> {new Date(request.pickup_date).toLocaleDateString('nl-NL')}</p>
                                </div>
                              </div>
                              
                              {request.special_instructions && (
                                <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                                  <p className="text-sm text-gray-600">💬 <strong>Special Instructions:</strong></p>
                                  <p className="text-sm text-gray-900">{request.special_instructions}</p>
                                </div>
                              )}
                              
                              {request.pickup_address && (
                                <div className="mt-2 text-sm text-gray-600">
                                  <p>📍 <strong>Pickup Address:</strong> {request.pickup_address}</p>
                                  {request.delivery_address && (
                                    <p>📍 <strong>Delivery Address:</strong> {request.delivery_address}</p>
                                  )}
                                </div>
                              )}
                              </div>
                            </div>
                          </div>
                          
                        <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2 w-full sm:w-auto sm:ml-4 mt-3 sm:mt-0">
                          <button
                            onClick={() => handleViewRequest(request)}
                            className="px-4 py-3 sm:py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium transition-colors min-h-[44px] flex items-center justify-center"
                          >
                            👁️ View Details
                          </button>
                          <button
                            onClick={() => handleEditRequest(request)}
                            className="px-4 py-3 sm:py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 font-medium transition-colors min-h-[44px] flex items-center justify-center"
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => handleDeleteRequest(request.id)}
                            className="px-4 py-3 sm:py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 font-medium transition-colors min-h-[44px] flex items-center justify-center"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                          </div>
                        </div>
                    </li>
                  ))}
                  {filteredRequests.length === 0 && (
                    <li className="px-6 py-8 text-center">
                      <p className="text-gray-500">No requests found matching your filters.</p>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          )}
        </div>

          {/* Edit User Modal */}
          {editingUser && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
              <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">{t('editUser')}</h3>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const updateData = Object.fromEntries(formData.entries());
                  handleUpdateUser(editingUser.id, updateData);
                }}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Full Name</label>
                      <input
                        type="text"
                        name="full_name"
                        defaultValue={editingUser.full_name}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <input
                        type="email"
                        name="email"
                        defaultValue={editingUser.email}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    {editingUser.user_type === 'transport_company' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Company Name</label>
                          <input
                            type="text"
                            name="company_name"
                            defaultValue={editingUser.company_name || ''}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Country of Origin</label>
                          <input
                            type="text"
                            name="country_origin"
                            defaultValue={editingUser.country_origin || ''}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setEditingUser(null)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      {t('cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {loading ? 'Saving...' : t('save')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Request View/Edit Modal */}
          {showRequestModal && (selectedRequest || editingRequest) && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">
                    {editingRequest ? '✏️ Edit Transport Request' : '👁️ View Request Details'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowRequestModal(false);
                      setSelectedRequest(null);
                      setEditingRequest(null);
                    }}
                    className="text-gray-400 hover:text-gray-600 text-3xl font-bold"
                  >
                    ×
                  </button>
                </div>
                
                {editingRequest ? (
                  /* Edit Form */
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const updatedData = {
                      pickup_city: formData.get('pickup_city'),
                      delivery_city: formData.get('delivery_city'),
                      pickup_date: formData.get('pickup_date'),
                      baggage_description: formData.get('baggage_description'),
                      weight: formData.get('weight'),
                      special_instructions: formData.get('special_instructions'),
                      pickup_address: formData.get('pickup_address'),
                      delivery_address: formData.get('delivery_address'),
                      status: formData.get('status')
                    };
                    handleUpdateRequest(updatedData);
                  }}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Pickup City</label>
                        <input
                          name="pickup_city"
                          defaultValue={editingRequest.pickup_city}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Delivery City</label>
                        <input
                          name="delivery_city"
                          defaultValue={editingRequest.delivery_city}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Pickup Date</label>
                        <input
                          name="pickup_date"
                          type="date"
                          defaultValue={editingRequest.pickup_date ? new Date(editingRequest.pickup_date).toISOString().split('T')[0] : ''}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Weight</label>
                        <input
                          name="weight"
                          defaultValue={editingRequest.weight || ''}
                          placeholder="e.g., 25kg"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                        <select
                          name="status"
                          defaultValue={editingRequest.status}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="active">Active</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Baggage Description</label>
                        <textarea
                          name="baggage_description"
                          defaultValue={editingRequest.baggage_description || ''}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Pickup Address</label>
                        <input
                          name="pickup_address"
                          defaultValue={editingRequest.pickup_address || ''}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Address</label>
                        <input
                          name="delivery_address"
                          defaultValue={editingRequest.delivery_address || ''}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Special Instructions</label>
                        <textarea
                          name="special_instructions"
                          defaultValue={editingRequest.special_instructions || ''}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    
                    <div className="flex justify-end space-x-3 mt-8">
                      <button
                        type="button"
                        onClick={() => {
                          setShowRequestModal(false);
                          setEditingRequest(null);
                        }}
                        className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
                      >
                        💾 Save Changes
                      </button>
                    </div>
                  </form>
                ) : selectedRequest && (
                  /* View Details */
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-lg mb-3">🛣️ Route Information</h4>
                        <p><strong>From:</strong> {selectedRequest.pickup_city}</p>
                        <p><strong>To:</strong> {selectedRequest.delivery_city}</p>
                        <p><strong>Pickup Date:</strong> {new Date(selectedRequest.pickup_date).toLocaleDateString('nl-NL')}</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-lg mb-3">📦 Baggage Details</h4>
                        <p><strong>Description:</strong> {selectedRequest.baggage_description || 'Not specified'}</p>
                        <p><strong>Weight:</strong> {selectedRequest.weight || 'Not specified'}</p>
                      </div>
                    </div>
                    
                    {(selectedRequest.pickup_address || selectedRequest.delivery_address) && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-lg mb-3">📍 Addresses</h4>
                        {selectedRequest.pickup_address && (
                          <p><strong>Pickup Address:</strong> {selectedRequest.pickup_address}</p>
                        )}
                        {selectedRequest.delivery_address && (
                          <p><strong>Delivery Address:</strong> {selectedRequest.delivery_address}</p>
                        )}
                      </div>
                    )}
                    
                    {selectedRequest.special_instructions && (
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-lg mb-3">💬 Special Instructions</h4>
                        <p>{selectedRequest.special_instructions}</p>
                      </div>
                    )}
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-lg mb-3">ℹ️ Request Information</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <p><strong>Request ID:</strong> {selectedRequest.id}</p>
                        <p><strong>Customer ID:</strong> {selectedRequest.customer_id}</p>
                        <p><strong>Status:</strong> <span className={`px-2 py-1 text-xs rounded-full ${
                          selectedRequest.status === 'active' ? 'bg-green-100 text-green-800' :
                          selectedRequest.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>{selectedRequest.status.toUpperCase()}</span></p>
                        <p><strong>Created:</strong> {new Date(selectedRequest.created_at).toLocaleDateString('nl-NL')}</p>
                      </div>
                    </div>
                    
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={() => {
                          setSelectedRequest(null);
                          setEditingRequest(selectedRequest);
                        }}
                        className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium"
                      >
                        ✏️ Edit This Request
                      </button>
                      <button
                        onClick={() => {
                          setShowRequestModal(false);
                          handleDeleteRequest(selectedRequest.id);
                        }}
                        className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium"
                      >
                        🗑️ Delete Request
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Logo Generator Component
const LogoGenerator = () => {
  const [generatedLogo, setGeneratedLogo] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const { t } = useLanguage();

  // Load saved logo on component mount
  useEffect(() => {
    const savedLogo = localStorage.getItem('send2maroc_logo');
    if (savedLogo) {
      setGeneratedLogo(savedLogo);
    }
  }, []);

  const generateLogo = async () => {
    setIsGenerating(true);
    try {
      const response = await axios.post(`${API}/generate-logo`);
      if (response.data.image_base64) {
        const logoDataUrl = `data:image/png;base64,${response.data.image_base64}`;
        setGeneratedLogo(logoDataUrl);
        // Store the logo in localStorage for use throughout the app
        localStorage.setItem('send2maroc_logo', logoDataUrl);
        // Trigger a custom event to notify other components
        window.dispatchEvent(new CustomEvent('logoUpdated', { detail: logoDataUrl }));
      }
    } catch (error) {
      console.error('Logo generation failed:', error);
      alert('Failed to generate logo. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadLogo = () => {
    if (generatedLogo) {
      const link = document.createElement('a');
      link.href = generatedLogo;
      link.download = 'Send2Maroc-Logo.png';
      link.click();
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg">
      <h3 className="text-xl font-bold mb-4">🎨 Logo Generator</h3>
      <p className="text-gray-600 mb-4">Generate a professional logo for Send2Maroc. Once generated, the logo will automatically be applied to all website headers.</p>
      
      <div className="space-y-4">
        <button
          onClick={generateLogo}
          disabled={isGenerating}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {isGenerating ? (
            <>
              <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Generating Logo...</span>
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
              </svg>
              <span>Generate Logo</span>
            </>
          )}
        </button>

        {generatedLogo && (
          <div className="space-y-4">
            <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
              <h4 className="font-semibold mb-2">Generated Logo:</h4>
              <img 
                src={generatedLogo} 
                alt="Send2Maroc Logo" 
                className="max-w-full h-auto border rounded"
                style={{ maxHeight: '200px' }}
              />
            </div>
            <button
              onClick={downloadLogo}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-4-4m4 4l4-4m5-4H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V8z" />
              </svg>
              <span>Download Logo</span>
            </button>
            
            <div className="mt-2 text-center">
              <p className="text-sm text-green-600 font-medium">
                ✅ Logo has been applied to the website header!
              </p>
              <p className="text-xs text-gray-500 mt-1">
                The logo will now appear on all pages where the header is displayed.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Company Landing Page
const CompanyLanding = () => {
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const logo = useLogo();

  const companyStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Vind transportopdrachten tussen Europa en Marokko",
    "description": "Transporteurs tussen Europa en Marokko: ontvang dagelijks nieuwe opdrachten zonder tussenpersonen. Directe klantcontact en betaling.",
    "url": `${window.location.origin}/companies`,
    "mainEntity": {
      "@type": "Service",
      "name": "Transportopdrachten Europa-Marokko",
      "description": "Platform voor transporteurs om rechtstreeks in contact te komen met klanten voor baggagetransport tussen Europa en Marokko",
      "provider": {
        "@type": "Organization",
        "name": "Send2Maroc"
      },
      "audience": {
        "@type": "Audience",
        "audienceType": "Transport Companies"
      }
    }
  };

  const handleRegisterNow = () => {
    navigate('/register/company');
  };

  return (
    <>
      <SEO 
        title="🚛 Voor Transportbedrijven - Gratis Registreren, Direct Opdrachten Ontvangen!"
        description="🆓 Transportbedrijven: Registreer GRATIS en ontvang dagelijks lucratieve opdrachten Europa→Marokko. Verdien honderden per transport! 🎯 Direct klantcontact via het platform"
        keywords="transportbedrijf opdrachten, europa marokko transport, gratis transportbedrijf registreren, bagage transport verdienen, transport platform nederland, transporteur registratie"
        image="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=630&q=80"
        url={`${window.location.origin}/companies`}
        structuredData={companyStructuredData}
        author="Send2Maroc"
        twitterHandle="@Send2Maroc"
      />
      
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        {/* Header */}
        <header className="bg-white shadow-lg border-b-2 border-blue-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
              <div className="flex items-center space-x-3">
                {logo ? (
                  <img 
                    src={logo} 
                    alt="Send2Maroc Logo" 
                    className="h-10 sm:h-12 w-auto object-contain"
                  />
                ) : (
                  <>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-600 to-blue-800 rounded-full flex items-center justify-center shadow-lg">
                      <svg className="w-5 h-5 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10" />
                      </svg>
                    </div>
                    <div>
                      <h1 className="text-lg sm:text-2xl font-bold text-blue-900">Send2Maroc</h1>
                      <p className="text-xs sm:text-sm text-blue-600 font-medium">{t('forTransportCompanies')}</p>
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-center space-x-2 sm:space-x-4">
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="px-2 py-1 sm:px-4 sm:py-2 border-2 border-blue-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="nl">{t('dutch')}</option>
                  <option value="en">{t('english')}</option>
                  <option value="fr">{t('french')}</option>
                  <option value="ar">{t('arabic')}</option>
                </select>
                <button
                  onClick={() => navigate('/')}
                  className="text-blue-600 hover:text-blue-800 font-medium text-xs sm:text-sm"
                >
                  ← {t('backToHome')}
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <div className="relative min-h-[70vh] sm:min-h-[80vh] flex items-center">
          {/* Background with transport imagery */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `linear-gradient(135deg, rgba(30, 64, 175, 0.9) 0%, rgba(37, 99, 235, 0.85) 50%, rgba(59, 130, 246, 0.8) 100%), 
                               url('https://customer-assets.emergentagent.com/job_euro-morocco-cargo/artifacts/b0dia8w6_milad-alizadeh-JibMa0FbyHw-unsplash.jpg')`
            }}
          ></div>
          
          {/* Moroccan decorative elements */}
          <div className="absolute inset-0 opacity-15">
            <div className="w-full h-full" style={{
              backgroundImage: `url('data:image/svg+xml,<svg width="120" height="120" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg"><defs><pattern id="transport-pattern" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse"><polygon points="30,5 35,20 50,20 38,30 43,45 30,35 17,45 22,30 10,20 25,20" fill="%23ffffff" fill-opacity="0.1"/><circle cx="30" cy="30" r="3" fill="%23ffffff" fill-opacity="0.15"/></pattern></defs><rect width="120" height="120" fill="url(%23transport-pattern)"/></svg>')`,
              backgroundSize: '120px 120px'
            }}></div>
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left side - Content */}
              <div className="text-center lg:text-left">
                <div className="mb-4 sm:mb-6">
                  <div className="inline-flex items-center px-3 py-2 sm:px-6 sm:py-3 bg-white/20 backdrop-blur-md rounded-full text-white text-xs sm:text-sm font-medium mb-4 sm:mb-6 border border-white/30">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    🚚 Voor Transportbedrijven
                  </div>
                </div>
                
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                  {t('companyPageTitle')}
                </h1>
                
                <div className="text-base sm:text-lg md:text-xl lg:text-2xl text-blue-100 mb-6 sm:mb-8 leading-relaxed space-y-2 sm:space-y-4">
                  <p>{t('companyIntro')}</p>
                  <p className="font-semibold">{t('companyDailyOrders')}</p>
                </div>
                
                {/* Benefits */}
                <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                  <div className="flex items-center space-x-3 text-white">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm sm:text-lg font-medium">{t('companyBenefit1')}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-white">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm sm:text-lg font-medium">{t('companyBenefit2')}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-white">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm sm:text-lg font-medium">{t('companyBenefit3')}</span>
                  </div>
                </div>
                
                {/* CTA */}
                <div className="space-y-4">
                  <p className="text-lg sm:text-xl text-white font-semibold">{t('companyCTA')}</p>
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                    <button 
                      onClick={handleRegisterNow}
                      className="group relative inline-flex items-center justify-center px-6 py-3 sm:px-12 sm:py-4 text-lg sm:text-xl font-bold text-blue-900 bg-white rounded-xl sm:rounded-2xl hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl border-2 border-white/20"
                    >
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      {t('registerNow')}
                    </button>
                    
                    <button 
                      onClick={() => navigate('/login?type=company')}
                      className="group relative inline-flex items-center justify-center px-6 py-3 sm:px-12 sm:py-4 text-lg sm:text-xl font-bold text-white bg-white/20 backdrop-blur-md border-2 border-white/30 rounded-xl sm:rounded-2xl hover:bg-white/30 transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl"
                    >
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 0a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {t('alreadyAccount')}
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Right side - Visual element */}
              <div className="relative lg:block hidden">
                <div className="relative w-full h-96">
                  <div className="absolute inset-0 bg-white/10 backdrop-blur-sm rounded-3xl border-2 border-white/20 shadow-2xl overflow-hidden">
                    <div className="w-full h-full bg-gradient-to-br from-blue-400/30 to-blue-600/30 flex items-center justify-center relative">
                      <div className="text-center text-white p-6">
                        <div className="w-24 h-24 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                          <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </div>
                        <h3 className="text-2xl font-bold mb-2">{t('directContact')}</h3>
                        <p className="text-blue-100">{t('clientsToYourBusiness')}</p>
                        <p className="text-sm opacity-75 mt-2">{t('noIntermediaries')}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Floating elements */}
                  <div className="absolute -top-6 -right-6 w-20 h-20 bg-white rounded-3xl shadow-xl flex items-center justify-center border-4 border-blue-100">
                    <span className="text-2xl">💰</span>
                  </div>
                  <div className="absolute -bottom-6 -left-6 w-16 h-16 bg-green-500 rounded-2xl shadow-xl flex items-center justify-center border-4 border-white">
                    <span className="text-white text-2xl">✓</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Benefits Section */}
        <div className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-blue-900 mb-6">{t('whyChooseUs')}</h2>
              <p className="text-xl text-blue-700 max-w-3xl mx-auto">{t('whyChooseSubtitle')}</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-6 bg-blue-50 rounded-2xl">
                <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-blue-900 mb-3">{t('directPayment')}</h3>
                <p className="text-blue-700">{t('directPaymentDesc')}</p>
              </div>
              
              <div className="text-center p-6 bg-green-50 rounded-2xl">
                <div className="w-16 h-16 bg-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-green-900 mb-3">{t('verifiedClients')}</h3>
                <p className="text-green-700">{t('verifiedClientsDesc')}</p>
              </div>
              
              <div className="text-center p-6 bg-orange-50 rounded-2xl">
                <div className="w-16 h-16 bg-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-orange-900 mb-3">{t('dailyOrders')}</h3>
                <p className="text-orange-700">{t('dailyOrdersDesc')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Section */}
        <div className="py-20 bg-gradient-to-br from-green-50 via-blue-50 to-green-50 relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-10 left-10 w-32 h-32 bg-green-600 rounded-full"></div>
            <div className="absolute bottom-10 right-10 w-24 h-24 bg-blue-600 rounded-full"></div>
            <div className="absolute top-1/2 left-1/3 w-16 h-16 bg-yellow-500 rounded-full"></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-green-900 mb-6">100% Gratis Registreren</h2>
              <p className="text-xl lg:text-2xl text-green-700 max-w-3xl mx-auto font-semibold">Geen abonnement, geen maandelijkse kosten. Start vandaag nog!</p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              {/* Main pricing card */}
              <div className="bg-white rounded-3xl shadow-2xl border-4 border-green-200 p-8 lg:p-12 text-center relative overflow-hidden">
                {/* Popular badge */}
                <div className="absolute top-0 right-8">
                  <div className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-2 rounded-b-xl font-bold text-sm">
                    GRATIS
                  </div>
                </div>
                
                {/* Price */}
                <div className="mb-8">
                  <div className="flex items-baseline justify-center mb-2">
                    <span className="text-6xl lg:text-7xl font-bold text-green-900">€0</span>
                    <span className="text-2xl text-gray-500 ml-2">/maand</span>
                  </div>
                  <p className="text-lg text-gray-600">Geen abonnement nodig</p>
                </div>
                
                {/* Benefits */}
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-700 font-medium">{t('pricingBenefit1')}</span>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-700 font-medium">{t('pricingBenefit2')}</span>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-700 font-medium">{t('pricingBenefit3')}</span>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-700 font-medium">{t('pricingBenefit4')}</span>
                  </div>
                </div>
                
                {/* Free platform info */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-6 mb-8">
                  <div className="flex items-center justify-center space-x-3 mb-3">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-green-800">100% Gratis - Geen Verborgen Kosten</h3>
                  </div>
                  <p className="text-green-800 font-medium text-center">
                    Geen abonnement, geen commissie, geen opstartkosten. Registreer en begin direct met verdienen!
                  </p>
                </div>
                
                {/* Call to action text */}
                <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6 mb-8">
                  <p className="text-lg text-blue-900 font-semibold leading-relaxed">
                    {t('pricingCTA')}
                  </p>
                </div>
                
                {/* Registration button */}
                <button 
                  onClick={handleRegisterNow}
                  className="group relative inline-flex items-center justify-center px-12 py-4 text-xl font-bold text-white bg-gradient-to-r from-green-600 to-green-700 rounded-2xl hover:from-green-700 hover:to-green-800 transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl border-2 border-green-300"
                >
                  <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  {t('registerNow')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10" />
                </svg>
              </div>
              <div className="text-left">
                <h3 className="text-2xl font-bold">Send2Maroc</h3>
                <p className="text-blue-200 text-sm">{t('forTransportCompanies')}</p>
              </div>
            </div>
            <p className="text-blue-200 max-w-2xl mx-auto leading-relaxed">
              © 2025 Send2Maroc. Alle rechten voorbehouden. 
              Het platform dat Europa met Marokko verbindt.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

// Company Registration Page
const CompanyRegister = () => {
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const { register } = useAuth();
  const logo = useLogo();
  const [loading, setLoading] = useState(false);
  const [inviteCodeValid, setInviteCodeValid] = useState(null);
  const [formData, setFormData] = useState({
    // Essential fields only
    company_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    country: 'Netherlands',
    invite_code: ''
  });

  const companyRegisterStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Transportbedrijf Registratie - Send2Maroc",
    "description": "Registreer uw transportbedrijf bij Send2Maroc voor baggagetransport tussen Europa en Marokko",
    "url": `${window.location.origin}/register/company`
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Simple validation
    if (!formData.company_name.trim()) {
      alert(t('enterCompanyName'));
      return;
    }
    
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      alert(t('enterValidEmail'));
      return;
    }
    
    if (!formData.phone.trim()) {
      alert(t('enterPhone'));
      return;
    }
    
    if (!formData.password || formData.password.length < 8) {
      alert(t('passwordMin8'));
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      alert(t('passwordsNotMatch'));
      return;
    }

    setLoading(true);
    
    try {
      // Always register directly - free model (commission based)
      const registrationData = {
        email: formData.email,
        password: formData.password,
        full_name: formData.company_name,
        user_type: 'transport_company',
        company_name: formData.company_name,
        country_origin: formData.country,
        phone_number: formData.phone,
        terms_accepted: true,
        invite_code: formData.invite_code ? formData.invite_code.toUpperCase() : undefined
      };
      
      const result = await register(registrationData);
      
      if (result.success) {
        alert(t('registrationSuccessAlert'));
        navigate('/login');
      } else {
        alert(t('registrationErrorPrefix') + (result.error || t('registrationFailed')));
      }
    } catch (error) {
      console.error('Registration error:', error);
      alert(t('registrationErrorGeneral') + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <SEO 
        title="Transportbedrijf Registratie"
        description="Registreer uw transportbedrijf bij Send2Maroc voor baggagetransport tussen Europa en Marokko. Ontvang dagelijks nieuwe opdrachten."
        keywords="transportbedrijf registratie, bedrijf aanmelden, Europa Marokko transport, cargo registreren"
        structuredData={companyRegisterStructuredData}
        url={`${window.location.origin}/register/company`}
      />
      
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        {/* Header */}
        <header className="bg-white shadow-lg border-b-2 border-blue-100">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                {logo ? (
                  <img 
                    src={logo} 
                    alt="Send2Maroc Logo" 
                    className="h-10 sm:h-12 w-auto object-contain flex-shrink-0"
                  />
                ) : (
                  <>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-600 to-blue-800 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
                      <svg className="w-5 h-5 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <h1 className="text-lg sm:text-2xl font-bold text-blue-900 truncate">Send2Maroc</h1>
                      <p className="text-xs sm:text-sm text-blue-600 font-medium truncate">{t('companyRegistration')}</p>
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="px-2 sm:px-4 py-2 border-2 border-blue-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white min-w-0"
                >
                  <option value="nl">{t('dutch')}</option>
                  <option value="en">{t('english')}</option>
                  <option value="fr">{t('french')}</option>
                  <option value="ar">{t('arabic')}</option>
                </select>
                <button
                  onClick={() => navigate('/companies')}
                  className="text-blue-600 hover:text-blue-800 font-medium text-xs sm:text-sm whitespace-nowrap"
                >
                  <span className="hidden sm:inline">{t('backToHome')}</span>
                  <span className="sm:hidden">← Home</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Simple Registration Form */}
        <div className="max-w-md mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 mb-2">
                🚚 {t('becomePartner')}
              </h2>
              <p className="text-sm sm:text-base text-gray-600">
                {t('startTodayReceiveRequests')}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('companyName')} *
                </label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('yourCompanyName')}
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('emailPlaceholder')}
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('phoneNumber')} *
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="+31 6 12345678"
                  required
                />
              </div>

              {/* Country */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('country')} *
                </label>
                <select
                  value={formData.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Netherlands">{t('netherlands')}</option>
                  <option value="Belgium">{t('belgium')}</option>
                  <option value="Germany">{t('germany')}</option>
                  <option value="France">{t('france')}</option>
                  <option value="Spain">{t('spain')}</option>
                  <option value="Morocco">{t('morocco')}</option>
                </select>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('password')} *
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('minCharacters')}
                  required
                  minLength={8}
                />
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('confirmPassword')} *
                </label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('repeatPassword')}
                  required
                />
              </div>

              {/* Invite Code (Optional) */}
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <label className="block text-sm font-medium text-green-800 mb-1">
                  🎟️ {t('inviteCodeOptional')}
                </label>
                <input
                  type="text"
                  value={formData.invite_code}
                  onChange={(e) => handleInputChange('invite_code', e.target.value.toUpperCase())}
                  className="w-full px-4 py-3 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 uppercase font-mono"
                  placeholder=""
                />
                <p className="text-xs text-green-600 mt-1">
                  {t('inviteCodeDesc')}
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>{t('registeringText')}</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('freeRegister')}</span>
                  </>
                )}
              </button>

              {/* Free Registration Info */}
              <div className="text-center mt-4">
                <div className="inline-block bg-green-100 border-2 border-green-500 rounded-lg px-4 py-3 mb-2">
                  <p className="text-lg font-bold text-green-700">
                    ✅ {t('freeRegisterBadge')}
                  </p>
                </div>
                <p className="text-sm text-gray-600">
                  {t('freeRegisterDesc')}
                </p>
              </div>

              {/* Login Link */}
              <div className="text-center text-sm text-gray-600 mt-4">
                {t('alreadyAccount')} {' '}
                <button
                  type="button"
                  onClick={() => navigate('/companies')}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  {t('loginHere')}
                </button>
              </div>
            </form>

            {/* Benefits */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-3">{t('benefitsForPartners')}</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{t('directAccessRequests')}</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{t('chatDirectly')}</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span><strong>{t('firstMonthFree')}</strong> - {t('thenMonthly')}</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{t('earnExtra')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
const CustomerDashboard = () => {
  const { user, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const { protectedClick, isProcessing } = useClickProtection();
  
  const [activeTab, setActiveTab] = useState('requests');
  const [requests, setRequests] = useState([]);
  const [quotes, setQuotes] = useState({});
  const [conversations, setConversations] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showChat, setShowChat] = useState(null);
  const [pendingDeliveryNotifications, setPendingDeliveryNotifications] = useState([]);
  const [showDeliveryNotification, setShowDeliveryNotification] = useState(false);
  // Company directory states
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showCompanyProfile, setShowCompanyProfile] = useState(false);
  const [companyReviews, setCompanyReviews] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [newReview, setNewReview] = useState({
    rating: 5,
    review_text: ''
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Search and filter states with debouncing for performance
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedDepartureDate, setSelectedDepartureDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Debounced search for better mobile performance
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const debouncedLanguage = useDebounce(selectedLanguage, 300);
  const debouncedCountry = useDebounce(selectedCountry, 300);
  // Pagination states
  const [currentPage, setCurrentPage] = useState({
    requests: 1,
    companies: 1,
    conversations: 1
  });
  const [itemsPerPage] = useState(10); // Show 10 items per page
  // Loading states for better UX
  const [loading, setLoading] = useState({
    requests: false,
    companies: false,
    conversations: false
  });
  const [newRequest, setNewRequest] = useState({
    pickup_city: '',
    pickup_country: '',
    delivery_city: '',
    baggage_size: '',
    baggage_weight: '',
    preferred_date: '',
    special_requirements: '',
    baggage_image: ''
  });

  useEffect(() => {
    if (user && user.user_type === 'customer') {
      // PERFORMANCE: Only load essential data on initial load
      fetchRequests(); // Load first page of requests only
      fetchConversations(); // Load conversations only
      
      // Companies are loaded lazily when user clicks companies tab
      // PERFORMANCE: Removed automatic 30-second refresh to improve performance
      // Removed manual refresh button as requested
    }
  }, [user]);

  // Reset company pagination when filters change
  useEffect(() => {
    setCurrentPage(prev => ({
      ...prev,
      companies: 1
    }));
  }, [searchQuery, selectedLanguage, selectedCountry, selectedDepartureDate]);

  // PERFORMANCE: Removed automatic delivery notification checking to improve speed
  // Removed manual refresh functionality as requested

  // Additional check when component mounts and when tab becomes active
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && activeTab === 'requests') {
        fetchRequests();
        setTimeout(() => checkForDeliveryNotifications(), 1000);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [activeTab]);

  const checkForDeliveryNotifications = () => {
    const pendingDeliveries = requests.filter(request => {
      const hasAcceptedQuotes = quotes[request.id]?.some(quote => quote.status === 'accepted');
      return hasAcceptedQuotes && request.company_confirmed_delivery && !request.customer_confirmed_delivery;
    });

    if (pendingDeliveries.length > 0) {
      setPendingDeliveryNotifications(pendingDeliveries);
      setShowDeliveryNotification(true);
    }
  };

  const fetchConversations = async (page = 1) => {
    try {
      const response = await axios.get(`${API}/chat/conversations?page=${page}&limit=${itemsPerPage}`);
      if (page === 1) {
        setConversations(response.data);
      } else {
        // For subsequent pages, append to existing data
        setConversations(prev => [...prev, ...response.data]);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const fetchRequests = async (page = 1) => {
    setLoading(prev => ({ ...prev, requests: true }));
    try {
      const response = await axios.get(`${API}/transport-requests?page=${page}&limit=${itemsPerPage}`);
      if (page === 1) {
        setRequests(response.data);
      } else {
        // For subsequent pages, append to existing data
        setRequests(prev => [...prev, ...response.data]);
      }
      
      // PERFORMANCE: Removed automatic quote fetching - only fetch when user expands request details
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(prev => ({ ...prev, requests: false }));
    }
  };

  const deleteRequest = async (requestId) => {
    if (!window.confirm('Weet je zeker dat je deze aanvraag wilt verwijderen?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/transport-requests/${requestId}`);
      alert('✅ Aanvraag succesvol verwijderd!');
      fetchRequests(); // Refresh the list
    } catch (error) {
      console.error('Error deleting request:', error);
      if (error.response?.status === 400) {
        alert('❌ ' + (error.response.data.detail || 'Kan aanvraag niet verwijderen - er zijn geaccepteerde offertes.'));
      } else {
        alert('❌ Fout bij het verwijderen van de aanvraag.');
      }
    }
  };

  const confirmDelivery = async (requestId, quoteId) => {
    if (!window.confirm('Bevestig je dat je de bagage hebt ontvangen?')) {
      return;
    }
    
    try {
      await axios.post(`${API}/delivery/customer-confirm`, {
        transport_request_id: requestId,
        quote_id: quoteId
      });
      alert('✅ Levering bevestigd!');
      fetchRequests(); // Refresh to update status
      // Clear notifications for this request
      setPendingDeliveryNotifications(prev => 
        prev.filter(req => req.id !== requestId)
      );
      // Hide notification popup if no more notifications
      if (pendingDeliveryNotifications.length <= 1) {
        setShowDeliveryNotification(false);
      }
    } catch (error) {
      console.error('Error confirming delivery:', error);
      alert('❌ Fout bij het bevestigen van levering.');
    }
  };

  // Function to check if a request can be deleted
  const canDeleteRequest = (request) => {
    // Can delete if no quotes are accepted OR if status is delivered
    const hasAcceptedQuotes = quotes[request.id]?.some(quote => quote.status === 'accepted');
    return !hasAcceptedQuotes || request.status === 'delivered';
  };

  // Function to check if customer needs to confirm delivery
  const needsDeliveryConfirmation = (request) => {
    // Customer needs to confirm if company has confirmed delivery but customer hasn't
    const hasAcceptedQuotes = quotes[request.id]?.some(quote => quote.status === 'accepted');
    return hasAcceptedQuotes && request.company_confirmed_delivery && !request.customer_confirmed_delivery;
  };

  const fetchQuotes = async (requestId) => {
    try {
      const response = await axios.get(`${API}/quotes/request/${requestId}`);
      setQuotes(prev => ({ ...prev, [requestId]: response.data }));
    } catch (error) {
      console.error('Error fetching quotes:', error);
    }
  };

  const createRequest = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/transport-requests`, newRequest);
      fetchRequests();
      setShowCreateForm(false);
      setNewRequest({
        pickup_city: '',
        pickup_country: '',
        delivery_city: '',
        baggage_size: '',
        baggage_weight: '',
        preferred_date: '',
        special_requirements: '',
        baggage_image: ''
      });
      // Removed error popup - just show success silently
    } catch (error) {
      console.error('Error creating request:', error);
      // Still log errors but don't show popup to user
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewRequest({...newRequest, baggage_image: reader.result});
      };
      reader.readAsDataURL(file);
    }
  };

  const respondToQuote = async (quoteId, status) => {
    try {
      await axios.post(`${API}/quotes/${quoteId}/respond`, { quote_id: quoteId, status });
      // Refresh quotes for all requests
      requests.forEach(request => fetchQuotes(request.id));
      // Refresh conversations if quote was accepted
      if (status === 'accept') {
        fetchConversations();
        // Find the accepted quote to get company name and auto-open chat
        setTimeout(() => {
          const acceptedQuote = Object.values(quotes).flat().find(q => q.id === quoteId);
          if (acceptedQuote) {
            setShowChat({
              quoteId: quoteId,
              otherPartyName: acceptedQuote.company_name
            });
          }
        }, 1000); // Small delay to ensure backend has processed the acceptance
      }
    } catch (error) {
      console.error('Error responding to quote:', error);
    }
  };

  // Payment function for accepted quotes
  const payForQuote = async (quoteId) => {
    try {
      const response = await axios.post(`${API}/payments/create-booking-checkout`, {
        quote_id: quoteId,
        origin_url: window.location.origin
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Er ging iets mis bij het aanmaken van de betaling.';
      alert(msg);
      console.error('Error creating booking checkout:', error);
    }
  };

  const openChat = (conversation) => {
    console.log('Opening chat for conversation:', conversation);
    console.log('User type:', user?.user_type);
    
    const calculatedCompanyId = user?.user_type === 'customer' ? 
      conversation.company_id : 
      user?.id;
    
    console.log('Calculated companyId:', calculatedCompanyId);
    
    setShowChat({
      quoteId: conversation.quote_id,
      companyId: calculatedCompanyId,
      otherPartyName: conversation.other_party_name,
      otherPartyType: user?.user_type === 'customer' ? 'transport_company' : 'customer',
      companyName: user?.user_type === 'customer' ? conversation.other_party_name : user?.company_name || user?.full_name,
      customerName: user?.user_type === 'customer' ? user?.full_name : conversation.other_party_name,
      customerId: user?.user_type === 'customer' ? user?.id : conversation.customer_id,
      customerEmail: user?.user_type === 'customer' ? user?.email : conversation.customer_email
    });
  };

  // Company directory functions - LAZY LOADING
  const fetchCompanies = async (page = 1) => {
    // Prevent multiple simultaneous calls
    if (loading.companies) {
      console.log('⏳ Companies already loading, skipping...');
      return;
    }
    
    setLoading(prev => ({ ...prev, companies: true }));
    try {
      const response = await axios.get(`${API}/companies/directory?page=${page}&limit=${itemsPerPage}`);
      
      if (page === 1) {
        setCompanies(response.data.companies || []);
      } else {
        // For subsequent pages, append to existing data
        setCompanies(prev => [...prev, ...(response.data.companies || [])]);
      }
    } catch (error) {
      console.error('❌ Error fetching companies:', error);
      
      // BETTER ERROR HANDLING
      if (error.response?.status === 403) {
        alert('❌ Toegang geweigerd: Alleen klanten kunnen bedrijven bekijken');
      } else if (error.response?.status === 401) {
        alert('❌ U bent niet ingelogd. Log opnieuw in.');
        logout(); // Redirect to login
      } else {
        alert('❌ Fout bij het laden van bedrijven: ' + (error.response?.data?.detail || error.message));
      }
      
      // Set empty array on error to prevent crashes
      setCompanies([]);
    } finally {
      setLoading(prev => ({ ...prev, companies: false }));
    }
  };

  const fetchCompanyProfile = async (companyId) => {
    try {
      const response = await axios.get(`${API}/companies/${companyId}/profile`);
      setSelectedCompany(response.data);
      setCompanyReviews(response.data.reviews || []);
      setShowCompanyProfile(true);
    } catch (error) {
      console.error('Error fetching company profile:', error);
    }
  };

  const startChatWithCompany = (company) => {
    // Check if there's an existing conversation with this company
    const existingConversation = conversations.find(conv => 
      conv.other_party_name === company.company_name
    );
    
    if (existingConversation) {
      // Open existing chat
      openChat(existingConversation);
    } else {
      // Start new general chat with company
      setShowChat({
        quoteId: null,
        companyId: company.id,
        otherPartyName: company.company_name,
        otherPartyType: 'transport_company',
        companyName: company.company_name,
        customerName: user?.full_name,
        customerId: user?.id,
        customerEmail: user?.email,
        isNewGeneralChat: true
      });
    }
  };

  const deleteConversation = async (conversation) => {
    // Confirm deletion
    const confirmDelete = window.confirm(t('confirmDeleteConversation'));
    if (!confirmDelete) {
      return;
    }

    try {
      const params = new URLSearchParams();
      
      if (conversation.quote_id) {
        // Quote-based conversation
        params.append('quote_id', conversation.quote_id);
      } else if (conversation.conversation_type === 'general') {
        // General conversation
        if (user?.user_type === 'customer') {
          params.append('company_id', conversation.company_id);
        } else {
          // For companies, we need to pass the customer_id as company_id parameter
          params.append('company_id', conversation.customer_id);
        }
      }

      await axios.delete(`${API}/chat/conversation?${params.toString()}`);
      
      alert(t('conversationDeleted'));
      
      // Refresh conversations list
      fetchConversations();
      
    } catch (error) {
      console.error('Error deleting conversation:', error);
      alert('Fout bij het verwijderen van gesprek: ' + (error.response?.data?.detail || error.message));
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!selectedCompany) return;

    try {
      await axios.post(`${API}/reviews`, {
        company_id: selectedCompany.id,
        rating: newReview.rating,
        review_text: newReview.review_text
      });
      
      // Refresh company profile to show new review
      await fetchCompanyProfile(selectedCompany.id);
      setShowReviewForm(false);
      setNewReview({ rating: 5, review_text: '' });
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Error submitting review. You may have already reviewed this company.');
    }
  };

  // Fetch companies when companies tab is accessed
  useEffect(() => {
    if (activeTab === 'companies' && companies.length === 0) {
      fetchCompanies();
    }
  }, [activeTab, companies.length, fetchCompanies]);

  // Filter companies based on search and filter criteria - OPTIMIZED FOR MOBILE
  const filterCompanies = useCallback(() => {
    return companies.filter(company => {
      const matchesSearch = debouncedSearchQuery === '' || 
        company.company_name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
        (company.current_location && company.current_location.toLowerCase().includes(debouncedSearchQuery.toLowerCase()));
      const matchesLanguage = debouncedLanguage === '' || 
        (company.languages && company.languages.includes(debouncedLanguage));
      const matchesCountry = debouncedCountry === '' || 
        company.country_origin === debouncedCountry;
      const matchesDepartureDate = selectedDepartureDate === '' || 
        (company.departure_dates_morocco && company.departure_dates_morocco.some(date => {
          const selectedDate = new Date(selectedDepartureDate);
          const companyDate = new Date(date);
          return companyDate >= selectedDate;
        }));
      return matchesSearch && matchesLanguage && matchesCountry && matchesDepartureDate;
    });
  }, [companies, debouncedSearchQuery, debouncedLanguage, debouncedCountry, selectedDepartureDate]);

  const filteredCompanies = filterCompanies();

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedLanguage('');
    setSelectedLocation('');
    setSelectedCountry('');
    setSelectedDepartureDate('');
    // Reset to first page when filters change
    setCurrentPage(prev => ({
      ...prev,
      companies: 1
    }));
  };

  // Pagination helper functions - SIMPLIFIED FOR PERFORMANCE
  const LoadMoreButton = ({ section, hasMore, loading, onLoadMore }) => {
    if (!hasMore) return null;
    
    return (
      <div className="flex justify-center mt-6">
        <button
          onClick={onLoadMore}
          disabled={loading}
          className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? `⏳ ${t('loading')}` : `➕ ${t('loadMore')}`}
        </button>
      </div>
    );
  };

  // Keep minimal functions for existing usage
  const paginate = (items, page, perPage) => {
    const start = (page - 1) * perPage;
    const end = start + perPage;
    return items.slice(start, end);
  };

  const getTotalPages = (totalItems, perPage) => {
    return Math.ceil(totalItems / perPage);
  };

  const changePage = (section, page) => {
    setCurrentPage(prev => ({
      ...prev,
      [section]: page
    }));
  };

  const getPaginatedRequests = () => {
    return paginate(requests, currentPage.requests, itemsPerPage);
  };

  const getPaginatedCompanies = () => {
    const filteredCompanies = companies.filter(company => {
      // Search by company name and location
      const matchesSearch = searchQuery === '' || 
        company.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (company.current_location && company.current_location.toLowerCase().includes(searchQuery.toLowerCase()));
      
      // Filter by communication languages
      const matchesLanguage = selectedLanguage === '' || 
        (company.communication_languages && company.communication_languages.includes(selectedLanguage));
      
      // Filter by current location (for location-based filtering)
      const matchesLocation = selectedLocation === '' || 
        (company.current_location && company.current_location.toLowerCase().includes(selectedLocation.toLowerCase()));
      
      // Filter by country of origin
      const matchesCountry = selectedCountry === '' || company.country_origin === selectedCountry;
      
      // Filter by departure date to Morocco
      const matchesDepartureDate = selectedDepartureDate === '' || 
        (company.departure_dates_morocco && company.departure_dates_morocco.some(date => {
          const selectedDate = new Date(selectedDepartureDate);
          const companyDate = new Date(date);
          // Check if company has departure on or after the selected date
          return companyDate >= selectedDate;
        }));
      
      return matchesSearch && matchesLanguage && matchesLocation && matchesCountry && matchesDepartureDate;
    });
    return paginate(filteredCompanies, currentPage.companies, itemsPerPage);
  };

  const getPaginatedConversations = () => {
    return paginate(conversations, currentPage.conversations, itemsPerPage);
  };

  // Simplified Pagination Component
  const PaginationControls = ({ currentPage, totalItems, itemsPerPage, onPageChange, section }) => {
    const totalPages = getTotalPages(totalItems, itemsPerPage);
    
    if (totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-between mt-6 px-4 py-3 bg-white border border-gray-200 rounded-lg">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => onPageChange(section, Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('previous')}
          </button>
          <button
            onClick={() => onPageChange(section, Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('next')}
          </button>
        </div>
        
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Toont <span className="font-medium">{Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}</span> tot{' '}
              <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span> van{' '}
              <span className="font-medium">{totalItems}</span> resultaten
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => onPageChange(section, Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ‹
              </button>
              
              <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                {currentPage} / {totalPages}
              </span>
              
              <button
                onClick={() => onPageChange(section, Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ›
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  // Fixed list of countries for filter dropdown with translations
  const getAvailableCountries = () => {
    const countries = [
      { value: 'Netherlands', label: t('netherlands') },
      { value: 'Belgium', label: t('belgium') },
      { value: 'Spain', label: t('spain') },
      { value: 'France', label: t('france') },
      { value: 'Germany', label: t('germany') },
      { value: 'Italy', label: t('italy') },
      { value: 'Morocco', label: t('morocco') }
    ];
    return countries;
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Modern Sidebar Navigation */}
      <div className={`
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} 
        lg:translate-x-0 
        fixed lg:static inset-y-0 left-0 z-50 
        w-64 lg:w-64 
        bg-white shadow-lg flex flex-col 
        transition-transform duration-300 ease-in-out
      `}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">📦</span>
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">{t('customerPanel') || 'Klant Panel'}</h1>
                <p className="text-xs text-gray-500">{t('manageRequests') || 'Beheer uw aanvragen'}</p>
              </div>
            </div>
            {/* Close button for mobile */}
            <button
              onClick={() => protectedClick(async () => {
                setIsMobileMenuOpen(false);
              }, 200)}
              disabled={isProcessing}
              className={`lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-600 ${
                isProcessing ? 'opacity-50' : ''
              }`}
            >
              ✕
            </button>
          </div>
          
          {/* Language Selector */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">{t('language') || 'Taal'}:</span>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
            >
              <option value="nl">{t('dutch')}</option>
              <option value="en">{t('english')}</option>
              <option value="fr">{t('french')}</option>
              <option value="ar">{t('arabic')}</option>
            </select>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 px-4 py-6 space-y-2">
          <button
            onClick={() => protectedClick(async () => {
              setActiveTab('requests');
              setIsMobileMenuOpen(false); // Close mobile menu
            }, 500)}
            disabled={isProcessing}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
              activeTab === 'requests'
                ? 'bg-indigo-50 text-indigo-600 border-r-2 border-indigo-600'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span className="text-xl">📋</span>
            <div>
              <div className="font-medium">{t('myRequests')}</div>
              <div className="text-sm opacity-75">Transport aanvragen</div>
            </div>
          </button>

          <button
            onClick={() => protectedClick(async () => {
              setActiveTab('companies');
              setIsMobileMenuOpen(false); // Close mobile menu
              // LAZY LOADING: Only fetch companies when user actually wants to see them
              if (companies.length === 0 && !loading.companies) {
                setTimeout(() => {
                  if (companies.length === 0) {
                    fetchCompanies();
                  }
                }, 100);
              }
            }, 500)}
            disabled={isProcessing}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
              activeTab === 'companies'
                ? 'bg-indigo-50 text-indigo-600 border-r-2 border-indigo-600'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span className="text-xl">🏢</span>
            <div>
              <div className="font-medium">{t('availableCompanies')}</div>
              <div className="text-sm opacity-75">Transportbedrijven</div>
            </div>
          </button>

          <button
            onClick={() => protectedClick(async () => {
              setActiveTab('chat');
              setIsMobileMenuOpen(false); // Close mobile menu
            }, 500)}
            disabled={isProcessing}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
              activeTab === 'chat'
                ? 'bg-indigo-50 text-indigo-600 border-r-2 border-indigo-600'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span className="text-xl">💬</span>
            <div className="flex-1 flex items-center justify-between">
              <div>
                <div className="font-medium">{t('conversations')}</div>
                <div className="text-sm opacity-75">Chat met bedrijven</div>
              </div>
              {conversations.some(c => c.unread_count > 0) && (
                <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 ml-2">
                  {conversations.reduce((total, c) => total + c.unread_count, 0)}
                </span>
              )}
            </div>
          </button>

          <button
            onClick={() => protectedClick(async () => {
              setActiveTab('tracking');
              setIsMobileMenuOpen(false); // Close mobile menu
            }, 500)}
            disabled={isProcessing}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
              activeTab === 'tracking'
                ? 'bg-indigo-50 text-indigo-600 border-r-2 border-indigo-600'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span className="text-xl">📍</span>
            <div>
              <div className="font-medium">{t('tracking')}</div>
              <div className="text-sm opacity-75">Volg uw zendingen</div>
            </div>
          </button>
        </nav>

        {/* User Info & Logout */}
        <div className="px-4 py-4 border-t border-gray-200">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
              <span className="text-indigo-600 font-semibold text-sm">{user.full_name.charAt(0)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user.full_name}</p>
              <p className="text-xs text-gray-500">Klant</p>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button-customer"
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm font-semibold shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span>{t('logout')}</span>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Mobile Menu Button */}
              <button
                onClick={() => protectedClick(async () => {
                  setIsMobileMenuOpen(true);
                }, 200)}
                disabled={isProcessing}
                className={`lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 ${
                  isProcessing ? 'opacity-50' : ''
                }`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {activeTab === 'requests' && t('myRequests')}
                  {activeTab === 'companies' && t('availableCompanies')}
                  {activeTab === 'chat' && t('conversations')}
                  {activeTab === 'tracking' && t('tracking')}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  {activeTab === 'requests' && 'Beheer uw transportaanvragen en leveringen'}
                  {activeTab === 'companies' && 'Ontdek betrouwbare transportbedrijven'}
                  {activeTab === 'chat' && 'Communiceer direct met transportbedrijven'}
                  {activeTab === 'tracking' && 'Volg de status van uw zendingen'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">Klant Dashboard</div>
                <div className="text-xs text-gray-500">{new Date().toLocaleDateString('nl-NL')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          {activeTab === 'requests' && (
              <div>
                <div className="mb-6 flex justify-between items-center">
                  <h2 className="text-lg font-medium text-gray-900">{t('myRequests')}</h2>
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
                  >
                    {t('createRequest')}
                  </button>
                </div>

                {/* Delivery Notification Banner */}
                {requests.some(request => {
                  const hasAcceptedQuotes = quotes[request.id]?.some(quote => quote.status === 'accepted');
                  return hasAcceptedQuotes && request.company_confirmed_delivery && !request.customer_confirmed_delivery;
                }) && (
                  <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="text-orange-500 text-2xl mr-3">📦</div>
                      <div className="flex-1">
                        <h3 className="text-orange-800 font-semibold">Levering Ontvangen!</h3>
                        <p className="text-orange-700 text-sm">
                          Een transportbedrijf heeft aangegeven dat uw bagage is afgeleverd. 
                          Scroll naar beneden om de levering te bevestigen.
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          const pendingDeliveries = requests.filter(request => {
                            const hasAcceptedQuotes = quotes[request.id]?.some(quote => quote.status === 'accepted');
                            return hasAcceptedQuotes && request.company_confirmed_delivery && !request.customer_confirmed_delivery;
                          });
                          setPendingDeliveryNotifications(pendingDeliveries);
                          setShowDeliveryNotification(true);
                        }}
                        className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700 text-sm"
                      >
                        Bevestig Nu
                      </button>
                    </div>
                  </div>
                )}

                {showCreateForm && (
                  <div className="mb-6 bg-white p-6 rounded-lg shadow">
                    <form onSubmit={createRequest}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('pickupCity')}
                          </label>
                          <input
                            type="text"
                            required
                            value={newRequest.pickup_city}
                            onChange={(e) => setNewRequest({...newRequest, pickup_city: e.target.value})}
                            className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            🌍 Pickup Land
                          </label>
                          <select
                            required
                            value={newRequest.pickup_country}
                            onChange={(e) => setNewRequest({...newRequest, pickup_country: e.target.value})}
                            className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
                          >
                            <option value="">{t('selectCountry')}</option>
                            <option value="Netherlands">{t('netherlands')}</option>
                            <option value="Belgium">{t('belgium')}</option>
                            <option value="Germany">{t('germany')}</option>
                            <option value="France">{t('france')}</option>
                            <option value="Spain">{t('spain')}</option>
                            <option value="Italy">{t('italy')}</option>
                            <option value="Morocco">{t('morocco')}</option>
                            <option value="Other">{t('other')}</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('deliveryCity')}
                          </label>
                          <input
                            type="text"
                            required
                            value={newRequest.delivery_city}
                            onChange={(e) => setNewRequest({...newRequest, delivery_city: e.target.value})}
                            className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('baggageSize')}
                          </label>
                          <input
                            type="text"
                            required
                            value={newRequest.baggage_size}
                            onChange={(e) => setNewRequest({...newRequest, baggage_size: e.target.value})}
                            className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('baggageWeight')}
                          </label>
                          <input
                            type="text"
                            required
                            value={newRequest.baggage_weight}
                            onChange={(e) => setNewRequest({...newRequest, baggage_weight: e.target.value})}
                            className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('preferredDate')}
                          </label>
                          <input
                            type="date"
                            required
                            value={newRequest.preferred_date}
                            onChange={(e) => setNewRequest({...newRequest, preferred_date: e.target.value})}
                            className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('specialRequirements')}
                          </label>
                          <textarea
                            value={newRequest.special_requirements}
                            onChange={(e) => setNewRequest({...newRequest, special_requirements: e.target.value})}
                            className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
                            rows="3"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('baggageImage')}
                          </label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
                          />
                          {newRequest.baggage_image && (
                            <div className="mt-2">
                              <img 
                                src={newRequest.baggage_image} 
                                alt="Baggage preview" 
                                className="max-w-xs h-32 object-cover rounded border"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-6 flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={() => setShowCreateForm(false)}
                          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        >
                          Annuleren
                        </button>
                        <button
                          type="submit"
                          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                        >
                          {t('submit')}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                <div className="space-y-4">
                  {requests.slice(0, currentPage.requests * itemsPerPage).map(request => (
                    <div key={request.id} className="bg-white p-6 rounded-lg shadow">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">
                            🌍 {translateCountry(request.pickup_country, t)} - {request.pickup_city} → {request.delivery_city}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {t('baggageSize')}: {request.baggage_size} | {t('baggageWeight')}: {request.baggage_weight}
                          </p>
                          <p className="text-sm text-gray-500">
                            {t('preferredDate')}: {request.preferred_date}
                          </p>
                          {request.baggage_image && (
                            <div className="mt-2">
                              <img 
                                src={request.baggage_image} 
                                alt="Your baggage" 
                                className="max-w-xs h-32 object-cover rounded border"
                              />
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end space-y-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            request.status === 'active' ? 'bg-green-100 text-green-800' : 
                            request.status === 'delivered' ? 'bg-blue-100 text-blue-800' :
                            request.status === 'completed' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {t(request.status)}
                          </span>
                          
                          {/* Delete button - show only when allowed */}
                          {canDeleteRequest(request) && (
                            <button
                              onClick={() => deleteRequest(request.id)}
                              className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
                            >
                              🗑️ {t('delete')}
                            </button>
                          )}
                          
                          {/* Delivery confirmation button - show when company delivered but customer hasn't confirmed */}
                          {needsDeliveryConfirmation(request) && (
                            <button
                              onClick={() => {
                                const acceptedQuote = quotes[request.id]?.find(quote => quote.status === 'accepted');
                                if (acceptedQuote) {
                                  confirmDelivery(request.id, acceptedQuote.id);
                                }
                              }}
                              className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                            >
                              ✅ Bevestig Ontvangst
                            </button>
                          )}
                        </div>
                      </div>
                      
                      <button
                        onClick={() => fetchQuotes(request.id)}
                        className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                      >
                        {t('viewQuotes')}
                      </button>
                      
                      {quotes[request.id] && (
                        <div className="mt-4 space-y-3">
                          <h4 className="font-medium text-gray-900">Offertes:</h4>
                          {quotes[request.id].map(quote => (
                            <div key={quote.id} className="p-4 border rounded-lg">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium">{quote.company_name}</p>
                                  <p className="text-lg font-semibold text-green-600">
                                    €{quote.price} ({quote.currency})
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    {quote.estimated_delivery_days} dagen
                                  </p>
                                  {quote.message && (
                                    <p className="text-sm text-gray-700 mt-2">{quote.message}</p>
                                  )}
                                  {quote.status === 'accepted' && (
                                    <div className="mt-3 p-4 bg-green-50 rounded border border-green-200">
                                      <h5 className="font-medium text-green-800 mb-3">✅ Offerte Geaccepteerd!</h5>
                                      <div className="space-y-2 mb-3">
                                        <p className="text-sm text-green-700">📞 {quote.contact_phone}</p>
                                        {quote.contact_address && (
                                          <p className="text-sm text-green-700">📍 {quote.contact_address}</p>
                                        )}
                                      </div>
                                      {/* Payment button - hidden until commission model is activated
                                      {quote.payment_status !== 'paid' && (
                                        <button
                                          data-testid={`pay-quote-${quote.id}`}
                                          onClick={() => payForQuote(quote.id)}
                                          className="w-full px-4 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors mb-2 flex items-center justify-center space-x-2"
                                        >
                                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/></svg>
                                          <span>Betaal €{quote.price}</span>
                                        </button>
                                      )}
                                      {quote.payment_status === 'paid' && (
                                        <div className="w-full px-4 py-2 bg-green-100 text-green-800 font-medium rounded-lg text-center mb-2">
                                          ✅ Betaald
                                        </div>
                                      )}
                                      */}
                                      <button
                                        onClick={() => openChat({
                                          quote_id: quote.id,
                                          other_party_name: quote.company_name
                                        })}
                                        className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition-colors"
                                      >
                                        💬 {t('chatWith')} {quote.company_name}
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <span className={`px-2 py-1 text-xs rounded-full ${
                                    quote.status === 'accepted' ? 'bg-green-100 text-green-800' :
                                    quote.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                    'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {t(quote.status)}
                                  </span>
                                </div>
                              </div>
                              
                              {quote.status === 'pending' && (
                                <div className="mt-3 flex space-x-2">
                                  <button
                                    onClick={() => respondToQuote(quote.id, 'accept')}
                                    className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                                  >
                                    {t('accept')}
                                  </button>
                                  <button
                                    onClick={() => respondToQuote(quote.id, 'reject')}
                                    className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                                  >
                                    {t('reject')}
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Load More for Requests */}
                <LoadMoreButton
                  section="requests"
                  hasMore={requests.length >= currentPage.requests * itemsPerPage}
                  loading={loading.requests}
                  onLoadMore={() => {
                    const nextPage = currentPage.requests + 1;
                    fetchRequests(nextPage);
                    setCurrentPage(prev => ({ ...prev, requests: nextPage }));
                  }}
                />
              </div>
            )}

            {activeTab === 'companies' && (
              <div>
                {/* Header with Search and Filter Button */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
                  <h2 className="text-lg font-medium text-gray-900 mb-4 sm:mb-0">{t('availableCompanies')}</h2>
                  <div className="text-sm text-gray-500">
                    {companies.filter(company => {
                      const matchesSearch = searchQuery === '' || 
                        company.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        (company.current_location && company.current_location.toLowerCase().includes(searchQuery.toLowerCase()));
                      const matchesLanguage = selectedLanguage === '' || 
                        (company.communication_languages && company.communication_languages.includes(selectedLanguage));
                      const matchesCountry = selectedCountry === '' || company.country_origin === selectedCountry;
                      const matchesDepartureDate = selectedDepartureDate === '' || 
                        (company.departure_dates_morocco && company.departure_dates_morocco.some(date => {
                          const selectedDate = new Date(selectedDepartureDate);
                          const companyDate = new Date(date);
                          return companyDate >= selectedDate;
                        }));
                      return matchesSearch && matchesLanguage && matchesCountry && matchesDepartureDate;
                    }).length} {t('companiesFound')}
                  </div>
                </div>

                {/* Search Bar and Filter Toggle */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  {/* Search Input */}
                  <div className="flex-1">
                    <div className="relative">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t('searchPlaceholder')}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Filter Toggle Button */}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
                      showFilters 
                        ? 'bg-indigo-600 text-white hover:bg-indigo-700' 
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    <span>{showFilters ? t('hideFilters') : t('showFilters')}</span>
                  </button>
                </div>

                {/* Collapsible Filter Panel */}
                {showFilters && (
                  <div className="bg-white p-6 rounded-lg shadow-lg mb-6 border border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">{t('filterBy')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* Language Filter */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('languages')}
                        </label>
                        <select
                          value={selectedLanguage}
                          onChange={(e) => setSelectedLanguage(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="">{t('allLanguages')}</option>
                          <option value="nl">{t('dutch')}</option>
                          <option value="fr">{t('french')}</option>
                          <option value="ar">{t('arabic')}</option>
                          <option value="en">{t('english')}</option>
                          <option value="de">{t('german')}</option>
                          <option value="es">{t('spanish')}</option>
                        </select>
                      </div>

                      {/* Country Filter */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('countryFilter')}
                        </label>
                        <select
                          value={selectedCountry}
                          onChange={(e) => setSelectedCountry(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="">{t('allCountries')}</option>
                          {getAvailableCountries().map(country => (
                            <option key={country.value} value={country.value}>{country.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Departure Date Filter */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('departureDateFilter')}
                        </label>
                        <input
                          type="date"
                          value={selectedDepartureDate}
                          onChange={(e) => setSelectedDepartureDate(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                          min={new Date().toISOString().split('T')[0]} // Minimum date is today
                        />
                      </div>

                      {/* Clear Filters Button */}
                      <div className="flex items-end">
                        <button
                          onClick={clearFilters}
                          className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                        >
                          {t('clearFilters')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {getPaginatedCompanies().length === 0 ? (
                  <div className="text-center text-gray-500 mt-8">
                    <div className="text-6xl mb-4">🏢</div>
                    {companies.length === 0 ? (
                      <p>{t('noCompanies')}</p>
                    ) : (
                      <div>
                        <p className="mb-2">Geen bedrijven gevonden met de huidige filters</p>
                        <button
                          onClick={clearFilters}
                          className="text-indigo-600 hover:text-indigo-800 underline"
                        >
                          {t('clearFilters')}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {companies.slice(0, currentPage.companies * itemsPerPage).map(company => (
                      <div key={company.id} className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow">
                        <div className="flex items-start space-x-4">
                          {company.profile_picture && (
                            <img 
                              src={company.profile_picture} 
                              alt={company.company_name}
                              className="w-16 h-16 rounded-full object-cover"
                            />
                          )}
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900">{company.company_name}</h3>
                            <p className="text-sm text-gray-600">{translateCountry(company.country_origin, t)}</p>
                            
                            {/* Rating */}
                            <div className="flex items-center mt-2">
                              <div className="flex text-yellow-400">
                                {[1, 2, 3, 4, 5].map(star => (
                                  <span key={star} className={star <= Math.floor(company.average_rating) ? 'text-yellow-400' : 'text-gray-300'}>
                                    ⭐
                                  </span>
                                ))}
                              </div>
                              <span className="ml-2 text-sm text-gray-600">
                                {company.average_rating.toFixed(1)} ({company.total_reviews} {t('reviews')})
                              </span>
                            </div>

                            {/* Current Location and Status */}
                            {company.current_location && (
                              <p className="text-sm text-gray-600 mt-2">
                                📍 {company.current_location}
                              </p>
                            )}

                            {/* Departure Dates */}
                            {(company.departure_dates_morocco?.length > 0 || company.departure_dates_europe?.length > 0) && (
                              <div className="mt-3 text-sm">
                                {company.departure_dates_morocco?.length > 0 && (
                                  <p className="text-green-600">
                                    🇲🇦 {t('toMorocco')}: {company.departure_dates_morocco.slice(0, 2).join(', ')}
                                    {company.departure_dates_morocco.length > 2 && '...'}
                                  </p>
                                )}
                                {company.departure_dates_europe?.length > 0 && (
                                  <p className="text-blue-600">
                                    🇪🇺 {t('toEurope')}: {company.departure_dates_europe.slice(0, 2).join(', ')}
                                    {company.departure_dates_europe.length > 2 && '...'}
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Phone Number */}
                            {company.phone_number && (
                              <p className="text-sm text-gray-600 mt-2">
                                📞 {company.phone_number}
                              </p>
                            )}

                            {/* Languages */}
                            {company.languages && company.languages.length > 0 && (
                              <div className="mt-2">
                                <p className="text-xs text-gray-500 mb-1">🗣️ {t('languages')}:</p>
                                <div className="flex flex-wrap gap-1">
                                  {company.languages.map(lang => {
                                    const langNames = {
                                      'nl': 'NL', 'fr': 'FR', 'ar': 'AR', 
                                      'en': 'EN', 'de': 'DE', 'es': 'ES'
                                    };
                                    return (
                                      <span key={lang} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                                        {langNames[lang] || lang.toUpperCase()}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex space-x-2 mt-4">
                              <button
                                onClick={() => fetchCompanyProfile(company.id)}
                                className="flex-1 bg-indigo-600 text-white px-3 py-2 rounded text-sm hover:bg-indigo-700"
                              >
                                {t('viewProfile')}
                              </button>
                              <button
                                onClick={() => startChatWithCompany(company)}
                                className="flex-1 bg-green-600 text-white px-3 py-2 rounded text-sm hover:bg-green-700"
                              >
                                {t('contactCompany')}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Load More for Companies */}
                <LoadMoreButton
                  section="companies"
                  hasMore={companies.length >= currentPage.companies * itemsPerPage}
                  loading={loading.companies}
                  onLoadMore={() => {
                    const nextPage = currentPage.companies + 1;
                    fetchCompanies(nextPage);
                    setCurrentPage(prev => ({ ...prev, companies: nextPage }));
                  }}
                />
              </div>
            )}

            {activeTab === 'chat' && (
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-6">{t('conversations')}</h2>
                {conversations.length === 0 ? (
                  <div className="text-center text-gray-500 mt-8">
                    <p>Geen gesprekken. Accepteer eerst een offerte om te kunnen chatten.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {conversations.slice(0, currentPage.conversations * itemsPerPage).map(conversation => (
                      <div key={conversation.quote_id} className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow border border-gray-100">
                        <div className="p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              {/* Route Header */}
                              <div className="flex items-center space-x-3 mb-3">
                                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                  </svg>
                                </div>
                                <div>
                                  <h3 className="text-lg font-semibold text-gray-900">
                                    {conversation.transport_request ? (
                                      <>🌍 {translateCountry(conversation.transport_request.pickup_country, t)} - {conversation.transport_request.pickup_city} → {conversation.transport_request.delivery_city}</>
                                    ) : (
                                      <>💬 {t('generalConversation')} met {conversation.other_party_name}</>
                                    )}
                                  </h3>
                                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                                    <span>🏢 {conversation.other_party_name}</span>
                                    <span>💰 €{conversation.quote_price}</span>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Last Message Preview */}
                              {conversation.last_message && (
                                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <span className="text-xs font-medium text-gray-700">{t('lastMessage')}:</span>
                                    <span className="text-xs text-gray-500">{conversation.last_message.sender_name}</span>
                                  </div>
                                  <p className="text-sm text-gray-700 line-clamp-2">
                                    {conversation.last_message.message_type === 'image' ? '📷 ' + t('imageMessage') : conversation.last_message.message}
                                  </p>
                                  <div className="text-xs text-gray-500 mt-1">
                                    {new Date(conversation.last_message.timestamp).toLocaleString('nl-NL', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* Action Area */}
                            <div className="flex flex-col items-end space-y-2 ml-4">
                              {conversation.unread_count > 0 && (
                                <div className="flex items-center space-x-1">
                                  <span className="bg-red-500 text-white text-xs font-bold rounded-full px-2 py-1 min-w-[20px] text-center">
                                    {conversation.unread_count}
                                  </span>
                                  <span className="text-xs text-red-600 font-medium">{t('unreadMessages')}</span>
                                </div>
                              )}
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => openChat(conversation)}
                                  className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105 flex items-center space-x-2 shadow-md"
                                >
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                                  </svg>
                                  <span>{t('chat')}</span>
                                </button>
                                <button
                                  onClick={() => deleteConversation(conversation)}
                                  className="bg-red-500 text-white p-2 rounded-lg hover:bg-red-600 transition-all duration-200 transform hover:scale-105 shadow-md"
                                  title={t('deleteConversation')}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Load More for Conversations */}
                <LoadMoreButton
                  section="conversations"
                  hasMore={conversations.length >= currentPage.conversations * itemsPerPage}
                  loading={false}
                  onLoadMore={() => {
                    const nextPage = currentPage.conversations + 1;
                    fetchConversations(nextPage);
                    setCurrentPage(prev => ({ ...prev, conversations: nextPage }));
                  }}
                />
              </div>
            )}

            {activeTab === 'tracking' && (
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-6">{t('tracking')}</h2>
                {requests.filter(request => 
                  quotes[request.id]?.some(quote => quote.status === 'accepted')
                ).length === 0 ? (
                  <div className="text-center text-gray-500 mt-8">
                    <div className="text-4xl mb-2">📦</div>
                    <p>Geen actieve zendingen om te volgen.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {requests
                      .filter(request => 
                        quotes[request.id]?.some(quote => quote.status === 'accepted')
                      )
                      .map(request => {
                        const acceptedQuote = quotes[request.id]?.find(quote => quote.status === 'accepted');
                        return (
                          <div key={request.id} className="bg-white p-6 rounded-lg shadow">
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h3 className="text-lg font-medium text-gray-900">
                                  {request.pickup_city} → {request.delivery_city}
                                </h3>
                                <p className="text-sm text-gray-600">
                                  Transport: {acceptedQuote?.company_name} | €{acceptedQuote?.price}
                                </p>
                                <p className="text-sm text-gray-500">
                                  Geschatte levering: {acceptedQuote?.estimated_delivery_days} dagen
                                </p>
                              </div>
                              {acceptedQuote && (
                                <button
                                  onClick={() => openChat({
                                    quote_id: acceptedQuote.id,
                                    other_party_name: acceptedQuote.company_name
                                  })}
                                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                                >
                                  💬 {t('chat')}
                                </button>
                              )}
                            </div>
                            
                            {/* Company Tracking Timeline */}
                            {acceptedQuote && (
                              <div className="border-t pt-4">
                                <CompanyTrackingTimeline companyId={acceptedQuote.company_id} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            )}

          {/* Delivery Notification Popup */}
          {showDeliveryNotification && pendingDeliveryNotifications.length > 0 && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
                <div className="text-center">
                  <div className="text-6xl mb-4">📦</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Levering Ontvangen!</h3>
                  <p className="text-gray-600 mb-6">
                    Het transportbedrijf heeft aangegeven dat uw bagage is afgeleverd. 
                    Kunt u de ontvangst bevestigen?
                  </p>
                  
                  <div className="space-y-3 mb-6">
                    {pendingDeliveryNotifications.map(request => (
                      <div key={request.id} className="bg-gray-50 p-3 rounded border">
                        <p className="font-medium text-gray-900">
                          {request.pickup_city} → {request.delivery_city}
                        </p>
                        <p className="text-sm text-gray-600">
                          Bagage: {request.baggage_size} ({request.baggage_weight})
                        </p>
                        <button
                          onClick={() => {
                            const acceptedQuote = quotes[request.id]?.find(quote => quote.status === 'accepted');
                            if (acceptedQuote) {
                              confirmDelivery(request.id, acceptedQuote.id);
                              setShowDeliveryNotification(false);
                              setPendingDeliveryNotifications([]);
                            }
                          }}
                          className="mt-2 w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                        >
                          ✅ Bevestig Ontvangst
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => setShowDeliveryNotification(false)}
                    className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                  >
                    Later Bevestigen
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Company Profile Modal */}
          {showCompanyProfile && selectedCompany && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-screen overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">{t('companyProfile')}</h2>
                  <button
                    onClick={() => {
                      setShowCompanyProfile(false);
                      setSelectedCompany(null);
                      setShowReviewForm(false);
                    }}
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Company Header */}
                  <div className="flex items-start space-x-6">
                    {selectedCompany.profile_picture && (
                      <img 
                        src={selectedCompany.profile_picture} 
                        alt={selectedCompany.company_name}
                        className="w-24 h-24 rounded-full object-cover"
                      />
                    )}
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-900">{selectedCompany.company_name}</h3>
                      <p className="text-gray-600">{translateCountry(selectedCompany.country_origin, t)}</p>
                      
                      {/* Rating */}
                      <div className="flex items-center mt-2">
                        <div className="flex text-yellow-400 text-lg">
                          {[1, 2, 3, 4, 5].map(star => (
                            <span key={star} className={star <= Math.floor(selectedCompany.average_rating) ? 'text-yellow-400' : 'text-gray-300'}>
                              ⭐
                            </span>
                          ))}
                        </div>
                        <span className="ml-2 text-gray-600">
                          {selectedCompany.average_rating.toFixed(1)} ({selectedCompany.total_reviews} {t('reviews')})
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">{t('contactInfo')}</h4>
                    <div className="space-y-2">
                      {selectedCompany.phone_number && (
                        <p className="flex items-center text-gray-700">
                          <span className="mr-2">📞</span>
                          {selectedCompany.phone_number}
                        </p>
                      )}
                      {selectedCompany.current_location && (
                        <p className="flex items-center text-gray-700">
                          <span className="mr-2">📍</span>
                          {selectedCompany.current_location}
                        </p>
                      )}
                      {selectedCompany.languages && selectedCompany.languages.length > 0 && (
                        <div className="flex items-start">
                          <span className="mr-2 mt-1">🗣️</span>
                          <div>
                            <p className="text-gray-900 font-medium mb-1">{t('communicationLanguages')}:</p>
                            <div className="flex flex-wrap gap-1">
                              {selectedCompany.languages.map(lang => {
                                const langNames = {
                                  'nl': t('dutch'), 'fr': t('french'), 'ar': t('arabic'), 
                                  'en': t('english'), 'de': t('german'), 'es': t('spanish')
                                };
                                return (
                                  <span key={lang} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                                    {langNames[lang] || lang}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Company Description */}
                  {selectedCompany.company_description && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('companyDescription')}</h4>
                      <p className="text-gray-700 whitespace-pre-wrap">{selectedCompany.company_description}</p>
                    </div>
                  )}

                  {/* Departure Dates */}
                  {(selectedCompany.departure_dates_morocco?.length > 0 || selectedCompany.departure_dates_europe?.length > 0) && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">{t('departureDates')}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedCompany.departure_dates_morocco?.length > 0 && (
                          <div className="bg-green-50 p-3 rounded-lg">
                            <h5 className="font-medium text-green-800 mb-2">🇲🇦 {t('toMorocco')}</h5>
                            <ul className="text-sm text-green-700 space-y-1">
                              {selectedCompany.departure_dates_morocco.map((date, index) => (
                                <li key={index}>• {date}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {selectedCompany.departure_dates_europe?.length > 0 && (
                          <div className="bg-blue-50 p-3 rounded-lg">
                            <h5 className="font-medium text-blue-800 mb-2">🇪🇺 {t('toEurope')}</h5>
                            <ul className="text-sm text-blue-700 space-y-1">
                              {selectedCompany.departure_dates_europe.map((date, index) => (
                                <li key={index}>• {date}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Price List */}
                  {selectedCompany.price_list && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('priceList')}</h4>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-700 whitespace-pre-wrap">{selectedCompany.price_list}</p>
                      </div>
                    </div>
                  )}

                  {/* Reviews Section */}
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-semibold text-gray-900">{t('reviews')} ({companyReviews.length})</h4>
                      <button
                        onClick={() => setShowReviewForm(!showReviewForm)}
                        className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 text-sm"
                      >
                        {t('writeReview')}
                      </button>
                    </div>

                    {/* Review Form */}
                    {showReviewForm && (
                      <div className="bg-gray-50 p-4 rounded-lg mb-4">
                        <form onSubmit={submitReview}>
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('rating')} ({newReview.rating} {t('stars')})
                            </label>
                            <input
                              type="range"
                              min="0"
                              max="5"
                              step="1"
                              value={newReview.rating}
                              onChange={(e) => setNewReview({...newReview, rating: parseInt(e.target.value)})}
                              className="w-full"
                            />
                            <div className="flex justify-between text-xs text-gray-500 mt-1">
                              <span>0</span>
                              <span>1</span>
                              <span>2</span>
                              <span>3</span>
                              <span>4</span>
                              <span>5</span>
                            </div>
                          </div>
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('reviewText')} ({t('optional')})
                            </label>
                            <textarea
                              value={newReview.review_text}
                              onChange={(e) => setNewReview({...newReview, review_text: e.target.value})}
                              rows="4"
                              className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
                              placeholder={`${t('writeReview')}...`}
                            />
                          </div>
                          <div className="flex space-x-2">
                            <button
                              type="submit"
                              className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 text-sm"
                            >
                              {t('submitReview')}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowReviewForm(false);
                                setNewReview({ rating: 5, review_text: '' });
                              }}
                              className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 text-sm"
                            >
                              {t('cancel')}
                            </button>
                          </div>
                        </form>
                      </div>
                    )}

                    {/* Reviews List */}
                    {companyReviews.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">{t('noReviews')}</p>
                    ) : (
                      <div className="space-y-4">
                        {companyReviews.map(review => (
                          <div key={review.id} className="border-b pb-4 last:border-b-0">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="font-medium text-gray-900">{review.customer_name}</p>
                                <div className="flex items-center text-sm text-gray-600">
                                  <div className="flex text-yellow-400 mr-2">
                                    {[1, 2, 3, 4, 5].map(star => (
                                      <span key={star} className={star <= review.rating ? 'text-yellow-400' : 'text-gray-300'}>
                                        ⭐
                                      </span>
                                    ))}
                                  </div>
                                  <span>{new Date(review.created_at).toLocaleDateString()}</span>
                                </div>
                              </div>
                            </div>
                            {review.review_text && (
                              <p className="text-gray-700">{review.review_text}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-4 pt-4 border-t">
                    <button
                      onClick={() => startChatWithCompany(selectedCompany)}
                      className="flex-1 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                    >
                      {t('contactCompany')}
                    </button>
                    <button
                      onClick={() => {
                        setShowCompanyProfile(false);
                        setSelectedCompany(null);
                        setShowReviewForm(false);
                      }}
                      className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                    >
                      {t('close')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Chat Modal */}
          {showChat && (
            <Chat
              quoteId={showChat.quoteId}
              companyId={showChat.companyId}
              otherPartyName={showChat.otherPartyName}
              otherPartyType={showChat.otherPartyType}
              companyName={showChat.companyName}
              customerName={showChat.customerName}
              customerId={showChat.customerId}
              customerEmail={showChat.customerEmail}
              onClose={() => setShowChat(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Transport Company Dashboard
const TransportCompanyDashboard = () => {
  const { user, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const { protectedClick, isProcessing } = useClickProtection();
  const [activeTab, setActiveTab] = useState('available');
  const [availableRequests, setAvailableRequests] = useState([]);
  const [myQuotes, setMyQuotes] = useState([]);
  const [acceptedOffers, setAcceptedOffers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeShipments, setActiveShipments] = useState([]);
  
  // Direct Bookings (Eigen Klanten) states
  const [directBookings, setDirectBookings] = useState([]);
  const [showDirectBookingForm, setShowDirectBookingForm] = useState(false);
  const [showBookingDetails, setShowBookingDetails] = useState(null);
  const [editingPrice, setEditingPrice] = useState(null); // booking id being edited
  const [newPrice, setNewPrice] = useState('');

  // Stripe Connect states
  const [stripeConnectStatus, setStripeConnectStatus] = useState({ connected: false, status: 'loading' });
  const [stripeConnectLoading, setStripeConnectLoading] = useState(false);
  const [newDirectBooking, setNewDirectBooking] = useState({
    customer_name: '',
    customer_phone: '',
    customer_country_code: '+31',
    customer_email: '',
    description: '',
    pickup_location: '',
    delivery_location: '',
    pickup_date: '',
    delivery_date: '',
    agreed_price: ''
  });
  
  const [showQuoteForm, setShowQuoteForm] = useState(null);
  const [showChat, setShowChat] = useState(null);
  const [showTrackingForm, setShowTrackingForm] = useState(null);
  const [showRequestDetails, setShowRequestDetails] = useState(null);
  
  // Search and filter states for available requests
  const [requestSearchQuery, setRequestSearchQuery] = useState('');
  const [selectedDepartureCountry, setSelectedDepartureCountry] = useState('');
  const [selectedDepartureCity, setSelectedDepartureCity] = useState('');
  const [showRequestFilters, setShowRequestFilters] = useState(false);
  
  // Debounced search for better performance
  const debouncedRequestSearch = useDebounce(requestSearchQuery, 500);
  const debouncedDepartureCountry = useDebounce(selectedDepartureCountry, 300);
  const debouncedDepartureCity = useDebounce(selectedDepartureCity, 300);
  const [newCompanyUpdate, setNewCompanyUpdate] = useState({
    status: 'in_transit',
    location: '',
    message: ''
  });
  const [newPickupUpdate, setNewPickupUpdate] = useState({
    pickup_location: '',
    pickup_notes: ''
  });
  const [newQuote, setNewQuote] = useState({
    price: '',
    currency: 'EUR',
    estimated_delivery_days: '',
    message: '',
    contact_phone: '',
    contact_address: ''
  });
  // Profile management states
  const [profileData, setProfileData] = useState({
    profile_picture: '',
    company_description: '',
    phone_number: '',
    price_list: '',
    current_location: '',
    departure_dates_morocco: [],
    departure_dates_europe: [],
    is_online: true,
    languages: []
  });
  const [companyReviews, setCompanyReviews] = useState([]);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [showStatusEdit, setShowStatusEdit] = useState(false);
  const [newDepartureDateMorocco, setNewDepartureDateMorocco] = useState('');
  const [newDepartureDateEurope, setNewDepartureDateEurope] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState({
    conversations: 1
  });
  const [itemsPerPage] = useState(10); // Show 10 items per page

  // Pagination helper functions - SIMPLIFIED FOR PERFORMANCE
  const LoadMoreButton = ({ section, hasMore, loading, onLoadMore }) => {
    if (!hasMore) return null;
    
    return (
      <div className="flex justify-center mt-6">
        <button
          onClick={onLoadMore}
          disabled={loading}
          className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? `⏳ ${t('loading')}` : `➕ ${t('loadMore')}`}
        </button>
      </div>
    );
  };

  // Keep minimal functions for existing usage
  const paginate = (items, page, perPage) => {
    const start = (page - 1) * perPage;
    const end = start + perPage;
    return items.slice(start, end);
  };

  const getTotalPages = (totalItems, perPage) => {
    return Math.ceil(totalItems / perPage);
  };

  const changePage = (section, page) => {
    setCurrentPage(prev => ({
      ...prev,
      [section]: page
    }));
  };

  const getPaginatedConversations = () => {
    return paginate(conversations, currentPage.conversations, itemsPerPage);
  };

  // Simplified Pagination Component
  const PaginationControls = ({ currentPage, totalItems, itemsPerPage, onPageChange, section }) => {
    const totalPages = getTotalPages(totalItems, itemsPerPage);
    
    if (totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-6">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={() => onPageChange(section, Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('previous')}
          </button>
          <button
            onClick={() => onPageChange(section, Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('next')}
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              {t('showing')} <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> - {' '}
              <span className="font-medium">
                {Math.min(currentPage * itemsPerPage, totalItems)}
              </span>{' '}
              {t('of')} <span className="font-medium">{totalItems}</span> {t('results')}
            </p>
          </div>
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={() => onPageChange(section, Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">{t('previous')}</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                </svg>
              </button>
              
              <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300">
                {currentPage} / {totalPages}
              </span>
              
              <button
                onClick={() => onPageChange(section, Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">{t('next')}</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (user && user.user_type === 'transport_company') {
      fetchAvailableRequests();
      fetchMyQuotes();
      fetchAcceptedOffers();
      fetchConversations();
      fetchActiveShipments();
      fetchDirectBookings(); // Fetch eigen klanten boekingen
      fetchStripeConnectStatus(); // Check Stripe Connect status
      
      // Check if returning from Stripe onboarding
      const params = new URLSearchParams(window.location.search);
      if (params.get('stripe_connected') === 'true') {
        fetchStripeConnectStatus();
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [user]);

  const fetchAcceptedOffers = async () => {
    try {
      const response = await axios.get(`${API}/quotes/company/accepted`);
      setAcceptedOffers(response.data);
    } catch (error) {
      console.error('Error fetching accepted offers:', error);
    }
  };

  const fetchActiveShipments = async () => {
    try {
      const response = await axios.get(`${API}/tracking/company/active`);
      setActiveShipments(response.data);
    } catch (error) {
      console.error('Error fetching active shipments:', error);
    }
  };

  const fetchConversations = async (page = 1) => {
    try {
      const response = await axios.get(`${API}/chat/conversations?page=${page}&limit=${itemsPerPage}`);
      if (page === 1) {
        setConversations(response.data);
      } else {
        // For subsequent pages, append to existing data
        setConversations(prev => [...prev, ...response.data]);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const fetchAvailableRequests = async () => {
    try {
      const response = await axios.get(`${API}/transport-requests`);
      setAvailableRequests(response.data);
    } catch (error) {
      console.error('Error fetching available requests:', error);
    }
  };

  const fetchMyQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes/company`);
      setMyQuotes(response.data);
    } catch (error) {
      console.error('Error fetching my quotes:', error);
    }
  };

  // ========== DIRECT BOOKINGS (EIGEN KLANTEN) FUNCTIONS ==========
  
  const fetchDirectBookings = async () => {
    try {
      const response = await axios.get(`${API}/direct-bookings`);
      setDirectBookings(response.data);
    } catch (error) {
      console.error('Error fetching direct bookings:', error);
    }
  };

  // Stripe Connect functions
  const fetchStripeConnectStatus = async () => {
    try {
      const response = await axios.get(`${API}/stripe/connect/status`);
      setStripeConnectStatus(response.data);
    } catch (error) {
      console.error('Error fetching Stripe Connect status:', error);
      setStripeConnectStatus({ connected: false, status: 'error' });
    }
  };

  const startStripeOnboarding = async () => {
    setStripeConnectLoading(true);
    try {
      const response = await axios.post(`${API}/stripe/connect/create-account`, {
        origin_url: window.location.origin
      });
      if (response.data.onboarding_url) {
        window.location.href = response.data.onboarding_url;
      } else if (response.data.status === 'already_connected') {
        setStripeConnectStatus({ connected: true, status: 'active' });
      }
    } catch (error) {
      console.error('Error starting Stripe onboarding:', error);
      alert('Er ging iets mis bij het koppelen van Stripe. Probeer het opnieuw.');
    } finally {
      setStripeConnectLoading(false);
    }
  };

  const openStripeDashboard = async () => {
    try {
      const response = await axios.post(`${API}/stripe/connect/dashboard-link`, {});
      if (response.data.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (error) {
      console.error('Error opening Stripe dashboard:', error);
    }
  };

  const createDirectBooking = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/direct-bookings`, {
        ...newDirectBooking,
        agreed_price: newDirectBooking.agreed_price ? parseFloat(newDirectBooking.agreed_price) : null
      });
      
      // Reset form
      setNewDirectBooking({
        customer_name: '',
        customer_phone: '',
        customer_country_code: '+31',
        customer_email: '',
        description: '',
        pickup_location: '',
        delivery_location: '',
        pickup_date: '',
        delivery_date: '',
        agreed_price: ''
      });
      
      setShowDirectBookingForm(false);
      fetchDirectBookings();
      alert(`✅ ${t('bookingSuccessfullyCreated')}`);
    } catch (error) {
      console.error('Error creating direct booking:', error);
      alert(`❌ ${t('errorCreatingBooking')}`);
    }
  };

  const updateBookingStatus = async (bookingId, newStatus, note = '') => {
    try {
      await axios.post(`${API}/direct-bookings/${bookingId}/status`, {
        status: newStatus,
        note: note
      });
      fetchDirectBookings();
      alert(`✅ ${t('successStatusChanged')}`);
    } catch (error) {
      console.error('Error updating booking status:', error);
      alert(`❌ ${t('errorUpdatingBookingStatus')}`);
    }
  };

  const updateBookingPrice = async (bookingId) => {
    if (!newPrice || isNaN(parseFloat(newPrice))) {
      alert(t('enterValidPrice'));
      return;
    }
    
    try {
      await axios.put(`${API}/direct-bookings/${bookingId}`, {
        agreed_price: parseFloat(newPrice)
      });
      fetchDirectBookings();
      setEditingPrice(null);
      setNewPrice('');
      // Update showBookingDetails if open
      if (showBookingDetails && showBookingDetails.id === bookingId) {
        setShowBookingDetails({...showBookingDetails, agreed_price: parseFloat(newPrice)});
      }
      alert(`✅ ${t('priceSuccessfullyAdded')}`);
    } catch (error) {
      console.error('Error updating booking price:', error);
      alert(`❌ ${t('errorUpdatingPrice')}`);
    }
  };

  const getPersonalBookingLink = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/t/${user?.id}`;
  };

  const shareViaWhatsApp = (type = 'booking') => {
    const link = getPersonalBookingLink();
    const message = type === 'booking' 
      ? `Boek je zending bij ${user?.company_name || user?.full_name}:\n${link}`
      : `Track je zending:\n${link}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const deleteDirectBooking = async (bookingId) => {
    if (!window.confirm('Weet je zeker dat je deze boeking wilt verwijderen? Dit kan niet ongedaan worden gemaakt.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/direct-bookings/${bookingId}`);
      fetchDirectBookings();
      alert('Boeking succesvol verwijderd!');
    } catch (error) {
      console.error('Error deleting booking:', error);
      alert('Fout bij verwijderen boeking. Probeer opnieuw.');
    }
  };

  const openWhatsAppChat = (phoneNumber, customerName) => {
    const message = `Hallo ${customerName}, `;
    const whatsappUrl = `https://wa.me/${phoneNumber.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const submitQuote = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/quotes`, {
        ...newQuote,
        transport_request_id: showQuoteForm,
        estimated_delivery_days: parseInt(newQuote.estimated_delivery_days)
      });
      setNewQuote({
        price: '',
        currency: 'EUR',
        estimated_delivery_days: '',
        message: '',
        contact_phone: '',
        contact_address: ''
      });
      setShowQuoteForm(null);
      fetchMyQuotes();
    } catch (error) {
      console.error('Error submitting quote:', error);
    }
  };

  const openChat = (conversation) => {
    console.log('Opening chat for conversation:', conversation);
    console.log('User type:', user?.user_type);
    
    const calculatedCompanyId = user?.user_type === 'customer' ? 
      conversation.company_id : 
      user?.id;
    
    console.log('Calculated companyId:', calculatedCompanyId);
    
    setShowChat({
      quoteId: conversation.quote_id,
      companyId: calculatedCompanyId,
      otherPartyName: conversation.other_party_name,
      otherPartyType: user?.user_type === 'customer' ? 'transport_company' : 'customer',
      companyName: user?.user_type === 'customer' ? conversation.other_party_name : user?.company_name || user?.full_name,
      customerName: user?.user_type === 'customer' ? user?.full_name : conversation.other_party_name,
      customerId: user?.user_type === 'customer' ? user?.id : conversation.customer_id,
      customerEmail: user?.user_type === 'customer' ? user?.email : conversation.customer_email
    });
  };

  const deleteConversation = async (conversation) => {
    // Confirm deletion
    const confirmDelete = window.confirm(t('confirmDeleteConversation'));
    if (!confirmDelete) {
      return;
    }

    try {
      const params = new URLSearchParams();
      
      if (conversation.quote_id) {
        // Quote-based conversation
        params.append('quote_id', conversation.quote_id);
      } else if (conversation.conversation_type === 'general') {
        // General conversation
        if (user?.user_type === 'customer') {
          params.append('company_id', conversation.company_id);
        } else {
          // For companies, we need to pass the customer_id as company_id parameter
          params.append('company_id', conversation.customer_id);
        }
      }

      await axios.delete(`${API}/chat/conversation?${params.toString()}`);
      
      alert(t('conversationDeleted'));
      
      // Refresh conversations list
      fetchConversations();
      
    } catch (error) {
      console.error('Error deleting conversation:', error);
      alert('Fout bij het verwijderen van gesprek: ' + (error.response?.data?.detail || error.message));
    }
  };

  const quickPickup = async (quoteId) => {
    try {
      console.log('Quick pickup for quote:', quoteId);
      
      const response = await axios.post(`${API}/tracking/pickup`, {
        quote_id: quoteId,
        pickup_location: '',
        pickup_notes: 'Pakket succesvol opgehaald'
      });
      
      console.log('Quick pickup response:', response.data);
      
      fetchActiveShipments();
      fetchAcceptedOffers(); // Refresh the accepted offers list
      alert('✅ Ophaling bevestigd!');
    } catch (error) {
      console.error('Error updating pickup status:', error);
      alert(`❌ ${t('errorConfirmingPickup')}: ` + (error.response?.data?.detail || error.message));
    }
  };

  const confirmDeliveryAsCompany = async (requestId, quoteId) => {
    if (!window.confirm(t('confirmDeliveryQuestion'))) {
      return;
    }
    
    try {
      await axios.post(`${API}/delivery/company-confirm`, {
        transport_request_id: requestId,
        quote_id: quoteId
      });
      alert(`✅ ${t('deliveryConfirmedByCompany')}`);
      fetchAcceptedOffers(); // Refresh the offers list
    } catch (error) {
      console.error('Error confirming delivery:', error);
      alert(`❌ ${t('errorConfirmingDelivery')}: ` + (error.response?.data?.detail || error.message));
    }
  };

  const removeDeliveredRequest = async (requestId) => {
    if (window.confirm(t('confirmRemoveRequest'))) {
      try {
        await axios.delete(`${API}/transport-requests/${requestId}`);
        alert('✅ ' + t('requestRemoved'));
        fetchAcceptedOffers(); // Refresh the offers list
      } catch (error) {
        console.error('Error removing request:', error);
        alert('❌ ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const submitCompanyUpdate = async (e) => {
    e.preventDefault();
    try {
      console.log('Submitting company update:', newCompanyUpdate);
      
      const response = await axios.post(`${API}/tracking/company/update`, newCompanyUpdate);
      
      console.log('Company update response:', response.data);
      
      setNewCompanyUpdate({
        status: '',
        location: '',
        message: ''
      });
      
      setShowTrackingForm(false);
      fetchActiveShipments(); // Refresh the active shipments list
      alert(`✅ ${t('successCompanyStatusUpdated')}`);
    } catch (error) {
      console.error('Error updating company status:', error);
      alert(`❌ ${t('errorUpdatingCompanyStatus')}: ` + (error.response?.data?.detail || error.message));
    }
  };

  const clearTracking = async () => {
    if (!window.confirm(t('clearTrackingConfirm'))) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/tracking/company/clear`);
      
      console.log('Clear tracking response:', response.data);
      
      // Refresh the active shipments list
      fetchActiveShipments();
      alert(`✅ ${t('trackingCleared')} (${response.data.deleted_count} ${t('updatesCleared')})`);
    } catch (error) {
      console.error('Error clearing tracking:', error);
      alert(`❌ ${t('errorClearingTracking')}: ` + (error.response?.data?.detail || error.message));
    }
  };

  const viewRequestDetails = (request) => {
    setShowRequestDetails(request);
  };

  // Filter available requests based on search and filter criteria
  const filterAvailableRequests = useCallback(() => {
    return availableRequests.filter(request => {
      // Search by general text (pickup/delivery cities, customer name)
      const matchesSearch = debouncedRequestSearch === '' || 
        request.pickup_city.toLowerCase().includes(debouncedRequestSearch.toLowerCase()) ||
        request.delivery_city.toLowerCase().includes(debouncedRequestSearch.toLowerCase()) ||
        (request.customer_name && request.customer_name.toLowerCase().includes(debouncedRequestSearch.toLowerCase()));
      
      // Filter by departure country (pickup country) - exact matching for dropdown
      const matchesDepartureCountry = debouncedDepartureCountry === '' || 
        request.pickup_country === debouncedDepartureCountry;
      
      // Filter by departure city (pickup city) - now uses partial matching
      const matchesDepartureCity = debouncedDepartureCity === '' || 
        request.pickup_city.toLowerCase().includes(debouncedDepartureCity.toLowerCase());
      
      return matchesSearch && matchesDepartureCountry && matchesDepartureCity;
    });
  }, [availableRequests, debouncedRequestSearch, debouncedDepartureCountry, debouncedDepartureCity]);

  // Clear all filters
  const clearRequestFilters = () => {
    setRequestSearchQuery('');
    setSelectedDepartureCountry('');
    setSelectedDepartureCity('');
  };

  // Profile management functions
  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/companies/${user.id}/profile`);
      setProfileData({
        profile_picture: response.data.profile_picture || '',
        company_description: response.data.company_description || '',
        phone_number: response.data.phone_number || '',
        price_list: response.data.price_list || '',
        current_location: response.data.current_location || '',
        departure_dates_morocco: response.data.departure_dates_morocco || [],
        departure_dates_europe: response.data.departure_dates_europe || [],
        is_online: response.data.is_online !== undefined ? response.data.is_online : true,
        languages: response.data.languages || []
      });
      setCompanyReviews(response.data.reviews || []);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const updateProfile = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/companies/profile`, {
        profile_picture: profileData.profile_picture,
        company_description: profileData.company_description,
        phone_number: profileData.phone_number,
        price_list: profileData.price_list,
        languages: profileData.languages
      });
      setShowProfileEdit(false);
      alert(`✅ ${t('successProfileUpdated')}`);
    } catch (error) {
      console.error('Error updating profile:', error);
      alert(`❌ ${t('errorUpdatingProfile')}: ` + (error.response?.data?.detail || error.message));
    }
  };

  const updateStatus = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/companies/status`, {
        current_location: profileData.current_location,
        departure_dates_morocco: profileData.departure_dates_morocco,
        departure_dates_europe: profileData.departure_dates_europe
      });
      setShowStatusEdit(false);
      alert(`✅ ${t('successStatusUpdated')}`);
    } catch (error) {
      console.error('Error updating status:', error);
      alert(`❌ ${t('errorUpdatingStatus')}: ` + (error.response?.data?.detail || error.message));
    }
  };

  const addDepartureDate = (destination) => {
    const dateValue = destination === 'morocco' ? newDepartureDateMorocco : newDepartureDateEurope;
    if (!dateValue) return;

    if (destination === 'morocco') {
      setProfileData(prev => ({
        ...prev,
        departure_dates_morocco: [...prev.departure_dates_morocco, dateValue]
      }));
      setNewDepartureDateMorocco('');
    } else {
      setProfileData(prev => ({
        ...prev,
        departure_dates_europe: [...prev.departure_dates_europe, dateValue]
      }));
      setNewDepartureDateEurope('');
    }
  };

  const removeDepartureDate = (destination, index) => {
    if (destination === 'morocco') {
      setProfileData(prev => ({
        ...prev,
        departure_dates_morocco: prev.departure_dates_morocco.filter((_, i) => i !== index)
      }));
    } else {
      setProfileData(prev => ({
        ...prev,
        departure_dates_europe: prev.departure_dates_europe.filter((_, i) => i !== index)
      }));
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileData(prev => ({
          ...prev,
          profile_picture: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleOnlineStatus = async () => {
    try {
      const response = await axios.put(`${API}/companies/toggle-online`);
      setProfileData(prev => ({
        ...prev,
        is_online: response.data.is_online
      }));
      const statusText = response.data.is_online ? 'online' : 'offline';
      alert(`✅ ${t('successStatusUpdatedTo')} ${statusText}!`);
    } catch (error) {
      console.error('Error toggling online status:', error);
      alert(`❌ ${t('errorUpdatingOnlineStatus')}: ` + (error.response?.data?.detail || error.message));
    }
  };

  // Available languages for selection
  const availableLanguages = [
    { code: 'nl', name: t('dutch') },
    { code: 'fr', name: t('french') },
    { code: 'ar', name: t('arabic') },
    { code: 'en', name: t('english') },
    { code: 'de', name: t('german') },
    { code: 'es', name: t('spanish') }
  ];

  const toggleLanguage = (languageCode) => {
    setProfileData(prev => ({
      ...prev,
      languages: prev.languages.includes(languageCode)
        ? prev.languages.filter(lang => lang !== languageCode)
        : [...prev.languages, languageCode]
    }));
  };

  // Fetch profile when profile tab is accessed
  useEffect(() => {
    if (activeTab === 'profile') {
      fetchProfile();
    }
  }, [activeTab]);

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Modern Sidebar Navigation */}
      <div className={`
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} 
        lg:translate-x-0 
        fixed lg:static inset-y-0 left-0 z-50 
        w-64 lg:w-64 
        bg-white shadow-lg flex flex-col 
        transition-transform duration-300 ease-in-out
      `}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">🚛</span>
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">{t('transportPanel') || 'Transport Panel'}</h1>
                <p className="text-xs text-gray-500">{t('manageYourBusiness') || 'Beheer uw business'}</p>
              </div>
            </div>
            {/* Close button for mobile */}
            <button
              onClick={() => protectedClick(async () => {
                setIsMobileMenuOpen(false);
              }, 200)}
              disabled={isProcessing}
              className={`lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-600 ${
                isProcessing ? 'opacity-50' : ''
              }`}
            >
              ✕
            </button>
          </div>
          
          {/* Language Selector */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">{t('language') || 'Taal'}:</span>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
            >
              <option value="nl">{t('dutch')}</option>
              <option value="en">{t('english')}</option>
              <option value="fr">{t('french')}</option>
              <option value="ar">{t('arabic')}</option>
            </select>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 px-4 py-6 space-y-2">
          <button
            onClick={() => setActiveTab('available')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
              activeTab === 'available'
                ? 'bg-indigo-50 text-indigo-600 border-r-2 border-indigo-600'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`}
          >
            <span className="text-xl">📋</span>
            <div>
              <div className="font-medium">{t('availableRequests')}</div>
              <div className="text-sm opacity-75">{t('newTransportOpportunities')}</div>
            </div>
          </button>

          <button
            onClick={() => setActiveTab('offers')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
              activeTab === 'offers'
                ? 'bg-indigo-50 text-indigo-600 border-r-2 border-indigo-600'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`}
          >
            <span className="text-xl">💼</span>
            <div>
              <div className="font-medium">{t('myOffers')}</div>
              <div className="text-sm opacity-75">{t('acceptedJobs')}</div>
            </div>
          </button>

          <button
            onClick={() => setActiveTab('eigen-klanten')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
              activeTab === 'eigen-klanten'
                ? 'bg-green-50 text-green-600 border-r-2 border-green-600'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`}
          >
            <span className="text-xl">👥</span>
            <div>
              <div className="font-medium">{t('ownCustomers')}</div>
              <div className="text-sm opacity-75">{t('bookingsFromMyCustomers')}</div>
            </div>
            {directBookings.length > 0 && (
              <span className="ml-auto bg-green-100 text-green-600 text-xs px-2 py-1 rounded-full">
                {directBookings.length}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('chat')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
              activeTab === 'chat'
                ? 'bg-indigo-50 text-indigo-600 border-r-2 border-indigo-600'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`}
          >
            <span className="text-xl">💬</span>
            <div className="flex-1 flex items-center justify-between">
              <div>
                <div className="font-medium">{t('conversations')}</div>
                <div className="text-sm opacity-75">{t('chatWithCustomers')}</div>
              </div>
              {conversations.some(c => c.unread_count > 0) && (
                <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 ml-2">
                  {conversations.reduce((total, c) => total + c.unread_count, 0)}
                </span>
              )}
            </div>
          </button>

          <button
            onClick={() => setActiveTab('tracking')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
              activeTab === 'tracking'
                ? 'bg-indigo-50 text-indigo-600 border-r-2 border-indigo-600'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`}
          >
            <span className="text-xl">📍</span>
            <div>
              <div className="font-medium">{t('tracking')}</div>
              <div className="text-sm opacity-75">{t('statusUpdates')}</div>
            </div>
          </button>

          <button
            onClick={() => setActiveTab('profile')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
              activeTab === 'profile'
                ? 'bg-indigo-50 text-indigo-600 border-r-2 border-indigo-600'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`}
          >
            <span className="text-xl">👤</span>
            <div>
              <div className="font-medium">{t('myProfile')}</div>
              <div className="text-sm opacity-75">{t('profileAndStatus')}</div>
            </div>
          </button>
        </nav>

        {/* User Info & Logout */}
        <div className="px-4 py-4 border-t border-gray-200">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
              <span className="text-indigo-600 font-semibold text-sm">{user.full_name.charAt(0)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user.company_name || user.full_name}</p>
              <p className="text-xs text-gray-500">{t('transportCompanyLabel')}</p>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button-transporter"
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm font-semibold shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span>{t('logout')}</span>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Mobile Menu Button */}
              <button
                onClick={() => protectedClick(async () => {
                  setIsMobileMenuOpen(true);
                }, 200)}
                disabled={isProcessing}
                className={`lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 ${
                  isProcessing ? 'opacity-50' : ''
                }`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {activeTab === 'available' && t('availableRequests')}
                  {activeTab === 'offers' && t('myOffers')}
                  {activeTab === 'eigen-klanten' && t('ownCustomers')}
                  {activeTab === 'chat' && t('conversations')}
                  {activeTab === 'tracking' && t('tracking')}
                  {activeTab === 'profile' && t('myProfile')}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  {activeTab === 'available' && t('viewNewOpportunities')}
                  {activeTab === 'offers' && t('manageAcceptedJobs')}
                  {activeTab === 'eigen-klanten' && t('bookAndManageCustomers')}
                  {activeTab === 'chat' && t('communicateDirectly')}
                  {activeTab === 'tracking' && t('updateShipmentStatus')}
                  {activeTab === 'profile' && t('manageBusinessProfile')}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">{t('transportDashboard')}</div>
                <div className="text-xs text-gray-500">{new Date().toLocaleDateString('nl-NL')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-gray-50">
          {activeTab === 'available' && (
            <div>
              {/* Search and Filter Section */}
              <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100 mb-4 sm:mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-4">
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                    📋 {t('availableTransportRequests')}
                  </h2>
                  <div className="text-xs sm:text-sm text-gray-500">
                    {filterAvailableRequests().length} {t('ofRequests')} {availableRequests.length} {t('requests')}
                  </div>
                </div>
                
                {/* Search Bar */}
                <div className="mb-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder={t('searchCityCustomer')}
                      value={requestSearchQuery}
                      onChange={(e) => setRequestSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
                
                {/* Filter Toggle Button */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-4">
                  <button
                    onClick={() => setShowRequestFilters(!showRequestFilters)}
                    className="flex items-center justify-center sm:justify-start space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707v4.586l-4-2V11.414a1 1 0 00-.293-.707L3.293 4.707A1 1 0 013 4z" />
                    </svg>
                    <span>{showRequestFilters ? t('hideFilters') : t('showFilters')}</span>
                  </button>
                  
                  {(selectedDepartureCountry || selectedDepartureCity) && (
                    <button
                      onClick={clearRequestFilters}
                      className="text-indigo-600 hover:text-indigo-800 text-xs sm:text-sm font-medium text-center sm:text-left"
                    >
                      {t('clearAllFilters')}
                    </button>
                  )}
                </div>
                
                {/* Filters Panel */}
                {showRequestFilters && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg border">
                    {/* Departure Country Filter */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                        🌍 {t('departureCountryFilter') || 'Vertrek Land'}
                      </label>
                      <select
                        value={selectedDepartureCountry}
                        onChange={(e) => setSelectedDepartureCountry(e.target.value)}
                        className="w-full px-3 py-2 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      >
                        <option value="">{t('allCountries') || 'Alle Landen'}</option>
                        <option value="Netherlands">{t('netherlands')}</option>
                        <option value="Belgium">{t('belgium')}</option>
                        <option value="Spain">{t('spain')}</option>
                        <option value="France">{t('france')}</option>
                        <option value="Germany">{t('germany')}</option>
                        <option value="Italy">{t('italy')}</option>
                        <option value="Morocco">{t('morocco')}</option>
                      </select>
                    </div>
                    
                    {/* Departure City Filter */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                        🏙️ {t('departureCity')}
                      </label>
                      <input
                        type="text"
                        placeholder={t('typeCityName')}
                        value={selectedDepartureCity}
                        onChange={(e) => setSelectedDepartureCity(e.target.value)}
                        className="w-full px-3 py-2 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Requests List */}
              <div className="space-y-4 sm:space-y-6">
                {filterAvailableRequests().map(request => (
                  <div key={request.id} className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4">
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 mb-2">
                          <h3 className="text-base sm:text-xl font-semibold text-gray-900 mb-1 sm:mb-0">
                            🌍 {translateCountry(request.pickup_country, t)} - {request.pickup_city} → {request.delivery_city}
                          </h3>
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full font-medium inline-block self-start">
                            Actief
                          </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 text-sm text-gray-600 mb-3">
                          <div className="flex items-center space-x-2">
                            <span>🏛️</span>
                            <span><strong>Van:</strong> {translateCountry(request.pickup_country, t)}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span>📦</span>
                            <span><strong>Grootte:</strong> {request.baggage_size}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span>⚖️</span>
                            <span><strong>Gewicht:</strong> {request.baggage_weight}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span>📅</span>
                            <span><strong>Datum:</strong> {request.preferred_date}</span>
                          </div>
                        </div>
                        {request.special_requirements && (
                          <div className="bg-yellow-50 p-3 rounded-lg mb-3">
                            <p className="text-xs sm:text-sm text-yellow-800">
                              <strong>⚠️ Bijzondere eisen:</strong> {request.special_requirements}
                            </p>
                          </div>
                        )}
                        {request.baggage_image && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700 mb-2">📷 Bagage foto:</p>
                            <img 
                              src={request.baggage_image} 
                              alt="Customer baggage" 
                              className="w-full sm:max-w-xs h-32 object-cover rounded-lg border"
                            />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-row sm:flex-col space-x-2 sm:space-x-0 sm:space-y-2 mt-4 sm:mt-0 sm:ml-6">
                        <button
                          onClick={() => setShowQuoteForm(request.id)}
                          className="flex-1 sm:flex-none px-4 sm:px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium text-sm sm:text-base whitespace-nowrap"
                        >
                          💰 {t('quote')}
                        </button>
                        <button
                          onClick={() => viewRequestDetails(request)}
                          className="flex-1 sm:flex-none px-4 sm:px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm whitespace-nowrap"
                        >
                          👁️ Details
                        </button>
                      </div>
                    </div>
                    
                    {showQuoteForm === request.id && (
                      <div className="mt-4 p-4 border rounded-lg bg-gray-50">
                        <form onSubmit={submitQuote}>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('price')} (EUR)
                              </label>
                              <input
                                type="number"
                                value={newQuote.price}
                                onChange={(e) => setNewQuote({...newQuote, price: e.target.value})}
                                className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('estimatedDays')}
                              </label>
                              <input
                                type="number"
                                value={newQuote.estimated_delivery_days}
                                onChange={(e) => setNewQuote({...newQuote, estimated_delivery_days: e.target.value})}
                                className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('contactPhone')}
                              </label>
                              <input
                                type="tel"
                                value={newQuote.contact_phone}
                                onChange={(e) => setNewQuote({...newQuote, contact_phone: e.target.value})}
                                className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Contact Adres
                              </label>
                              <input
                                type="text"
                                value={newQuote.contact_address}
                                onChange={(e) => setNewQuote({...newQuote, contact_address: e.target.value})}
                                className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
                              />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('message')}
                              </label>
                              <textarea
                                value={newQuote.message}
                                onChange={(e) => setNewQuote({...newQuote, message: e.target.value})}
                                className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
                                rows="3"
                              />
                            </div>
                          </div>
                          <div className="flex justify-end space-x-2 mt-4">
                            <button
                              type="button"
                              onClick={() => setShowQuoteForm(null)}
                              className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
                            >
                              Annuleren
                            </button>
                            <button
                              type="submit"
                              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
                            >
                              {t('submitQuote')}
                            </button>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                ))}
                {filterAvailableRequests().length === 0 && availableRequests.length > 0 && (
                  <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-100">
                    <div className="text-6xl mb-4">🔍</div>
                    <h3 className="text-xl font-medium text-gray-900 mb-2">Geen aanvragen gevonden</h3>
                    <p className="text-gray-600 mb-4">
                      Geen transportaanvragen voldoen aan uw huidige zoek- en filtercriteria.
                    </p>
                    <button
                      onClick={clearRequestFilters}
                      className="text-indigo-600 hover:text-indigo-800 font-medium"
                    >
                      Wis filters en probeer opnieuw
                    </button>
                  </div>
                )}
                {availableRequests.length === 0 && (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">📦</div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Geen beschikbare verzoeken</h3>
                    <p className="text-gray-500">Er zijn momenteel geen nieuwe transportverzoeken beschikbaar.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'offers' && (
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-6">{t('myOffers')}</h2>
                {acceptedOffers.length === 0 ? (
                  <div className="text-center text-gray-500 mt-8">
                    <div className="text-4xl mb-2">📦</div>
                    <p>Geen geaccepteerde offertes gevonden.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {acceptedOffers.map(offer => (
                      <div key={offer.quote_id} className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
                        {/* Header with route and status */}
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-xl font-bold text-gray-900">
                              {offer.transport_request.pickup_city} → {offer.transport_request.delivery_city}
                            </h3>
                            <p className="text-sm text-gray-600">
                              Klant: <span className="font-medium">{offer.customer_name}</span> | 
                              {t('price')}: <span className="font-bold text-green-600">€{offer.quote.price}</span>
                            </p>
                          </div>
                          <div className="flex flex-col items-end space-y-2">
                            {offer.transport_request.status === 'delivered' ? (
                              <div className="flex flex-col space-y-2">
                                <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full font-medium">
                                  📦 Afgeleverd
                                </span>
                                <button
                                  onClick={() => removeDeliveredRequest(offer.transport_request.id)}
                                  className="px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 font-medium transition-colors"
                                >
                                  🗑️ {t('removeRequest')}
                                </button>
                              </div>
                            ) : offer.transport_request.company_confirmed_delivery && !offer.transport_request.customer_confirmed_delivery ? (
                              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm rounded-full font-medium">
                                ⏳ Wacht op klant bevestiging
                              </span>
                            ) : offer.is_picked_up ? (
                              <button
                                onClick={() => confirmDeliveryAsCompany(offer.transport_request.id, offer.quote_id)}
                                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium transition-colors"
                              >
                                📦 Afgeleverd
                              </button>
                            ) : (
                              <button
                                onClick={() => quickPickup(offer.quote_id)}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors"
                              >
                                🚚 {t('pickedUp')}
                              </button>
                            )}
                            <button
                              onClick={() => openChat({
                                quoteId: offer.quote_id,
                                otherPartyName: offer.customer_name,
                                transport_request: offer.transport_request,
                                quote_price: offer.quote.price
                              })}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              💬 Chat
                            </button>
                          </div>
                        </div>

                        {/* Detailed Request Information */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          {/* Baggage Details */}
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-gray-900 mb-2">📦 Bagage Details</h4>
                            <div className="space-y-1 text-sm">
                              <p><span className="font-medium">Grootte:</span> {offer.transport_request.baggage_size}</p>
                              <p><span className="font-medium">Gewicht:</span> {offer.transport_request.baggage_weight}</p>
                              <p><span className="font-medium">Gewenste datum:</span> {offer.transport_request.preferred_date}</p>
                            </div>
                          </div>

                          {/* Quote Details */}
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-blue-900 mb-2">💼 {t('quoteDetails')}</h4>
                            <div className="space-y-1 text-sm">
                              <p><span className="font-medium">{t('price')}:</span> €{offer.quote.price} {offer.quote.currency}</p>
                              <p><span className="font-medium">{t('estimatedDelivery')}:</span> {offer.quote.estimated_delivery_days} {t('days')}</p>
                              <p><span className="font-medium">{t('contact')}:</span> {offer.quote.contact_phone}</p>
                              {offer.quote.contact_address && (
                                <p><span className="font-medium">Adres:</span> {offer.quote.contact_address}</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Special Requirements */}
                        {offer.transport_request.special_requirements && (
                          <div className="bg-yellow-50 p-4 rounded-lg mt-4">
                            <h4 className="font-semibold text-yellow-900 mb-2">⚠️ Bijzondere Eisen</h4>
                            <p className="text-yellow-800 text-sm">{offer.transport_request.special_requirements}</p>
                          </div>
                        )}

                        {/* Quote Message */}
                        {offer.quote.message && (
                          <div className="bg-indigo-50 p-4 rounded-lg mt-4">
                            <h4 className="font-semibold text-indigo-900 mb-2">💬 Jouw Bericht</h4>
                            <p className="text-indigo-800 text-sm">{offer.quote.message}</p>
                          </div>
                        )}

                        {/* Baggage Image */}
                        {offer.transport_request.baggage_image && (
                          <div className="bg-purple-50 p-4 rounded-lg mt-4">
                            <h4 className="font-semibold text-purple-900 mb-2">📷 Bagage Foto</h4>
                            <img 
                              src={offer.transport_request.baggage_image} 
                              alt="Customer baggage" 
                              className="w-full max-w-md h-48 object-cover rounded border mx-auto"
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'chat' && (
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-6">{t('conversations')}</h2>
                {conversations.length === 0 ? (
                  <div className="text-center text-gray-500 mt-8">
                    <p>Geen gesprekken. Wacht tot een klant uw offerte accepteert om te kunnen chatten.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {conversations.slice(0, currentPage.conversations * itemsPerPage).map(conversation => (
                      <div key={conversation.quote_id} className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow border border-gray-100">
                        <div className="p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              {/* Route Header */}
                              <div className="flex items-center space-x-3 mb-3">
                                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                  </svg>
                                </div>
                                <div>
                                  <h3 className="text-lg font-semibold text-gray-900">
                                    {conversation.transport_request ? (
                                      <>🌍 {translateCountry(conversation.transport_request.pickup_country, t)} - {conversation.transport_request.pickup_city} → {conversation.transport_request.delivery_city}</>
                                    ) : (
                                      <>💬 {t('generalConversation')} met {conversation.other_party_name}</>
                                    )}
                                  </h3>
                                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                                    <span>🏢 {conversation.other_party_name}</span>
                                    <span>💰 €{conversation.quote_price}</span>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Last Message Preview */}
                              {conversation.last_message && (
                                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <span className="text-xs font-medium text-gray-700">{t('lastMessage')}:</span>
                                    <span className="text-xs text-gray-500">{conversation.last_message.sender_name}</span>
                                  </div>
                                  <p className="text-sm text-gray-700 line-clamp-2">
                                    {conversation.last_message.message_type === 'image' ? '📷 ' + t('imageMessage') : conversation.last_message.message}
                                  </p>
                                  <div className="text-xs text-gray-500 mt-1">
                                    {new Date(conversation.last_message.timestamp).toLocaleString('nl-NL', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* Action Area */}
                            <div className="flex flex-col items-end space-y-2 ml-4">
                              {conversation.unread_count > 0 && (
                                <div className="flex items-center space-x-1">
                                  <span className="bg-red-500 text-white text-xs font-bold rounded-full px-2 py-1 min-w-[20px] text-center">
                                    {conversation.unread_count}
                                  </span>
                                  <span className="text-xs text-red-600 font-medium">{t('unreadMessages')}</span>
                                </div>
                              )}
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => openChat(conversation)}
                                  className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105 flex items-center space-x-2 shadow-md"
                                >
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                                  </svg>
                                  <span>{t('chat')}</span>
                                </button>
                                <button
                                  onClick={() => deleteConversation(conversation)}
                                  className="bg-red-500 text-white p-2 rounded-lg hover:bg-red-600 transition-all duration-200 transform hover:scale-105 shadow-md"
                                  title={t('deleteConversation')}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Load More for Conversations */}
                <LoadMoreButton
                  section="conversations"
                  hasMore={conversations.length >= currentPage.conversations * itemsPerPage}
                  loading={false}
                  onLoadMore={() => {
                    const nextPage = currentPage.conversations + 1;
                    fetchConversations(nextPage);
                    setCurrentPage(prev => ({ ...prev, conversations: nextPage }));
                  }}
                />
              </div>
            )}

            {activeTab === 'tracking' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-medium text-gray-900">{t('tracking')}</h2>
                  <div className="flex space-x-3">
                    <button
                      onClick={clearTracking}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                    >
                      🗑️ {t('clearTracking')}
                    </button>
                    <button
                      onClick={() => setShowTrackingForm(true)}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
                    >
                      📍 {t('updateCompanyStatus')}
                    </button>
                  </div>
                </div>

                {/* Company Tracking Updates */}
                <div className="mb-8 bg-white p-6 rounded-lg shadow">
                  <CompanyTrackingTimeline companyId={user.id} />
                </div>
                
                {/* Individual Shipments */}
                <h3 className="text-lg font-medium text-gray-900 mb-4">Individuele Zendingen</h3>
                {activeShipments.length === 0 ? (
                  <div className="text-center text-gray-500 mt-8">
                    <div className="text-4xl mb-2">📦</div>
                    <p>Geen actieve zendingen.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {activeShipments.map(shipment => (
                      <div key={shipment.quote_id} className="bg-white p-6 rounded-lg shadow">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="text-lg font-medium text-gray-900">
                              {shipment.transport_request.pickup_city} → {shipment.transport_request.delivery_city}
                            </h4>
                            <p className="text-sm text-gray-600">
                              Klant: {shipment.customer_name} | €{shipment.quote_price}
                            </p>
                            <p className="text-sm text-gray-500">
                              {shipment.transport_request.baggage_size} | {shipment.transport_request.baggage_weight}
                            </p>
                            <div className="mt-2">
                              {shipment.is_picked_up ? (
                                <span className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full font-medium">
                                  ✅ {t('pickupConfirmed')}
                                </span>
                              ) : (
                                <span className="px-3 py-1 bg-gray-100 text-gray-800 text-sm rounded-full">
                                  ⏳ Wacht op ophaling
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col space-y-2">
                            {!shipment.is_picked_up && (
                              <button
                                onClick={() => quickPickup(shipment.quote_id)}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors"
                              >
                                🚚 {t('pickedUp')}
                              </button>
                            )}
                            <button
                              onClick={() => viewRequestDetails(shipment.transport_request)}
                              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm transition-colors"
                            >
                              📋 Details
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Direct Bookings (Eigen Klanten) Tracking */}
                <h3 className="text-lg font-medium text-gray-900 mb-4 mt-8">{t('ownCustomers')} {t('tracking')}</h3>
                {directBookings.length === 0 ? (
                  <div className="text-center text-gray-500 mt-8 bg-white p-6 rounded-lg shadow">
                    <div className="text-4xl mb-2">👥</div>
                    <p>Geen eigen klanten boekingen.</p>
                    <button
                      onClick={() => setActiveTab('eigen-klanten')}
                      className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      ➕ Maak Eerste Boeking
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {directBookings.map(booking => (
                      <div key={booking.id} className="bg-white p-6 rounded-lg shadow border-l-4 border-green-500">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">EIGEN KLANT</span>
                              <span className="text-xs font-mono text-gray-500">#{booking.tracking_code}</span>
                            </div>
                            <h4 className="text-lg font-medium text-gray-900">
                              {booking.pickup_location} → {booking.delivery_location}
                            </h4>
                            <p className="text-sm text-gray-600">
                              Klant: <span 
                                onClick={() => setShowBookingDetails(booking)} 
                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                              >
                                {booking.customer_name}
                              </span> | {booking.customer_country_code || '+31'} {booking.customer_phone}
                            </p>
                            <p className="text-sm text-gray-500">
                              {booking.description}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                              booking.status === 'afgeleverd' ? 'bg-green-100 text-green-800' :
                              booking.status === 'onderweg' ? 'bg-blue-100 text-blue-800' :
                              booking.status === 'opgehaald' ? 'bg-purple-100 text-purple-800' :
                              booking.status === 'bevestigd' ? 'bg-indigo-100 text-indigo-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {booking.status === 'in_afwachting' ? '⏳ In afwachting' :
                               booking.status === 'bevestigd' ? '✅ Bevestigd' :
                               booking.status === 'opgehaald' ? '📦 Opgehaald' :
                               booking.status === 'onderweg' ? '🚚 Onderweg' :
                               booking.status === 'aangekomen_marokko' ? '🇲🇦 Aangekomen' :
                               booking.status === 'afgeleverd' ? '✅ Afgeleverd' :
                               booking.status}
                            </span>
                            {/* Price Display/Edit */}
                            {editingPrice === booking.id ? (
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  value={newPrice}
                                  onChange={(e) => setNewPrice(e.target.value)}
                                  placeholder="0.00"
                                  className="w-24 px-2 py-1 border rounded text-sm"
                                  step="0.01"
                                  min="0"
                                />
                                <button
                                  onClick={() => updateBookingPrice(booking.id)}
                                  className="px-2 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={() => { setEditingPrice(null); setNewPrice(''); }}
                                  className="px-2 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                                >
                                  ✕
                                </button>
                              </div>
                            ) : booking.agreed_price ? (
                              <div className="flex items-center gap-2">
                                <span className="text-lg font-bold text-green-600">€{booking.agreed_price}</span>
                                <button
                                  onClick={() => { setEditingPrice(booking.id); setNewPrice(booking.agreed_price.toString()); }}
                                  className="text-gray-400 hover:text-gray-600 text-sm"
                                  title="Prijs bewerken"
                                >
                                  ✏️
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => { setEditingPrice(booking.id); setNewPrice(''); }}
                                className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium hover:bg-yellow-200"
                              >
                                💰 Prijs toevoegen
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Status Timeline */}
                        {booking.status_updates && booking.status_updates.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <p className="text-sm font-medium text-gray-700 mb-2">📍 Status Updates:</p>
                            <div className="space-y-2">
                              {booking.status_updates.slice(-3).reverse().map((update, idx) => (
                                <div key={idx} className="flex items-start gap-2 text-sm">
                                  <span className="text-gray-400">•</span>
                                  <div className="flex-1">
                                    <span className="font-medium text-gray-700">
                                      {update.status === 'in_afwachting' ? 'In afwachting' :
                                       update.status === 'bevestigd' ? 'Bevestigd' :
                                       update.status === 'opgehaald' ? 'Opgehaald' :
                                       update.status === 'onderweg' ? 'Onderweg' :
                                       update.status === 'aangekomen_marokko' ? 'Aangekomen in Marokko' :
                                       update.status === 'afgeleverd' ? 'Afgeleverd' :
                                       update.status}
                                    </span>
                                    {update.note && <span className="text-gray-600"> - {update.note}</span>}
                                    <span className="text-gray-400 text-xs ml-2">
                                      {new Date(update.timestamp).toLocaleString('nl-NL', { 
                                        day: '2-digit', 
                                        month: '2-digit', 
                                        hour: '2-digit', 
                                        minute: '2-digit' 
                                      })}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Photos */}
                        {booking.photos && booking.photos.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <p className="text-sm font-medium text-gray-700 mb-3">📷 Foto's van bagage:</p>
                            <div className="grid grid-cols-3 gap-2">
                              {booking.photos.map((photo, idx) => (
                                <img 
                                  key={idx}
                                  src={photo} 
                                  alt={`Bagage foto ${idx + 1}`}
                                  className="w-full h-24 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-75 transition-opacity"
                                  onClick={() => window.open(photo, '_blank')}
                                />
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-200">
                          {booking.status !== 'afgeleverd' && booking.status !== 'geannuleerd' && (
                            <>
                              {booking.status === 'in_afwachting' && (
                                <button
                                  onClick={() => updateBookingStatus(booking.id, 'bevestigd')}
                                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                                >
                                  ✅ {t('confirm')}
                                </button>
                              )}
                              {booking.status === 'bevestigd' && (
                                <button
                                  onClick={() => updateBookingStatus(booking.id, 'opgehaald')}
                                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
                                >
                                  📦 Opgehaald
                                </button>
                              )}
                              {booking.status === 'opgehaald' && (
                                <button
                                  onClick={() => updateBookingStatus(booking.id, 'onderweg')}
                                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                                >
                                  🚚 Onderweg
                                </button>
                              )}
                              {booking.status === 'onderweg' && (
                                <button
                                  onClick={() => updateBookingStatus(booking.id, 'aangekomen_marokko')}
                                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium"
                                >
                                  🇲🇦 Aangekomen Marokko
                                </button>
                              )}
                              {booking.status === 'aangekomen_marokko' && (
                                <button
                                  onClick={() => updateBookingStatus(booking.id, 'afgeleverd')}
                                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                                >
                                  ✅ Afgeleverd
                                </button>
                              )}
                            </>
                          )}
                          <button
                            onClick={() => {
                              const trackingUrl = `${window.location.origin}/track/${booking.tracking_code}`;
                              const message = `Track je zending:\n${trackingUrl}`;
                              const countryCode = (booking.customer_country_code || '+31').replace(/\D/g, '');
                              const phone = booking.customer_phone.replace(/\D/g, '');
                              const whatsappUrl = `https://wa.me/${countryCode}${phone}?text=${encodeURIComponent(message)}`;
                              window.open(whatsappUrl, '_blank');
                            }}
                            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm font-medium"
                          >
                            📱 Deel Tracking
                          </button>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(`${window.location.origin}/track/${booking.tracking_code}`);
                              alert('Tracking link gekopieerd!');
                            }}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-medium"
                          >
                            🔗 Kopieer Link
                          </button>
                          {booking.agreed_price && (
                            <>
                              <button
                                onClick={() => window.open(`/invoice/${booking.id}`, '_blank')}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                              >
                                📄 Factuur
                              </button>
                              <button
                                onClick={() => {
                                  const invoiceUrl = `${window.location.origin}/invoice/${booking.id}`;
                                  const message = `Hier is je factuur:\n${invoiceUrl}`;
                                  const countryCode = (booking.customer_country_code || '+31').replace(/\D/g, '');
                                  const phone = booking.customer_phone.replace(/\D/g, '');
                                  const whatsappUrl = `https://wa.me/${countryCode}${phone}?text=${encodeURIComponent(message)}`;
                                  window.open(whatsappUrl, '_blank');
                                }}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium"
                              >
                                📤 Deel Factuur
                              </button>
                            </>
                          )}
                          {/* Chat knop */}
                          <button
                            onClick={() => {
                              const countryCode = (booking.customer_country_code || '+31').replace(/\D/g, '');
                              const phone = booking.customer_phone.replace(/\D/g, '');
                              const message = `Hallo ${booking.customer_name}, `;
                              const whatsappUrl = `https://wa.me/${countryCode}${phone}?text=${encodeURIComponent(message)}`;
                              window.open(whatsappUrl, '_blank');
                            }}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                          >
                            💬 Chat via WhatsApp
                          </button>
                          {/* Verwijder knop */}
                          <button
                            onClick={() => deleteDirectBooking(booking.id)}
                            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm font-medium"
                          >
                            🗑️ {t('delete')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'profile' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-medium text-gray-900">{t('myProfile')}</h2>
                  <div className="flex space-x-3">
                    <button
                      onClick={toggleOnlineStatus}
                      className={`px-4 py-2 text-white rounded-lg font-medium ${
                        profileData.is_online 
                          ? 'bg-red-600 hover:bg-red-700' 
                          : 'bg-green-600 hover:bg-green-700'
                      }`}
                    >
                      {profileData.is_online ? '🔴 Offline Gaan' : '🟢 Online Gaan'}
                    </button>
                    <button
                      onClick={() => setShowProfileEdit(true)}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
                    >
                      ✏️ {t('updateProfile')}
                    </button>
                    <button
                      onClick={() => setShowStatusEdit(true)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                    >
                      📍 {t('updateStatus')}
                    </button>
                  </div>
                </div>

                {/* Stripe Connect Section - Hidden until commission model is activated */}
                {/* <div data-testid="stripe-connect-section" className={`mb-6 p-5 rounded-lg border-2 ${
                  stripeConnectStatus.connected
                    ? 'bg-green-50 border-green-200'
                    : 'bg-amber-50 border-amber-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        stripeConnectStatus.connected ? 'bg-green-100' : 'bg-amber-100'
                      }`}>
                        <svg className={`w-5 h-5 ${stripeConnectStatus.connected ? 'text-green-600' : 'text-amber-600'}`} fill="currentColor" viewBox="0 0 24 24">
                          <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {stripeConnectStatus.connected ? 'Stripe gekoppeld' : 'Koppel je Stripe account'}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {stripeConnectStatus.connected
                            ? 'Je ontvangt automatisch betalingen van klanten (minus 15% platformcommissie)'
                            : 'Ontvang betalingen rechtstreeks op je bankrekening. Klanten betalen via het platform.'}
                        </p>
                      </div>
                    </div>
                    <div>
                      {stripeConnectStatus.connected ? (
                        <button
                          data-testid="stripe-dashboard-btn"
                          onClick={openStripeDashboard}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm transition-colors"
                        >
                          Stripe Dashboard
                        </button>
                      ) : (
                        <button
                          data-testid="stripe-connect-btn"
                          onClick={startStripeOnboarding}
                          disabled={stripeConnectLoading}
                          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium text-sm transition-colors disabled:opacity-50"
                        >
                          {stripeConnectLoading ? 'Bezig...' : 'Stripe Koppelen'}
                        </button>
                      )}
                    </div>
                  </div>
                </div> */}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Company Information Card */}
                  <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Bedrijfsinformatie</h3>
                    
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4">
                        {profileData.profile_picture ? (
                          <img 
                            src={profileData.profile_picture} 
                            alt="Company Logo"
                            className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                            <span className="text-gray-500 text-2xl">🏢</span>
                          </div>
                        )}
                        <div>
                          <h4 className="font-semibold text-gray-900">{user.company_name}</h4>
                          <p className="text-gray-600">{translateCountry(user.country_origin, t)}</p>
                          <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-2 ${
                            profileData.is_online 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {profileData.is_online ? '🟢 Online' : '🔴 Offline'}
                          </div>
                        </div>
                      </div>

                      <div>
                        <h5 className="font-medium text-gray-900 mb-2">📞 {t('phoneNumber')}</h5>
                        <p className="text-gray-700">{profileData.phone_number || 'Niet ingesteld'}</p>
                      </div>

                      <div>
                        <h5 className="font-medium text-gray-900 mb-2">📍 {t('currentLocation')}</h5>
                        <p className="text-gray-700">{profileData.current_location || 'Niet ingesteld'}</p>
                      </div>

                      <div>
                        <h5 className="font-medium text-gray-900 mb-2">🗣️ {t('communicationLanguages')}</h5>
                        {profileData.languages && profileData.languages.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {profileData.languages.map(lang => {
                              const langName = availableLanguages.find(l => l.code === lang)?.name || lang;
                              return (
                                <span key={lang} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                                  {langName}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-gray-700">Geen talen ingesteld</p>
                        )}
                      </div>

                      <div>
                        <h5 className="font-medium text-gray-900 mb-2">📝 {t('companyDescription')}</h5>
                        <p className="text-gray-700 whitespace-pre-wrap">
                          {profileData.company_description || 'Geen beschrijving ingesteld'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Status & Scheduling Card */}
                  <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Status & Planning</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <h5 className="font-medium text-gray-900 mb-3">🇲🇦 Vertrek naar Marokko</h5>
                        {profileData.departure_dates_morocco.length === 0 ? (
                          <p className="text-gray-500">Geen geplande vertrekdata</p>
                        ) : (
                          <div className="space-y-2">
                            {profileData.departure_dates_morocco.map((date, index) => (
                              <div key={index} className="flex items-center justify-between bg-green-50 px-3 py-2 rounded">
                                <span className="text-green-800">{date}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div>
                        <h5 className="font-medium text-gray-900 mb-3">🇪🇺 Vertrek naar Europa</h5>
                        {profileData.departure_dates_europe.length === 0 ? (
                          <p className="text-gray-500">Geen geplande vertrekdata</p>
                        ) : (
                          <div className="space-y-2">
                            {profileData.departure_dates_europe.map((date, index) => (
                              <div key={index} className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded">
                                <span className="text-blue-800">{date}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Price List Card */}
                  <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">💰 {t('priceList')}</h3>
                    <div className="bg-gray-50 p-4 rounded">
                      <pre className="text-gray-700 whitespace-pre-wrap text-sm">
                        {profileData.price_list || 'Geen prijslijst ingesteld'}
                      </pre>
                    </div>
                  </div>

                  {/* Reviews Card */}
                  <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">⭐ {t('reviews')} ({companyReviews.length})</h3>
                    
                    {user.average_rating && (
                      <div className="flex items-center mb-4 p-3 bg-yellow-50 rounded">
                        <div className="flex text-yellow-400 text-xl mr-3">
                          {[1, 2, 3, 4, 5].map(star => (
                            <span key={star} className={star <= Math.floor(user.average_rating) ? 'text-yellow-400' : 'text-gray-300'}>
                              ⭐
                            </span>
                          ))}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{user.average_rating?.toFixed(1) || '0.0'}/5.0</p>
                          <p className="text-sm text-gray-600">{user.total_reviews || 0} reviews</p>
                        </div>
                      </div>
                    )}

                    {companyReviews.length === 0 ? (
                      <p className="text-gray-500">Nog geen reviews ontvangen</p>
                    ) : (
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {companyReviews.slice(0, 5).map(review => (
                          <div key={review.id} className="border-b pb-3 last:border-b-0">
                            <div className="flex justify-between items-start mb-1">
                              <p className="font-medium text-gray-900">{review.customer_name}</p>
                              <div className="flex text-yellow-400">
                                {[1, 2, 3, 4, 5].map(star => (
                                  <span key={star} className={star <= review.rating ? 'text-yellow-400' : 'text-gray-300'}>
                                    ⭐
                                  </span>
                                ))}
                              </div>
                            </div>
                            {review.review_text && (
                              <p className="text-gray-700 text-sm">{review.review_text}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(review.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        ))}
                        {companyReviews.length > 5 && (
                          <p className="text-sm text-gray-500 text-center">
                            En {companyReviews.length - 5} meer reviews...
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* EIGEN KLANTEN TAB */}
            {activeTab === 'eigen-klanten' && (
              <div>
                {/* Header met acties */}
                <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-xl shadow-sm border border-green-100 mb-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">👥 {t('bookingsFromOwnCustomers')}</h2>
                      <p className="text-gray-600">{t('ownCustomersDescription')}</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={() => setShowDirectBookingForm(true)}
                        className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors flex items-center justify-center gap-2 shadow-md"
                      >
                        ➕ {t('newBooking')}
                      </button>
                      <button
                        onClick={() => shareViaWhatsApp('booking')}
                        className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium transition-colors flex items-center justify-center gap-2 shadow-md"
                      >
                        📱 Deel Inboeklink
                      </button>
                    </div>
                  </div>
                  
                  {/* Persoonlijke link */}
                  <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
                    <p className="text-sm font-medium text-gray-700 mb-2">🔗 Uw persoonlijke boekingslink:</p>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={getPersonalBookingLink()}
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(getPersonalBookingLink());
                          alert('Link gekopieerd!');
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                      >
                        📋 Kopieer
                      </button>
                    </div>
                  </div>
                </div>

                {/* Boekingen lijst */}
                {directBookings.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl shadow-sm">
                    <div className="text-6xl mb-4">📦</div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Nog geen boekingen</h3>
                    <p className="text-gray-500 mb-4">Begin met het boeken van zendingen voor uw klanten</p>
                    <button
                      onClick={() => setShowDirectBookingForm(true)}
                      className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors"
                    >
                      ➕ Eerste Boeking Aanmaken
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {directBookings.map(booking => (
                      <div key={booking.id} className="bg-white p-6 rounded-xl shadow-md border border-gray-200 hover:shadow-lg transition-shadow">
                        {/* Header */}
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 
                              onClick={() => setShowBookingDetails(booking)}
                              className="text-xl font-bold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                            >
                              {booking.customer_name}
                            </h3>
                            <p className="text-sm text-gray-600">📞 {booking.customer_country_code || '+31'} {booking.customer_phone}</p>
                            <p className="text-xs text-gray-500 mt-1">Tracking: <span className="font-mono font-bold">{booking.tracking_code}</span></p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                              booking.status === 'afgeleverd' ? 'bg-green-100 text-green-800' :
                              booking.status === 'onderweg' ? 'bg-blue-100 text-blue-800' :
                              booking.status === 'opgehaald' ? 'bg-purple-100 text-purple-800' :
                              booking.status === 'bevestigd' ? 'bg-indigo-100 text-indigo-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {booking.status === 'in_afwachting' ? '⏳ In afwachting' :
                               booking.status === 'bevestigd' ? '✅ Bevestigd' :
                               booking.status === 'opgehaald' ? '📦 Opgehaald' :
                               booking.status === 'onderweg' ? '🚚 Onderweg' :
                               booking.status === 'aangekomen_marokko' ? '🇲🇦 Aangekomen' :
                               booking.status === 'afgeleverd' ? '✅ Afgeleverd' :
                               booking.status}
                            </span>
                            {/* Price Display/Edit */}
                            {editingPrice === booking.id ? (
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  value={newPrice}
                                  onChange={(e) => setNewPrice(e.target.value)}
                                  placeholder="0.00"
                                  className="w-24 px-2 py-1 border rounded text-sm"
                                  step="0.01"
                                  min="0"
                                />
                                <button
                                  onClick={() => updateBookingPrice(booking.id)}
                                  className="px-2 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={() => { setEditingPrice(null); setNewPrice(''); }}
                                  className="px-2 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                                >
                                  ✕
                                </button>
                              </div>
                            ) : booking.agreed_price ? (
                              <div className="flex items-center gap-2">
                                <span className="text-lg font-bold text-green-600">€{booking.agreed_price}</span>
                                <button
                                  onClick={() => { setEditingPrice(booking.id); setNewPrice(booking.agreed_price.toString()); }}
                                  className="text-gray-400 hover:text-gray-600 text-sm"
                                  title="Prijs bewerken"
                                >
                                  ✏️
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => { setEditingPrice(booking.id); setNewPrice(''); }}
                                className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium hover:bg-yellow-200"
                              >
                                💰 Prijs toevoegen
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Details */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <p className="text-sm text-gray-600"><strong>📦 Wat:</strong> {booking.description}</p>
                            <p className="text-sm text-gray-600 mt-1"><strong>📍 Van:</strong> {booking.pickup_location}</p>
                            <p className="text-sm text-gray-600 mt-1"><strong>📍 Naar:</strong> {booking.delivery_location}</p>
                          </div>
                          <div>
                            {booking.pickup_date && (
                              <p className="text-sm text-gray-600"><strong>📅 Ophalen:</strong> {booking.pickup_date}</p>
                            )}
                            {booking.delivery_date && (
                              <p className="text-sm text-gray-600 mt-1"><strong>📅 Afleveren:</strong> {booking.delivery_date}</p>
                            )}
                          </div>
                        </div>

                        {/* Acties */}
                        <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
                          {booking.status === 'in_afwachting' && (
                            <button
                              onClick={() => updateBookingStatus(booking.id, 'bevestigd')}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                            >
                              ✅ {t('confirm')}
                            </button>
                          )}
                          {booking.status === 'bevestigd' && (
                            <button
                              onClick={() => updateBookingStatus(booking.id, 'opgehaald')}
                              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
                            >
                              📦 Opgehaald
                            </button>
                          )}
                          {booking.status === 'opgehaald' && (
                            <button
                              onClick={() => updateBookingStatus(booking.id, 'onderweg')}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                            >
                              🚚 Onderweg
                            </button>
                          )}
                          {booking.status === 'onderweg' && (
                            <button
                              onClick={() => updateBookingStatus(booking.id, 'aangekomen_marokko')}
                              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium"
                            >
                              🇲🇦 Aangekomen Marokko
                            </button>
                          )}
                          {booking.status === 'aangekomen_marokko' && (
                            <button
                              onClick={() => updateBookingStatus(booking.id, 'afgeleverd')}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                            >
                              ✅ Afgeleverd
                            </button>
                          )}
                          <button
                            onClick={() => {
                              const trackingUrl = `${window.location.origin}/track/${booking.tracking_code}`;
                              const message = `Track je zending: ${trackingUrl}`;
                              const countryCode = (booking.customer_country_code || '+31').replace(/\D/g, '');
                              const phone = booking.customer_phone.replace(/\D/g, '');
                              const whatsappUrl = `https://wa.me/${countryCode}${phone}?text=${encodeURIComponent(message)}`;
                              window.open(whatsappUrl, '_blank');
                            }}
                            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm font-medium"
                          >
                            📱 Deel Tracking via WhatsApp
                          </button>
                          <button
                            onClick={() => window.open(`/invoice/${booking.id}`, '_blank')}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                          >
                            📄 Bekijk Factuur
                          </button>
                          <button
                            onClick={() => {
                              const invoiceUrl = `${window.location.origin}/invoice/${booking.id}`;
                              const message = `Hier is je factuur:\n${invoiceUrl}`;
                              const countryCode = (booking.customer_country_code || '+31').replace(/\D/g, '');
                              const phone = booking.customer_phone.replace(/\D/g, '');
                              const whatsappUrl = `https://wa.me/${countryCode}${phone}?text=${encodeURIComponent(message)}`;
                              window.open(whatsappUrl, '_blank');
                            }}
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium"
                          >
                            📤 Deel Factuur
                          </button>
                          {/* Chat knop */}
                          <button
                            onClick={() => {
                              const countryCode = (booking.customer_country_code || '+31').replace(/\D/g, '');
                              const phone = booking.customer_phone.replace(/\D/g, '');
                              const message = `Hallo ${booking.customer_name}, `;
                              const whatsappUrl = `https://wa.me/${countryCode}${phone}?text=${encodeURIComponent(message)}`;
                              window.open(whatsappUrl, '_blank');
                            }}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                          >
                            💬 Chat via WhatsApp
                          </button>
                          {/* Verwijder knop */}
                          <button
                            onClick={() => deleteDirectBooking(booking.id)}
                            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm font-medium"
                          >
                            🗑️ {t('delete')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Nieuwe Boeking Modal */}
            {showDirectBookingForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-2xl font-bold text-gray-900">➕ {t('newBooking')}</h2>
                      <button
                        onClick={() => setShowDirectBookingForm(false)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        ✕
                      </button>
                    </div>
                    
                    <form onSubmit={createDirectBooking} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Klantnaam *</label>
                        <input
                          type="text"
                          required
                          value={newDirectBooking.customer_name}
                          onChange={(e) => setNewDirectBooking({...newDirectBooking, customer_name: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Telefoonnummer *</label>
                        <div className="flex gap-2">
                          <select
                            value={newDirectBooking.customer_country_code}
                            onChange={(e) => setNewDirectBooking({...newDirectBooking, customer_country_code: e.target.value})}
                            className="w-24 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                          >
                            <option value="+31">🇳🇱 +31</option>
                            <option value="+32">🇧🇪 +32</option>
                            <option value="+33">🇫🇷 +33</option>
                            <option value="+49">🇩🇪 +49</option>
                            <option value="+212">🇲🇦 +212</option>
                            <option value="+34">🇪🇸 +34</option>
                            <option value="+44">🇬🇧 +44</option>
                            <option value="+39">🇮🇹 +39</option>
                          </select>
                          <input
                            type="tel"
                            required
                            value={newDirectBooking.customer_phone}
                            onChange={(e) => setNewDirectBooking({...newDirectBooking, customer_phone: e.target.value})}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                            placeholder="6 12345678"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                        <input
                          type="email"
                          required
                          value={newDirectBooking.customer_email}
                          onChange={(e) => setNewDirectBooking({...newDirectBooking, customer_email: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                          placeholder="klant.email@voorbeeld.nl"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Wat verstuur je? *</label>
                        <input
                          type="text"
                          required
                          placeholder="bijv. 3 koffers, 2 dozen"
                          value={newDirectBooking.description}
                          onChange={(e) => setNewDirectBooking({...newDirectBooking, description: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Ophaallocatie *</label>
                          <input
                            type="text"
                            required
                            placeholder="bijv. Amsterdam, NL"
                            value={newDirectBooking.pickup_location}
                            onChange={(e) => setNewDirectBooking({...newDirectBooking, pickup_location: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Afleverlocatie *</label>
                          <input
                            type="text"
                            required
                            placeholder="bijv. Tanger, Marokko"
                            value={newDirectBooking.delivery_location}
                            onChange={(e) => setNewDirectBooking({...newDirectBooking, delivery_location: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Ophaaldatum</label>
                          <input
                            type="date"
                            value={newDirectBooking.pickup_date}
                            onChange={(e) => setNewDirectBooking({...newDirectBooking, pickup_date: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Afleverdatum</label>
                          <input
                            type="date"
                            value={newDirectBooking.delivery_date}
                            onChange={(e) => setNewDirectBooking({...newDirectBooking, delivery_date: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Afgesproken prijs (€)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={newDirectBooking.agreed_price}
                          onChange={(e) => setNewDirectBooking({...newDirectBooking, agreed_price: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                      </div>
                      
                      <div className="flex gap-3 pt-4">
                        <button
                          type="submit"
                          className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                        >
                          💾 Boeking Opslaan
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowDirectBookingForm(false)}
                          className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                        >
                          Annuleren
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modals Container */}
        <div>
          {/* Tracking Update Modal */}
          {showTrackingForm && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">{t('updateCompanyStatus')}</h3>
                <form onSubmit={submitCompanyUpdate}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <select
                        value={newCompanyUpdate.status}
                        onChange={(e) => setNewCompanyUpdate({...newCompanyUpdate, status: e.target.value})}
                        className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
                      >
                        <option value="in_transit">Onderweg</option>
                        <option value="at_customs">Bij de Douane</option>
                        <option value="at_border">Bij de Grens</option>
                        <option value="arrived_morocco">Aangekomen in Marokko</option>
                        <option value="ready_delivery">Klaar voor Bezorging</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('currentLocation')}</label>
                      <input
                        type="text"
                        value={newCompanyUpdate.location}
                        onChange={(e) => setNewCompanyUpdate({...newCompanyUpdate, location: e.target.value})}
                        placeholder="Bijv. Grens Frankrijk-Spanje, Tanger Port"
                        className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('messageForCustomers')}</label>
                      <textarea
                        value={newCompanyUpdate.message}
                        onChange={(e) => setNewCompanyUpdate({...newCompanyUpdate, message: e.target.value})}
                        placeholder="Bijv. Alles verloopt volgens planning, verwachte aankomst morgenavond"
                        className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
                        rows="3"
                        required
                      />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowTrackingForm(null)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      {t('cancel')}
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                    >
                      {t('addUpdate')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Request Details Modal */}
          {showRequestDetails && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-screen overflow-y-auto">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-bold text-gray-900">Transport Aanvraag Details</h3>
                  <button
                    onClick={() => setShowRequestDetails(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="space-y-4">
                  {/* Route Information */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-2">🗺️ Route</h4>
                    <div className="flex items-center justify-center">
                      <span className="text-lg font-medium text-blue-800">
                        {showRequestDetails.pickup_city}
                      </span>
                      <svg className="w-6 h-6 mx-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                      <span className="text-lg font-medium text-blue-800">
                        {showRequestDetails.delivery_city}
                      </span>
                    </div>
                  </div>

                  {/* Baggage Information */}
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-green-900 mb-2">📦 Bagage Informatie</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-green-700">Grootte:</span>
                        <span className="font-medium text-green-800 ml-2">{showRequestDetails.baggage_size}</span>
                      </div>
                      <div>
                        <span className="text-sm text-green-700">Gewicht:</span>
                        <span className="font-medium text-green-800 ml-2">{showRequestDetails.baggage_weight}</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-sm text-green-700">Gewenste datum:</span>
                        <span className="font-medium text-green-800 ml-2">{showRequestDetails.preferred_date}</span>
                      </div>
                    </div>
                  </div>

                  {/* Special Requirements */}
                  {showRequestDetails.special_requirements && (
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-yellow-900 mb-2">⚠️ Bijzondere Eisen</h4>
                      <p className="text-yellow-800">{showRequestDetails.special_requirements}</p>
                    </div>
                  )}

                  {/* Baggage Image */}
                  {showRequestDetails.baggage_image && (
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-purple-900 mb-2">📷 Bagage Foto</h4>
                      <img 
                        src={showRequestDetails.baggage_image} 
                        alt="Customer baggage" 
                        className="w-full max-w-md h-48 object-cover rounded border mx-auto"
                      />
                    </div>
                  )}

                  {/* Status */}
                  <div className="bg-indigo-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-indigo-900 mb-2">📊 Status</h4>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      showRequestDetails.status === 'active' ? 'bg-green-100 text-green-800' : 
                      showRequestDetails.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {t(showRequestDetails.status)}
                    </span>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowRequestDetails(null)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Sluiten
                  </button>
                  <button
                    onClick={() => {
                      setShowRequestDetails(null);
                      setShowQuoteForm(showRequestDetails.id);
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    {t('submitQuote')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Profile Edit Modal */}
          {showProfileEdit && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-screen overflow-y-auto">
                <h3 className="text-lg font-medium text-gray-900 mb-4">✏️ Profiel Bijwerken</h3>
                <form onSubmit={updateProfile}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Profielfoto</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
                      />
                      {profileData.profile_picture && (
                        <img 
                          src={profileData.profile_picture} 
                          alt="Preview"
                          className="mt-2 w-16 h-16 rounded-full object-cover"
                        />
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Telefoonnummer</label>
                      <input
                        type="tel"
                        value={profileData.phone_number}
                        onChange={(e) => setProfileData({...profileData, phone_number: e.target.value})}
                        placeholder="Bijv. +31-20-1234567"
                        className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bedrijfsbeschrijving</label>
                      <textarea
                        value={profileData.company_description}
                        onChange={(e) => setProfileData({...profileData, company_description: e.target.value})}
                        rows="4"
                        placeholder="Beschrijf uw bedrijf, ervaring en specialiteiten..."
                        className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('communicationLanguages')}</label>
                      <div className="grid grid-cols-2 gap-2">
                        {availableLanguages.map(lang => (
                          <label key={lang.code} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={profileData.languages.includes(lang.code)}
                              onChange={() => toggleLanguage(lang.code)}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-sm text-gray-700">{lang.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('priceList')}</label>
                      <textarea
                        value={profileData.price_list}
                        onChange={(e) => setProfileData({...profileData, price_list: e.target.value})}
                        rows="6"
                        placeholder="Bijv:&#10;Amsterdam → Casablanca: €50&#10;Rotterdam → Rabat: €45&#10;Den Haag → Marrakech: €60"
                        className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
                      />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowProfileEdit(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      {t('cancel')}
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                    >
                      {t('save')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Status Edit Modal */}
          {showStatusEdit && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-screen overflow-y-auto">
                <h3 className="text-lg font-medium text-gray-900 mb-4">📍 Status Bijwerken</h3>
                <form onSubmit={updateStatus}>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Huidige Locatie</label>
                      <input
                        type="text"
                        value={profileData.current_location}
                        onChange={(e) => setProfileData({...profileData, current_location: e.target.value})}
                        placeholder="Bijv. Amsterdam, Rotterdam, Antwerpen"
                        className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">🇲🇦 Vertrekdata naar Marokko</label>
                      <div className="flex gap-2 mb-2">
                        <input
                          type="date"
                          value={newDepartureDateMorocco}
                          onChange={(e) => setNewDepartureDateMorocco(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500"
                        />
                        <button
                          type="button"
                          onClick={() => addDepartureDate('morocco')}
                          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          Toevoegen
                        </button>
                      </div>
                      <div className="space-y-2">
                        {profileData.departure_dates_morocco.map((date, index) => (
                          <div key={index} className="flex items-center justify-between bg-green-50 px-3 py-2 rounded">
                            <span className="text-green-800">{date}</span>
                            <button
                              type="button"
                              onClick={() => removeDepartureDate('morocco', index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              ❌
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">🇪🇺 Vertrekdata naar Europa</label>
                      <div className="flex gap-2 mb-2">
                        <input
                          type="date"
                          value={newDepartureDateEurope}
                          onChange={(e) => setNewDepartureDateEurope(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => addDepartureDate('europe')}
                          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Toevoegen
                        </button>
                      </div>
                      <div className="space-y-2">
                        {profileData.departure_dates_europe.map((date, index) => (
                          <div key={index} className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded">
                            <span className="text-blue-800">{date}</span>
                            <button
                              type="button"
                              onClick={() => removeDepartureDate('europe', index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              ❌
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowStatusEdit(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      Annuleren
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                    >
                      Opslaan
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Chat Modal */}
          {showChat && (
            <Chat
              quoteId={showChat.quoteId}
              companyId={showChat.companyId}
              otherPartyName={showChat.otherPartyName}
              otherPartyType={showChat.otherPartyType}
              companyName={showChat.companyName}
              customerName={showChat.customerName}
              customerId={showChat.customerId}
              customerEmail={showChat.customerEmail}
              onClose={() => setShowChat(null)}
            />
          )}

          {/* Booking Details Modal */}
      {showBookingDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowBookingDetails(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-2xl">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{showBookingDetails.customer_name}</h3>
                <p className="text-sm text-gray-500">Tracking: <span className="font-mono font-bold">{showBookingDetails.tracking_code}</span></p>
              </div>
              <button
                data-testid="close-booking-modal"
                onClick={() => setShowBookingDetails(null)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500">Status</span>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  showBookingDetails.status === 'afgeleverd' ? 'bg-green-100 text-green-800' :
                  showBookingDetails.status === 'onderweg' ? 'bg-blue-100 text-blue-800' :
                  showBookingDetails.status === 'opgehaald' ? 'bg-purple-100 text-purple-800' :
                  showBookingDetails.status === 'bevestigd' ? 'bg-indigo-100 text-indigo-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {showBookingDetails.status === 'in_afwachting' ? '⏳ In afwachting' :
                   showBookingDetails.status === 'bevestigd' ? '✅ Bevestigd' :
                   showBookingDetails.status === 'opgehaald' ? '📦 Opgehaald' :
                   showBookingDetails.status === 'onderweg' ? '🚚 Onderweg' :
                   showBookingDetails.status === 'aangekomen_marokko' ? '🇲🇦 Aangekomen' :
                   showBookingDetails.status === 'afgeleverd' ? '✅ Afgeleverd' :
                   showBookingDetails.status}
                </span>
              </div>

              {/* Price Section */}
              <div data-testid="booking-price-section" className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">Afgesproken Prijs</span>
                  {editingPrice === showBookingDetails.id ? (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">€</span>
                      <input
                        data-testid="booking-price-input"
                        type="number"
                        value={newPrice}
                        onChange={(e) => setNewPrice(e.target.value)}
                        placeholder="0.00"
                        className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-right font-bold text-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        step="0.01"
                        min="0"
                        autoFocus
                      />
                      <button
                        data-testid="save-price-btn"
                        onClick={() => updateBookingPrice(showBookingDetails.id)}
                        className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                      >
                        Opslaan
                      </button>
                      <button
                        data-testid="cancel-price-btn"
                        onClick={() => { setEditingPrice(null); setNewPrice(''); }}
                        className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300 transition-colors"
                      >
                        Annuleren
                      </button>
                    </div>
                  ) : showBookingDetails.agreed_price ? (
                    <div className="flex items-center gap-3">
                      <span className="text-2xl font-bold text-green-600">€{showBookingDetails.agreed_price.toFixed(2)}</span>
                      <button
                        data-testid="edit-price-btn"
                        onClick={() => { setEditingPrice(showBookingDetails.id); setNewPrice(showBookingDetails.agreed_price.toString()); }}
                        className="px-3 py-1 text-sm text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 font-medium transition-colors"
                      >
                        Bewerken
                      </button>
                    </div>
                  ) : (
                    <button
                      data-testid="add-price-btn"
                      onClick={() => { setEditingPrice(showBookingDetails.id); setNewPrice(''); }}
                      className="px-4 py-2 bg-amber-100 text-amber-800 rounded-lg text-sm font-semibold hover:bg-amber-200 transition-colors"
                    >
                      + Prijs Toevoegen
                    </button>
                  )}
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Klantgegevens</h4>
                <div className="grid grid-cols-1 gap-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-400 w-5">📞</span>
                    <span className="text-gray-700">{showBookingDetails.customer_country_code || '+31'} {showBookingDetails.customer_phone}</span>
                  </div>
                  {showBookingDetails.customer_email && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-400 w-5">✉️</span>
                      <span className="text-gray-700">{showBookingDetails.customer_email}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Shipment Details */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Zendingsdetails</h4>
                <div className="grid grid-cols-1 gap-2">
                  <div className="flex items-start gap-2 text-sm">
                    <span className="text-gray-400 w-5 mt-0.5">📦</span>
                    <span className="text-gray-700">{showBookingDetails.description || 'Geen beschrijving'}</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <span className="text-gray-400 w-5 mt-0.5">📍</span>
                    <div>
                      <span className="text-gray-500">Van: </span>
                      <span className="text-gray-700 font-medium">{showBookingDetails.pickup_location}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <span className="text-gray-400 w-5 mt-0.5">🏁</span>
                    <div>
                      <span className="text-gray-500">Naar: </span>
                      <span className="text-gray-700 font-medium">{showBookingDetails.delivery_location}</span>
                    </div>
                  </div>
                  {showBookingDetails.estimated_weight && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-400 w-5">⚖️</span>
                      <span className="text-gray-700">{showBookingDetails.estimated_weight} kg</span>
                    </div>
                  )}
                  {showBookingDetails.number_of_items && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-400 w-5">📋</span>
                      <span className="text-gray-700">{showBookingDetails.number_of_items} stuks</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Status Timeline */}
              {showBookingDetails.status_updates && showBookingDetails.status_updates.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Status Updates</h4>
                  <div className="space-y-2">
                    {showBookingDetails.status_updates.slice().reverse().map((update, idx) => (
                      <div key={idx} className="flex items-start gap-3 text-sm">
                        <div className="w-2 h-2 bg-indigo-400 rounded-full mt-1.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium text-gray-800">{update.status}</span>
                          {update.note && <span className="text-gray-500 ml-1">- {update.note}</span>}
                          <p className="text-xs text-gray-400">{update.timestamp ? new Date(update.timestamp).toLocaleString('nl-NL') : ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  onClick={() => {
                    const trackingUrl = `${window.location.origin}/track/${showBookingDetails.tracking_code}`;
                    navigator.clipboard.writeText(trackingUrl);
                    alert('Tracking link gekopieerd!');
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium transition-colors"
                >
                  🔗 Kopieer Tracking
                </button>
                <button
                  onClick={() => {
                    const countryCode = (showBookingDetails.customer_country_code || '+31').replace(/\D/g, '');
                    const phone = showBookingDetails.customer_phone.replace(/\D/g, '');
                    const message = `Hallo ${showBookingDetails.customer_name}, `;
                    const whatsappUrl = `https://wa.me/${countryCode}${phone}?text=${encodeURIComponent(message)}`;
                    window.open(whatsappUrl, '_blank');
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors"
                >
                  💬 WhatsApp
                </button>
                {showBookingDetails.agreed_price && (
                  <button
                    onClick={() => window.open(`/invoice/${showBookingDetails.id}`, '_blank')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                  >
                    📄 Factuur
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
  );
};

// Welcome Screen Component
const WelcomeScreen = () => {
  const { user, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const [showDashboard, setShowDashboard] = useState(false);
  const [dashboardType, setDashboardType] = useState('');
  const logo = useLogo();

  // Welcome screen is now shown first - users can choose when to go to their dashboard

  if (showDashboard) {
    if (dashboardType === 'admin') {
      return <AdminDashboard />;
    } else if (dashboardType === 'customer') {
      return <CustomerDashboard />;
    } else if (dashboardType === 'transport') {
      return <TransportCompanyDashboard />;
    }
  }

  const getWelcomeMessage = () => {
    if (user.user_type === 'admin') {
      return t('welcomeAdmin');
    } else if (user.user_type === 'customer') {
      return t('welcomeCustomer');
    } else {
      return t('welcomeTransport');
    }
  };

  const getMenuItems = () => {
    if (user.user_type === 'admin') {
      return [
        {
          id: 'admin',
          title: t('adminDashboardTitle'),
          description: t('adminDashboardDesc'),
          icon: '👨‍💼',
          color: 'bg-purple-500'
        }
      ];
    } else if (user.user_type === 'customer') {
      return [
        {
          id: 'customer',
          title: t('myDashboardTitle'),
          description: t('myDashboardDesc'),
          icon: '📦',
          color: 'bg-blue-500'
        }
      ];
    } else {
      return [
        {
          id: 'transport',
          title: t('transportDashboardTitle'),
          description: t('transportDashboardDesc'),
          icon: '🚛',
          color: 'bg-green-500'
        }
      ];
    }
  };

  const handleMenuClick = (menuId) => {
    setDashboardType(menuId);
    setShowDashboard(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              {logo ? (
                <img 
                  src={logo} 
                  alt="Send2Maroc Logo" 
                  className="h-10 w-auto object-contain"
                />
              ) : (
                <>
                  <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-xl">B</span>
                  </div>
                  <div>
                    <h1 className="text-xl font-bold text-gray-900">{t('brandName')}</h1>
                    <p className="text-sm text-gray-500">{t('brandSubtitle')}</p>
                  </div>
                </>
              )}
            </div>
            
            <div className="flex items-center space-x-4">
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="nl">{t('dutch')}</option>
                <option value="en">{t('english')}</option>
                <option value="fr">{t('french')}</option>
                <option value="ar">{t('arabic')}</option>
              </select>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user.full_name}</p>
                <p className="text-xs text-gray-500 capitalize">{user.user_type}</p>
              </div>
              <button
                onClick={logout}
                data-testid={`logout-button-${user.user_type}`}
                className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-semibold shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>{t('logout')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Welcome Section */}
        <div className="text-center mb-12">
          <div className="mb-6">
            <div className="w-24 h-24 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl">👋</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('welcomeBack')}, {user.full_name}!
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {getWelcomeMessage()}
            </p>
          </div>
        </div>

        {/* Quick Stats/Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <span className="text-2xl">🌍</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{t('europeToMorocco')}</h3>
                <p className="text-sm text-gray-500">{t('reliableConnection')}</p>
              </div>
            </div>
            <p className="text-gray-600 text-sm">
              {t('europeToMoroccoDesc')}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <span className="text-2xl">📱</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{t('realTimeTracking')}</h3>
                <p className="text-sm text-gray-500">{t('alwaysUpToDate')}</p>
              </div>
            </div>
            <p className="text-gray-600 text-sm">
              {t('realTimeTrackingDesc')}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <span className="text-2xl">🛡️</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{t('securePlatform')}</h3>
                <p className="text-sm text-gray-500">{t('hundredPercentSecure')}</p>
              </div>
            </div>
            <p className="text-gray-600 text-sm">
              {t('securePlatformDesc')}
            </p>
          </div>
        </div>

        {/* Navigation Menu */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            {t('whereToGo')}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {getMenuItems().map((item) => (
              <button
                key={item.id}
                onClick={() => handleMenuClick(item.id)}
                className="group relative overflow-hidden bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-indigo-300 hover:shadow-lg transition-all duration-300 text-left"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-50 to-blue-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <div className="relative z-10">
                  <div className={`w-16 h-16 ${item.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <span className="text-2xl">{item.icon}</span>
                  </div>
                  
                  <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-indigo-600 transition-colors">
                    {item.title}
                  </h4>
                  
                  <p className="text-gray-600 text-sm mb-4">
                    {item.description}
                  </p>
                  
                  <div className="flex items-center text-indigo-600 font-medium text-sm group-hover:text-indigo-700">
                    <span>{t('goToDashboard')}</span>
                    <svg className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Footer Info */}
        <div className="text-center mt-12">
          <p className="text-gray-500 text-sm">
            {t('brandName')} - {t('trustedPartner')}
          </p>
        </div>
      </div>
    </div>
  );
};

// Payment Success Component
// Booking Payment Success Page
const BookingPaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkStatus = async () => {
      const params = new URLSearchParams(location.search);
      const sessionId = params.get('session_id');
      if (!sessionId) { setLoading(false); return; }

      // Poll up to 5 times
      for (let i = 0; i < 5; i++) {
        try {
          const resp = await axios.get(`${API}/payments/booking-status/${sessionId}`);
          setPaymentData(resp.data);
          if (resp.data.payment_status === 'paid') break;
        } catch (err) {
          console.error('Poll error:', err);
        }
        await new Promise(r => setTimeout(r, 2000));
      }
      setLoading(false);
    };
    checkStatus();
  }, [location]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
      <div data-testid="booking-payment-success" className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        {loading ? (
          <div className="space-y-4">
            <div className="animate-spin mx-auto h-12 w-12 border-4 border-green-200 border-t-green-600 rounded-full" />
            <p className="text-gray-600">Betaling verifiëren...</p>
          </div>
        ) : paymentData?.payment_status === 'paid' ? (
          <div className="space-y-5">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Betaling Geslaagd!</h2>
            <div className="bg-green-50 rounded-lg p-4 text-left space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Totaal betaald</span>
                <span className="font-bold text-gray-900">€{(paymentData.total_amount || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Transporteur ontvangt</span>
                <span className="font-medium text-green-700">€{(paymentData.transporteur_amount || 0).toFixed(2)}</span>
              </div>
            </div>
            <p className="text-sm text-gray-500">Het transportbedrijf is op de hoogte gebracht van je betaling.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full px-4 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Terug naar Dashboard
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900">Betaling wordt verwerkt</h2>
            <p className="text-gray-500 text-sm">We konden de betaling nog niet bevestigen. Controleer je dashboard voor de status.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full px-4 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Naar Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const PaymentSuccess = () => {
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const logo = useLogo();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      try {
        const urlParams = new URLSearchParams(location.search);
        const sessionId = urlParams.get('session_id');
        
        if (sessionId) {
          // Check payment status without requiring authentication (since registration might be pending)
          const response = await axios.get(`${API}/payments/status-public/${sessionId}`);
          setPaymentStatus(response.data);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      } finally {
        setLoading(false);
      }
    };

    checkPaymentStatus();
  }, [location]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      <header className="bg-white shadow-lg border-b-2 border-green-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              {logo ? (
                <img 
                  src={logo} 
                  alt="Send2Maroc Logo" 
                  className="h-12 w-auto object-contain"
                />
              ) : (
                <>
                  <div className="w-12 h-12 bg-gradient-to-r from-green-600 to-green-800 rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-green-900">Send2Maroc</h1>
                    <p className="text-sm text-green-600 font-medium">Betaling Succesvol</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {loading ? (
            <div className="space-y-4">
              <div className="animate-spin mx-auto h-12 w-12 text-green-600">
                <svg fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <p className="text-gray-600">Bezig met controleren van betaling...</p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <div>
                <h2 className="text-3xl font-bold text-green-900 mb-2">🎉 Registratie Voltooid!</h2>
                <p className="text-lg text-gray-600 mb-6">
                  Uw bedrijf is geregistreerd en uw account is succesvol aangemaakt.
                </p>
              </div>

              {paymentStatus && (
                <div className="bg-green-50 p-6 rounded-lg border border-green-200 text-left">
                  <h3 className="font-semibold text-green-800 mb-3">Betalingsdetails:</h3>
                  <div className="space-y-2 text-sm text-green-700">
                    <div className="flex justify-between">
                      <span>Bedrag:</span>
                      <span>€{(paymentStatus.amount_total / 100).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('status')}:</span>
                      <span className="font-medium">✅ Betaald</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-3">Wat gebeurt er nu?</h3>
                <div className="text-sm text-blue-700 space-y-2 text-left">
                  <p>✅ <strong>Stap 1:</strong> Uw bedrijf is geregistreerd en betaling is verwerkt</p>
                  <p>⏳ <strong>Stap 2:</strong> Uw bedrijfsregistratie wordt beoordeeld door onze admin (1-2 werkdagen)</p>
                  <p>📧 <strong>Stap 3:</strong> U ontvangt een e-mail zodra uw bedrijf is goedgekeurd</p>
                  <p>🚀 <strong>Stap 4:</strong> Uw maandelijkse abonnement start en u kunt vervoersverzoeken ontvangen</p>
                </div>
              </div>

              <div className="space-y-4">
                <button
                  onClick={() => navigate('/login?type=company')}
                  className="w-full px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-4 focus:ring-green-300"
                >
                  Ga naar Login
                </button>
                <button
                  onClick={() => navigate('/companies')}
                  className="w-full px-6 py-3 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-4 focus:ring-gray-300"
                >
                  Terug naar Homepage
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Payment Cancelled Component
const PaymentCancelled = () => {
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const logo = useLogo();

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-white">
      <header className="bg-white shadow-lg border-b-2 border-red-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              {logo ? (
                <img 
                  src={logo} 
                  alt="Send2Maroc Logo" 
                  className="h-12 w-auto object-contain"
                />
              ) : (
                <>
                  <div className="w-12 h-12 bg-gradient-to-r from-red-600 to-red-800 rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-red-900">Send2Maroc</h1>
                    <p className="text-sm text-red-600 font-medium">Betaling Geannuleerd</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="space-y-6">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            
            <div>
              <h2 className="text-3xl font-bold text-red-900 mb-2">Registratie Geannuleerd</h2>
              <p className="text-lg text-gray-600 mb-6">
                U heeft de betaling geannuleerd. Uw bedrijfsregistratie is niet voltooid.
              </p>
            </div>

            <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
              <h3 className="font-semibold text-yellow-800 mb-3">Wat nu?</h3>
              <div className="text-sm text-yellow-700 space-y-2 text-left">
                <p>• U moet de registratie opnieuw beginnen om uw bedrijf te registreren</p>
                <p>• Alle registratiegegevens zijn verloren gegaan</p>
                <p>• Uw registratie is gratis. U betaalt alleen een commissie per succesvolle boeking.</p>
                <p>• Na betaling en goedkeuring kunt u vervoersverzoeken ontvangen</p>
              </div>
            </div>

            <div className="space-y-4">
              <button
                onClick={() => navigate('/register/company')}
                className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300"
              >
                Terug naar Registratie
              </button>
              <button
                onClick={() => navigate('/companies')}
                className="w-full px-6 py-3 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-4 focus:ring-gray-300"
              >
                Terug naar Homepage
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// PWA Install Banner Component
const PWAInstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa-banner-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    // iOS detection
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isIOSDevice && isSafari) {
      setIsIOS(true);
      setShowBanner(true);
      return;
    }

    // Android/Chrome install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setShowBanner(false);
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-banner-dismissed', Date.now().toString());
  };

  if (!showBanner) return null;

  return (
    <div data-testid="pwa-install-banner" className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t-2 border-blue-500 shadow-2xl safe-area-bottom">
      <div className="max-w-lg mx-auto flex items-center gap-4">
        <img src="/android-chrome-192x192.png" alt="Send2Maroc" className="w-12 h-12 rounded-xl shadow" />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-gray-900 text-sm">Send2Maroc App</p>
          <p className="text-xs text-gray-500 truncate">
            {isIOS 
              ? t('iosInstallHint')
              : t('installAppHint')
            }
          </p>
        </div>
        {isIOS ? (
          <button
            onClick={handleDismiss}
            className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700"
          >
            OK
          </button>
        ) : (
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={handleDismiss} className="px-3 py-2 text-xs text-gray-500">
              {t('laterText')}
            </button>
            <button
              data-testid="pwa-install-btn"
              onClick={handleInstall}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('installText')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <LanguageProvider>
          <div className="App">
            <BrowserRouter>
              <PWAInstallBanner />
              <Routes>
                {/* Main application routes */}
                <Route path="/" element={<AppContent />} />
                <Route path="/login" element={<AppContent />} />
                <Route path="/dashboard" element={<AppContent />} />
                <Route path="/reset-password" element={<AppContent />} />
                <Route path="/admin" element={<AppContent />} />
                
                {/* SEO Pages - Direct routes */}
                <Route path="/about" element={<AboutUs />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/services" element={<Services />} />
                <Route path="/coverage" element={<CoverageAreas />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/contact" element={<ContactUs />} />
                <Route path="/blog" element={<BlogNews />} />
                <Route path="/export-rules" element={<ExportRules />} />
                
                {/* Public Tracking Page */}
                <Route path="/track" element={<PublicTrackingPage />} />
                <Route path="/track/:trackingCode" element={<PublicTrackingPage />} />
                
                {/* Public Booking Page - Klant boekt via transporteur link */}
                <Route path="/t/:transporteurId" element={<PublicBookingPage />} />
                
                {/* Invoice Page - Publieke factuur */}
                <Route path="/invoice/:bookingId" element={<InvoicePage />} />
                
                {/* Company Registration Routes */}
                <Route path="/companies" element={<CompanyLanding />} />
                <Route path="/register/company" element={<CompanyRegister />} />
                <Route path="/register/company/payment-success" element={<PaymentSuccess />} />
                <Route path="/register/company/payment-cancelled" element={<PaymentCancelled />} />
                <Route path="/payment-success" element={<BookingPaymentSuccess />} />
              </Routes>
            </BrowserRouter>
          </div>
        </LanguageProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

// Main App Component
const AppContent = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { t } = useLanguage();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-indigo-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  // If user is logged in, show welcome screen
  if (user) {
    return <WelcomeScreen />;
  }

  // If user is not logged in, show appropriate page based on route
  if (location.pathname === '/login') {
    return <Auth />;
  }
  
  if (location.pathname === '/reset-password') {
    return <ResetPassword />;
  }

  // Admin dashboard route (requires admin user)
  if (location.pathname === '/admin') {
    if (user && user.user_type === 'admin') {
      return <AdminDashboard />;
    } else {
      // Redirect non-admin users to login
      return <Auth />;
    }
  }

  // Default to homepage
  return <Homepage />;
};

// SEO Pages Components

// About Us Page - Simplified version
const AboutUs = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <PageHeader />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Over Send2Maroc</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Uw betrouwbare partner voor baggagetransport tussen Europa en Marokko
          </p>
        </div>

        {/* Main Content */}
        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Ons verhaal</h2>
            <p className="text-gray-600 mb-4">
              Send2Maroc is opgericht met een simpele missie: het verbinden van mensen in Europa 
              met betrouwbare transportbedrijven voor het verzenden van persoonlijke bagage naar Marokko.
            </p>
            <p className="text-gray-600 mb-4">
              We begrijpen dat uw bagage meer is dan alleen spullen - het zijn herinneringen, 
              geschenken voor familie, en belangrijke persoonlijke bezittingen. Daarom werken 
              we alleen samen met geverifieerde transportbedrijven die bekend staan om hun 
              betrouwbaarheid en zorgvuldigheid.
            </p>
            <p className="text-gray-600">
              Ons platform maakt het eenvoudig om meerdere offertes te vergelijken en de beste 
              optie te kiezen voor uw specifieke behoeften en budget.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Waarom Send2Maroc?</h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-1">
                  <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Betrouwbare Partners</h4>
                  <p className="text-gray-600">Alleen geverifieerde transportbedrijven</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-1">
                  <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Transparante Prijzen</h4>
                  <p className="text-gray-600">Geen verborgen kosten of verrassingen</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-1">
                  <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">24/7 Ondersteuning</h4>
                  <p className="text-gray-600">Hulp wanneer u het nodig heeft</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center bg-blue-600 text-white py-16 px-8 rounded-lg">
          <h2 className="text-3xl font-bold mb-4">Klaar om te beginnen?</h2>
          <p className="text-xl mb-8 opacity-90">
            Krijg vandaag nog offertes van betrouwbare transportbedrijven
          </p>
          <button 
            onClick={() => window.location.href = '/login'}
            className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Start Nu
          </button>
        </div>
      </main>

      <footer className="bg-gray-900 text-white py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p>© 2025 Send2Maroc. Alle rechten voorbehouden.</p>
        </div>
      </footer>
    </div>
  );
};

// Services Page - Simplified version  
const Services = () => {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <PageHeader />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('ourServices')}</h1>
          <p className="text-xl text-gray-600">{t('reliableTransport')}</p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-bold mb-4">{t('personalBaggage')}</h3>
            <p className="text-gray-600">{t('personalBaggageDesc')}</p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-bold mb-4">{t('businessGoods')}</h3>
            <p className="text-gray-600">{t('businessGoodsDesc')}</p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-bold mb-4">{t('documents')}</h3>
            <p className="text-gray-600">{t('documentsDesc')}</p>
          </div>
        </div>
        
        <div className="text-center mt-12">
          <button 
            onClick={() => window.location.href = '/login'} 
            className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700"
          >
            {t('requestQuote')}
          </button>
        </div>
      </main>
    </div>
  );
};

// Reset Password Component
const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  // Get token from URL params
  const urlParams = new URLSearchParams(location.search);
  const token = urlParams.get('token');
  
  useEffect(() => {
    if (!token) {
      setError('Ongeldige reset link. Vraag een nieuwe aan.');
    }
  }, [token]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    
    if (newPassword !== confirmPassword) {
      setError('Wachtwoorden komen niet overeen.');
      setLoading(false);
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Wachtwoord moet minimaal 6 karakters lang zijn.');
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.post(`${API}/auth/password-reset`, {
        token: token,
        new_password: newPassword
      });
      
      setMessage(response.data.message);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Er is een fout opgetreden.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-indigo-100">
            <svg className="h-6 w-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-1a2 2 0 00-2-2H6a2 2 0 00-2 2v1a2 2 0 002 2zM12 9V7a4 4 0 118 0v2" />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Nieuw Wachtwoord Instellen
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Voer uw nieuwe wachtwoord in
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {message && (
            <div className="rounded-md bg-green-50 p-4 border border-green-200">
              <div className="text-sm text-green-700">{message}</div>
            </div>
          )}
          
          {error && (
            <div className="rounded-md bg-red-50 p-4 border border-red-200">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-2">
                Nieuw Wachtwoord
              </label>
              <input
                id="new-password"
                type="password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            
            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-2">
                Bevestig Nieuw Wachtwoord
              </label>
              <input
                id="confirm-password"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
          
          <div>
            <button
              type="submit"
              disabled={loading || !token}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : null}
              Wachtwoord Wijzigen
            </button>
          </div>
          
          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-sm text-indigo-600 hover:text-indigo-500"
            >
              Terug naar inloggen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Coverage Areas Page
const CoverageAreas = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Helmet>
        <title>Werkgebied - Send2Maroc Transport Dekking Europa naar Marokko</title>
        <meta name="description" content="Bekijk ons uitgebreide werkgebied voor baggagetransport van Nederland, België, Frankrijk naar alle grote steden in Marokko." />
      </Helmet>
      <PageHeader />
      <main className="max-w-7xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold text-center mb-8">Ons Werkgebied</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <h2 className="text-2xl font-bold text-blue-600 mb-4">🇪🇺 Europa - Vertrek</h2>
            <div className="space-y-4">
              <div><h3 className="font-semibold">Nederland</h3><p className="text-gray-600">Amsterdam, Rotterdam, Den Haag, Utrecht, Eindhoven</p></div>
              <div><h3 className="font-semibold">België</h3><p className="text-gray-600">Brussel, Antwerpen, Gent, Brugge, Luik</p></div>
              <div><h3 className="font-semibold">Frankrijk</h3><p className="text-gray-600">Parijs, Marseille, Lyon, Toulouse, Strasbourg</p></div>
            </div>
          </div>
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <h2 className="text-2xl font-bold text-green-600 mb-4">🇲🇦 Marokko - Bestemming</h2>
            <div className="space-y-4">
              <div><h3 className="font-semibold">Grote Steden</h3><p className="text-gray-600">Casablanca, Rabat, Marrakech, Fes, Tanger</p></div>
              <div><h3 className="font-semibold">Kustgebieden</h3><p className="text-gray-600">Agadir, Tetouan, Nador, Al Hoceima</p></div>
              <div><h3 className="font-semibold">Binnenland</h3><p className="text-gray-600">Meknes, Oujda, Beni Mellal, Khouribga</p></div>
            </div>
          </div>
        </div>
        <div className="text-center mt-12">
          <button onClick={() => navigate('/login')} className="bg-blue-600 text-white px-8 py-3 rounded-lg">{t('requestTransport')}</button>
        </div>
      </main>
    </div>
  );
};

// Contact Us Page
const ContactUs = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Helmet>
        <title>Contact - Send2Maroc Klantenservice en Support</title>
        <meta name="description" content="Neem contact op met Send2Maroc voor vragen over baggagetransport naar Marokko. 24/7 klantenservice beschikbaar." />
      </Helmet>
      <PageHeader />
      <main className="max-w-7xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold text-center mb-8">Contact</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <h2 className="text-2xl font-bold mb-6">Neem Contact Op</h2>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">📧</div>
                <div><p className="font-medium">E-mail</p><p className="text-gray-600">info@send2maroc.com</p></div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">📱</div>
                <div><p className="font-medium">WhatsApp</p><p className="text-gray-600">+31 6 1234 5678</p></div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">🕒</div>
                <div><p className="font-medium">Openingstijden</p><p className="text-gray-600">24/7 Beschikbaar</p></div>
              </div>
            </div>
            <div className="mt-8">
              <h3 className="text-lg font-semibold mb-4">Veelgestelde Vragen</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Hoe lang duurt transport naar Marokko?</li>
                <li>• Welke items kan ik verzenden?</li>
                <li>• Hoe volg ik mijn zending?</li>
                <li>• Wat zijn de kosten?</li>
              </ul>
            </div>
          </div>
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <h2 className="text-2xl font-bold mb-6">Heeft u een vraag?</h2>
            <form className="space-y-4">
              <input type="text" placeholder="Uw naam" className="w-full p-3 border rounded-lg" />
              <input type="email" placeholder="E-mailadres" className="w-full p-3 border rounded-lg" />
              <textarea placeholder="Uw bericht" rows="5" className="w-full p-3 border rounded-lg"></textarea>
              <button type="submit" className="w-full bg-blue-600 text-white p-3 rounded-lg hover:bg-blue-700">Versturen</button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

// Terms of Service Page
const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Algemene Voorwaarden - Send2Maroc Transport Diensten</title>
        <meta name="description" content="Lees de volledige algemene voorwaarden van Send2Maroc voor baggagetransport diensten tussen Europa en Marokko." />
      </Helmet>
      <PageHeader />
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold mb-8">Algemene Voorwaarden</h1>
        <div className="prose prose-lg max-w-none">
          <h2>1. Platformrol</h2>
          <p>Send2Maroc functioneert uitsluitend als een <strong>bemiddelingsplatform</strong> dat klanten in Europa verbindt met transportbedrijven die bagagetransportdiensten naar Marokko aanbieden.</p>
          
          <h2>2. Beperking van Aansprakelijkheid</h2>
          <p><strong>BELANGRIJK:</strong> Send2Maroc.com is <strong>NIET VERANTWOORDELIJK</strong> voor:</p>
          <ul>
            <li>Verloren, gestolen of vermiste goederen tijdens transport</li>
            <li>Schade aan goederen tijdens behandeling, laden of transport</li>
            <li>Vertragingen bij ophalen of bezorgen</li>
            <li>Handelingen of nalatigheden van transportbedrijven</li>
          </ul>
          
          <h2>3. Directe Overeenkomst</h2>
          <p>Alle transportovereenkomsten worden direct gesloten tussen klanten en transportbedrijven. Send2Maroc is geen partij bij deze overeenkomsten.</p>
          
          <h2>4. Gebruikersverantwoordelijkheden</h2>
          <p>Gebruikers stemmen ermee in om juiste informatie te verstrekken en goederen op de juiste manier in te pakken voor transport.</p>
          
          <p className="text-sm text-gray-600 mt-8">Laatst bijgewerkt: {new Date().toLocaleDateString()}</p>
        </div>
      </main>
    </div>
  );
};

// Privacy Policy Page (required for Google Play Store publication)
const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Privacybeleid - Send2Maroc</title>
        <meta name="description" content="Privacybeleid van Send2Maroc: hoe wij uw persoonsgegevens verzamelen, gebruiken en beschermen." />
      </Helmet>
      <PageHeader />
      <main className="max-w-4xl mx-auto px-4 py-12" data-testid="privacy-policy-page">
        <h1 className="text-4xl font-bold mb-4">Privacybeleid</h1>
        <p className="text-sm text-gray-600 mb-8">Laatst bijgewerkt: {new Date().toLocaleDateString('nl-NL')}</p>
        <div className="prose prose-lg max-w-none">
          <h2>1. Inleiding</h2>
          <p>Send2Maroc ("wij", "ons") respecteert uw privacy en zet zich in om uw persoonsgegevens te beschermen. Dit privacybeleid legt uit hoe wij uw gegevens verzamelen, gebruiken en beveiligen wanneer u onze website (send2maroc.com) of onze mobiele applicatie gebruikt.</p>

          <h2>2. Welke gegevens wij verzamelen</h2>
          <ul>
            <li><strong>Accountgegevens:</strong> naam, e-mailadres, telefoonnummer en wachtwoord (versleuteld).</li>
            <li><strong>Boekingsgegevens:</strong> ophaal- en afleveradres, beschrijving van bagage en transportvoorkeuren.</li>
            <li><strong>Bedrijfsgegevens (transporteurs):</strong> bedrijfsnaam, land van herkomst en contactgegevens.</li>
            <li><strong>Technische gegevens:</strong> IP-adres, apparaattype, browser en gebruiksstatistieken.</li>
          </ul>

          <h2>3. Hoe wij uw gegevens gebruiken</h2>
          <ul>
            <li>Om uw account aan te maken en te beheren.</li>
            <li>Om transportboekingen tussen klanten en transportbedrijven mogelijk te maken.</li>
            <li>Om u te informeren over de status van uw zending.</li>
            <li>Om onze diensten te verbeteren en te beveiligen.</li>
          </ul>

          <h2>4. Delen van gegevens</h2>
          <p>Wij delen uw gegevens uitsluitend met de transportbedrijven die nodig zijn om uw boeking uit te voeren. Wij verkopen uw persoonsgegevens nooit aan derden. Gegevens kunnen worden gedeeld met dienstverleners (zoals e-mail- en betaaldiensten) voor zover nodig voor de werking van het platform.</p>

          <h2>5. Beveiliging</h2>
          <p>Wij nemen passende technische en organisatorische maatregelen om uw gegevens te beschermen. Wachtwoorden worden versleuteld opgeslagen en de toegang tot persoonsgegevens is beperkt.</p>

          <h2>6. Uw rechten (AVG/GDPR)</h2>
          <p>U heeft het recht op inzage, correctie en verwijdering van uw persoonsgegevens. U kunt uw account verwijderen of een verzoek indienen via de contactgegevens hieronder.</p>

          <h2>7. Bewaartermijn</h2>
          <p>Wij bewaren uw gegevens zolang uw account actief is of zolang nodig is om onze diensten te leveren en te voldoen aan wettelijke verplichtingen.</p>

          <h2>8. Cookies</h2>
          <p>Wij gebruiken essentiële cookies en lokale opslag om u ingelogd te houden (indien gekozen) en de werking van het platform te garanderen.</p>

          <h2>9. Contact</h2>
          <p>Voor vragen over dit privacybeleid of over uw gegevens kunt u contact opnemen via: <a href="mailto:info@send2maroc.com">info@send2maroc.com</a>.</p>
        </div>
      </main>
    </div>
  );
};


// How It Works Page
const HowItWorks = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Helmet>
        <title>Hoe Het Werkt - Send2Maroc Baggagetransport Stap voor Stap</title>
        <meta name="description" content="Ontdek hoe eenvoudig het is om uw bagage te verzenden naar Marokko via Send2Maroc. Stap-voor-stap uitleg van ons transportproces." />
      </Helmet>
      <PageHeader />
      <main className="max-w-7xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold text-center mb-8">Hoe Het Werkt</h1>
        <p className="text-xl text-gray-600 text-center mb-12">Verzend uw bagage naar Marokko in 4 eenvoudige stappen</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
            <h3 className="text-xl font-bold mb-3">Maak een Aanvraag</h3>
            <p className="text-gray-600">Beschrijf uw bagage, ophaal- en afleverlocatie via ons eenvoudige formulier.</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
            <h3 className="text-xl font-bold mb-3">{t('receiveQuotes')}</h3>
            <p className="text-gray-600">Geverifieerde transportbedrijven bieden hun diensten aan met transparante prijzen.</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
            <h3 className="text-xl font-bold mb-3">Kies & Betaal</h3>
            <p className="text-gray-600">Selecteer het beste aanbod en betaal veilig direct aan het transportbedrijf.</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-orange-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">4</div>
            <h3 className="text-xl font-bold mb-3">Track & Ontvang</h3>
            <p className="text-gray-600">Volg uw zending real-time en ontvang bevestiging bij aflevering in Marokko.</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <h2 className="text-2xl font-bold text-center mb-8">{t('whySend2Maroc')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="font-bold mb-2">{t('hundredPercentSafe')}</h3>
              <p className="text-gray-600 text-sm">{t('verifiedCompanies')}</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <h3 className="font-bold mb-2">{t('bestPrices')}</h3>
              <p className="text-gray-600 text-sm">{t('compareQuotes')}</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="font-bold mb-2">{t('fastReliable')}</h3>
              <p className="text-gray-600 text-sm">{t('averageDelivery')}</p>
            </div>
          </div>
        </div>

        <div className="text-center">
          <button onClick={() => navigate('/login')} className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700">
            {t('startNow')}
          </button>
        </div>
      </main>
    </div>
  );
};

// Blog/News Page
const BlogNews = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Helmet>
        <title>Blog & Nieuws - Send2Maroc Transport Tips en Updates</title>
        <meta name="description" content="Laatste nieuws, transport tips en updates van Send2Maroc voor baggagetransport naar Marokko." />
      </Helmet>
      <PageHeader />
      <main className="max-w-7xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold text-center mb-8">Blog & Nieuws</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <article className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold mb-3">Tips voor Veilig Inpakken</h2>
            <p className="text-gray-600 mb-4">Leer hoe u uw bagage het beste kunt inpakken voor transport naar Marokko.</p>
            <span className="text-blue-600 text-sm">Lees meer →</span>
          </article>
          <article className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold mb-3">Nieuwe Routes Beschikbaar</h2>
            <p className="text-gray-600 mb-4">We hebben ons netwerk uitgebreid met nieuwe transportroutes naar Marokko.</p>
            <span className="text-blue-600 text-sm">Lees meer →</span>
          </article>
          <article className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold mb-3">Douane Informatie Update</h2>
            <p className="text-gray-600 mb-4">Nieuwe douaneregels voor persoonlijke items naar Marokko.</p>
            <span className="text-blue-600 text-sm">Lees meer →</span>
          </article>
        </div>
        <div className="text-center mt-12">
          <button onClick={() => navigate('/login')} className="bg-blue-600 text-white px-8 py-3 rounded-lg">Start Transport</button>
        </div>
      </main>
    </div>
  );
};

export default App;